import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../auth/AuthContext';
import { publishNav, registerSelect } from './dealNav';
import DocumentsCard from './DocumentsCard';
import DiscrepanciesCard from './DiscrepanciesCard';
import LenderEmailDrafts from './LenderEmailDrafts';
import SubmissionsCard from './SubmissionsCard';

const STAGES = ['New application','Review','Sent to lenders','Terms received','Processing','Funded','Closed lost'];
const LOAN_TYPE_GROUPS = [
  ['Residential / bridge', ['Fix & Flip','Ground-Up Construction','Bridge','DSCR']],
  ['Land',                 ['Land','Agricultural']],
  ['Commercial',           ['Commercial','Conventional commercial','SBA 7(a)','SBA 504','Working capital / LOC','Portfolio / Blanket']],
  ['Manufactured',         ['MH Chattel','MH Land/Home']],
  ['Other',                ['2nd Mortgage','Gap / Mezzanine','Other']],
];
const LOAN_TYPES = LOAN_TYPE_GROUPS.reduce((a, g) => a.concat(g[1]), []);
const LOAN_TYPES_ALPHA = [...LOAN_TYPES].sort((a,b)=>a.localeCompare(b));
const LOAN_PURPOSES = ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'];

const PURPOSES_BY_LOAN_TYPE = {
  'Fix & Flip':             ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'],
  'Ground-Up Construction': ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'],
  'Bridge':                 ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'],
  'DSCR':                   ['Purchase','Refinance','Cash-out refinance'],
  'Land':                   ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'],
  'Agricultural':           ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'],
  'Commercial':             ['Purchase','Refinance','Cash-out refinance','Bridge'],
  'Conventional commercial':['Purchase','Refinance','Cash-out refinance','Bridge'],
  'SBA 7(a)':               ['Purchase','Refinance','Cash-out refinance'],
  'SBA 504':                ['Purchase','Refinance','Cash-out refinance','Construction'],
  'Working capital / LOC':  ['Purchase','Refinance','Cash-out refinance'],
  '2nd Mortgage':           ['Purchase','Refinance','Cash-out refinance'],
  'Gap / Mezzanine':        ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'],
  'MH Chattel':             ['Purchase','Refinance','Cash-out refinance'],
  'MH Land/Home':           ['Purchase','Refinance','Cash-out refinance','Construction'],
  'Portfolio / Blanket':    ['Purchase','Refinance','Cash-out refinance','Bridge'],
  'Other':                  ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'],
};
const getAvailablePurposes = lt => lt ? (PURPOSES_BY_LOAN_TYPE[lt] || LOAN_PURPOSES) : LOAN_PURPOSES;

const AMORT_TYPES = ['Fully amortizing','Interest only','Balloon'];
const RATE_TYPES = ['Fixed','Adjustable'];
const SUBORDINATE_TYPES = ['None','Seller carryback','Institutional 2nd','Other'];
const FLOOD_ZONES = ['Not in a flood zone','Zone A','Zone AE','Zone V','Zone X','Unknown'];
const PROPERTY_CONDITIONS = ['C1 excellent','C2 good','C3 average','C4 fair','C5 poor','C6 unlivable'];
const LISTED_STATUSES = ['No','Yes, listed now','Was listed, withdrawn'];
const PROP_TYPES = ['2-4 Units','Agricultural','Commercial','Condo / Townhome','Land / Vacant Lot','Manufactured / Mobile','Mixed-Use','Multifamily 5+','Other','SFR'];
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
const ENTITY_TYPES = ['Individual','LLC','Corporation','Partnership','Trust','Other'];
const STORIES = ['1','2','3','4+'];
const OCC_SETUP = ['Owner-occupied','Owner + tenant','Tenant-occupied','Vacant'];
const LOAN_TERMS = ['6 months','12 months','18 months','24 months','30 years','Other'];
const PPP_OPTS = ['None','1 year','2 year','3 year','5 year','Flexible'];
const UTILITIES = ['All in','Partial','None'];
const ACCESS = ['Paved road','Dirt road','Easement','None'];
const AI_STATUS = ['Not started','In progress','Complete','Flagged - needs review'];
const FUNDS_SOURCES = ['Own funds','Gift','Partner','Business','Other'];
const REHAB_FINANCING = ['Lender funds rehab','Borrower self-funds rehab'];
const SREO_PROP_TYPES = ['SFR','Condo','Multi-Family','Mobile / Manufactured','Commercial','Land','Other'];
const SREO_USES = ['Primary residence','Second home','Investment','Buy For'];
const SREO_STATUSES = ['Retained','Pending sale','Listed for sale','Sold'];
const ASSET_TYPES = ['401k','Boat','Bonds','Bridge loan proceeds','Brokerage','Business checking','Business interest','CD','Crypto','Gift funds','Individual development account','Investment','IRA','Life insurance','Money market fund','Mutual fund','Note receivable','Other','Personal checking','Proceeds from sale of non real estate asset','Proceeds from secured loan','Proceeds from unsecured loan','Retirement account','RV','Savings account','Stock options','Stocks','Trust account','Vehicle'];
const FIN_STYLES_MARK = 1;
const ASSET_LIQUID = ['Personal checking','Business checking','Savings account','Money market fund','CD','Brokerage','Investment','Mutual fund','Stocks','Bonds','Gift funds'];
const REFI_TYPES = ['Rate and term','Cash out'];
const TITLE_HELD_BY = ['Individuals','An entity','A trust','Entity and individuals'];
const TITLE_ROLES_INDIV = ['Borrower','Co-borrower','Spouse','Non-borrowing owner','Non-borrowing spouse','Other'];
const TITLE_ROLES_ENTITY = ['Managing member','Member','Manager','Officer','Partner','Shareholder','Guarantor only','Other'];
const TITLE_ROLES_TRUST = ['Trustee','Successor trustee','Grantor / Settlor','Beneficiary','Other'];
const ENTITY_TYPES_FULL = ['LLC','S corporation','C corporation','Limited partnership','LLLP','Sole proprietorship','Other'];
const TRUST_TYPES = ['Revocable living','Irrevocable','Land trust','Other'];
const MEMBER_ROLES = ['Managing member','Member','Officer','Partner'];
const MORTGAGE_TYPES = ['Conventional','FHA','VA','USDA','Other'];
const DISPOSITIONS = ['Omitted','Paid at close','Paid in full'];
const LIABILITY_TYPES = ['2nd','Alimony','Auto','Blanket','Boat','Business loan','Child support','Collections judgments and liens','Credit card','HELOC','Installment','Job related expenses','Lease','Line of credit','Mortgage','Open 30 day','Other','Other expense','Other liability','Personal loan','Revolving','RV','Separate maintenance','Student loan','Tax lien','Taxes'];
const LIAB_SECURED = ['Mortgage','2nd','HELOC','Blanket'];
const LIAB_SIMPLE = ['Alimony','Child support','Separate maintenance','Job related expenses','Other expense'];
const LIAB_LINE = ['HELOC','Line of credit'];
const LIAB_DESC = ['Other','Other liability','Other expense'];
const ACCOUNT_STATUSES = ['Open','Closed','Paid'];
const CURRENT_RATINGS = ['Current','30 days','60 days','90+ days','Collection','Charge off'];
const INCOME_TYPES = ['Base employment income','Military employment income','Accessory unit income','Alimony','Automobile allowance','Boarder income','Capital gains','Child support','Contract basis','Defined contribution plan','Disability','Employment related account','Foster care','Housing choice voucher program','Housing or parsonage','Interest and dividends','Miscellaneous income','Mortgage credit certificate','Mortgage differential payments','Non borrower household income','Notes receivable','Public assistance','Restricted stock','Retirement','Royalty payments','Separate maintenance','Social security','Temporary leave','Tip income','Trust income','Unemployment benefits','VA compensation','Other'];
const INC_EMP = ['Base employment income','Military employment income'];
const incIsPrev = r => INC_EMP.includes(String(r&&r['Type']||''))&&!r['Current']&&!!(String(r&&r['Source']||'').trim()||String(r&&r['End date']||'').trim());
const ADU_TYPES = ['Detached','Attached','Garage conversion','Basement unit'];
const GARAGE_TYPES = ['Attached','Detached'];
const BASEMENT_TYPES = ['Finished','Unfinished'];
const EXIT_STRATEGIES = ['Sell','Hold & Rent','Refi Conv','Refi DSCR','Build & Sell','Build & Hold','1031 Exchange','Other'];
const BANKRUPTCY_TYPES = ['Chapter 7','Chapter 11','Chapter 13'];
const BANKRUPTCY_STATUS = ['Active','Discharged'];
const ASSIGNMENT_FEE_BASIS = ['Flat fee','% of purchase'];
const ASSIGNMENT_FEE_PAID_BY = ['Borrower / buyer','Seller'];
const COLLECTIONS_OPTS = ['None','Some, see notes'];
const DECL_ROWS = [
  ['Decl A occupy primary','A. Will occupy as primary residence',0],
  ['Decl A ownership interest 3yr','Ownership interest in another property in the last 3 years',1],
  ['Decl B relationship with seller','B. Family or business relationship with seller',0],
  ['Decl C borrowing undisclosed money','C. Borrowing undisclosed money',0],
  ['Decl D1 other mortgage before closing','D1. Mortgage on another property before closing',0],
  ['Decl D2 new credit before closing','D2. New credit before closing',0],
  ['Decl E priority lien PACE','E. Priority lien such as PACE',0],
  ['Decl F cosigner or guarantor','F. Co-signer or guarantor on undisclosed debt',0],
  ['Decl G outstanding judgments','G. Outstanding judgments',0],
  ['Decl H delinquent federal debt','H. Delinquent on federal debt',0],
  ['Decl I party to lawsuit','I. Party to a lawsuit',0],
  ['Decl J deed in lieu 7yr','J. Deed in lieu (past 7 years)',0],
  ['Decl K short sale 7yr','K. Short sale (past 7 years)',0],
  ['Decl L foreclosure 7yr','L. Foreclosure (past 7 years)',0],
  ['Decl M bankruptcy 7yr','M. Bankruptcy (past 7 years)',0],
];
const DECL_PROP_TYPES = ['Primary residence','Second home','Investment property','FHA secondary residence'];
const YEARS_OPTS = Array.from({length:41},(_,i)=>String(i));
const MONTHS_OPTS = Array.from({length:12},(_,i)=>String(i));
const DECL_TITLE_HELD = ['By yourself','Jointly with spouse','Jointly with another person'];
const MILITARY_OPTS = ['None','Currently serving on active duty','Currently retired, discharged or separated','Non-activated member of the Reserve or National Guard'];
const LANGUAGE_OPTS = ['English','Spanish','Chinese','Korean','Tagalog','Vietnamese','Other','I do not wish to respond'];
const ETH_OPTS = ['Hispanic or Latino','Not Hispanic or Latino','I do not wish to provide'];
const ETH_SUB = ['Mexican','Puerto Rican','Cuban','Other Hispanic or Latino'];
const RACE_OPTS = ['American Indian or Alaska Native','Asian','Black or African American','Native Hawaiian or Other Pacific Islander','White','I do not wish to provide'];
const RACE_ASIAN = ['Asian Indian','Chinese','Filipino','Japanese','Korean','Vietnamese','Other Asian'];
const RACE_PI = ['Native Hawaiian','Guamanian or Chamorro','Samoan','Other Pacific Islander'];
const SEX_OPTS = ['Female','Male','I do not wish to provide'];
const DEMO_THROUGH = ['Face-to-face interview','Telephone interview','Fax or mail','Email or internet'];
const BAND = {border:'1px solid #DCE7F5',borderRadius:'10px',overflow:'hidden',marginBottom:'14px',background:'#fff'};
const BANDH = {background:'#EAF1FA',padding:'10px 15px',fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#154D97'};
const SUBBOX = {border:'1px solid #DCE7F5',background:'#F7FAFE',borderRadius:'9px',padding:'11px 13px',margin:'9px 0 9px 24px'};
const CORR_TYPES = ['Call','Email','Text','Note'];

// Manufactured / mobile home option sets. These match the Airtable single-select options exactly.
const MH_SECTIONS = ['Single-wide','Double-wide','Triple-wide'];
const MH_PARK_TYPES = ['Lease land','Co op park','Condo park','PUD','Private land','Other'];
const MH_FEE_TYPES = ['Space rent','HOA dues'];
const MH_NEW_USED = ['New','Used'];
const MH_RESIDENCY_AGES = ['All ages','55+'];
const MH_SITE_PLACEMENTS = ['Lease land','Co op park','Condo park','PUD','Private land','Other'];
const MH_REFI_TYPES = ['Rate/term refinance','Cash-out refinance'];
const MH_OCCUPANCY = ['Primary residence','Second home','Investment','Buy For'];

const SITUATIONS = ['Auction / courthouse-step','REO / bank-owned','Foreclosure bailout','Short sale','Tax-lien / tax-deed','Probate / estate / inherited','Trust-held (rev/irrev)','Partner buyout','Cash-out business purpose','Delayed financing','Seller 2nds / land contracts','Wholetail','Wholesale','Condo conversion'];
const ASSET_CLASSES = ['Cannabis','Senior/assisted living','Hotels/hospitality','Self-storage','Mobile/manufactured home parks','Religious / faith-based','Gas station / C-store','Automotive (car wash / repair / dealership)','Restaurant / bar / nightlife','Daycare / education','Industrial/warehouse','Retail','Office','Medical/healthcare','Special-purpose','Non-warrantable condos','Short-term/Airbnb','Condotels','Student housing','RV parks','Cold storage','Distribution center','Flex space','Wineries','Marinas','Campgrounds','Funeral homes','Event venues','Parking structures','Gym / fitness'];

const ASSET_CLASS_GROUPS = [
  ['Core commercial',['Industrial/warehouse','Retail','Office','Flex space','Distribution center','Cold storage','Self-storage']],
  ['Hospitality and leisure',['Hotels/hospitality','Short-term/Airbnb','Restaurant / bar / nightlife','Campgrounds','RV parks','Marinas','Wineries','Event venues','Gym / fitness']],
  ['Residential specialty',['Non-warrantable condos','Condotels','Student housing','Senior/assisted living','Mobile/manufactured home parks']],
  ['Care and education',['Medical/healthcare','Daycare / education','Funeral homes','Religious / faith-based']],
  ['Special purpose',['Cannabis','Gas station / C-store','Automotive (car wash / repair / dealership)','Parking structures','Special-purpose']],
];
const SITUATION_GROUPS = [
  ['Distressed or forced sale',['Auction / courthouse-step','REO / bank-owned','Foreclosure bailout','Short sale','Tax-lien / tax-deed']],
  ['Title and ownership',['Probate / estate / inherited','Trust-held (rev/irrev)','Partner buyout','Condo conversion']],
  ['Investor strategy',['Cash-out business purpose','Delayed financing','Seller 2nds / land contracts','Wholetail','Wholesale']],
];

const DEAL_BORROWER_FIELDS = ['American Indian tribe','Asian other description','Asian subtype','Citizenship','Credit pull authorized','Decl A occupy primary','Decl A ownership interest 3yr','Decl A property type owned','Decl A title held','Decl B relationship with seller','Decl C amount','Decl C borrowing undisclosed money','Decl D1 other mortgage before closing','Decl D2 new credit before closing','Decl E priority lien PACE','Decl F cosigner or guarantor','Decl G outstanding judgments','Decl H delinquent federal debt','Decl I party to lawsuit','Decl J deed in lieu 7yr','Decl K short sale 7yr','Decl L foreclosure 7yr','Decl M bankruptcy 7yr','Decl M bankruptcy type','Declarations notes','Demographic info provided through','Dependent ages','Dependents count','Est credit score','Ethnicity','Ethnicity by visual observation','FICO score','Hispanic origin','Hispanic origin other','Language other','Language preference','Mailing address different','Mailing city','Mailing county','Mailing state','Mailing street','Mailing zip','Marital status','Military service','Monthly rent','Months at current address','Own or rent','Pacific Islander other description','Pacific Islander subtype','Previous addresses','Projected expiration of service','Race','Race by visual observation','Same as subject property','Sex','Sex by visual observation','Surviving spouse','Years at current address','eConsent authorized'];
const BORROWER_FIELDS = ['American Indian tribe','Asian other description','Asian subtype','Citizenship','City','Company / entity','County','Credit pull authorized','Current unit','Date of birth','Decl A occupy primary','Decl A ownership interest 3yr','Decl A property type owned','Decl A title held','Decl B relationship with seller','Decl C amount','Decl C borrowing undisclosed money','Decl D1 other mortgage before closing','Decl D2 new credit before closing','Decl E priority lien PACE','Decl F cosigner or guarantor','Decl G outstanding judgments','Decl H delinquent federal debt','Decl I party to lawsuit','Decl J deed in lieu 7yr','Decl K short sale 7yr','Decl L foreclosure 7yr','Decl M bankruptcy 7yr','Decl M bankruptcy type','Declarations notes','Demographic info provided through','Dependent ages','Dependents count','Email','Entity type','Est credit score','Ethnicity','First name','Hispanic origin','Hispanic origin other','Home phone','Home state','Language other','Language preference','Last name','Mailing address different','Mailing city','Mailing county','Mailing state','Mailing street','Mailing zip','Marital status','Middle name','Military service','Monthly rent','Months at current address','Own or rent','Pacific Islander other description','Pacific Islander subtype','Phone','Previous addresses','Projected expiration of service','Race','SSN','Same as subject property','Sex','Street address','Suffix','Surviving spouse','Work phone','Work phone ext','Years at current address','ZIP','eConsent authorized','Ethnicity by visual observation','Race by visual observation','Sex by visual observation'];
const CONTACT_BOOLEANS = ['Same as subject property','eConsent authorized','Credit pull authorized','Mailing address different','Surviving spouse'];

const CONTACT_TYPES = ['Appraiser','Attorney','Borrower','Broker','Buyers Agent','CPA','Capital Partner','Contractor','Escrow Officer','GAP Funder','HOA','Insurance Agent','JV Partner','Listing Agent','Loan Officer','Notary','Other','PM Lender','Park Manager','Park Owner','Processor','Property Manager','Referral Partner','Seller','Title Officer','Wholesaler'];
const PAYOUT_ROLES = ['Brokerage split','Loan officer','Referral partner','Sub broker','Processing','Other'];
const BROKERAGES = ['Crafted Capital LLC','MobileHomeLoans.com'];
const FEE_PAID_BY = ['Lender paid','Borrower paid'];
const LEAD_SOURCES = ['Company generated','Self generated'];

const DEAL_CONTACT_GROUPS = [
  ['Property',  ['Park manager','Park owner','HOA','Appraiser','Insurance agent','Contractor','Property manager']],
  ['Primary',   ['Broker','Listing agent',"Buyer's agent",'Seller','Wholesaler']],
  ['Closing',   ['Title officer','Escrow officer','Attorney','Notary']],
  ['Financial', ['Capital partner','JV partner','GAP funder','CPA']],
];
const DEAL_CONTACT_ROLES = DEAL_CONTACT_GROUPS.reduce((a,g)=>a.concat(g[1]),[]);
const MH_DEFAULT_ROLES = ['Park manager','Park owner'];

const TX_PARTY_GROUPS = {
  'Primary':   ['Broker','Listing agent',"Buyer's agent",'Seller','Wholesaler'],
  'Closing':   ['Title officer','Escrow officer','Attorney','Notary'],
  'Financial': ['Capital partner','JV partner','GAP funder','CPA'],
  'Property':  ['Appraiser','Insurance agent','Contractor','Property manager'],
};
const ALL_TX_ROLES = Object.values(TX_PARTY_GROUPS).flat();

const DEAL_ROLE_TO_CONTACT_ROLE = {
  'Broker':            'Broker',
  'Listing agent':     'Listing Agent',
  "Buyer's agent":     'Buyers Agent',
  'Seller':            'Seller',
  'Wholesaler':        'Wholesaler',
  'Title officer':     'Title Officer',
  'Escrow officer':    'Escrow Officer',
  'Attorney':          'Attorney',
  'Notary':            'Notary',
  'Capital partner':   'Capital Partner',
  'JV partner':        'JV Partner',
  'GAP funder':        'GAP Funder',
  'CPA':               'CPA',
  'Appraiser':         'Appraiser',
  'Insurance agent':   'Insurance Agent',
  'Contractor':        'Contractor',
  'Property manager':  'Property Manager',
};

const INCOME_LOAN_TYPES = ['DSCR','Commercial','Conventional commercial','SBA 7(a)','SBA 504','Portfolio / Blanket'];
const NOI_LOAN_TYPES = ['Commercial','Conventional commercial','SBA 7(a)','SBA 504','Portfolio / Blanket'];
const REHAB_TOGGLE_LOAN_TYPES = ['Bridge','Commercial','Conventional commercial'];

const PT_FIELDS = {
  'SFR':                       ['Bedrooms','Bathrooms','Lot size (sqft)','Sqft','Stories','_garage','_adu','_basement'],
  'Condo / Townhome':          ['Bedrooms','Bathrooms','Sqft','Floor level','HOA'],
  '2-4 Units':                 ['Number of units','Sqft','Stories','Total beds','Total baths','Occupied units','Current monthly rent','Lease terms','Annual NOI'],
  'Multifamily 5+':            ['Number of units','Sqft','Stories','Total beds','Total baths','Occupied units','Current monthly rent','Lease terms','Annual NOI'],
  'Mixed-Use':                 ['Number of units','Stories','Total beds','Total baths','Occupied units','Current monthly rent','Commercial sqft','Residential sqft','Commercial %','Residential tenants','Commercial tenants','Lease terms','Annual NOI'],
  'Commercial':                ['Sqft','Stories','Floor level','Tenant count','Current monthly rent','Annual NOI','Lease terms','HOA'],
  'Land / Vacant Lot':         ['Land acreage','Land zoning','Land entitled','Utilities','Access / ingress'],
  'Agricultural':              ['Land acreage','Land zoning','Land entitled','Utilities','Access / ingress'],
  'Manufactured / Mobile':     ['_mh'],
  'Other':                     [],
};

const LIEN_POSITIONS = ['1st lien','2nd lien'];
const RECOURSE_PREF = ['','Recourse OK','Non-recourse required','Either'];
const APPRAISAL_PREF = ['','Lender required OK','No appraisal needed','Either'];
function defaultLienPosition(loanType){
  return (loanType === '2nd Mortgage' || loanType === 'Gap / Mezzanine') ? '2nd lien' : '1st lien';
}

function getLoanFieldSpec(loanType, loanPurpose, includesRehab) {
  const show = {};
  const required = {};
  const labels = {};
  if (!loanType || !loanPurpose) return { show, required, labels };

  if (loanType === 'Working capital / LOC') {
    show.loanAmount = true; required.loanAmount = true;
    return { show, required, labels };
  }

  if (loanType === 'SBA 504') {
    show.purchasePrice = true; required.purchasePrice = true;
    labels.purchasePrice = 'Purchase price (land + building)';
    if (loanPurpose === 'Construction') {
      show.constructionBudget = true; required.constructionBudget = true;
      labels.purchasePrice = 'Land purchase price';
    }
    if (loanPurpose === 'Refinance' || loanPurpose === 'Cash-out refinance') {
      show.asIsValue = true; required.asIsValue = true;
      show.existingLoanBalance = true; required.existingLoanBalance = true;
      if (loanPurpose === 'Cash-out refinance') { show.cashOutAmount = true; }
    }
    show.rehabBudget = true; labels.rehabBudget = 'Improvements / build-out';
    show.otherProjectCosts = true;
    show.loanAmount = true; required.loanAmount = true; labels.loanAmount = 'Bank 1st lien (the piece we place)';
    show.cdcPortion = true;
    show.downPayment = true; labels.downPayment = 'Borrower injection';
    return { show, required, labels };
  }

  if (loanPurpose === 'Purchase') {
    show.purchasePrice = true; required.purchasePrice = true;
    show.downPayment = true;
  }
  if (loanPurpose === 'Refinance') {
    show.asIsValue = true; required.asIsValue = true;
    show.existingLoanBalance = true; required.existingLoanBalance = true;
  }
  if (loanPurpose === 'Cash-out refinance') {
    show.asIsValue = true; required.asIsValue = true;
    show.existingLoanBalance = true; required.existingLoanBalance = true;
    show.cashOutAmount = true; required.cashOutAmount = true;
  }
  if (loanPurpose === 'Construction') {
    show.purchasePrice = true; labels.purchasePrice = 'Land / Property purchase price';
    show.downPayment = true;
    show.asIsValue = true; labels.asIsValue = 'Current land / project value';
    show.existingLoanBalance = true;
    show.constructionBudget = true; required.constructionBudget = true;
    show.arv = true; labels.arv = 'Completed value / ARV'; required.arv = true;
  }
  if (loanPurpose === 'Bridge') {
    show.purchasePrice = true; labels.purchasePrice = 'Purchase price (if applicable)';
    show.downPayment = true;
    show.asIsValue = true; labels.asIsValue = 'Current value (if refi scenario)';
    show.existingLoanBalance = true;
  }

  if (loanType === 'Fix & Flip' && loanPurpose === 'Construction') {
    delete show.purchasePrice; delete required.purchasePrice;
    delete show.downPayment;
    delete show.asIsValue; delete required.asIsValue;
    delete show.existingLoanBalance; delete required.existingLoanBalance;
    delete show.constructionBudget; delete required.constructionBudget;
    show.asIsValue = true; labels.asIsValue = 'Current value'; required.asIsValue = true;
    show.rehabBudget = true; labels.rehabBudget = 'Remaining rehab budget'; required.rehabBudget = true;
    show.arv = true; labels.arv = 'ARV'; required.arv = true;
  }

  if (loanType === 'Ground-Up Construction' && loanPurpose === 'Construction') {
    labels.purchasePrice = 'Land purchase price';
    labels.asIsValue = 'Current land value';
  }
  if (loanType === 'Ground-Up Construction' && (loanPurpose === 'Refinance' || loanPurpose === 'Cash-out refinance')) {
    labels.asIsValue = 'Current land / project value';
    show.constructionBudget = true; labels.constructionBudget = 'Remaining construction budget'; required.constructionBudget = true;
    show.arv = true; labels.arv = 'Completed value / ARV'; required.arv = true;
  }
  if (loanType === 'Ground-Up Construction' && loanPurpose === 'Purchase') {
    labels.purchasePrice = 'Land purchase price';
    show.constructionBudget = true; required.constructionBudget = true;
    show.arv = true; labels.arv = 'Completed value / ARV'; required.arv = true;
  }
  if (loanType === 'Ground-Up Construction' && loanPurpose === 'Bridge') {
    labels.asIsValue = 'Current project value';
    show.constructionBudget = true; labels.constructionBudget = 'Remaining construction budget'; required.constructionBudget = true;
    show.arv = true; labels.arv = 'Completed value / ARV'; required.arv = true;
  }

  if (loanType === 'Fix & Flip' && loanPurpose !== 'Construction') {
    show.rehabBudget = true; required.rehabBudget = true;
    show.arv = true; required.arv = true;
  }

  if (REHAB_TOGGLE_LOAN_TYPES.includes(loanType) && includesRehab) {
    show.rehabBudget = true;
    show.arv = true;
  }

  if (loanType === 'Bridge' && loanPurpose === 'Bridge') {
    delete show.purchasePrice;
    delete show.downPayment;
    labels.asIsValue = 'Current value';
    required.asIsValue = true;
  }

  if ((loanType === 'Land' || loanType === 'Agricultural') && loanPurpose === 'Construction') {
    show.constructionBudget = true; required.constructionBudget = true;
    show.arv = true; labels.arv = 'Completed value / ARV'; required.arv = true;
  }

  if (INCOME_LOAN_TYPES.includes(loanType)) {
    show.currentMonthlyRent = true;
    show.projectedMonthlyRent = true;
    show.vacancyRate = true;
    show.operatingExpenseRatio = true;
  }
  if (NOI_LOAN_TYPES.includes(loanType)) {
    show.annualNOI = true;
  }

  show.loanAmount = true; required.loanAmount = true;
  return { show, required, labels };
}

const FIELD_TO_AIRTABLE = {
  purchasePrice:'Purchase price', downPayment:'Down payment', asIsValue:'As-is value',
  existingLoanBalance:'Existing loan balance', cashOutAmount:'Cash-out amount',
  rehabBudget:'Rehab budget', constructionBudget:'Construction budget',
  arv:'ARV', loanAmount:'Loan amount',
  currentMonthlyRent:'Current monthly rent', projectedMonthlyRent:'Projected monthly rent',
  annualNOI:'Annual NOI', vacancyRate:'Vacancy rate', operatingExpenseRatio:'Operating expense ratio',
  cdcPortion:'CDC portion', cdcRate:'CDC rate', cdcTerm:'CDC term', otherProjectCosts:'Other project costs',
};
const CHECKKEY_TO_AIRTABLE = { purchase:'Purchase price', asis:'As-is value', arv:'ARV', fico:'FICO score' };
const DEFAULT_LABELS = {
  purchasePrice:'Purchase price', downPayment:'Down payment', asIsValue:'As-is value',
  existingLoanBalance:'Existing loan balance', cashOutAmount:'Cash-out amount',
  rehabBudget:'Rehab budget', constructionBudget:'Construction budget',
  arv:'ARV', loanAmount:'Loan amount',
  currentMonthlyRent:'Current monthly rent', projectedMonthlyRent:'Projected / market monthly rent',
  annualNOI:'Annual NOI', vacancyRate:'Vacancy rate (%)', operatingExpenseRatio:'Operating expense ratio (%)',
  cdcPortion:'CDC / SBA gross debenture (2nd)', cdcRate:'CDC rate (%)', cdcTerm:'CDC debenture term', otherProjectCosts:'Other costs (closing, fees, contingency)',
};
const FIELD_TYPES = {
  purchasePrice:'currency', downPayment:'currency', asIsValue:'currency',
  existingLoanBalance:'currency', cashOutAmount:'currency',
  rehabBudget:'currency', constructionBudget:'currency',
  arv:'currency', loanAmount:'currency',
  currentMonthlyRent:'currency', projectedMonthlyRent:'currency',
  annualNOI:'currency', vacancyRate:'percent', operatingExpenseRatio:'percent',
  cdcPortion:'currency', cdcRate:'percent', otherProjectCosts:'currency',
};
const LOAN_FIELD_ORDER = ['purchasePrice','asIsValue','existingLoanBalance','downPayment','rehabBudget','constructionBudget','arv','cashOutAmount','loanAmount'];
const SBA_FIELD_ORDER = ['purchasePrice','downPayment','rehabBudget','otherProjectCosts','asIsValue','existingLoanBalance','cashOutAmount','constructionBudget','loanAmount','cdcPortion'];
const SPEC_FIELD_OPTIONS = { cdcTerm: ['10 years','20 years','25 years'] };
const SBA_BANK_TERMS = ['10 years','15 years','20 years','25 years'];
const INCOME_FIELD_ORDER = ['currentMonthlyRent','projectedMonthlyRent','annualNOI'];
const SECONDARY_INCOME_ORDER = ['vacancyRate','operatingExpenseRatio'];

// Deal name convention, locked 8/15/26: "Borrower - Street address".
// Two segments, short hyphen, and any segment we do not have yet is left out
// rather than filled with a placeholder. The same string names the Airtable
// record, the Drive folder and the Gmail label, so it is built in one place.
function buildDealName(borrowerName, propertyAddress) {
  return [borrowerName, propertyAddress]
    .map(v => String(v == null ? '' : v).replace(/\s+/g, ' ').trim())
    .filter(v => v !== '')
    .join(' - ');
}

// A subordinate lien exists only when there is a real amount. A blank, a zero
// or a "0" must not tick the box open on load.
function hasSubAmount(formObj) {
  const raw = String((formObj && formObj['Subordinate amount']) || '').replace(/[^0-9.]/g, '');
  return Number(raw) > 0;
}

const SC = {
  'New application':{bg:'#E4EDF8',color:'#1A5FA8'},
  'Review':{bg:'#E4EDF8',color:'#1A5FA8'},'Sent to lenders':{bg:'#E4EDF8',color:'#1A5FA8'},
  'Terms received':{bg:'#E4EBF0',color:'#4A6885'},'Processing':{bg:'#E4EDF8',color:'#1A5FA8'},
  'Funded':{bg:'#E4EBF0',color:'#4A6885'},'Closed lost':{bg:'#f2f5fa',color:'#7a8aa0'},
};
const stagePill = st => { const c=SC[st]||{bg:'#f2f5fa',color:'#7a8aa0'}; return {background:c.bg,color:c.color,fontSize:'12px',fontWeight:500,padding:'4px 11px',borderRadius:'20px',whiteSpace:'nowrap'}; };

const POP="'Inter',sans-serif";

const s = {
  backBtn:{display:'flex',alignItems:'center',gap:'6px',background:'none',border:'none',color:'#2B72C2',fontSize:'13px',fontWeight:500,cursor:'pointer',marginBottom:'14px',padding:0,fontFamily:'inherit'},
  header:{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'16px',flexWrap:'wrap',marginBottom:0},
  dealTitle:{fontFamily:POP,fontSize:'15px',fontWeight:600,color:'#2E4763'},
  dealHeadCard:{background:'#fff',border:'1px solid #C9D9EE',borderRadius:'14px',overflow:'hidden',boxShadow:'0 1px 2px rgba(20,60,120,0.04)',marginBottom:'14px'},
  dealHeadBar:{background:'#D0E0F5',borderBottom:'1px solid #BCD2EC',padding:'0 18px',minHeight:'40px',display:'flex',alignItems:'center',gap:'10px'},
  dealHeadBarLbl:{fontFamily:POP,fontSize:'11.5px',fontWeight:600,color:'#154D97',letterSpacing:'0.06em',textTransform:'uppercase'},
  dealHeadBody:{padding:'16px 18px'},
  dealSub:{fontSize:'12px',color:'#8a9ab5',marginTop:'2px'},
  stageSelect:{padding:'8px 12px',border:'1px solid #C9D9EE',borderRadius:'9px',fontSize:'13px',color:'#1a2b40',fontFamily:'inherit',outline:'none',cursor:'pointer',background:'#fff',fontWeight:600},
  dealMeta:{display:'flex',gap:'7px',flexWrap:'wrap',alignItems:'center'},
  ltBadge:{display:'inline-block',fontSize:'10px',fontWeight:600,padding:'2px 7px',borderRadius:'4px',background:'#D6E8F8',color:'#1A5FA8'},
  ptBadge:{display:'inline-block',fontSize:'10px',fontWeight:600,padding:'2px 7px',borderRadius:'4px',background:'#E4EDF8',color:'#4A88C8'},
  ooBadge:{display:'inline-block',fontSize:'10px',fontWeight:600,padding:'2px 7px',borderRadius:'4px',background:'#FBF7F3',color:'#B08968'},
  editBtn:{padding:'7px 16px',background:'transparent',color:'#2B72C2',border:'1.5px solid #B8D4F0',borderRadius:'7px',fontSize:'12px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'5px'},
  saveBtn:{padding:'8px 20px',background:'#2B72C2',color:'#fff',border:'none',borderRadius:'7px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'},
  cancelBtn:{padding:'8px 16px',background:'transparent',color:'#8a9ab5',border:'1.5px solid #B8D4F0',borderRadius:'7px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'},
  savedMsg:{fontSize:'12px',color:'#4A6885'},
  errorMsg:{fontSize:'12px',color:'#c0392b'},
  card:{background:'#fff',borderRadius:'14px',border:'1px solid #C9D9EE',overflow:'hidden',marginBottom:'14px',boxShadow:'0 1px 2px rgba(20,60,120,0.04)'},
  cardHead:{background:'#D0E0F5',borderBottom:'1px solid #BCD2EC',padding:'0 20px',minHeight:'40px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'7px'},
  cardTitle:{color:'#154D97',fontSize:'12px',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',fontFamily:"'Inter',sans-serif"},
  collapseBtn:{background:'none',border:'none',color:'#4A7FBF',cursor:'pointer',fontSize:'14px',padding:0},
  cardBody:{padding:'16px'},
  fieldGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))',gap:'14px'},
  fieldGrid3:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:'14px'},
  fieldGrid4:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))',gap:'14px'},
  fieldGrid2:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'},
  field:{display:'flex',flexDirection:'column',gap:'3px'},
  fieldLabel:{fontSize:'10px',fontWeight:700,color:'#4A88C8',letterSpacing:'0.07em',textTransform:'uppercase'},
  fieldLabelCalc:{fontSize:'10px',fontWeight:700,color:'#7AADE0',letterSpacing:'0.07em',textTransform:'uppercase'},
  input:{padding:'7px 10px',border:'1.5px solid #B8D4F0',borderRadius:'6px',fontSize:'13px',color:'#0D2545',fontFamily:'inherit',outline:'none',width:'100%',boxSizing:'border-box'},
  select:{padding:'7px 10px',border:'1.5px solid #B8D4F0',borderRadius:'6px',fontSize:'13px',color:'#0D2545',fontFamily:'inherit',outline:'none',width:'100%',cursor:'pointer',background:'#fff',boxSizing:'border-box'},
  notesArea:{width:'100%',padding:'10px 12px',border:'1px solid #C9D9EE',borderRadius:'9px',fontSize:'13px',color:'#2E4763',fontFamily:'inherit',resize:'vertical',minHeight:'100px',outline:'none',boxSizing:'border-box'},
  summaryArea:{width:'100%',padding:'10px 12px',border:'1.5px solid #B8D4F0',borderRadius:'7px',fontSize:'13px',color:'#0D2545',fontFamily:'inherit',resize:'vertical',minHeight:'120px',outline:'none',boxSizing:'border-box'},
  postRehabArea:{width:'100%',padding:'10px 12px',border:'1.5px solid #B8D4F0',borderRadius:'7px',fontSize:'13px',color:'#0D2545',fontFamily:'inherit',resize:'vertical',minHeight:'80px',outline:'none',boxSizing:'border-box'},
  editBanner:{background:'#E8F0FA',border:'1px solid #B8D4F0',borderRadius:'8px',padding:'10px 14px',marginTop:'12px',fontSize:'12px',color:'#2B72C2',display:'flex',alignItems:'center',gap:'7px'},
  saveState:(dirty)=>({fontSize:'12px',fontWeight:600,color:dirty?'#154D97':'#8a9ab5'}),
  saveBtnD:(dirty)=>({padding:'8px 18px',background:dirty?'#2B72C2':'#E4EDF8',color:dirty?'#fff':'#A8B8D0',border:'none',borderRadius:'7px',fontSize:'13px',fontWeight:700,cursor:dirty?'pointer':'default',fontFamily:'inherit'}),
  syncNote:{background:'#E4EDF8',border:'1px solid #B8D4F0',borderRadius:'6px',padding:'8px 12px',marginBottom:'14px',fontSize:'11px',color:'#4A88C8',display:'flex',alignItems:'center',gap:'6px'},
  divider:{borderTop:'1px solid #E4EDF8',margin:'14px 0'},
  subLabel:{fontSize:'10px',fontWeight:700,color:'#4A88C8',letterSpacing:'0.07em',textTransform:'uppercase',marginBottom:'10px'},
  subHint:{fontSize:'11px',color:'#8A94A6',marginBottom:'8px',fontStyle:'italic'},
  aiBtn:{display:'flex',alignItems:'center',gap:'6px',background:'transparent',border:'1.5px solid #B8D4F0',color:'#ccc',borderRadius:'7px',padding:'7px 14px',fontSize:'12px',fontWeight:600,cursor:'not-allowed',fontFamily:'inherit',marginTop:'10px'},
  sb:val=>({fontSize:'11px',fontWeight:600,padding:'2px 8px',borderRadius:'4px',background:val?'#E4EBF0':'#F1EFE8',color:val?'#4A6885':'#444441'}),
  tableWrap:{overflowX:'auto',marginTop:'4px'},
  table:{width:'100%',borderCollapse:'collapse',fontSize:'12px'},
  th:{background:'#E4EDF8',color:'#4A88C8',fontWeight:700,padding:'7px 10px',textAlign:'left',fontSize:'10px',letterSpacing:'0.06em',textTransform:'uppercase',borderBottom:'1px solid #B8D4F0',whiteSpace:'nowrap'},
  td:{padding:'7px 10px',borderBottom:'1px solid #E4EDF8',color:'#0D2545',verticalAlign:'middle'},
  tdFoot:{padding:'7px 10px',fontWeight:700,color:'#0D3565',background:'#E4EDF8',verticalAlign:'middle'},
  tableInput:{padding:'5px 7px',border:'1.5px solid #B8D4F0',borderRadius:'5px',fontSize:'12px',color:'#0D2545',fontFamily:'inherit',outline:'none',width:'100%',boxSizing:'border-box'},
  tableInputWide:{padding:'5px 7px',border:'1.5px solid #B8D4F0',borderRadius:'5px',fontSize:'12px',color:'#0D2545',fontFamily:'inherit',outline:'none',width:'100%',boxSizing:'border-box',minWidth:'180px'},
  tableSelect:{padding:'5px 7px',border:'1.5px solid #B8D4F0',borderRadius:'5px',fontSize:'12px',color:'#0D2545',fontFamily:'inherit',outline:'none',width:'100%',cursor:'pointer',background:'#fff',boxSizing:'border-box',minWidth:'100px'},
  deleteRowBtn:{background:'transparent',border:'none',color:'#ccc',cursor:'pointer',padding:'3px',fontFamily:'inherit',display:'flex',alignItems:'center'},
  addRowBtn:{display:'flex',alignItems:'center',gap:'6px',background:'transparent',border:'1px dashed #C5D5EA',color:'#1C4E8A',borderRadius:'9px',padding:'7px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',marginTop:'10px'},
  addCoBtn:{background:'transparent',border:'1.5px dashed #B8D4F0',color:'#4A88C8',borderRadius:'7px',padding:'7px 14px',fontSize:'12px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'5px',marginTop:'4px'},
  metricInput:{padding:'0',border:'none',borderRadius:'0',fontSize:'14px',color:'#1C4E8A',fontFamily:'inherit',outline:'none',width:'100%',boxSizing:'border-box',fontWeight:600,background:'transparent'},
  linkedPill:{display:'inline-flex',alignItems:'center',gap:'5px',background:'#D6E8F8',color:'#1A5FA8',fontSize:'11px',fontWeight:600,padding:'3px 9px',borderRadius:'4px'},
  searchWrap:{position:'relative'},
  searchResults:{position:'absolute',width:'100%',border:'1px solid #B8D4F0',borderRadius:'7px',background:'#fff',marginTop:'4px',maxHeight:'240px',overflowY:'auto',zIndex:100,boxShadow:'0 4px 12px rgba(0,0,0,0.08)'},
  searchItem:{padding:'8px 12px',cursor:'pointer',fontSize:'12px',color:'#0D2545',borderBottom:'0.5px solid #E4EDF8'},
  createNewItem:{padding:'10px 12px',cursor:'pointer',fontSize:'12px',fontWeight:600,color:'#2B72C2',background:'#E4EDF8',borderTop:'1px solid #B8D4F0',display:'flex',alignItems:'center',gap:'6px'},
  emptyText:{fontSize:'13px',color:'#8a9ab5'},
  checkboxRow:{display:'flex',width:'fit-content',alignItems:'center',gap:'7px',fontSize:'13px',color:'#0D2545',cursor:'pointer',userSelect:'none',marginBottom:'8px'},
  indented:{marginLeft:'20px',marginTop:'8px',display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:'10px'},
  autoCalc:{fontSize:'13px',fontWeight:600,color:'#2B72C2',fontStyle:'italic'},
  rehabToggleBox:{background:'#E4EDF8',border:'1px solid #B8D4F0',borderRadius:'7px',padding:'10px 14px',marginTop:'10px',marginBottom:'4px',display:'flex',alignItems:'center',gap:'10px'},
  partyRow:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 12px',background:'#F5F9FE',border:'0.5px solid #B8D4F0',borderRadius:'6px',marginBottom:'6px',gap:'10px'},
  partyRoleLabel:{fontSize:'10px',fontWeight:700,color:'#4A88C8',letterSpacing:'0.06em',textTransform:'uppercase',minWidth:'120px',flexShrink:0},
  partyName:{fontSize:'13px',color:'#0D2545',fontWeight:500,flex:1},
  partyRemoveBtn:{background:'transparent',border:'none',color:'#8a9ab5',cursor:'pointer',padding:'4px',fontFamily:'inherit',display:'flex',alignItems:'center',fontSize:'14px'},
  partyChangeBtn:{background:'transparent',border:'none',color:'#2B72C2',cursor:'pointer',padding:'4px 8px',fontFamily:'inherit',fontSize:'11px',fontWeight:600,textDecoration:'underline'},
  partyAddBox:{marginTop:'12px',padding:'14px',background:'#F7FAFD',border:'1px solid #E1E8EF',borderRadius:'11px'},
  partyAddBoxTitle:{fontSize:'10px',fontWeight:700,color:'#1C4E8A',letterSpacing:'0.07em',textTransform:'uppercase',marginBottom:'10px'},
  partyAddRow:{display:'grid',gridTemplateColumns:'180px 1fr',gap:'10px',alignItems:'center'},
  partyAddCancel:{background:'transparent',border:'none',color:'#8a9ab5',cursor:'pointer',fontSize:'11px',fontWeight:600,textDecoration:'underline',padding:'4px',marginTop:'8px'},
  emptyParties:{fontSize:'13px',color:'#8A94A6',fontStyle:'italic',padding:'10px 0'},
  createForm:{marginTop:'8px',padding:'12px',background:'#fff',border:'1px solid #B8D4F0',borderRadius:'7px'},
  createFormTitle:{fontSize:'10px',fontWeight:700,color:'#2B72C2',letterSpacing:'0.07em',textTransform:'uppercase',marginBottom:'10px',display:'flex',alignItems:'center',gap:'5px'},
  createFormGrid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'8px'},
  createFormActions:{display:'flex',alignItems:'center',gap:'8px',marginTop:'10px'},
  createSaveBtn:{padding:'7px 15px',background:'#154D97',color:'#fff',border:'none',borderRadius:'8px',fontSize:'12px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'},
  createCancelBtn:{padding:'7px 13px',background:'transparent',color:'#8A94A6',border:'1px solid #C9D9EE',borderRadius:'8px',fontSize:'12px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'},
  createErrorMsg:{fontSize:'11px',color:'#c0392b',marginTop:'6px'},
  inlineSearchPanel:{marginTop:'6px',border:'1px solid #B8D4F0',borderRadius:'7px',background:'#fff',maxHeight:'240px',overflowY:'auto'},
  chipGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',gap:'6px',marginTop:'4px'},
  chip:on=>({display:'flex',alignItems:'center',gap:'7px',fontSize:'12px',padding:'6px 10px',
    border:'1px solid '+(on?'#4A88C8':'#B8D4F0'),borderRadius:'6px',cursor:'pointer',userSelect:'none',
    background:on?'#E4EDF8':'#fff',fontWeight:on?600:400,color:'#0D2545'}),
  pill:on=>({display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'12px',padding:'5px 11px',borderRadius:'999px',border:'1px solid '+(on?'#154D97':'#C5D5EA'),background:on?'#154D97':'#fff',color:on?'#fff':'#0D2545',cursor:'pointer',userSelect:'none',margin:'0 6px 6px 0',lineHeight:1.25,fontWeight:on?600:400}),
  pillbox:{display:'flex',flexWrap:'wrap'},
  acRow:{display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',border:'1px solid #C5D5EA',borderRadius:'8px',background:'#E4EDF8',cursor:'pointer'},
  acLbl:{fontSize:'12px',fontWeight:600,color:'#0D2545'},
  acCnt:{fontSize:'11px',color:'#0D3565',background:'#D0E0F5',borderRadius:'999px',padding:'1px 9px',fontWeight:500},
  acChev:{marginLeft:'auto',color:'#3A6BA8'},
  metricTileRow:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))',gap:'10px',marginTop:'4px'},
  metricTile:{border:'1.5px solid #B8D4F0',borderRadius:'6px',padding:'8px 10px',background:'#fff'},
  metricTileLabel:{fontSize:'9px',fontWeight:700,color:'#7189B0',letterSpacing:'0.07em',textTransform:'uppercase'},
  metricTileVal:{fontSize:'14px',fontWeight:600,color:'#1C4E8A',marginTop:'2px'},
  metricTileAuto:{fontWeight:400,color:'#9bb4d4',fontSize:'8px',marginLeft:'4px'},
  narrowField:{maxWidth:'180px'},
  reveal:{marginTop:'14px',padding:'14px',background:'#E4EDF8',border:'1px solid #B8D4F0',borderRadius:'8px'},
  corrItem:{display:'flex',gap:'12px',padding:'10px 0',borderBottom:'1px solid #F2F5F9'},
  corrIcon:{width:'30px',height:'30px',borderRadius:'8px',background:'#E4EDF8',color:'#3A6BA8',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'14px'},
  corrTop:{display:'flex',justifyContent:'space-between',fontSize:'12px',gap:'8px'},
  corrText:{fontSize:'12px',color:'#33415A',marginTop:'3px',whiteSpace:'pre-wrap'},
  chrome:{position:'sticky',top:0,zIndex:50,background:'#F6F8FB',paddingTop:'10px'},
  tabbar:{display:'flex',gap:'20px',borderTop:'1px solid #eef2f8',marginTop:'14px',paddingTop:'2px',overflowX:'auto'},
  tab:active=>({display:'flex',alignItems:'center',gap:'6px',padding:'12px 0',fontSize:'13px',fontWeight:active?600:400,color:active?'#154D97':'#8a9ab5',cursor:'pointer',border:'none',background:'none',boxShadow:active?'inset 0 -2px 0 #154D97':'none',whiteSpace:'nowrap',fontFamily:'inherit'}),
  tabBadge:active=>({fontSize:'10px',fontWeight:600,background:'#E4EDF8',color:'#1A5FA8',borderRadius:'10px',padding:'1px 7px',marginLeft:'2px'}),
  stripWrap:{display:'flex',flexWrap:'wrap',gap:'8px',marginTop:'14px'},
  stripTile:{background:'#F7FAFD',border:'1px solid #e0e9f4',borderRadius:'9px',padding:'7px 12px',minWidth:'96px'},
  stripK:{fontFamily:POP,fontSize:'10px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:'#1C4E8A'},
  stripV:{fontSize:'15px',fontWeight:500,color:'#2E4763',marginTop:'3px'},
  stripAuto:{fontSize:'8px',color:'#9bb4d4',fontWeight:400,marginLeft:'3px'},
  reconWrap:{background:'#fff',border:'1px solid #B08968',borderLeft:'4px solid #B08968',borderRadius:'10px',overflow:'hidden',marginBottom:'14px'},
  reconHead:{background:'#FBF7F3',padding:'9px 14px',display:'flex',alignItems:'center',gap:'8px',fontSize:'12px',fontWeight:700,color:'#B08968'},
  reconCount:{background:'#B08968',color:'#fff',borderRadius:'10px',padding:'1px 8px',fontSize:'11px'},
  findingRow:{display:'flex',alignItems:'center',gap:'12px',padding:'11px 14px',borderTop:'0.5px solid #E4EDF8',fontSize:'12.5px',flexWrap:'wrap'},
  findingWhere:{fontSize:'9px',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'#4A88C8',minWidth:'110px',flexShrink:0},
  findingMsg:{flex:1,minWidth:'180px',color:'#3a4a63'},
  fBtnUse:{background:'#2B72C2',color:'#fff',border:'none',borderRadius:'6px',padding:'5px 12px',fontSize:'11px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'},
  fBtnKeep:{background:'transparent',color:'#8a9ab5',border:'1.5px solid #B8D4F0',borderRadius:'6px',padding:'5px 12px',fontSize:'11px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'},
  inlineNote:{display:'flex',alignItems:'center',gap:'8px',marginTop:'6px',background:'#FBF7F3',border:'1px solid #B08968',borderRadius:'6px',padding:'6px 9px',fontSize:'11px',color:'#B08968'},
  inlineNoteText:{flex:1},
  inlineBtnUse:{background:'#2B72C2',color:'#fff',border:'none',borderRadius:'5px',padding:'3px 9px',fontSize:'10px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'},
  inlineBtnKeep:{background:'#fff',color:'#8a9ab5',border:'1px solid #B08968',borderRadius:'5px',padding:'3px 9px',fontSize:'10px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'},
  ooToggleBox:{background:'#FBF7F3',border:'1px solid #B08968',borderRadius:'7px',padding:'10px 14px',marginTop:'10px',marginBottom:'4px',display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'},
  ovTiles:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))',gap:'12px',marginTop:'16px'},
  ovTile:{background:'#fff',borderRadius:'12px',padding:'14px 16px',boxShadow:'0 1px 3px rgba(20,60,120,0.06)'},
  ovTileK:{fontSize:'12.5px',color:'#8a9ab5'},
  ovTileV:(amber)=>({fontSize:'23px',fontWeight:600,fontFamily:POP,color:amber?'#B08968':'#2E4763',marginTop:'2px'}),
  ovMiss:{display:'inline-flex',alignItems:'center',fontSize:'13px',fontWeight:600,color:'#B08968',background:'#FBF7F3',borderRadius:'20px',padding:'3px 10px',marginTop:'6px'},
  secWrap:{marginTop:'14px',display:'grid',gap:'0',minWidth:0},
  ovCard:{background:'#fff',border:'1px solid #C9D9EE',borderRadius:'14px',padding:'18px 20px',boxShadow:'0 1px 2px rgba(20,60,120,0.04)',marginTop:'14px',overflow:'hidden',minWidth:0,display:'grid',gridTemplateColumns:'minmax(0,1fr)'},
  ovLbl:{fontFamily:POP,margin:'-18px -20px 16px',background:'#D0E0F5',color:'#154D97',borderBottom:'1px solid #BCD2EC',padding:'0 20px',minHeight:'40px',display:'flex',alignItems:'center',fontSize:'12px',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase'},
  barLbl:{fontSize:'12px',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',color:'#154D97'},
  ovGrid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 22px',marginTop:'14px'},
  ovK:{fontSize:'12.5px',color:'#8a9ab5'},
  ovV:(amber)=>({fontSize:'15px',fontWeight:600,marginTop:'2px',color:amber?'#B08968':'#1a2b40'}),
  ovName:{fontSize:'16px',fontWeight:600,marginTop:'8px',color:'#1a2b40'},
  ovLine:{fontSize:'13.5px',color:'#5a6b82',marginTop:'3px',lineHeight:1.7},
  ovFlag:{display:'inline-flex',alignItems:'center',gap:'6px',background:'#FBF7F3',color:'#B08968',fontSize:'12.5px',padding:'5px 11px',borderRadius:'20px'},
  ovDot:{width:'7px',height:'7px',borderRadius:'50%',background:'#B08968'},
  appLbl:{fontSize:'11px',color:'#8a9ab5'},
  appLblAmber:{fontSize:'11px',color:'#B08968'},
  appInput:{width:'100%',boxSizing:'border-box',marginTop:'4px',border:'1px solid #C9D9EE',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:'#1a2b40',fontFamily:'inherit',outline:'none'},
  appInputAmber:{width:'100%',boxSizing:'border-box',marginTop:'4px',border:'1px solid #B08968',background:'#FBF7F3',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:'#1a2b40',fontFamily:'inherit',outline:'none'},
  appSelect:{width:'100%',boxSizing:'border-box',marginTop:'4px',border:'1px solid #C9D9EE',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:'#1a2b40',fontFamily:'inherit',outline:'none',background:'#fff',cursor:'pointer'},
  appSelectAmber:{width:'100%',boxSizing:'border-box',marginTop:'4px',border:'1px solid #B08968',background:'#FBF7F3',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:'#1a2b40',fontFamily:'inherit',outline:'none',cursor:'pointer'},
  pickField:filled=>({marginTop:'4px',border:'1px solid #C9D9EE',background:'#fff',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:filled?'#1a2b40':'#8a9ab5',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px',cursor:'pointer',minHeight:'33px'}),
  pickVal:{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  pickCaret:{color:'#8a9ab5',fontSize:'11px',flexShrink:0},
  appGrid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 20px',marginTop:'14px'},
  dealSetup:{background:'#fff',border:'1px solid #C9D9EE',borderRadius:'12px',padding:'14px 16px',marginTop:'14px'},
  dealSetupLbl:{fontSize:'11px',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'#3A6BA8',marginBottom:'12px',display:'flex',alignItems:'center',gap:'10px'},
  dealSetupHint:{fontSize:'10px',fontWeight:600,color:'#B08968',background:'#FBF7F3',borderRadius:'20px',padding:'2px 9px',letterSpacing:0,textTransform:'none'},
  dealGrid:{display:'grid',gridTemplateColumns:'repeat(4,minmax(0,1fr))',gap:'15px 16px'},
  dealFlow:{display:'flex',flexWrap:'wrap',gap:'12px 16px',marginTop:'2px'},
  dealSec:{background:'#fff',border:'1px solid #D9E5F4',borderRadius:'12px',marginTop:'10px',overflow:'hidden'},
  dealSecHead:{display:'flex',alignItems:'center',gap:'9px',padding:'9px 14px',cursor:'pointer',background:'#E4EDF8'},
  dealCaret:{color:'#4A7FBF',fontSize:'15px',flexShrink:0,lineHeight:1},
  dealSecName:{fontFamily:"'Inter',sans-serif",fontSize:'11.5px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:'#154D97'},
  dealDot:done=>({width:'7px',height:'7px',borderRadius:'50%',background:done?'#B7C6D6':'#D9B48A',flexShrink:0}),
  dealSecSum:{marginLeft:'auto',fontSize:'12px',color:'#8a9ab5',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'52%'},
  dealSecBody:{padding:'13px 14px 15px',borderTop:'1px solid #D9E5F4'},
  subBand:{border:'1px solid #DCE7F5',borderRadius:'10px',overflow:'hidden',marginTop:'14px',background:'#fff'},
  subBandHead:{background:'#EAF1FA',padding:'10px 15px',fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#154D97'},
  subBandBody:{padding:'12px 15px 14px'},
  dcStrip:{display:'flex',gap:'34px',background:'#F5F9FE',border:'1px solid #DCE7F5',borderRadius:'8px',padding:'11px 16px',marginBottom:'12px'},
  dcStK:{fontSize:'9.5px',letterSpacing:'.07em',textTransform:'uppercase',color:'#8FA6C4',fontWeight:700},
  dcStV:{fontSize:'15px',color:'#154D97',fontWeight:700,marginTop:'3px'},
  dcBand:{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#E4EDF8',borderRadius:'6px',padding:'6px 12px',margin:'14px 0 8px'},
  dcBandK:{fontSize:'10.5px',letterSpacing:'.07em',textTransform:'uppercase',color:'#154D97',fontWeight:700},
  dcBandV:{fontSize:'11.5px',color:'#3A6BA8',fontWeight:600},
  dcRow:{display:'grid',gridTemplateColumns:'150px minmax(150px,1.2fr) minmax(130px,1fr) minmax(150px,1.2fr) minmax(120px,1fr) 78px',gap:'14px',alignItems:'center',background:'#fff',border:'1px solid #C9D9EE',borderRadius:'8px',padding:'9px 12px',marginBottom:'6px',minHeight:'44px'},
  dcRole:{fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#154D97'},
  dcName:{fontSize:'13px',color:'#0D2545',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  dcCell:{fontSize:'12.5px',color:'#33415A',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  dcActs:{textAlign:'right',whiteSpace:'nowrap'},
  dcAct:{fontSize:'11.5px',color:'#3A6BA8',fontWeight:600,cursor:'pointer',marginLeft:'10px'},
  dcActRm:{fontSize:'11.5px',color:'#9FB4CE',fontWeight:600,cursor:'pointer',marginLeft:'10px'},
  dcFoot:{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'14px'},
  dcPick:{marginTop:'14px',paddingTop:'14px',borderTop:'1px solid #EEF1F5'},
  dcPickCols:{display:'grid',gridTemplateColumns:'repeat(4,minmax(0,1fr))',gap:'16px 22px'},
  dcPickHead:{fontSize:'10.5px',letterSpacing:'.07em',fontWeight:700,color:'#154D97',textTransform:'uppercase',paddingBottom:'6px',marginBottom:'9px',borderBottom:'1px solid #EEF3F9'},
  dcOpt:{display:'block',fontSize:'13px',color:'#33415A',cursor:'pointer',padding:'3px 0'},
  dpHdr:{display:'grid',gridTemplateColumns:'minmax(0,1.15fr) 170px 150px minmax(0,1.3fr) minmax(0,1fr) 90px',gap:'14px',padding:'0 13px 7px',fontSize:'10px',letterSpacing:'.07em',textTransform:'uppercase',color:'#9FB4CE',fontWeight:700},
  dpRow:{display:'grid',gridTemplateColumns:'minmax(0,1.15fr) 170px 150px minmax(0,1.3fr) minmax(0,1fr) 90px',gap:'14px',alignItems:'center',background:'#fff',border:'1px solid #E9EFF6',borderRadius:'10px',padding:'10px 12px',marginBottom:'8px',fontSize:'12.5px',minHeight:'44px'},
  dpName:{fontWeight:600,color:'#0D2545',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  dpRole:{color:'#3A6BA8'},
  dpCell:{color:'#33415A',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  dpDrop:{position:'absolute',top:'46px',left:'12px',width:'320px',border:'1px solid #C9D9EE',borderRadius:'8px',background:'#fff',padding:'5px 0',boxShadow:'0 4px 14px rgba(20,60,120,0.12)',zIndex:20},
  dpOpt:{fontSize:'13px',color:'#33415A',padding:'7px 12px',cursor:'pointer'},
  dpOptMeta:{color:'#8a9ab5',fontSize:'11.5px',marginLeft:'6px'},
  dpOptNew:{fontSize:'13px',color:'#154D97',fontWeight:600,padding:'9px 12px 7px',cursor:'pointer',borderTop:'1px solid #EEF3F9',marginTop:'4px'},
  dpNewBox:{border:'1px dashed #C5D5EA',borderRadius:'9px',background:'#FBFCFE',padding:'13px 14px',marginBottom:'8px'},
  dpEdit:{border:'1px solid #E9EFF6',borderRadius:'10px',background:'#fff',padding:'14px 15px 15px',margin:'10px 0 12px'},
  dpNewT:{fontSize:'10.5px',letterSpacing:'.07em',fontWeight:700,color:'#154D97',textTransform:'uppercase',marginBottom:'11px'},
  cSubLbl:{fontSize:'10.5px',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'#154D97',marginTop:'16px',marginBottom:'9px'},
  dpSave:{background:'#154D97',border:'none',color:'#fff',borderRadius:'8px',padding:'0 20px',height:'33px',fontSize:'13px',fontWeight:600,fontFamily:'inherit',cursor:'pointer'},
  dpCancel:{background:'#fff',border:'1px solid #C5D5EA',color:'#33415A',borderRadius:'8px',padding:'0 16px',height:'33px',fontSize:'13px',fontWeight:600,fontFamily:'inherit',cursor:'pointer'},
  poHdr:{display:'grid',gridTemplateColumns:'16px minmax(0,1.5fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',gap:'16px',padding:'0 13px 7px',fontSize:'10px',letterSpacing:'.07em',textTransform:'uppercase',color:'#9FB4CE',fontWeight:700},
  poPayee:{marginTop:'14px',paddingTop:'13px',borderTop:'1px solid #F2F5F9',display:'flex',gap:'34px',flexWrap:'wrap'},
  poPayK:{fontSize:'9.5px',letterSpacing:'.07em',textTransform:'uppercase',color:'#8FA6C4',fontWeight:700},
  poPayV:{fontSize:'13px',color:'#0D2545',marginTop:'4px'},
  poPayMiss:{fontSize:'13px',color:'#B4532A',fontWeight:600,marginTop:'4px'},
  poRow:{display:'grid',gridTemplateColumns:'16px minmax(0,1.5fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',gap:'16px',alignItems:'center',background:'#fff',border:'1px solid #E9EFF6',borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',fontSize:'12.5px',minHeight:'42px'},
  poCar:{color:'#4A7FBF',fontSize:'13px',cursor:'pointer'},
  poWho:{fontWeight:600,color:'#0D2545',overflow:'hidden'},
  poVia:{display:'block',fontSize:'11.5px',color:'#8a9ab5',fontWeight:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  poRole:{color:'#3A6BA8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  poAmt:{fontWeight:600,color:'#1B2A3D',whiteSpace:'nowrap'},
  poDt:{color:'#8a9ab5',fontSize:'11.5px',whiteSpace:'nowrap'},
  poEdit:{border:'1px solid #C9D9EE',borderRadius:'8px',background:'#FBFCFE',padding:'14px',margin:'0 0 8px'},
  poEditT:{fontSize:'10.5px',letterSpacing:'.07em',fontWeight:700,color:'#154D97',textTransform:'uppercase',marginBottom:'10px'},
  poGross:{display:'flex',justifyContent:'space-between',alignItems:'baseline',background:'#F3F7FD',border:'1px solid #DCE7F5',borderRadius:'9px',padding:'11px 14px',marginTop:'14px'},
  poGrossK:{fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#3A6BA8'},
  poGrossV:{color:'#154D97',fontSize:'17px',fontWeight:700},
  poNet:{display:'flex',justifyContent:'space-between',alignItems:'baseline',background:'#154D97',borderRadius:'9px',padding:'12px 14px',marginTop:'10px'},
  poNetK:{fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#fff'},
  poNetV:{color:'#fff',fontSize:'18px',fontWeight:700},
  actRow:{display:'grid',gridTemplateColumns:'82px 96px 170px 1fr 70px',gap:'14px',alignItems:'start',background:'#fff',border:'1px solid #C9D9EE',borderRadius:'8px',padding:'10px 12px',marginBottom:'6px'},
  actChip:{fontSize:'9.5px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#154D97',background:'#EAF1FA',borderRadius:'11px',padding:'3px 0',textAlign:'center'},
  actDate:{fontSize:'12.5px',color:'#8a9ab5'},
  actWho:{fontSize:'12.5px',color:'#0D2545',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  actTxt:{fontSize:'12.5px',color:'#33415A',lineHeight:1.5},
  actAdd:{display:'grid',gridTemplateColumns:'110px 140px 200px 1fr auto',gap:'12px',alignItems:'end',border:'1px dashed #C5D5EA',borderRadius:'9px',padding:'11px 12px',marginTop:'8px',background:'#FBFCFE'},
  actLog:{background:'#154D97',border:'none',color:'#fff',borderRadius:'8px',padding:'0 18px',fontSize:'13px',fontWeight:600,fontFamily:'inherit',height:'33px',cursor:'pointer'},
  pickGroup:{},
  pickGroupHead:{fontSize:'10.5px',letterSpacing:'.07em',fontWeight:700,color:'#154D97',textTransform:'uppercase',paddingBottom:'6px',marginBottom:'9px',borderBottom:'1px solid #EEF3F9'},
  pickCols:{display:'grid',gridTemplateColumns:'repeat(4,minmax(0,1fr))',gap:'16px 22px'},
  pickTop:{display:'flex',alignItems:'center',gap:'14px',marginBottom:'12px'},
  pickSearch:{width:'260px',padding:'7px 10px',border:'1px solid #C9D9EE',borderRadius:'8px',fontSize:'13px',fontFamily:'inherit',color:'#1a2b40',outline:'none'},
  pickCount:{fontSize:'12px',color:'#8a9ab5',marginLeft:'auto'},
  pickClear:{fontSize:'12px',color:'#154D97',fontWeight:600,cursor:'pointer'},
  pickChk:{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer'},
  miniChip:{fontSize:'9.5px',fontWeight:700,letterSpacing:'.04em',textTransform:'uppercase',color:'#154D97',background:'#EAF1FA',border:'1px solid #C9D9EE',borderRadius:'11px',padding:'1px 8px',cursor:'pointer',userSelect:'none'},
  reoRow:{border:'1px solid #E9EFF6',borderRadius:'10px',marginBottom:'8px',overflow:'hidden'},
  poWrap:{border:'1px solid #C9D9EE',borderRadius:'10px',marginBottom:'8px',overflow:'hidden',background:'#fff'},
  finHead:{display:'grid',gridTemplateColumns:'16px 1.1fr 1fr 1.3fr 140px',alignItems:'center',gap:'12px',padding:'11px 14px',fontSize:'13px'},
  finHead6:{display:'grid',gridTemplateColumns:'16px 1.1fr 1fr 1.2fr 130px 140px',alignItems:'center',gap:'12px',padding:'11px 14px',fontSize:'13px'},
  finHeadI:{display:'grid',gridTemplateColumns:'16px 1.1fr 1fr 1.2fr 150px 140px',alignItems:'center',gap:'12px',padding:'11px 14px',fontSize:'13px'},
  finC1:{fontWeight:600,color:'#1B2A3D',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  finC2:{color:'#3A6BA8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  finC3:{color:'#3A6BA8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  finAmt:{textAlign:'right',fontWeight:600,color:'#1B2A3D'},
  finSub:{display:'block',fontSize:'11px',color:'#8A94A6',fontWeight:400},
  finSave:{background:'#154D97',border:'1px solid #154D97',color:'#fff',borderRadius:'8px',padding:'8px 22px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'},
  finCancel:{background:'#fff',border:'1px solid #C5D5EA',color:'#1B2A3D',borderRadius:'8px',padding:'8px 18px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'},
  finDel:{background:'#fff',border:'1px solid #E0B4B4',color:'#A33333',borderRadius:'8px',padding:'8px 16px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',marginLeft:'auto'},
  finAdd:{background:'#fff',border:'1px dashed #7EA3D7',color:'#154D97',borderRadius:'20px',padding:'8px 18px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'},
  finTot:{marginLeft:'auto',fontSize:'13px',color:'#154D97',fontWeight:600},
  finColH:{fontSize:'10.5px',letterSpacing:'0.07em',fontWeight:700,color:'#154D97',marginBottom:'11px',textTransform:'uppercase'},
  finMRow:{display:'flex',alignItems:'center',gap:'11px',padding:'7px 0',borderBottom:'1px solid #EEF3F9',fontSize:'12.5px',color:'#33415A'},
  finBand:{display:'flex',alignItems:'center',justifyContent:'space-between',background:'#2C5697',color:'#fff',borderRadius:'8px',padding:'11px 15px',marginTop:'11px',fontSize:'13px',fontWeight:600},
  reoHead:{display:'flex',alignItems:'center',gap:'11px',padding:'11px 13px',cursor:'pointer'},
  reoCaret:{color:'#4A7FBF',fontSize:'15px',flexShrink:0,lineHeight:1},
  incChk:{display:'flex',width:'fit-content',alignItems:'center',gap:'7px',height:'33px',fontSize:'12.5px',color:'#33415A',cursor:'pointer'},
  // The ONLY change to these rows. A checkbox has no label above it, so it used
  // to sit level with the labels and stick up above the inputs. The 19px top
  // margin is the label line plus its 4px gap, and the 33px height matches an
  // input, so the box lands centred on the input line. Nothing else moves.
  rowChk:{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',marginTop:'19px',height:'33px',fontSize:'13px',color:'#33415A',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0},
  reoType:{fontSize:'9px',fontWeight:700,letterSpacing:'0.03em',textTransform:'uppercase',borderRadius:'6px',padding:'3px 7px',flexShrink:0,background:'#E4EDF8',color:'#3A6BA8'},
  reoMain:{minWidth:0,flex:1},
  reoAddr:{fontFamily:"'Inter',sans-serif",fontSize:'12.5px',fontWeight:600,color:'#2E4763',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  reoMeta:{fontSize:'11px',color:'#8A94A6',marginTop:'2px'},
  reoRight:{display:'flex',gap:'28px',alignItems:'center',flexShrink:0,marginLeft:'20px'},
  reoCol:{textAlign:'right',minWidth:'74px'},
  reoColK:{fontSize:'9px',fontWeight:700,letterSpacing:'0.03em',textTransform:'uppercase',color:'#A7B3C4'},
  reoColV:{fontSize:'12px',fontWeight:600,color:'#154D97',marginTop:'1px'},
  reoBody:{padding:'4px 14px 14px',borderTop:'1px solid #F2F5F9'},
  reoRemove:{marginTop:'11px',fontSize:'11.5px',color:'#B0564B',fontWeight:600,cursor:'pointer',display:'inline-block'},
  mbBar:{display:'flex',alignItems:'center',gap:'26px',flexWrap:'wrap',background:'#F0F5FB',border:'1px solid #D9E5F4',borderRadius:'11px',padding:'11px 16px',marginTop:'14px'},
  noteRow:{display:'flex',alignItems:'flex-end',gap:'14px',flexWrap:'wrap',padding:'11px 12px',border:'1px solid #EEF1F5',borderRadius:'9px',marginBottom:'8px',background:'#fff'},
  noteTag:{fontSize:'9.5px',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',padding:'3px 9px',borderRadius:'6px',background:'#E4EDF8',color:'#154D97',flexShrink:0,width:'118px',textAlign:'center',alignSelf:'center'},
  noteAuto:{padding:'8px 10px',border:'1px solid #C9D9EE',borderRadius:'8px',fontSize:'13px',color:'#154D97',background:'#fff',fontWeight:700,minHeight:'36px',boxSizing:'border-box'},
  mbTile:{minWidth:'70px',flex:'0 0 auto'},
  mbK:{fontFamily:"'Inter',sans-serif",fontSize:'9px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:'#7189B0'},
  mbV:{fontSize:'15px',fontWeight:600,color:'#154D97',marginTop:'2px',whiteSpace:'nowrap'},
  mbDiv:{width:'1px',alignSelf:'stretch',background:'#D9E5F4',margin:'2px 4px'},
  mbElig:{marginLeft:'auto',display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',fontWeight:600,color:'#1E7B4F',background:'#E4F3EB',border:'1px solid #CDE8D8',borderRadius:'20px',padding:'5px 12px'},
  appToggleRow:{display:'flex',flexWrap:'wrap',gap:'8px',marginTop:'9px'},
  appToggle:on=>({display:'inline-flex',alignItems:'center',gap:'6px',fontSize:'13px',padding:'7px 13px',borderRadius:'20px',cursor:'pointer',fontWeight:on?600:400,background:on?'#E4EDF8':'#fff',color:on?'#154D97':'#5a6b82',border:on?'none':'1px solid #C9D9EE'}),
  appSub:{background:'#F7FAFD',borderRadius:'12px',padding:'14px 16px',marginTop:'12px'},
  appSubTitle:{fontSize:'10.5px',fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'#154D97',marginBottom:'11px'},
  appSubLbl:{fontSize:'11.5px',fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',color:'#8a9ab5',marginTop:'18px'},
  tagRow:{border:'1px solid #E6EAF0',borderRadius:'9px',padding:'11px 13px',marginTop:'12px'},
  tagHead:{display:'flex',alignItems:'center',gap:'10px',cursor:'pointer'},
  tagLbl:{fontFamily:POP,fontSize:'11.5px',fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',color:'#2E4763'},
  tagSel:{marginLeft:'auto',display:'flex',gap:'6px',flexWrap:'wrap',alignItems:'center',justifyContent:'flex-end'},
  tagChip:{fontSize:'12px',fontWeight:500,padding:'3px 10px',borderRadius:'16px',background:'#1C4E8A',color:'#fff'},
  tagNone:{fontSize:'12.5px',color:'#8a94a6'},
  tagEdit:{fontSize:'12px',fontWeight:600,color:'#1C4E8A',background:'#E4EDF8',border:'1px solid #D4E2F1',borderRadius:'7px',padding:'5px 11px'},
  tagCaret:{color:'#9CB2CE',fontSize:'13px'},
  // Was display:flex + wrap, left over from the old loose-pill design. That
  // made the header row and the column grid SIBLING FLEX ITEMS, so they sat
  // side by side. Asset class only looked right because its 260px search box
  // was wide enough to push the columns onto the next line. Block stacks them.
  tagCloud:{marginTop:'12px',paddingTop:'12px',borderTop:'1px solid #EEF1F5'},
  appNote:{fontSize:'12.5px',color:'#8a9ab5',marginTop:'14px',textAlign:'center'},
  appTag:{fontSize:'11px',background:'#fff',color:'#154D97',border:'1px solid #C5D5EA',borderRadius:'16px',padding:'2px 9px'},
  appTrash:{fontSize:'15px',color:'#c2cedd',marginLeft:'auto',cursor:'pointer'},
  appAddBtn:{width:'100%',fontSize:'13.5px',fontWeight:600,padding:'11px',background:'#fff',color:'#154D97',border:'1px solid #c2d2e8',borderRadius:'11px',marginTop:'12px',cursor:'pointer',fontFamily:'inherit'},
  appHold:{display:'flex',alignItems:'center',gap:'8px',background:'#FBF7F3',color:'#B08968',borderRadius:'10px',padding:'10px 13px',marginTop:'12px',fontSize:'12.5px'},
  appTitleRow:{fontFamily:POP,margin:'-18px -20px 16px',background:'#D0E0F5',borderBottom:'1px solid #BCD2EC',padding:'0 20px',minHeight:'40px',display:'flex',alignItems:'center',gap:'8px'},
  secMetrics:{display:'flex',flexWrap:'wrap',background:'linear-gradient(180deg,#F4F8FD,#EAF2FB)',border:'1px solid #D9E5F3',borderRadius:'11px',overflow:'hidden',marginBottom:'16px'},
  secStat:{flex:1,minWidth:'120px',padding:'9px 13px',borderRight:'1px solid #DCE8F5'},
  secStatK:{fontFamily:POP,fontSize:'10px',letterSpacing:'0.05em',textTransform:'uppercase',color:'#1C4E8A',fontWeight:700,whiteSpace:'nowrap'},
  secStatV:{fontSize:'15px',fontWeight:500,color:'#2E4763',marginTop:'3px',whiteSpace:'nowrap'},
  secWrap:{overflowX:'auto',maxWidth:'100%',width:'100%',minWidth:0},
  secTbl:{borderCollapse:'collapse',fontSize:'12.5px',minWidth:'100%'},
  secTh:(al)=>({fontFamily:POP,fontWeight:700,fontSize:'10px',letterSpacing:'0.04em',textTransform:'uppercase',color:'#8A94A6',textAlign:al==='r'?'right':'left',padding:'7px 8px',borderBottom:'1px solid #E6EAF0',whiteSpace:'nowrap'}),
  secThAct:{width:'34px',borderBottom:'1px solid #E6EAF0'},
  secTd:{padding:'6px 8px',borderBottom:'1px solid #EEF1F5',textAlign:'left'},
  secTdR:{padding:'6px 8px',borderBottom:'1px solid #EEF1F5',textAlign:'right'},
  secTdAct:{padding:'6px 8px',borderBottom:'1px solid #EEF1F5',textAlign:'center',width:'34px'},
  secInput:{width:'100%',minWidth:'150px',border:'1px solid transparent',background:'transparent',borderRadius:'6px',padding:'5px 6px',fontFamily:'inherit',fontSize:'12.5px',color:'#2E4763'},
  secInputR:{width:'100%',minWidth:'96px',border:'1px solid transparent',background:'transparent',borderRadius:'6px',padding:'5px 6px',fontFamily:'inherit',fontSize:'12.5px',color:'#2E4763',textAlign:'right',fontVariantNumeric:'tabular-nums'},
  secSel:{width:'100%',minWidth:'120px',border:'1px solid transparent',background:'transparent',borderRadius:'6px',padding:'5px 6px',fontFamily:'inherit',fontSize:'12.5px',color:'#2E4763',cursor:'pointer'},
  secTrash:{color:'#C0CAD8',cursor:'pointer',fontSize:'15px'},
  secFoot:{padding:'9px 8px',borderTop:'2px solid #E6EAF0',background:'#FBFCFE'},
  secFootR:{padding:'9px 8px',borderTop:'2px solid #E6EAF0',background:'#FBFCFE',textAlign:'right',fontWeight:600,color:'#2E4763',fontVariantNumeric:'tabular-nums'},
  secFootLbl:{padding:'9px 8px',borderTop:'2px solid #E6EAF0',background:'#FBFCFE',fontFamily:POP,fontSize:'10px',letterSpacing:'0.04em',textTransform:'uppercase',color:'#8A94A6',fontWeight:700,textAlign:'left'},
  appNav:{flex:'0 0 190px',display:'flex',flexDirection:'column',gap:'2px'},
  appNavItem:on=>({display:'flex',alignItems:'center',gap:'9px',width:'100%',textAlign:'left',padding:'9px 11px',border:'none',borderRadius:'9px',fontSize:'13px',fontWeight:on?600:400,color:on?'#154D97':'#5a6b82',background:on?'#E4EDF8':'transparent',cursor:'pointer',fontFamily:'inherit',lineHeight:1.3}),
  appNavInd:{width:'14px',display:'flex',justifyContent:'center',flexShrink:0},
  appNavDot:{width:'7px',height:'7px',borderRadius:'50%',background:'#B08968'},
};

function clean(str){ return String(str||'').replace(/\s*[\u2014\u2013\u2015\u2012\u2212]\s*/g,' - '); }

const ZIP_CACHE = {};
async function lookupZip(zip){
  const z=String(zip||'').replace(/\D/g,'').slice(0,5);
  if(z.length!==5) return null;
  if(ZIP_CACHE[z]!==undefined) return ZIP_CACHE[z];
  try{
    const r=await authFetch('/.netlify/functions/zip-lookup?zip='+z);
    if(!r.ok){ ZIP_CACHE[z]=null; return null; }
    const d=await r.json();
    const out=d&&d.city?{city:d.city||'',state:d.state||'',county:d.county||''}:null;
    ZIP_CACHE[z]=out;
    return out;
  }catch(e){ return null; }
}

let UID=0;
const newUid = () => 'r'+(++UID)+'_'+Date.now().toString(36);

function maskedSsn(v){const d=String(v==null?'':v).replace(/\D/g,'');if(!d)return '';return 'xxx-xx-'+d.slice(-4);}
function copyText(t){ if(!t) return; try{ if(navigator.clipboard&&navigator.clipboard.writeText){navigator.clipboard.writeText(t);return;} }catch(e){} try{ const a=document.createElement('textarea');a.value=t;document.body.appendChild(a);a.select();document.execCommand('copy');document.body.removeChild(a); }catch(e){} }

function maskSSN(v){const d=String(v==null?'':v).replace(/\D/g,'').slice(0,9);if(d.length<=3)return d;if(d.length<=5)return d.slice(0,3)+'-'+d.slice(3);return d.slice(0,3)+'-'+d.slice(3,5)+'-'+d.slice(5);}
function maskPhone(v){const d=String(v==null?'':v).replace(/\D/g,'').slice(0,10);if(d.length<=3)return d;if(d.length<=6)return d.slice(0,3)+'-'+d.slice(3);return d.slice(0,3)+'-'+d.slice(3,6)+'-'+d.slice(6);}
// Airtable hands date fields back as ISO (YYYY-MM-DD). Stripping the dashes
// first turned 1978-06-25 into 19780625, which then masked as 19/78/0625 and
// re-saved wrong, scrambling the value a little more on every save. Detect the
// ISO shape BEFORE touching the digits and reorder it to MM/DD/YYYY.
function maskDate(v){
  const str=String(v==null?'':v).trim();
  const iso=/^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  const src=iso?(iso[2]+iso[3]+iso[1]):str;
  const d=src.replace(/\D/g,'').slice(0,8);
  if(d.length<=2)return d;
  if(d.length<=4)return d.slice(0,2)+'/'+d.slice(2);
  return d.slice(0,2)+'/'+d.slice(2,4)+'/'+d.slice(4);
}
function maskMoney(v){const t=String(v==null?'':v).replace(/[^0-9.]/g,'');if(t==='')return '';const p=t.split('.');const w=p[0].replace(/^0+(?=\d)/,'');const dec=p.length>1?'.'+p.slice(1).join('').slice(0,2):'';return '$'+(w===''?'0':Number(w).toLocaleString())+dec;}
// Money reads as the whole figure with cents everywhere: no K or M shorthand and
// no rounding away the pennies. maskMoney stays as it is for LIVE TYPING, so the
// caret is never fought while she is mid-number; maskMoneyFixed is what an
// unfocused money input shows, which is where the .00 belongs.
function maskMoneyFixed(v){const t=String(v==null?'':v).replace(/[^0-9.]/g,'');if(t==='')return '';const n=Number(t);if(isNaN(n))return maskMoney(v);return '$'+n.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});}
// Normalises a money value to two decimals. Used on BLUR only, so typing is
// never fought mid-entry. Blank stays blank, it does not become 0.00.
function money2dp(v){const t=String(v==null?'':v).replace(/[^0-9.]/g,'');if(t==='')return '';const n=Number(t);return isFinite(n)?n.toFixed(2):t;}
// The Proposed monthly payment panel uses bare inputs rather than AF, so it did
// not pick up the cents treatment. Same rule as AF: raw while focused, padded to
// two decimals once she clicks out. Declared at module scope, never inside a
// render, so React keeps the input mounted and the caret stays put.
function MoneyCell({value,onChange,onCommit,style,placeholder}){
  const [foc,setFoc]=React.useState(false);
  const raw=clean(value);
  return (<input value={foc?maskMoney(raw):maskMoneyFixed(raw)} placeholder={placeholder||''} onFocus={()=>setFoc(true)} onBlur={e=>{setFoc(false); if(onCommit) onCommit(e.target.value);}} onChange={e=>onChange(e.target.value.replace(/[^0-9.]/g,''))} style={style}/>);
}

// The small HIDE / SHOW / COPY chips that sit on a field's label line.
// Inline styles cannot express :active, so the pressed state is held here:
// the chip shrinks and fills solid while the mouse is down, and a chip given
// a doneLabel holds that label briefly after the click so a copy is visibly
// confirmed. The timer is cleared on unmount so it cannot fire into nothing.
function MiniChip({label,doneLabel,onClick}){
  const [press,setPress]=React.useState(false);
  const [done,setDone]=React.useState(false);
  const timer=React.useRef(null);
  React.useEffect(()=>()=>{ if(timer.current) clearTimeout(timer.current); },[]);
  const lit=press||done;
  return (
    <span
      role="button"
      onMouseDown={()=>setPress(true)}
      onMouseUp={()=>setPress(false)}
      onMouseLeave={()=>setPress(false)}
      onClick={()=>{
        if(onClick) onClick();
        if(doneLabel){
          setDone(true);
          if(timer.current) clearTimeout(timer.current);
          timer.current=setTimeout(()=>setDone(false),1100);
        }
      }}
      style={{...s.miniChip,
        background:lit?'#154D97':s.miniChip.background,
        color:lit?'#fff':s.miniChip.color,
        borderColor:lit?'#154D97':'#C9D9EE',
        transform:press?'scale(0.85)':'none',
        transition:'transform .08s ease, background .08s ease, color .08s ease, border-color .08s ease'}}
    >{done&&doneLabel?doneLabel:label}</span>
  );
}

function AF({label,val,onChange,amber,placeholder,w,money,pct,ssn,phone,date,readOnly}){
  const [foc,setFoc]=React.useState(false);
  const raw=clean(val);
  let disp=raw;
  if(money){disp=foc?maskMoney(raw):maskMoneyFixed(raw);}
  else if(ssn){disp=maskSSN(raw);}
  else if(phone){disp=maskPhone(raw);}
  else if(date){disp=maskDate(raw);}
  else if(!foc&&raw!==''&&raw!=null&&pct){disp=raw+'%';}
  const handle=e=>{
    const t=e.target.value;
    if(money||pct) return onChange(t.replace(/[^0-9.]/g,''));
    if(ssn) return onChange(maskSSN(t));
    if(phone) return onChange(maskPhone(t));
    if(date) return onChange(maskDate(t));
    return onChange(t);
  };
  return (
    <div style={w?{width:w,flexShrink:0}:undefined}>
      <div style={amber?s.appLblAmber:s.appLbl}>{label}{amber?' \u00b7 needs you':''}</div>
      <input value={disp} placeholder={placeholder||''} readOnly={!!readOnly} inputMode={(money||pct||ssn||phone||date)?'numeric':undefined} onFocus={()=>setFoc(true)} onBlur={()=>setFoc(false)} onChange={handle} style={readOnly?{...(amber?s.appInputAmber:s.appInput),background:'#F0F5FC',color:'#6B7C93'}:(amber?s.appInputAmber:s.appInput)}/>
    </div>
  );
}

function DealSec({k,title,summary,done,children,openSec,setOpenSec}){
  const open=openSec[k]!==false;
  return (
    <div style={s.dealSec}>
      <div style={s.dealSecHead} onClick={()=>setOpenSec(p=>({...p,[k]:(p[k]===false)}))}>
        <span style={s.dealCaret}>{open?'\u25BE':'\u25B8'}</span>
        <span style={s.dealSecName}>{title}</span>
        <span style={s.dealDot(done)}/>
        {!open&&<span style={s.dealSecSum}>{summary}</span>}
      </div>
      {open&&<div style={s.dealSecBody}>{children}</div>}
    </div>
  );
}

function ASel({label,val,onChange,options,groups,amber,disabled,w}){
  return (
    <div style={w?{width:w,flexShrink:0}:undefined}>
      <div style={amber?s.appLblAmber:s.appLbl}>{label}{amber?' · needs you':''}</div>
      <select value={val||''} disabled={disabled} onChange={e=>onChange(e.target.value)} style={amber?s.appSelectAmber:s.appSelect}>
        <option value="">Select</option>
        {groups
          ? groups.map(([g,opts])=><optgroup key={g} label={g}>{opts.map(o=><option key={o}>{o}</option>)}</optgroup>)
          : (options||[]).map(o=><option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

const usd = n => Number(n||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});
const fmt = n => (!n&&n!==0)?'-':'$'+usd(n);
const fmtK = n => (!n&&n!==0)?'-':'$'+usd(n);
const fmtD = n => n?'$'+usd(n):'';
const fmtX = n => (!n&&n!==0)?'-':'$'+usd(n);
const pN = x => String(x).replace(/[^0-9.]/g,'');
const isoDate = v => { const t=String(v||'').trim(); if(!t) return ''; const m=t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/); if(m){ const y=m[3].length===2?'20'+m[3]:m[3]; return y+'-'+String(m[1]).padStart(2,'0')+'-'+String(m[2]).padStart(2,'0'); } return t; };
const v = x => x||'-';
const num = x => Number(x)||0;

function formatPhone(val) {
  const n = String(val).replace(/[^0-9]/g, '');
  if (n.length <= 3) return n;
  if (n.length <= 6) return n.slice(0, 3) + '-' + n.slice(3);
  return n.slice(0, 3) + '-' + n.slice(3, 6) + '-' + n.slice(6, 10);
}

const PCT_FIELDS = ['LTV','CLTV','LTARV','LTC','Cap rate','Occupancy %','Vacancy rate','Operating expense ratio'];
const CURRENCY_FIELDS = ['Purchase price','Loan amount','Down payment','ARV','Rehab budget','Construction budget','As-is value','Current monthly rent','Projected monthly rent','Annual NOI','Annual rent','Cash-out amount','Existing loan balance','Existing lien amount','HOA dues','Property taxes','Insurance','Other project costs','CDC portion','Paid outside closing','Total project cost','Est. profit at ARV','ADU monthly rent','Basement rent','MH monthly fee','Appraised value','Original purchase price','Mortgage insurance','Other financing'];
const NUMBER_FIELDS = ['Sqft','Bedrooms','Bathrooms','Number of units','Floor level','Lot size (sqft)','Tenant count','Land acreage','Requested rate','Requested points','Commercial sqft','Residential sqft','Est. DSCR','Garage count','ADU sqft','Total beds','Total baths','Occupied units','Basement sqft','Residential tenants','Commercial tenants','Mortgage lates 12mo','Commercial %','Assignment fee amount','Term months'];
// Airtable sends 0 for a number field that holds zero, and the loader below
// reads every value as `f[key]||''`, which turns that 0 into an empty string.
// A stored zero was therefore indistinguishable from a never-filled field, so
// it came back as a blank box every time. Restore it as the string '0' for the
// money and number keys only, so zero survives the round trip and still saves
// back as zero. Nothing else is touched: a key that is genuinely absent, null
// or blank stays blank.
function withStoredZeros(f, base){
  if(!f) return base;
  [...CURRENCY_FIELDS, ...NUMBER_FIELDS].forEach(k=>{ if(f[k]===0 && base[k]==='') base[k]='0'; });
  return base;
}

function EF({label,fieldKey,form,setForm,type='text',options,isCurrency,isNumber,placeholder,required,below}){
  const val = form[fieldKey]??'';
  return (
    <div style={s.field}>
      <div style={s.fieldLabel}>{label}{required && <span style={{color:'#c0392b',marginLeft:'2px'}}>*</span>}</div>
      {options?(
        <select style={s.select} value={val} onChange={e=>setForm(p=>({...p,[fieldKey]:e.target.value}))}>
          <option value="">Select…</option>
          {options.map(o=><option key={o}>{o}</option>)}
        </select>
      ):(
        <input style={s.input} type={isNumber?'number':type}
          value={isCurrency?(val?fmtD(val):''):val}
          onChange={e=>setForm(p=>({...p,[fieldKey]:isCurrency?pN(e.target.value):e.target.value}))}
          onFocus={e=>e.target.style.borderColor='#2B72C2'}
          onBlur={e=>e.target.style.borderColor='#B8D4F0'}
          placeholder={placeholder||(isCurrency?'$0':'')}/>
      )}
      {below}
    </div>
  );
}

function CB({label,checked,onChange}){
  return (
    <label style={s.checkboxRow}>
      <input type="checkbox" checked={!!checked} onChange={e=>onChange(e.target.checked)} style={{accentColor:'#2B72C2',width:'15px',height:'15px'}}/>
      {label}
    </label>
  );
}

function Card({id,title,icon,children,collapsed,onToggle}){
  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <i className={`ti ${icon}`} style={{color:'#154D97',fontSize:'14px'}}/>
        <div style={{...s.cardTitle,flex:1}}>{title}</div>
        <button style={s.collapseBtn} onClick={()=>onToggle(id)}>
          <i className={`ti ${collapsed?'ti-chevron-right':'ti-chevron-down'}`}/>
        </button>
      </div>
      {!collapsed&&<div style={s.cardBody}>{children}</div>}
    </div>
  );
}

function InlineCreateContact({initialName,defaultRole,onCreate,onCancel}){
  const initParts = (initialName || '').trim().split(/\s+/);
  const initFirst = initParts[0] || '';
  const initLast  = initParts.length > 1 ? initParts.slice(1).join(' ') : '';

  const [first,setFirst]   = useState(initFirst);
  const [last,setLast]     = useState(initLast);
  const [email,setEmail]   = useState('');
  const [phone,setPhone]   = useState('');
  const [company,setCompany] = useState('');
  const [saving,setSaving] = useState(false);
  const [error,setError]   = useState('');

  async function handleSave(){
    setError('');
    if(!first.trim() || !last.trim()){
      setError('First and last name are required.');
      return;
    }
    if(email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())){
      setError('Enter a valid email or leave blank.');
      return;
    }
    setSaving(true);
    try {
      const fields = {
        'First name': first.trim(),
        'Last name':  last.trim(),
      };
      if(email.trim())   fields['Email']            = email.trim();
      if(phone.trim())   fields['Phone']            = phone.trim();
      if(company.trim()) fields['Company / entity'] = company.trim();
      if(defaultRole)    fields['Role(s)']          = [defaultRole];

      const res = await authFetch('/.netlify/functions/create-contact',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({fields}),
      });
      const data = await res.json();
      if(data.error) throw new Error(data.error?.message || JSON.stringify(data.error));
      if(!data.id) throw new Error('No contact ID returned from server.');
      const newRecord = {
        id: data.id,
        fields: {
          ...data.fields,
          Name: data.fields?.Name || `${first.trim()} ${last.trim()}`.trim(),
        },
        createdTime: data.createdTime,
      };
      onCreate(newRecord);
    } catch(e){
      setError('Create failed: ' + e.message);
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={s.createForm}>
      <div style={s.createFormTitle}>
        <i className="ti ti-user-plus" style={{fontSize:'12px'}}/>
        New contact{defaultRole?`: ${defaultRole}`:''}
      </div>
      <div style={s.createFormGrid}>
        <input style={s.input} placeholder="First name *" value={first} onChange={e=>setFirst(e.target.value)} autoFocus/>
        <input style={s.input} placeholder="Last name *"  value={last}  onChange={e=>setLast(e.target.value)}/>
        <input style={s.input} placeholder="Email"        value={email} onChange={e=>setEmail(e.target.value)} type="email"/>
        <input style={s.input} placeholder="Phone"        value={phone} onChange={e=>setPhone(formatPhone(e.target.value))} type="tel"/>
      </div>
      <div style={{...s.createFormGrid,gridTemplateColumns:'1fr'}}>
        <input style={s.input} placeholder="Company / entity (optional)" value={company} onChange={e=>setCompany(e.target.value)}/>
      </div>
      {error && <div style={s.createErrorMsg}>{error}</div>}
      <div style={s.createFormActions}>
        <button style={s.createSaveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'Creating…' : 'Create & assign'}
        </button>
        <button style={s.createCancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

// The ONE contact layout. Rendered by DealParties, DealPayouts and mirrored by ContactDetail.jsx.
// Change it here, and change ContactDetail.jsx in the same pass.
function ContactFieldRows({d,cd}){
  return (
    <React.Fragment>
      <div style={{display:'flex',flexWrap:'wrap',gap:'12px 14px',alignItems:'flex-end'}}>
        <div style={{width:'130px'}}><AF label="First name" val={d.first} onChange={v=>cd('first',v)}/></div>
        <div style={{width:'130px'}}><AF label="Last name" val={d.last} onChange={v=>cd('last',v)}/></div>
        <div style={{width:'215px'}}><AF label="Email" val={d.email} onChange={v=>cd('email',v)}/></div>
        <div style={{width:'140px'}}><AF phone label="Phone" val={d.phone} onChange={v=>cd('phone',v)}/></div>
        <div style={{width:'190px'}}><ASel label="Role" val={(d.types&&d.types[0])||''} onChange={v=>cd('types',v?[v]:[])} options={CONTACT_TYPES}/></div>
      </div>
      <div style={s.cSubLbl}>Address</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:'12px 14px',alignItems:'flex-end'}}>
        <div style={{width:'430px'}}><AF label="Street address" val={d.street} onChange={v=>cd('street',v)}/></div>
        <div style={{width:'100px'}}><AF label="Unit / Suite" val={d.unit} onChange={v=>cd('unit',v)}/></div>
      </div>
      <div style={{display:'flex',flexWrap:'wrap',gap:'12px 14px',alignItems:'flex-end',marginTop:'12px'}}>
        <div style={{width:'180px'}}><AF label="City" val={d.city} onChange={v=>cd('city',v)}/></div>
        <div style={{width:'110px'}}><ASel label="State" val={d.state} onChange={v=>cd('state',v)} options={STATES}/></div>
        <div style={{width:'110px'}}><AF label="ZIP" val={d.zip} onChange={v=>cd('zip',v)}/></div>
        <div style={{width:'150px'}}><AF label="County" val={d.county} onChange={v=>cd('county',v)}/></div>
      </div>
      <div style={s.cSubLbl}>Company / Entity</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:'12px 14px',alignItems:'flex-end'}}>
        <div style={{width:'245px'}}><AF label="Business name" val={d.company} onChange={v=>cd('company',v)}/></div>
        <div style={{width:'150px'}}><ASel label="Entity type" val={d.entityType} onChange={v=>cd('entityType',v)} options={ENTITY_TYPES_FULL}/></div>
        <div style={{width:'130px'}}><AF label="Tax ID" val={d.taxId} onChange={v=>cd('taxId',v)}/></div>
        <div style={{width:'150px'}}>
          <div style={s.appLbl}>W-9 on file</div>
          <label style={{display:'flex',width:'fit-content',alignItems:'center',gap:'9px',fontSize:'13px',color:'#33415A',height:'33px',cursor:'pointer',marginTop:'4px'}}>
            <input type="checkbox" checked={!!d.w9} onChange={e=>cd('w9',e.target.checked)} style={{width:'16px',height:'16px',accentColor:'#154D97',cursor:'pointer'}}/>
            {d.w9?'On file':'Not on file'}
          </label>
        </div>
      </div>
    </React.Fragment>
  );
}

// Reads a contact record into the shape ContactFieldRows edits.
function contactToDraft(c){
  const f=(c&&c.fields)||{};
  const cl=x=>(x===undefined||x===null)?'':String(x);
  return {
    first:cl(f['First name']), last:cl(f['Last name']),
    types:Array.isArray(f['Role(s)'])?f['Role(s)']:(f['Role(s)']?[f['Role(s)']]:[]),
    phone:cl(f['Phone']), email:cl(f['Email']), company:cl(f['Company / entity']),
    street:cl(f['Street address']), unit:cl(f['Unit']), city:cl(f['City']),
    state:Array.isArray(f['Home state'])?(f['Home state'][0]||''):cl(f['Home state']),
    zip:cl(f['ZIP']), entityType:cl(f['Entity type']), taxId:cl(f['Tax ID']),
    county:cl(f['County']), w9:!!f['W-9 on file'],
  };
}

// Turns that draft back into an Airtable payload.
function draftToFields(d){
  const fields={
    'First name':d.first,'Last name':d.last,'Phone':d.phone,'Email':d.email,
    'Company / entity':d.company,'Street address':d.street,'Unit':d.unit,
    'City':d.city,'ZIP':d.zip,'County':d.county,'Entity type':d.entityType,'Tax ID':d.taxId,
    'W-9 on file':!!d.w9,
  };
  if(d.state) fields['Home state']=[d.state];
  if(d.types&&d.types.length) fields['Role(s)']=d.types;
  return fields;
}

// The first column of Deal Borrowers / Deal Contacts / Deal Payouts is a plain text
// field the portal fills in, so every row reads as a person and a deal rather than a
// counter. If that field has not been converted from an autonumber yet, Airtable
// rejects it and postWithLabel retries once without it, so nothing can break.
// Every child row is labelled "<deal name> - <the thing this row is>", so a
// table sorted by its first column groups a deal's rows together and you can
// tell at a glance which file any row belongs to.
function rowLabel(dealName, thing){
  const a=String(dealName||'').trim(), b=String(thing||'').trim();
  if(a&&b) return a+' - '+b;
  return a||b||'';
}
async function postWithLabel(url, fields, labelKey){
  const send=async(f)=>{
    const r=await authFetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(f)});
    return r.json();
  };
  let d=await send(fields);
  const msg=(d&&d.error&&(d.error.message||d.error))||'';
  if(labelKey&&/Unknown field name/i.test(String(msg))){
    const retry=JSON.parse(JSON.stringify(fields));
    if(retry.fields) delete retry.fields[labelKey];
    d=await send(retry);
  }
  return d;
}

function DealPayouts({dealId,brokerFee,allContacts,onTotals,onContactSaved,onContactPatched,dealLabel}){
  const [rows,setRows]=useState([]);
  const [open,setOpen]=useState({});
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState('');
  const [poAdd,setPoAdd]=useState(false);
  const [poQ,setPoQ]=useState('');
  const [editC,setEditC]=useState('');
  const [cDraft,setCDraft]=useState(null);
  const [cSaving,setCSaving]=useState(false);

  const cd=(k,v)=>setCDraft(p=>({...p,[k]:v}));
  const openContact=(cid)=>{
    if(editC===cid){ setEditC(''); setCDraft(null); return; }
    const c=(allContacts||[]).find(x=>x.id===cid); if(!c) return;
    setCDraft(contactToDraft(c)); setEditC(cid);
  };
  const saveContact=async(cid)=>{
    if(cSaving||!cDraft) return;
    setCSaving(true); setErr('');
    try{
      const res=await authFetch('/.netlify/functions/update-contact',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({contactId:cid,fields:draftToFields(cDraft)}),
      });
      const d=await res.json();
      if(d&&d.error){ setErr(d.error?.message||JSON.stringify(d.error)); }
      else { if(onContactSaved&&d&&d.id) onContactSaved(d); setEditC(''); setCDraft(null); }
    }catch(e){ setErr(e.message||'Could not save that contact.'); }
    setCSaving(false);
  };

  const load=React.useCallback(()=>{
    if(!dealId){setRows([]);return;}
    authFetch(`/.netlify/functions/list-deal-payouts?dealId=${dealId}`)
      .then(r=>r.json())
      .then(d=>setRows((d.records||[]).map(r=>({
        id:r.id,
        role:r.fields?.Role||'',
        pct:r.fields?.Percent!=null?String(Math.round(r.fields.Percent*10000)/100):'',
        amount:r.fields?.Amount!=null?String(r.fields.Amount):'',
        paidOn:r.fields?.['Paid on']||'',
        contactId:(r.fields?.Contact||[])[0]||'',
      }))))
      .catch(e=>setErr(e.message||'Could not load payouts.'));
  },[dealId]);
  useEffect(()=>{load();},[load]);

  const cOf=(cid)=>(allContacts||[]).find(x=>x.id===cid);
  const nameOf=(cid)=>{
    const c=cOf(cid); if(!c) return '';
    return clean(c.fields?.Name)
      || [clean(c.fields?.['First name']),clean(c.fields?.['Last name'])].filter(Boolean).join(' ')
      || clean(c.fields?.['Company / entity']) || '';
  };
  const entityOf=(cid)=>{
    const c=cOf(cid); if(!c) return '';
    const co=clean(c.fields?.['Company / entity']);
    const nm=[clean(c.fields?.['First name']),clean(c.fields?.['Last name'])].filter(Boolean).join(' ');
    return (co&&nm)?co:'';
  };
  const addrOf=(cid)=>{
    const c=cOf(cid); if(!c) return '';
    const f=c.fields||{};
    const line1=[clean(f['Street address']),clean(f['Unit'])].filter(Boolean).join(' ');
    const line2=[clean(f['City']),[clean(f['Home state']),clean(f['ZIP'])].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return [line1,line2].filter(Boolean).join(', ');
  };

  const nnp=v=>Number(String(v||'').replace(/[^0-9.]/g,''))||0;
  const fee=nnp(brokerFee);
  const out=rows.reduce((a,r)=>a+nnp(r.amount),0);
  const net=fee-out;
  useEffect(()=>{ if(onTotals) onTotals({out,net}); },[out,net]); // eslint-disable-line

  const patch=async(rowId,fields,localPatch)=>{
    setRows(p=>p.map(x=>x.id===rowId?{...x,...localPatch}:x));
    if(String(rowId).startsWith('tmp-')) return;
    try{
      const res=await authFetch('/.netlify/functions/update-deal-payout',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({recordId:rowId,fields}),
      });
      const d=await res.json();
      if(d&&d.error) setErr(d.error?.message||JSON.stringify(d.error));
    }catch(e){ setErr(e.message||'Could not save that payout.'); }
  };

  const setField=(row,key,val)=>{
    if(key==='pct'){
      const amt = fee ? Math.round(fee*nnp(val))/100 : 0;
      patch(row.id,{Percent:nnp(val)/100||null,Amount:amt||null},{pct:val,amount:amt?String(amt):''});
    } else if(key==='amount'){
      const pc = fee ? Math.round((nnp(val)/fee)*10000)/100 : 0;
      patch(row.id,{Amount:nnp(val)||null,Percent:pc?pc/100:null},{amount:val,pct:pc?String(pc):''});
    } else if(key==='role'){
      patch(row.id,{Role:val},{role:val});
    } else if(key==='paidOn'){
      patch(row.id,{'Paid on':val||null},{paidOn:val});
    } else if(key==='contactId'){
      patch(row.id,{Contact:val?[val]:[]},{contactId:val});
    }
  };

  // Payee details buffer into a draft, then Save contact writes them. Same shape as
  // Parties and Contacts. The payout's own fields still save on change like every other row.
  const [payee,setPayee]=useState(null);
  const [payeeSaving,setPayeeSaving]=useState(false);
  const payeeOf=(cid)=>(payee&&payee.cid===cid)?payee.d:contactToDraft(cOf(cid));
  const payeeDirty=(cid)=>!!(payee&&payee.cid===cid);
  const setPayeeField=(cid,k,v)=>setPayee({cid,d:{...payeeOf(cid),[k]:v}});
  const savePayee=async(cid)=>{
    if(payeeSaving||!payeeDirty(cid)) return;
    const fields=draftToFields(payeeOf(cid));
    setPayeeSaving(true); setErr('');
    if(onContactPatched) onContactPatched(cid,fields);
    try{
      const res=await authFetch('/.netlify/functions/update-contact',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({contactId:cid,fields}),
      });
      const dd=await res.json();
      if(dd&&dd.error) setErr(dd.error?.message||JSON.stringify(dd.error));
      else setPayee(null);
    }catch(e){ setErr(e.message||'Could not save that contact.'); }
    setPayeeSaving(false);
  };

  const saveContactField=async(cid,key,val)=>{
    if(!cid) return;
    if(onContactPatched) onContactPatched(cid,{[key]:val});
    try{
      const res=await authFetch('/.netlify/functions/update-contact',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({contactId:cid,fields:{[key]:val}}),
      });
      const d=await res.json();
      if(d&&d.error) setErr(d.error?.message||JSON.stringify(d.error));
    }catch(e){ setErr(e.message||'Could not save that contact detail.'); }
  };

  const addRow=async(contactId)=>{
    if(busy) return;
    setErr('');
    if(!dealId){ setErr('Save the deal first, then add payouts.'); return; }
    const cid=contactId||'';
    const tempId='tmp-'+Date.now();
    setRows(p=>[...p,{id:tempId,role:'',pct:'',amount:'',paidOn:'',contactId:cid}]);
    setOpen(p=>({...p,[tempId]:true}));
    setPoAdd(false); setPoQ('');
    setBusy(true);
    try{
      const fields={Deal:[dealId]};
      if(cid) fields.Contact=[cid];
      fields['Payout']=rowLabel(dealLabel,cid?(cOf(cid)?.fields?.Name||''):'');
      const d=await postWithLabel('/.netlify/functions/create-deal-payout',{fields},'Payout');
      if(d&&d.id){
        setRows(p=>p.map(r=>r.id===tempId?{...r,id:d.id}:r));
        setOpen(p=>{ const n={...p,[d.id]:true}; delete n[tempId]; return n; });
      } else {
        setRows(p=>p.filter(r=>r.id!==tempId));
        setErr((d&&(d.error&&(d.error.message||d.error)))||'Could not add a payout row.');
      }
    }catch(e){ setRows(p=>p.filter(r=>r.id!==tempId)); setErr(e.message||'Network error adding a payout.'); }
    setBusy(false);
  };

  const removeRow=async(rowId)=>{
    setRows(p=>p.filter(x=>x.id!==rowId));
    try{
      await authFetch('/.netlify/functions/delete-deal-payout',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({recordId:rowId}),
      });
    }catch(e){ setErr(e.message||'Could not remove that row.'); }
  };

  const contactOptions=(allContacts||[]).map(c=>({
    id:c.id,
    label:(clean(c.fields?.Name)||[clean(c.fields?.['First name']),clean(c.fields?.['Last name'])].filter(Boolean).join(' ')||clean(c.fields?.['Company / entity'])||'').trim()
  })).filter(o=>o.label).sort((a,b)=>a.label.localeCompare(b.label));

  return (
    <React.Fragment>
      <div style={s.poHdr}>
        <span/><span>Paid to</span><span>Role</span><span>Amount</span><span>Paid on</span>
      </div>

      {rows.length===0&&<div style={{fontSize:'12.5px',color:'#8a9ab5',marginBottom:'8px'}}>Nothing paid out yet.</div>}

      {rows.map(r=>(
        <div key={r.id} style={s.poWrap}>
          <div style={{...s.poRow,border:'none',marginBottom:0,cursor:'pointer'}} onClick={()=>setOpen(p=>({...p,[r.id]:!p[r.id]}))}>
            <span style={s.reoCaret}>{open[r.id]?'\u25BE':'\u25B8'}</span>
            <span style={s.poWho}>
              {nameOf(r.contactId)||<span style={{color:'#8a9ab5',fontWeight:400}}>Not set</span>}
              {entityOf(r.contactId)&&<span style={s.poVia}>{entityOf(r.contactId)}</span>}
            </span>
            <span style={s.poRole}>{r.role}</span>
            <span style={s.poAmt}>{r.amount?fmtD(Math.round(nnp(r.amount)*100)/100):''}</span>
            <span style={s.poDt}>{r.paidOn}</span>
          </div>
          {open[r.id]&&<div style={s.reoBody}>
            <div style={{display:'flex',gap:'15px',alignItems:'flex-end',flexWrap:'wrap'}}>
              <div style={{width:'190px'}}><ASel label="Role" val={r.role} onChange={v=>setField(r,'role',v)} options={PAYOUT_ROLES}/></div>
              <div style={{width:'110px'}}><AF pct placeholder="%" label="Percent" val={r.pct} onChange={v=>setField(r,'pct',v)}/></div>
              <div style={{width:'150px'}}><AF money placeholder="$" label="Amount" val={r.amount} onChange={v=>setField(r,'amount',v)}/></div>
              <div style={{width:'150px'}}><AF date label="Paid on" val={r.paidOn} onChange={v=>setField(r,'paidOn',v)}/></div>
            </div>
            <div style={{...s.subHint,marginTop:'11px'}}>Percent is a percent of the broker fee and only fills the amount in. Type over the amount for a flat fee or an odd split.</div>

            {r.contactId&&<div style={{borderTop:'1px solid #F2F5F9',marginTop:'16px',paddingTop:'14px'}}>
              <ContactFieldRows d={payeeOf(r.contactId)} cd={(k,v)=>setPayeeField(r.contactId,k,v)}/>
              <div style={{display:'flex',alignItems:'center',gap:'9px',marginTop:'16px'}}>
                <button style={s.dpSave} onClick={()=>savePayee(r.contactId)}>{payeeSaving?'Saving...':'Save contact'}</button>
                <button style={s.dpCancel} onClick={()=>setPayee(null)}>Cancel</button>
                <button style={s.finDel} onClick={()=>{if(!window.confirm('Delete this payout row?'))return;removeRow(r.id);}}>Delete payout</button>
              </div>
              <div style={{...s.subHint,marginTop:'11px'}}>This is their contact record. Changing it here changes it everywhere, including on their other deals.</div>
            </div>}
          </div>}
        </div>
      ))}

      {err&&<div style={{fontSize:'12px',color:'#B4532A',background:'#FBF3EE',border:'1px solid #E8D3C4',borderRadius:'8px',padding:'8px 11px',margin:'4px 0 8px'}}>{err}</div>}

      {poAdd&&<div style={{display:'flex',alignItems:'center',gap:'14px',padding:'8px 13px',border:'1px solid #E9EFF6',borderRadius:'10px',marginTop:'10px',position:'relative'}}>
        <div style={{position:'relative',width:'430px',flexShrink:0}}>
          <input autoFocus value={poQ} onChange={e=>setPoQ(e.target.value)} placeholder="Search contacts or type a name" style={{...s.appInput,marginTop:0,width:'100%',height:'32px',fontSize:'12.5px',borderColor:'#4A7FBF'}}/>
          {clean(poQ)&&<div style={{...s.dpDrop,top:'38px',left:0,width:'430px'}}>
            {(allContacts||[]).filter(c=>(c.fields?.Name||'').toLowerCase().includes(poQ.trim().toLowerCase())).slice(0,6).map(c=>(
              <div key={c.id} style={s.dpOpt} onClick={()=>addRow(c.id)}>
                {c.fields?.Name}<span style={s.dpOptMeta}>{[(c.fields?.['Role(s)']||[]).join(', '),c.fields?.['Company / entity']].filter(Boolean).join(' \u00B7 ')}</span>
              </div>
            ))}
            <div style={s.dpOptNew} onClick={()=>addRow('')}>Add a payout without a contact</div>
          </div>}
        </div>
        <span style={{marginLeft:'auto',fontSize:'11px',color:'#8A9AB5',cursor:'pointer'}} onClick={()=>{setPoAdd(false);setPoQ('');}}>Cancel</span>
      </div>}
      <button style={{...s.addRowBtn,marginTop:'2px'}} onClick={()=>{ if(poAdd){setPoAdd(false);setPoQ('');} else {setErr(''); setPoAdd(true);} }}>+ Payout</button>

      <div style={s.poNet}><span style={s.poNetK}>Net to me</span><span style={s.poNetV}>{fee?fmtD(Math.round(net*100)/100):'-'}</span></div>
      <div style={{...s.subHint,marginTop:'9px'}}>Broker fee less every payout above.</div>
    </React.Fragment>
  );
}

function DealParties({dealId,allContacts,borrowerName,borrowerContactId,onCreateNew,dealLabel}){
  const [rows,setRows]=useState([]);
  const [adding,setAdding]=useState(false);
  const [query,setQuery]=useState('');
  const [creating,setCreating]=useState(false);
  const [draft,setDraft]=useState({first:'',last:'',type:'',phone:'',email:'',company:''});
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState('');
  const [openC,setOpenC]=useState('');
  const [cDraft,setCDraft]=useState(null);
  const [cSaving,setCSaving]=useState(false);

  const load=React.useCallback(()=>{
    if(!dealId){setRows([]);return;}
    authFetch(`/.netlify/functions/list-deal-contacts?dealId=${dealId}`)
      .then(r=>r.json())
      .then(d=>setRows((d.records||[]).map(r=>({
        id:r.id,
        role:r.fields?.Role||'',
        contactId:(r.fields?.Contact||[])[0]||'',
      }))))
      .catch(e=>setErr(e.message||'Could not load parties.'));
  },[dealId]);
  useEffect(()=>{load();},[load]);

  const cOf=(cid)=>(allContacts||[]).find(x=>x.id===cid);
  const nameOf=(cid)=>{
    const c=cOf(cid); if(!c) return '';
    return clean(c.fields?.Name)
      || [clean(c.fields?.['First name']),clean(c.fields?.['Last name'])].filter(Boolean).join(' ')
      || clean(c.fields?.['Company / entity']) || '';
  };
  const typesOf=(cid)=>{ const c=cOf(cid); const r=c&&c.fields?.['Role(s)']; return Array.isArray(r)?r:(r?[r]:[]); };
  const fieldOf=(cid,k)=>{ const c=cOf(cid); return c?clean(c.fields?.[k]):''; };

  const openEditor=(cid,rec)=>{
    if(openC===cid&&!rec){ setOpenC(''); setCDraft(null); return; }
    const c=rec||cOf(cid); if(!c) return;
    setCDraft(contactToDraft(c));
    setOpenC(cid);
  };

  const saveContact=async(cid)=>{
    if(cSaving||!cDraft) return;
    setCSaving(true); setErr('');
    try{
      const fields=draftToFields(cDraft);
      const res=await authFetch('/.netlify/functions/update-contact',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({contactId:cid,fields}),
      });
      const d=await res.json();
      if(d&&d.error){ setErr(d.error?.message||JSON.stringify(d.error)); }
      else { if(onCreateNew&&d&&d.id) onCreateNew(d); setOpenC(''); setCDraft(null); }
    }catch(e){ setErr(e.message||'Could not save that contact.'); }
    setCSaving(false);
  };

  const used=rows.map(r=>r.contactId).filter(Boolean);
  const matches=(()=>{
    const q=query.trim().toLowerCase();
    if(!q) return [];
    return (allContacts||[])
      .filter(c=>!used.includes(c.id)&&c.id!==borrowerContactId)
      .map(c=>({id:c.id,label:nameOf(c.id),types:typesOf(c.id).join(', '),co:clean(c.fields?.['Company / entity'])}))
      .filter(o=>o.label&&(o.label.toLowerCase().includes(q)||(o.co||'').toLowerCase().includes(q)))
      .sort((a,b)=>a.label.localeCompare(b.label))
      .slice(0,8);
  })();

  const closeAdd=()=>{ setAdding(false); setCreating(false); setQuery(''); setDraft({first:'',last:'',type:'',phone:'',email:'',company:''}); setErr(''); };

  const attach=async(contactId,roleGuess,rec)=>{
    if(busy||!contactId) return;
    setErr('');
    if(!dealId){ setErr('Save the deal first, then add parties.'); return; }
    const role=roleGuess!==undefined?roleGuess:(typesOf(contactId)[0]||'');
    // Draw the row and open the editor straight away, then reconcile. Otherwise she
    // waits on a Netlify cold start with nothing on screen.
    const tempId='tmp-'+Date.now();
    setRows(p=>[...p,{id:tempId,role,contactId}]);
    closeAdd();
    openEditor(contactId,rec);
    setBusy(true);
    try{
      const d=await postWithLabel('/.netlify/functions/create-deal-contact',
        {fields:{Deal:[dealId],Contact:[contactId],Role:role,'Party':rowLabel(dealLabel,nameOf(contactId))}},'Party');
      if(d&&d.id){ setRows(p=>p.map(r=>r.id===tempId?{id:d.id,role,contactId}:r)); }
      else { setRows(p=>p.filter(r=>r.id!==tempId)); setErr((d&&(d.error&&(d.error.message||d.error)))||'Could not add that party.'); }
    }catch(e){ setRows(p=>p.filter(r=>r.id!==tempId)); setErr(e.message||'Network error adding a party.'); }
    setBusy(false);
  };

  const startCreate=()=>{
    const parts=query.trim().split(/\s+/);
    setDraft({first:parts[0]||'',last:parts.slice(1).join(' '),type:'',phone:'',email:'',company:''});
    setCreating(true);
  };

  const saveNew=async()=>{
    if(busy) return;
    setErr('');
    if(!clean(draft.first)&&!clean(draft.last)&&!clean(draft.company)){
      setErr('Enter a first and last name, or a company name.'); return;
    }
    if(clean(draft.email)&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(draft.email))){
      setErr('Enter a valid email or leave it blank.'); return;
    }
    setBusy(true);
    try{
      const fields={};
      if(clean(draft.first))   fields['First name']       = clean(draft.first);
      if(clean(draft.last))    fields['Last name']        = clean(draft.last);
      if(clean(draft.company)) fields['Company / entity'] = clean(draft.company);
      if(clean(draft.phone))   fields['Phone']            = clean(draft.phone);
      if(clean(draft.email))   fields['Email']            = clean(draft.email);
      if(draft.type)           fields['Role(s)']          = [draft.type];
      const res=await authFetch('/.netlify/functions/create-contact',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({fields}),
      });
      const d=await res.json();
      if(d&&d.id){
        if(onCreateNew) onCreateNew(d);
        setBusy(false);
        await attach(d.id,draft.type||'',d);
        return;
      }
      setErr((d&&(d.error&&(d.error.message||d.error)))||'Could not create that contact.');
    }catch(e){ setErr(e.message||'Network error creating the contact.'); }
    setBusy(false);
  };

  const setRole=async(rowId,role)=>{
    setRows(p=>p.map(x=>x.id===rowId?{...x,role}:x));
    if(String(rowId).startsWith('tmp-')) return; // the create call carries the role
    try{
      const res=await authFetch('/.netlify/functions/update-deal-contact',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({recordId:rowId,fields:{Role:role}}),
      });
      const d=await res.json();
      if(d&&d.error) setErr(d.error?.message||JSON.stringify(d.error));
    }catch(e){ setErr(e.message||'Could not change that role.'); }
  };

  const removeRow=async(rowId)=>{
    setRows(p=>p.filter(x=>x.id!==rowId));
    if(String(rowId).startsWith('tmp-')) return;
    try{
      await authFetch('/.netlify/functions/delete-deal-contact',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({recordId:rowId}),
      });
    }catch(e){ setErr(e.message||'Could not remove that party.'); }
  };

  const cd=(k,v)=>setCDraft(p=>({...p,[k]:v}));

  const partyRow=(key,cid,name,role,phone,email,company,types,onRole,onRemove)=>(
    <React.Fragment key={key}>
      <div style={s.dpRow}>
        <span style={s.dpName}>
          {cid&&<span style={{...s.reoCaret,marginRight:'8px',cursor:'pointer'}} onClick={()=>openEditor(cid)}>{openC===cid?'\u25BE':'\u25B8'}</span>}
          {name}
        </span>
        <span>
          {types&&types.length>1&&onRole
            ? <select style={s.appSelect} value={role} onChange={e=>onRole(e.target.value)}>
                {types.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            : <span style={s.dpRole}>{role||<span style={{color:'#8a9ab5'}}>No type on their record</span>}</span>}
        </span>
        <span style={s.dpCell}>{phone}</span>
        <span style={{...s.dpCell,color:'#154D97'}}>{email}</span>
        <span style={s.dpCell}>{company||'-'}</span>
        <span style={{textAlign:'right'}}></span>
      </div>
      {openC===cid&&cDraft&&<div style={s.dpEdit}>
        <ContactFieldRows d={cDraft} cd={cd}/>
        <div style={{display:'flex',alignItems:'center',gap:'9px',marginTop:'16px'}}>
          <button style={s.dpSave} onClick={()=>saveContact(cid)}>{cSaving?'Saving...':'Save contact'}</button>
          <button style={s.dpCancel} onClick={()=>{setOpenC('');setCDraft(null);}}>Cancel</button>
          {onRemove&&<button style={s.finDel} onClick={()=>{if(!window.confirm('Remove '+(name||'this person')+' from this deal? Their contact record is not deleted.'))return;setOpenC('');setCDraft(null);onRemove();}}>Remove from deal</button>}
        </div>
        <div style={{...s.subHint,marginTop:'11px'}}>This is their contact record. Changing it here changes it everywhere, including on their other deals.</div>
      </div>}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <div style={s.dpHdr}>
        <span>Name</span><span>Role on this deal</span><span>Phone</span><span>Email</span><span>Company / entity</span><span/>
      </div>

      {borrowerContactId&&partyRow('borrower',borrowerContactId,borrowerName||nameOf(borrowerContactId)||'Borrower','Borrower',
        fieldOf(borrowerContactId,'Phone'),fieldOf(borrowerContactId,'Email'),fieldOf(borrowerContactId,'Company / entity'),
        null,null,null)}

      {rows.map(r=>partyRow(r.id,r.contactId,nameOf(r.contactId)||'Unnamed contact',r.role,
        fieldOf(r.contactId,'Phone'),fieldOf(r.contactId,'Email'),fieldOf(r.contactId,'Company / entity'),
        typesOf(r.contactId),v=>setRole(r.id,v),()=>removeRow(r.id)))}

      {adding&&!creating&&<div style={{display:'flex',alignItems:'center',gap:'14px',padding:'8px 13px',border:'1px solid #E9EFF6',borderRadius:'10px',marginBottom:'8px',position:'relative'}}>
        <div style={{position:'relative',width:'430px',flexShrink:0}}>
          <input autoFocus style={{...s.appInput,marginTop:0,width:'100%',height:'32px',fontSize:'12.5px',borderColor:'#4A7FBF'}} placeholder="Search contacts or type a name" value={query} onChange={e=>setQuery(e.target.value)}/>
          {clean(query)&&<div style={{...s.dpDrop,top:'38px',left:0,width:'430px'}}>
            {matches.map(o=>(
              <div key={o.id} style={s.dpOpt} onClick={()=>attach(o.id)}>
                {o.label}{o.types&&<span style={s.dpOptMeta}>{o.types}</span>}
              </div>
            ))}
            <div style={s.dpOptNew} onClick={startCreate}>{'Create new contact: "'+query.trim()+'"'}</div>
          </div>}
        </div>
        <span style={{marginLeft:'auto',fontSize:'11px',color:'#8A9AB5',cursor:'pointer'}} onClick={closeAdd}>Cancel</span>
      </div>}

      {adding&&creating&&<div style={s.dpNewBox}>
        <div style={s.dpNewT}>New contact</div>
        <div style={{display:'flex',flexWrap:'wrap',gap:'12px 14px',alignItems:'flex-end'}}>
          <div style={{width:'160px'}}><AF label="First name" val={draft.first} onChange={v=>setDraft(d=>({...d,first:v}))}/></div>
          <div style={{width:'160px'}}><AF label="Last name" val={draft.last} onChange={v=>setDraft(d=>({...d,last:v}))}/></div>
          <div style={{width:'195px'}}><ASel label="Role" val={draft.type} onChange={v=>setDraft(d=>({...d,type:v}))} options={CONTACT_TYPES}/></div>
          <div style={{width:'160px'}}><AF phone label="Phone" val={draft.phone} onChange={v=>setDraft(d=>({...d,phone:v}))}/></div>
          <div style={{width:'215px'}}><AF label="Email" val={draft.email} onChange={v=>setDraft(d=>({...d,email:v}))}/></div>
          <div style={{width:'200px'}}><AF label="Business name" val={draft.company} onChange={v=>setDraft(d=>({...d,company:v}))}/></div>
          <button style={s.dpSave} onClick={saveNew}>{busy?'Saving...':'Save'}</button>
          <button style={s.dpCancel} onClick={closeAdd}>Cancel</button>
        </div>
      </div>}

      {err&&<div style={{fontSize:'12px',color:'#B4532A',background:'#FBF3EE',border:'1px solid #E8D3C4',borderRadius:'8px',padding:'8px 11px',margin:'4px 0 8px'}}>{err}</div>}

      <div style={s.dcFoot}>
        <button style={{...s.addRowBtn,marginTop:0}} onClick={()=>{ if(adding){closeAdd();} else {setErr(''); setAdding(true);} }}>+ Party</button>
        <span style={{fontSize:'12.5px',color:'#154D97',fontWeight:600}}>{rows.length+(borrowerContactId?1:0)} on this deal</span>
      </div>
    </React.Fragment>
  );
}

function ContactSearch({label,value,onChange,allContacts,onCreateNew,defaultRole,autoOpen}){
  const [query,setQuery]=useState('');
  const [results,setResults]=useState([]);
  const [show,setShow]=useState(false);
  const [creating,setCreating]=useState(false);

  useEffect(()=>{
    if(!query.trim()){setResults([]);if(!autoOpen)setShow(false);return;}
    const q=query.toLowerCase();
    const f=allContacts.filter(c=>(c.fields?.Name||'').toLowerCase().includes(q)).slice(0,8);
    setResults(f);
    if(!autoOpen)setShow(true);
  },[query,allContacts,autoOpen]);

  const selected = allContacts.find(c=>c.id===value);

  if(value&&selected){
    return(
      <div style={s.field}>
        {label && <div style={s.fieldLabel}>{label}</div>}
        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
          <span style={s.linkedPill}><i className="ti ti-user" style={{fontSize:'10px'}}/>{selected.fields?.Name||'Unknown'}</span>
          <button style={{background:'none',border:'none',color:'#8a9ab5',cursor:'pointer',padding:'2px',fontSize:'13px'}} onClick={()=>onChange('')}><i className="ti ti-x"/></button>
        </div>
      </div>
    );
  }

  if(creating && onCreateNew){
    return (
      <div style={s.field}>
        {label && <div style={s.fieldLabel}>{label}</div>}
        <InlineCreateContact
          initialName={query}
          defaultRole={defaultRole}
          onCreate={(newRecord)=>{
            onCreateNew(newRecord);
            onChange(newRecord.id);
            setQuery('');
            setShow(false);
            setCreating(false);
          }}
          onCancel={()=>setCreating(false)}
        />
      </div>
    );
  }

  if(autoOpen){
    return(
      <div style={s.field}>
        {label && <div style={s.fieldLabel}>{label}</div>}
        <input style={s.input} autoFocus placeholder="Search contacts by name…" value={query}
          onChange={e=>setQuery(e.target.value)}
          onFocus={e=>e.target.style.borderColor='#2B72C2'}
          onBlur={e=>e.target.style.borderColor='#B8D4F0'}/>
        <div style={s.inlineSearchPanel}>
          {query.trim() && results.length > 0 && results.map(c=>(
            <div key={c.id} style={s.searchItem}
              onMouseEnter={e=>e.currentTarget.style.background='#E4EDF8'}
              onMouseLeave={e=>e.currentTarget.style.background='#fff'}
              onClick={()=>{onChange(c.id);setQuery('');}}>
              <div style={{fontWeight:500}}>{c.fields?.Name||'Unnamed'}</div>
              <div style={{fontSize:'11px',color:'#8a9ab5'}}>{[c.fields?.['Company / entity'],(c.fields?.['Role(s)']||[]).join(', ')].filter(Boolean).join(' · ')}</div>
            </div>
          ))}
          {query.trim() && results.length === 0 && (
            <div style={{...s.searchItem,color:'#8a9ab5',fontStyle:'italic'}}>
              No matching contacts.
            </div>
          )}
          {!query.trim() && (
            <div style={{...s.searchItem,color:'#8a9ab5',fontStyle:'italic'}}>
              Start typing to search existing contacts, or create a new one below.
            </div>
          )}
          {onCreateNew && (
            <div style={s.createNewItem}
              onClick={()=>setCreating(true)}>
              <i className="ti ti-user-plus" style={{fontSize:'13px'}}/>
              {query.trim() ? `Create new contact: "${query.trim()}"` : 'Create new contact'}
            </div>
          )}
        </div>
      </div>
    );
  }

  return(
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <div style={s.searchWrap}>
        <input style={s.input} placeholder="Search contacts…" value={query}
          onChange={e=>setQuery(e.target.value)}
          onFocus={e=>e.target.style.borderColor='#2B72C2'}
          onBlur={()=>{setTimeout(()=>setShow(false),200);}}/>
        {show&&(
          <div style={s.searchResults}>
            {results.length > 0 && results.map(c=>(
              <div key={c.id} style={s.searchItem}
                onMouseEnter={e=>e.currentTarget.style.background='#E4EDF8'}
                onMouseLeave={e=>e.currentTarget.style.background='#fff'}
                onMouseDown={()=>{onChange(c.id);setQuery('');setShow(false);}}>
                <div style={{fontWeight:500}}>{c.fields?.Name||'Unnamed'}</div>
                <div style={{fontSize:'11px',color:'#8a9ab5'}}>{[c.fields?.['Company / entity'],(c.fields?.['Role(s)']||[]).join(', ')].filter(Boolean).join(' · ')}</div>
              </div>
            ))}
            {results.length === 0 && (
              <div style={{...s.searchItem,color:'#8a9ab5',fontStyle:'italic',cursor:'default'}}>
                No matching contacts.
              </div>
            )}
            {onCreateNew && (
              <div style={s.createNewItem}
                onMouseDown={(e)=>{e.preventDefault();setCreating(true);setShow(false);}}>
                <i className="ti ti-user-plus" style={{fontSize:'13px'}}/>
                {query.trim() ? `Create new contact: "${query.trim()}"` : 'Create new contact'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CoBorrowerCreateForm({initialName,onDone,onCancel}){
  const parts=(initialName||'').trim().split(/\s+/);
  const [cf,setCf]=useState({
    'First name':parts[0]||'','Last name':parts.length>1?parts.slice(1).join(' '):'',
    'Email':'','Phone':'',
    'Street address':'','City':'','Home state':'','ZIP':'',
    'Company / entity':'','Entity type':'',
  });
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');
  const set=k=>v=>setCf(p=>({...p,[k]:v}));
  async function create(){
    setError('');
    if(!cf['First name'].trim()||!cf['Last name'].trim()){ setError('First and last name are required.'); return; }
    setSaving(true);
    try{
      const fields={'First name':cf['First name'].trim(),'Last name':cf['Last name'].trim(),'Role(s)':['Borrower']};
      if(cf['Email'].trim()) fields['Email']=cf['Email'].trim();
      if(cf['Phone'].trim()) fields['Phone']=cf['Phone'].trim();
      if(cf['Street address'].trim()) fields['Street address']=cf['Street address'].trim();
      if(cf['City'].trim()) fields['City']=cf['City'].trim();
      if(cf['Home state']) fields['Home state']=[cf['Home state']];
      if(cf['ZIP'].trim()) fields['ZIP']=cf['ZIP'].trim();
      if(cf['Company / entity'].trim()) fields['Company / entity']=cf['Company / entity'].trim();
      if(cf['Entity type']) fields['Entity type']=cf['Entity type'];
      const res=await authFetch('/.netlify/functions/create-contact',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fields})});
      const data=await res.json();
      if(data.error) throw new Error(data.error?.message||JSON.stringify(data.error));
      if(!data.id) throw new Error('No contact ID returned from server.');
      onDone({id:data.id,fields:{...data.fields,Name:data.fields?.Name||`${cf['First name'].trim()} ${cf['Last name'].trim()}`},createdTime:data.createdTime});
    }catch(e){ setError('Create failed: '+e.message); }
    finally{ setSaving(false); }
  }
  return (
    <div style={{marginTop:'6px'}}>
      <div style={s.appGrid}>
        <AF label="First name" val={cf['First name']} onChange={set('First name')}/>
        <AF label="Last name" val={cf['Last name']} onChange={set('Last name')}/>
        <AF label="Email" val={cf['Email']} onChange={set('Email')}/>
        <AF label="Phone" val={cf['Phone']} onChange={set('Phone')}/>
      </div>
      <div style={{...s.subLabel,marginTop:'18px'}}>Home address</div>
      <div style={s.appGrid}>
        <AF label="Street address" val={cf['Street address']} onChange={set('Street address')}/>
        <AF label="City" val={cf['City']} onChange={set('City')}/>
        <ASel label="State" val={cf['Home state']} onChange={set('Home state')} options={STATES}/>
        <AF label="ZIP" val={cf['ZIP']} onChange={set('ZIP')}/>
      </div>
      <div style={{...s.subLabel,marginTop:'18px'}}>Entity / vesting</div>
      <div style={s.appGrid}>
        <AF label="Entity name" val={cf['Company / entity']} onChange={set('Company / entity')}/>
        <ASel label="Entity type" val={cf['Entity type']} onChange={set('Entity type')} options={ENTITY_TYPES}/>
      </div>
      {error && <div style={{marginTop:'10px',background:'#FBEFEF',border:'1px solid #EBC7C2',color:'#A23A2E',fontSize:'12px',borderRadius:'8px',padding:'8px 12px'}}>{error}</div>}
      <div style={{marginTop:'14px',display:'flex',gap:'8px',justifyContent:'flex-end'}}>
        <button style={{...s.createCancelBtn}} onClick={onCancel} disabled={saving}>Cancel</button>
        <button style={{...s.createSaveBtn}} onClick={create} disabled={saving}>{saving?'Creating...':'Create and link co-borrower'}</button>
      </div>
    </div>
  );
}

function TransactionParties({form,setForm,allContacts,onCreateNew}){
  const [addingRole,setAddingRole]=useState('');
  const filledRoles = ALL_TX_ROLES.filter(r => form[r]);

  function getContactName(id){
    const c = allContacts.find(x => x.id === id);
    if(!c) return 'Unknown';
    const name = c.fields?.Name || 'Unnamed';
    const company = c.fields?.['Company / entity'];
    return company ? `${name} (${company})` : name;
  }
  function setRole(role,contactId){
    setForm(p => ({...p,[role]:contactId}));
    setAddingRole('');
  }
  function clearRole(role){
    setForm(p => ({...p,[role]:''}));
  }

  return(
    <div>
      <div style={s.subHint}>Click a party to change or remove. Use "Add party" to assign new ones, or create a new contact inline if they don't exist yet.</div>

      {filledRoles.length === 0 ? (
        <div style={s.emptyParties}>No parties assigned yet.</div>
      ) : (
        <div>
          {filledRoles.map(role => (
            <div key={role} style={s.partyRow}>
              <div style={s.partyRoleLabel}>{role}</div>
              <div style={s.partyName}>{getContactName(form[role])}</div>
              <button style={s.partyChangeBtn} onClick={()=>setAddingRole(role)}>Change</button>
              <button style={s.partyRemoveBtn} onClick={()=>clearRole(role)} title="Remove">
                <i className="ti ti-x"/>
              </button>
            </div>
          ))}
        </div>
      )}

      {!addingRole ? (
        <button style={{...s.addRowBtn,marginTop:'12px'}}
          onClick={()=>setAddingRole('__pick__')}>
          <i className="ti ti-plus" style={{fontSize:'13px'}}/> Add party
        </button>
      ) : addingRole === '__pick__' ? (
        <div style={s.partyAddBox}>
          <div style={s.partyAddBoxTitle}>Pick a role to add</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:'8px'}}>
            {Object.entries(TX_PARTY_GROUPS).map(([groupName,roles])=>{
              const available = roles.filter(r => !form[r]);
              if(available.length === 0) return null;
              return (
                <div key={groupName}>
                  <div style={{fontSize:'9px',fontWeight:700,color:'#7AADE0',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:'4px'}}>{groupName}</div>
                  {available.map(r => (
                    <button key={r} style={{display:'block',width:'100%',textAlign:'left',padding:'6px 10px',background:'#fff',border:'1px solid #B8D4F0',borderRadius:'5px',color:'#0D2545',fontSize:'12px',marginBottom:'4px',cursor:'pointer',fontFamily:'inherit'}}
                      onClick={()=>setAddingRole(r)}>
                      + {r}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
          <button style={s.partyAddCancel} onClick={()=>setAddingRole('')}>Cancel</button>
        </div>
      ) : (
        <div style={s.partyAddBox}>
          <div style={s.partyAddBoxTitle}>Assign {addingRole}</div>
          <ContactSearch label="" value={form[addingRole]||''}
            onChange={val=>{ if(val) setRole(addingRole,val); }}
            allContacts={allContacts}
            onCreateNew={onCreateNew}
            defaultRole={DEAL_ROLE_TO_CONTACT_ROLE[addingRole]}
            autoOpen/>
          <button style={s.partyAddCancel} onClick={()=>setAddingRole('')}>Cancel</button>
        </div>
      )}
    </div>
  );
}

function SreoTable({sreoRows,setSreoRows,editing}){
  function updateRow(idx,key,val){setSreoRows(prev=>prev.map((r,i)=>i===idx?{...r,[key]:val}:r));}
  function addRow(){setSreoRows(prev=>[...prev,{'Property address':'','Property type':'','Use':'','Purchase price':'','Current value':'','Loan balance':'','Monthly payment':'','Rental income':'','Lender name':'','Status':'',_id:null,_isNew:true,_uid:newUid()}]);}
  function deleteRow(idx){setSreoRows(prev=>prev.filter((_,i)=>i!==idx));}
  if(!sreoRows.length) return <div style={s.emptyText}>No real estate owned on file.</div>;
  const tblBox={border:'0.5px solid #B8D4F0',borderRadius:'8px',overflow:'hidden'};
  return(
    <div>
      <div style={tblBox}>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>
            <th style={{...s.th,minWidth:'180px'}}>Property address</th>
            <th style={s.th}>Type</th>
            <th style={s.th}>Use</th>
            <th style={s.th}>Purchase price</th>
            <th style={s.th}>Current value</th>
            <th style={s.th}>Loan balance</th>
            {!editing&&<th style={s.th}>Equity</th>}
            <th style={s.th}>Mo. payment</th>
            <th style={s.th}>Rental income</th>
            <th style={s.th}>Lender</th>
            <th style={s.th}>Status</th>
            {editing&&<th style={s.th}></th>}
          </tr></thead>
          <tbody>
            {sreoRows.map((r,i)=>{
              const equity=num(r['Current value'])-num(r['Loan balance']);
              return editing?(
                <tr key={i}>
                  <td style={s.td}><input style={s.tableInputWide} value={r['Property address']||''} onChange={e=>updateRow(i,'Property address',e.target.value)} placeholder="123 Main St, City, ST"/></td>
                  <td style={s.td}><select style={s.tableSelect} value={r['Property type']||''} onChange={e=>updateRow(i,'Property type',e.target.value)}><option value="">-</option>{SREO_PROP_TYPES.map(o=><option key={o}>{o}</option>)}</select></td>
                  <td style={s.td}><select style={s.tableSelect} value={r['Use']||''} onChange={e=>updateRow(i,'Use',e.target.value)}><option value="">-</option>{SREO_USES.map(o=><option key={o}>{o}</option>)}</select></td>
                  <td style={s.td}><input style={s.tableInput} value={r['Purchase price']?fmtD(r['Purchase price']):''} onChange={e=>updateRow(i,'Purchase price',pN(e.target.value))} placeholder="$0"/></td>
                  <td style={s.td}><input style={s.tableInput} value={r['Current value']?fmtD(r['Current value']):''} onChange={e=>updateRow(i,'Current value',pN(e.target.value))} placeholder="$0"/></td>
                  <td style={s.td}><input style={s.tableInput} value={r['Loan balance']?fmtD(r['Loan balance']):''} onChange={e=>updateRow(i,'Loan balance',pN(e.target.value))} placeholder="$0"/></td>
                  <td style={s.td}><input style={s.tableInput} value={r['Monthly payment']?fmtD(r['Monthly payment']):''} onChange={e=>updateRow(i,'Monthly payment',pN(e.target.value))} placeholder="$0"/></td>
                  <td style={s.td}><input style={s.tableInput} value={r['Rental income']?fmtD(r['Rental income']):''} onChange={e=>updateRow(i,'Rental income',pN(e.target.value))} placeholder="$0"/></td>
                  <td style={s.td}><input style={s.tableInput} value={r['Lender name']||''} onChange={e=>updateRow(i,'Lender name',e.target.value)} placeholder="Lender"/></td>
                  <td style={s.td}><select style={s.tableSelect} value={r['Status']||''} onChange={e=>updateRow(i,'Status',e.target.value)}><option value="">-</option>{SREO_STATUSES.map(o=><option key={o}>{o}</option>)}</select></td>
                  <td style={s.td}><button style={s.deleteRowBtn} onClick={()=>deleteRow(i)}><i className="ti ti-trash" style={{fontSize:'13px'}}/></button></td>
                </tr>
              ):(
                <tr key={i} style={{background:i%2===0?'#fff':'#F5F9FE'}}>
                  <td style={{...s.td,minWidth:'180px'}}>{r['Property address']||'-'}</td>
                  <td style={s.td}>{r['Property type']||'-'}</td>
                  <td style={s.td}>{r['Use']||'-'}</td>
                  <td style={s.td}>{fmt(r['Purchase price'])}</td>
                  <td style={s.td}>{fmt(r['Current value'])}</td>
                  <td style={s.td}>{fmt(r['Loan balance'])}</td>
                  <td style={{...s.td,fontWeight:600,color:equity>=0?'#4A6885':'#B08968'}}>{fmt(equity)}</td>
                  <td style={s.td}>{fmt(r['Monthly payment'])}</td>
                  <td style={s.td}>{fmt(r['Rental income'])}</td>
                  <td style={s.td}>{r['Lender name']||'-'}</td>
                  <td style={s.td}>{r['Status']||'-'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot><tr>
            <td style={s.tdFoot} colSpan={3}>Totals</td>
            <td style={s.tdFoot}>{fmt(sreoRows.reduce((s,r)=>s+num(r['Purchase price']),0))}</td>
            <td style={s.tdFoot}>{fmt(sreoRows.reduce((s,r)=>s+num(r['Current value']),0))}</td>
            <td style={s.tdFoot}>{fmt(sreoRows.reduce((s,r)=>s+num(r['Loan balance']),0))}</td>
            {!editing&&<td style={{...s.tdFoot,color:'#2B72C2'}}>{fmt(sreoRows.reduce((s,r)=>s+num(r['Current value'])-num(r['Loan balance']),0))}<span style={{fontWeight:400,color:'#7AADE0',fontSize:'10px',marginLeft:4}}>AUTO</span></td>}
            <td style={s.tdFoot}>{fmt(sreoRows.reduce((s,r)=>s+num(r['Monthly payment']),0))}</td>
            <td style={s.tdFoot}>{fmt(sreoRows.reduce((s,r)=>s+num(r['Rental income']),0))}</td>
            <td style={s.tdFoot} colSpan={editing?3:2}/>
          </tr></tfoot>
        </table>
      </div>
      </div>
      {editing&&<button style={s.addRowBtn} onClick={addRow}><i className="ti ti-plus" style={{fontSize:'13px'}}/> Add property</button>}
    </div>
  );
}

function AssetsTable({assetRows,setAssetRows,editing}){
  function updateRow(idx,key,val){setAssetRows(prev=>prev.map((r,i)=>i===idx?{...r,[key]:val}:r));}
  function addRow(){setAssetRows(prev=>[...prev,{'Institution name':'','Asset type':'','Account last 4':'','Balance':'',_id:null,_isNew:true,_uid:newUid()}]);}
  function deleteRow(idx){setAssetRows(prev=>prev.filter((_,i)=>i!==idx));}
  const totalAll = assetRows.reduce((s,r)=>s+num(r['Balance']),0);
  if(!assetRows.length) return <div style={s.emptyText}>No assets on file.</div>;
  const tblBox={border:'0.5px solid #B8D4F0',borderRadius:'8px',overflow:'hidden'};
  return(
    <div>
      <div style={tblBox}>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Institution</th>
            <th style={s.th}>Type</th>
            <th style={s.th}>Acct last 4</th>
            <th style={s.th}>Balance</th>
            {editing&&<th style={s.th}></th>}
          </tr></thead>
          <tbody>
            {assetRows.map((r,i)=>editing?(
              <tr key={i}>
                <td style={s.td}><input style={s.tableInput} value={r['Institution name']||''} onChange={e=>updateRow(i,'Institution name',e.target.value)} placeholder="Chase"/></td>
                <td style={s.td}><select style={s.tableSelect} value={r['Asset type']||''} onChange={e=>updateRow(i,'Asset type',e.target.value)}><option value="">-</option>{ASSET_TYPES.map(o=><option key={o}>{o}</option>)}</select></td>
                <td style={s.td}><input style={s.tableInput} value={r['Account last 4']||''} onChange={e=>updateRow(i,'Account last 4',e.target.value)} placeholder="1234" maxLength={4}/></td>
                <td style={s.td}><input style={s.tableInput} value={r['Balance']?fmtD(r['Balance']):''} onChange={e=>updateRow(i,'Balance',pN(e.target.value))} placeholder="$0"/></td>
                <td style={s.td}><button style={s.deleteRowBtn} onClick={()=>deleteRow(i)}><i className="ti ti-trash" style={{fontSize:'13px'}}/></button></td>
              </tr>
            ):(
              <tr key={i} style={{background:i%2===0?'#fff':'#F5F9FE'}}>
                <td style={s.td}>{r['Institution name']||'-'}</td>
                <td style={s.td}>{r['Asset type']||'-'}</td>
                <td style={s.td}>{r['Account last 4']||'-'}</td>
                <td style={{...s.td,fontWeight:600}}>{fmt(r['Balance'])}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td style={s.tdFoot} colSpan={3}>Total assets on file</td><td style={{...s.tdFoot,color:'#2B72C2'}}>{fmt(totalAll)}</td>{editing&&<td style={s.tdFoot}/>}</tr>
          </tfoot>
        </table>
      </div>
      </div>
      {editing&&<button style={s.addRowBtn} onClick={addRow}><i className="ti ti-plus" style={{fontSize:'13px'}}/> Add asset</button>}
    </div>
  );
}

function InlineFinding({item,onUse,onKeep,busy}){
  if(!item) return null;
  return (
    <div style={s.inlineNote}>
      <i className="ti ti-alert-triangle" style={{fontSize:'12px'}}/>
      <div style={s.inlineNoteText}><b>AI:</b> verified {item.verifiedValue||'-'}{item.variance?` (${item.variance})`:''}</div>
      <button style={s.inlineBtnUse} disabled={busy} onClick={onUse}>{busy?'…':'Use'}</button>
      <button style={s.inlineBtnKeep} disabled={busy} onClick={onKeep}>Keep</button>
    </div>
  );
}

function ReconcileBar({label,findings,onResolve,busyId}){
  if(!findings||findings.length===0) return null;
  return (
    <div style={s.reconWrap}>
      <div style={s.reconHead}>
        <i className="ti ti-alert-triangle" style={{fontSize:'13px'}}/>
        <span style={{flex:1}}>AI found {findings.length} thing{findings.length>1?'s':''} to reconcile{label?` on ${label}`:''}</span>
        <span style={s.reconCount}>{findings.length}</span>
      </div>
      {findings.map(d=>{
        const busy=busyId===d.id;
        return (
          <div key={d.id} style={s.findingRow}>
            <div style={s.findingWhere}>{d.fieldChecked}</div>
            <div style={s.findingMsg}>
              Stated <b style={{color:'#0D2545'}}>{d.statedValue||'-'}</b> · verified <b style={{color:'#0D2545'}}>{d.verifiedValue||'-'}</b>{d.variance?` (${d.variance})`:''}
            </div>
            <button style={s.fBtnUse} disabled={busy} onClick={()=>onResolve(d,'use_verified')}>{busy?'Working…':`Use ${d.verifiedValue}`}</button>
            <button style={s.fBtnKeep} disabled={busy} onClick={()=>onResolve(d,'ignore','Dismissed. Stated value kept.')}>Keep</button>
          </div>
        );
      })}
    </div>
  );
}

const MATCH_INCOME_TYPES = ['DSCR','Commercial','Conventional commercial','SBA 7(a)','SBA 504','Portfolio / Blanket'];
function mNorm(n){ const v=Number(n); if(!v||isNaN(v)) return 0; return (v>0&&v<=1)?v*100:v; }
function mMoney(n){ return '$'+usd(n); }
function mStates(x){ if(Array.isArray(x)) return x.map(v=>String(v).trim().toUpperCase()).filter(Boolean); return String(x||'').split(',').map(v=>v.trim().toUpperCase()).filter(Boolean); }

function leverageEval(deal, L, p){
  const reasons=[], pass=[];
  const refi = deal.loanPurpose==='Refinance' || deal.loanPurpose==='Cash-out refinance';
  const ltvCap = refi ? p.maxLtvRefi : p.maxLtvPurchase;
  function testMax(name, val, cap){
    val=Number(val)||0; cap=(cap==null||cap==='')?null:Number(cap);
    if(val>0 && cap!=null && cap>0){ if(val>cap) reasons.push(name+' '+val+'% over max '+cap+'%'); else pass.push(name+' '+val+'% within '+cap+'%'); }
  }
  if(deal.isSecond){ testMax('CLTV', deal.cltv, mNorm(L.maxCltv)); return {reasons,pass}; }
  const lt=deal.loanType;
  if(lt==='Fix & Flip'){ testMax('LTARV', deal.ltarv, p.maxArv); testMax('LTC', deal.ltc, p.maxLtc); }
  else if(lt==='Ground-Up Construction'){ testMax('LTC', deal.ltc, p.maxLtc); testMax('LTARV', deal.ltarv, p.maxArv); }
  else if(MATCH_INCOME_TYPES.indexOf(lt)>=0){ testMax('LTV', deal.ltv, ltvCap); if(deal.dscr>0 && p.minDscr!=null && p.minDscr>0){ if(deal.dscr<p.minDscr) reasons.push('DSCR '+deal.dscr+' below min '+p.minDscr); else pass.push('DSCR '+deal.dscr+' meets '+p.minDscr); } }
  else if(lt==='Bridge'){ testMax('LTV', deal.ltv, ltvCap); testMax('LTC', deal.ltc, p.maxLtc); testMax('LTARV', deal.ltarv, p.maxArv); }
  else if(lt==='Working capital / LOC'){ /* loan amount only */ }
  else { testMax('LTV', deal.ltv, ltvCap); }
  return {reasons,pass};
}

function programEval(deal, L, p){
  if((p.loanType||'') !== deal.loanType) return {wrongType:true};
  const contradict=[], unknown=[], pass=['Loan type: '+deal.loanType];
  if(deal.propertyType){
    if(!(Array.isArray(p.propertyType) && p.propertyType.length)) unknown.push('Property type not on file');
    else if(p.propertyType.indexOf(deal.propertyType)<0) contradict.push('Property type '+deal.propertyType+' not funded');
    else pass.push(deal.propertyType);
  }
  if(deal.loanAmount>0){
    if(p.minLoan==null && p.maxLoan==null) unknown.push('Loan size not on file');
    else if(p.minLoan!=null && deal.loanAmount<p.minLoan) contradict.push('Below min loan '+mMoney(p.minLoan));
    else if(p.maxLoan!=null && deal.loanAmount>p.maxLoan) contradict.push('Above max loan '+mMoney(p.maxLoan));
    else pass.push('Loan in range');
  }
  if(deal.fico>0 && p.minFico!=null && p.minFico>0){
    if(deal.fico<p.minFico) contradict.push('FICO '+deal.fico+' below program min '+p.minFico);
    else pass.push('FICO '+deal.fico+' meets '+p.minFico);
  }
  const lev=leverageEval(deal,L,p);
  for(const r of lev.reasons) contradict.push(r);
  for(const ps of lev.pass) pass.push(ps);
  return {contradict, unknown, pass};
}

function lenderEval(deal, L){
  const contradict=[], unknown=[], pass=[];
  const states=mStates(L.states);
  if(deal.state){
    if(!states.length) unknown.push('States not on file');
    else if(states.indexOf(deal.state)<0) contradict.push('Not licensed in '+deal.state);
    else pass.push(deal.state);
  }
  if(deal.ownerOcc){
    if(!L.ownerOcc) unknown.push('Owner-occupied not on file');
    else if(L.ownerOcc==='No') contradict.push('No owner-occupied');
    else pass.push('Owner-occupied ok');
  }
  if(deal.borrowerIsIndividual){
    if(!L.entity) unknown.push('Entity policy not on file');
    else if(L.entity==='Entity required') contradict.push('Entity required, borrower is individual');
    else pass.push('Individual borrower ok');
  }
  if(deal.wholesale){
    if(!L.wholesale) unknown.push('Wholesale not on file');
    else if(L.wholesale==='No') contradict.push('No wholesale');
    else pass.push('Wholesale ok');
  }
  if(deal.lienPos && deal.lienPos!=='1st lien'){
    if(!(Array.isArray(L.lienPositions) && L.lienPositions.length)) unknown.push('Lien positions not on file');
    else if(L.lienPositions.indexOf(deal.lienPos)<0) contradict.push('Does not fund '+deal.lienPos);
    else pass.push(deal.lienPos);
  }
  if(deal.situations.length){
    if(!(Array.isArray(L.situations) && L.situations.length)) unknown.push('Situations not on file');
    else deal.situations.forEach(function(sit){ if(L.situations.indexOf(sit)<0) contradict.push('Does not accept '+sit); else pass.push(sit); });
  }
  if(deal.assetClasses.length){
    if(!(Array.isArray(L.specialties) && L.specialties.length)) unknown.push('Asset class not on file');
    else deal.assetClasses.forEach(function(a){ if(L.specialties.indexOf(a)<0) contradict.push('Does not fund '+a); else pass.push(a); });
  }
  if(deal.recoursePref==='Non-recourse required'){
    if(!L.recourseType) unknown.push('Recourse not on file');
    else if(L.recourseType==='Full recourse') contradict.push('Full recourse only');
    else pass.push('Non-recourse ok');
  }
  if(deal.appraisalPref==='No appraisal needed'){
    if(!L.appraisalRequired) unknown.push('Appraisal policy not on file');
    else if(L.appraisalRequired==='Yes') contradict.push('Requires appraisal');
    else pass.push('No appraisal ok');
  }
  const floor=(L.lenderMinFico===''||L.lenderMinFico==null)?0:Number(L.lenderMinFico);
  if(deal.fico>0 && floor>0 && deal.fico<floor) contradict.push('FICO '+deal.fico+' below lender floor '+floor);
  return {contradict, unknown, pass};
}

function matchLenders(deal, lenders){
  const potential=[], maybe=[], ruledout=[];
  (lenders||[]).forEach(function(L){
    const le=lenderEval(deal, L);
    let contradict=le.contradict.slice(), unknown=le.unknown.slice(), pass=le.pass.slice();
    const progs=(L.programs||[]).filter(function(p){return p.active!==false;});
    if(progs.length===0){
      unknown.push('No program criteria on file');
    } else {
      const typed=progs.map(function(p){return programEval(deal,L,p);}).filter(function(r){return !r.wrongType;});
      if(typed.length===0){
        contradict.push('No '+deal.loanType+' program on file');
      } else {
        typed.sort(function(a,b){return a.contradict.length-b.contradict.length;});
        const best=typed[0];
        contradict=contradict.concat(best.contradict);
        unknown=unknown.concat(best.unknown);
        pass=pass.concat(best.pass);
      }
    }
    const vstatus = L.verified ? 'verified' : 'unconfirmed';
    const entry={lender:L, contradict, unknown, pass, vstatus};
    if(vstatus==='verified' && contradict.length>0){ ruledout.push(entry); }
    else if(contradict.length>0 || unknown.length>0){ maybe.push(entry); }
    else { potential.push(entry); }
  });
  potential.sort(function(a,b){ return (b.vstatus==='verified'?1:0)-(a.vstatus==='verified'?1:0); });
  maybe.sort(function(a,b){ return a.contradict.length-b.contradict.length; });
  return {potential, maybe, ruledout};
}

const ms = {
  bar:{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap',marginBottom:'12px'},
  count:{fontSize:'13px',fontWeight:600,color:'#2E4763'},
  countZero:{fontSize:'13px',fontWeight:700,color:'#B08968'},
  exHead:{fontSize:'10px',fontWeight:700,color:'#8a9ab5',letterSpacing:'0.06em',textTransform:'uppercase',margin:'16px 0 8px'},
  card:{border:'1px solid #C5D5EA',borderLeft:'4px solid #4A7FBF',borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',background:'#fff',display:'flex',gap:'10px',alignItems:'flex-start'},
  cardX:{border:'1px solid #E3E9F2',borderLeft:'4px solid #C5D5EA',borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',background:'#fff'},
  chk:{width:'17px',height:'17px',marginTop:'2px',accentColor:'#154D97',cursor:'pointer',flexShrink:0},
  body:{flex:1,minWidth:0},
  top:{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:'8px'},
  name:{fontSize:'13.5px',fontWeight:600,color:'#2E4763'},
  nameX:{fontSize:'13.5px',fontWeight:700,color:'#5a6b85'},
  tag:{fontSize:'10px',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.04em',padding:'2px 8px',borderRadius:'5px',whiteSpace:'nowrap'},
  tagY:{background:'#E4EBF0',color:'#4A6885'},
  tagN:{background:'#F1EFE8',color:'#B08968'},
  tagCase:{background:'#FBF7F3',color:'#B08968'},
  reason:{fontSize:'11.5px',color:'#9B2C2C',marginTop:'5px',display:'flex',alignItems:'center',gap:'5px'},
  passrow:{display:'flex',flexWrap:'wrap',gap:'4px',marginTop:'6px'},
  pchip:{fontSize:'10px',background:'#E4EDF8',color:'#154D97',borderRadius:'4px',padding:'2px 7px',fontWeight:600},
  contact:{fontSize:'11.5px',color:'#8A94A6',marginTop:'2px'},
  empty:{fontSize:'13px',color:'#8a9ab5',padding:'10px 0'},
  selBar:{position:'sticky',bottom:'0',background:'#E4EDF8',border:'0.5px solid #B8D4F0',borderRadius:'8px',padding:'10px 14px',marginTop:'14px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px'},
  draftBtn:(on)=>({background:on?'#154D97':'#A8B8D0',color:'#fff',border:'none',borderRadius:'7px',padding:'8px 16px',fontSize:'12.5px',fontWeight:700,cursor:on?'pointer':'default',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:'6px'}),
  tierPot:{display:'flex',alignItems:'center',gap:'8px',fontSize:'11px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:'#154D97',background:'#E4EDF8',border:'1px solid #D9E5F4',borderRadius:'10px',padding:'8px 13px',margin:'4px 0 10px'},
  tierMay:{display:'flex',alignItems:'center',gap:'8px',fontSize:'11px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:'#8a5a12',background:'#FAEEDA',border:'1px solid #F2E1C2',borderRadius:'10px',padding:'8px 13px',margin:'18px 0 10px'},
  tierRem:{display:'flex',alignItems:'center',gap:'8px',fontSize:'11px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:'#A23A2E',background:'#FBEFEF',border:'1px solid #F0D9D6',borderRadius:'10px',padding:'8px 13px',margin:'18px 0 10px',cursor:'pointer'},
  pip:(c)=>({width:'9px',height:'9px',borderRadius:'50%',background:c,display:'inline-block'}),
  cardMay:{border:'1px solid #E4DAD0',borderLeft:'4px solid #B08968',borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',background:'#FBF7F3',display:'flex',gap:'10px',alignItems:'flex-start'},
  badgeV:{background:'#EAF3DE',color:'#3B6D11'},
  badgeU:{background:'#F0F5FB',color:'#7189B0',border:'1px solid #D9E5F4'},
  mchip:{fontSize:'10px',background:'#FBF7F3',color:'#B08968',borderRadius:'4px',padding:'2px 7px',fontWeight:600},
  mchipNo:{fontSize:'10px',background:'#FBE3DF',color:'#A23A2E',borderRadius:'4px',padding:'2px 7px',fontWeight:600},
  aRow:{display:'grid',gridTemplateColumns:'22px 1fr auto',gap:'12px',alignItems:'start',padding:'10px 4px',borderBottom:'1px solid #F2F5F9'},
  aMain:{minWidth:0},
  top2:{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'},
  aReason:(k)=>({fontSize:'11.5px',marginTop:'3px',color:k==='match'?'#4A6885':k==='check'?'#B08968':'#A23A2E'}),
  detailsLink:{fontSize:'11px',color:'#8a9ab5',textDecoration:'underline',cursor:'pointer',marginLeft:'6px'},
  aStatusCol:{display:'flex',justifyContent:'flex-end',paddingTop:'1px'},
  statusMini:(k)=>({fontSize:'11px',fontWeight:600,padding:'3px 10px',borderRadius:'14px',whiteSpace:'nowrap',background:k==='match'?'#E4EBF0':k==='check'?'#FBF7F3':'#FBEFEF',color:k==='match'?'#4A6885':k==='check'?'#B08968':'#A6544E'}),
};

export default function DealDetail({deal,onBack,onCreated}){
  const f = deal.fields||{};

  const [stage,setStage] = useState(f['Stage']||'New application');
  const [notes,setNotesRaw] = useState(f['Additional notes']||'');
  const [dirty,setDirty] = useState(false);
  const [saving,setSaving] = useState(false);
  const [saved,setSaved] = useState(false);
  const [saveError,setSaveError] = useState('');
  const [showCo,setShowCo] = useState(!!(f['Co-borrower name']||f['Co-borrower email']));
  const [savedFields,setSavedFields] = useState({...f});
  const [allContacts,setAllContacts] = useState([]);
  const [titleAdd,setTitleAdd] = useState(false);
  const [titleQ,setTitleQ] = useState('');
  const [titleCreating,setTitleCreating] = useState(false);
  const [borrowerContact,setBorrowerContact] = useState(null);
  const [liveDealId,setLiveDealId] = useState(deal.id||null);
  const [ssnShown,setSsnShown] = useState({});
  const MAX_BORROWERS = 4;
  const [coContacts,setCoContacts] = useState([]);
  const [coForms,setCoForms] = useState([]);
  // One Deal Borrowers row per borrower per deal. Holds the 29 application answers
  // that used to sit on the contact. If this list fails to load, dbRows stays empty
  // and every borrower field behaves exactly as it did before.
  const [dbRows,setDbRows] = useState([]);
  const [dbSeeded,setDbSeeded] = useState(false);
  const [activeB,setActiveB] = useState(0);
  const coIdx = activeB>0 ? activeB-1 : 0;
  const coContact = coContacts[coIdx] || null;
  const setCoContact = (rec) => setCoContacts(p=>{const n=[...p]; if(rec===null) n.splice(coIdx,1); else n[coIdx]=rec; return n;});
  const [coErr,setCoErr] = useState('');
  const [coQuery,setCoQuery] = useState('');
  const [coCreating,setCoCreating] = useState(false);
  const coForm = coForms[coIdx] || null;
  const setCoForm = (u) => setCoForms(p=>{const n=[...p]; const cur=n[coIdx]||null; const nv=(typeof u==='function')?u(cur):u; if(nv===null) n.splice(coIdx,1); else n[coIdx]=nv; return n;});
  const [coSreoRows,setCoSreoRows] = useState([]);
  const [coAssetRows,setCoAssetRows] = useState([]);
  const [sreoRows,setSreoRowsRaw] = useState([]);
  const [assetRows,setAssetRowsRaw] = useState([]);
  const [liabRows,setLiabRowsRaw] = useState([]);
  const [liabDeleted,setLiabDeleted] = useState([]);
  const [incRows,setIncRowsRaw] = useState([]);
  const [incDeleted,setIncDeleted] = useState([]);
  const [assetDeleted,setAssetDeleted] = useState([]);
  const [sreoDeleted,setSreoDeleted] = useState([]);
  const [collapsed,setCollapsed] = useState({});
  const [tagOpen,setTagOpen] = useState({});
  const [acSearch,setAcSearch] = useState('');
  // null means the user has not touched it, so the box seeds from saved data.
  // Once clicked it is user-owned and never re-derives from the form again.
  const [subOpen,setSubOpen] = useState(null);
  // Holds the last real street address so ticking "Street address is TBD"
  // cannot destroy something already typed. Unticking puts it back.
  const prevAddrRef = React.useRef('');
  const [tab,setTab] = useState('application');
  const [appSection,setAppSection] = useState('deal');
  useEffect(()=>{ try{ window.scrollTo({top:0,behavior:'auto'}); }catch(e){ window.scrollTo(0,0); } },[appSection]);
  const [openSec,setOpenSec] = useState({});
  const [reoOpen,setReoOpen] = useState({});
  const [assetOpen,setAssetOpen] = useState({});
  const [liabOpen,setLiabOpen] = useState({});
  const [incOpen,setIncOpen] = useState({});
  const [reoMore,setReoMore] = useState({});
  const [assetMore,setAssetMore] = useState({});
  const [liabMore,setLiabMore] = useState({});
  const [matchList,setMatchList] = useState(null);
  const [matchLoading,setMatchLoading] = useState(false);
  const [matchErr,setMatchErr] = useState('');
  const [matchSel,setMatchSel] = useState({});
  const [matchMsg,setMatchMsg] = useState('');
  const [showRuled,setShowRuled] = useState(false);
  const [expMatch,setExpMatch] = useState(()=>new Set());
  const toggleMatchDetails = (id)=>setExpMatch(prev=>{const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n;});
  const [showDrafts,setShowDrafts] = useState(false);
  useEffect(()=>{
    if((tab!=='match' && tab!=='submissions' && tab!=='closing') || matchList!==null || matchLoading) return;
    setMatchLoading(true); setMatchErr('');
    authFetch('/.netlify/functions/list-lenders').then(r=>r.json()).then(d=>{
      if(d.error) throw new Error(d.error?.message||'load failed');
      setMatchList(d.lenders||[]);
    }).catch(e=>setMatchErr('Could not load lenders: '+e.message)).finally(()=>setMatchLoading(false));
  },[tab]); // eslint-disable-line
  const allLenders = matchList||[];
  const lenderNameOf = (id) => { const l=(matchList||[]).find(x=>x.id===id); return l?(clean(l.fields&&l.fields['Lender name'])||'Unnamed'):'Selected lender'; };
  const contactNameOf = (id) => { const c=(allContacts||[]).find(x=>x.id===id); if(!c) return 'Selected'; return clean(c.fields?.Name)||[clean(c.fields?.['First name']),clean(c.fields?.['Last name'])].filter(Boolean).join(' ')||clean(c.fields?.['Company / entity'])||'Selected'; };
  const [genSummary,setGenSummary] = useState(false);
  const [coOpen,setCoOpen] = useState(false);
  const [narOpen,setNarOpen] = useState(false);
  const [genSummaryErr,setGenSummaryErr] = useState('');
  const [corr,setCorrRaw] = useState(()=>{ try{ const raw=f['Correspondence log']; if(!raw) return []; const a=JSON.parse(raw); return Array.isArray(a)?a:[]; }catch{ return []; } });
  const [corrSaved,setCorrSaved] = useState(false);
  const [showCorrAdd,setShowCorrAdd] = useState(false);
  const [corrDraft,setCorrDraft] = useState({type:'Call',date:'',with:'',note:''});
  const [discItems,setDiscItems] = useState([]);
  const [discLoading,setDiscLoading] = useState(true);
  const [discRunning,setDiscRunning] = useState(false);
  const [discBusyId,setDiscBusyId] = useState(null);
  const [discMsg,setDiscMsg] = useState('');
  const [discErr,setDiscErr] = useState('');

  useEffect(()=>{
    authFetch('/.netlify/functions/get-contacts').then(r=>r.json()).then(d=>setAllContacts(d.records||[])).catch(()=>{});
  },[]);

  const appendFinRows=(setter,forDealId)=>(d)=>setter(prev=>{const inc=(d.records||[]).map(x=>({...x.fields,_id:x.id,_isNew:false,_uid:newUid()})).filter(x=>{const dl=x['Deal'];if(!Array.isArray(dl)||dl.length===0) return true;return !forDealId||dl.includes(forDealId);});const have=new Set(prev.map(p=>p._id).filter(Boolean));return [...prev,...inc.filter(x=>x._id&&!have.has(x._id))];});

  useEffect(()=>{
    const borrowerIds=f['Borrower'];
    if(!borrowerIds||!borrowerIds.length) return;
    const contactId=borrowerIds[0];
    authFetch(`/.netlify/functions/get-single-contact?contactId=${contactId}`).then(r=>r.json()).then(d=>setBorrowerContact(d)).catch(()=>{});
    authFetch(`/.netlify/functions/get-sreo?contactId=${contactId}`).then(r=>r.json()).then(appendFinRows(setSreoRowsRaw,liveDealId)).catch(()=>{});
    authFetch(`/.netlify/functions/get-assets?contactId=${contactId}`).then(r=>r.json()).then(appendFinRows(setAssetRowsRaw,liveDealId)).catch(()=>{});
  },[]); // eslint-disable-line

  useEffect(()=>{
    const coIds=f['Co-borrower'];
    if(!coIds||!coIds.length) return;
    Promise.all(coIds.slice(0,3).map(id=>authFetch(`/.netlify/functions/get-single-contact?contactId=${id}`).then(r=>r.json()).catch(()=>null)))
      .then(list=>{const recs=list.filter(Boolean);setCoContacts(recs);setCoForms(recs.map(rec=>{const cf=rec.fields||{};return {...cf,'Home state':Array.isArray(cf['Home state'])?cf['Home state']:[cf['Home state']].filter(Boolean)};}));})
      .catch(()=>{});
  },[]);

  useEffect(()=>{
    if(!liveDealId) return;
    authFetch(`/.netlify/functions/list-deal-borrowers?dealId=${liveDealId}`).then(r=>r.json()).then(d=>{ setDbRows(d.records||[]); setDbSeeded(true); }).catch(()=>{ setDbRows([]); setDbSeeded(true); });
    authFetch(`/.netlify/functions/list-liabilities?dealId=${liveDealId}`).then(r=>r.json()).then(d=>setLiabRowsRaw((d.records||[]).map(r=>({...r.fields,_id:r.id,_isNew:false,_uid:newUid()})))).catch(()=>{});
    authFetch(`/.netlify/functions/list-income?dealId=${liveDealId}`).then(r=>r.json()).then(d=>setIncRowsRaw((d.records||[]).map(r=>({...r.fields,_id:r.id,_isNew:false,_uid:newUid()})))).catch(()=>{});
  },[]);

  function patchContactFields(cid,fields){
    setAllContacts(prev=>prev.map(c=>c.id===cid?{...c,fields:{...c.fields,...fields}}:c));
  }


  // Writes the 29 application answers to this borrower's Deal Borrowers row.
  // Creates the row on first save, seeded with whatever is on screen, which came
  // from the contact. Nothing is ever removed from the contact record.
  async function saveDealBorrower(dealId, contactId, formObj, order){
    if(!dealId||!contactId||!formObj) return;
    // Seed from the contact for anything the form does not hold. The form is
    // filled from the contact on load, but if that has not finished, or the
    // contact fetch returned a partial record, we would silently write an empty
    // deal row. Falling back to the contact makes the row correct either way.
    const srcContact=((allContacts||[]).find(c=>c.id===contactId)||{}).fields||{};
    const isEmpty=(v)=> v===undefined||v===null||(!Array.isArray(v)&&typeof v!=='boolean'&&String(v).trim()==='');
    const fields={};
    DEAL_BORROWER_FIELDS.forEach(k=>{
      let v=formObj[k];
      if(isEmpty(v)) v=srcContact[k];
      if(isEmpty(v)) return;
      fields[k]=v;
    });
    console.log('[deal borrower] sending', Object.keys(fields).length, 'fields');
    const existing=dbRowFor(contactId);
    const who=[clean(formObj['First name']),clean(formObj['Last name'])].filter(Boolean).join(' ');
    fields['Borrower']=rowLabel(clean(form['Deal name']),'Borrower '+(order||1));
    try{
      let d;
      if(existing){
        console.log('[deal borrower] UPDATE', existing.id, 'label=', fields['Borrower']);
        d=await postWithLabel('/.netlify/functions/update-deal-borrower',{recordId:existing.id,fields},'Borrower');
      } else {
        fields['Deal']=[dealId];
        fields['Contact']=[contactId];
        fields['Borrower order']=order;
        console.log('[deal borrower] CREATE label=', fields['Borrower']);
        d=await postWithLabel('/.netlify/functions/create-deal-borrower',{fields},'Borrower');
        if(d&&d.id) setDbRows(p=>[...p,d]);
      }
      console.log('[deal borrower] response', d);
      const emsg=(d&&d.error&&(d.error.message||d.error))||'';
      if(emsg) setSaveError('Borrower row: '+emsg);
      else if(d&&d.fields&&d.fields['Borrower']===undefined) setSaveError('Borrower row saved but the label did not stick. Airtable returned no Borrower value.');
    }catch(e){
      console.log('[deal borrower] threw', e);
      setSaveError('Borrower row: '+(e.message||'network error'));
    }
  }

  function handleContactCreated(newRecord){
    if(!newRecord||!newRecord.id) return;
    setAllContacts(prev => prev.some(c=>c.id===newRecord.id)
      ? prev.map(c=>c.id===newRecord.id?newRecord:c)
      : [...prev, newRecord]);
  }

  async function linkCoBorrower(contactId){
    if(!contactId) return;
    setCoErr('');
    try{
      const r=await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:liveDealId,fields:{'Co-borrower':[...coContacts.map(c=>c.id).filter(id=>id!==contactId),contactId]}})});
      const d=await r.json();
      if(d.error) throw new Error(d.error?.message||JSON.stringify(d.error));
      const rec=allContacts.find(c=>c.id===contactId);
      if(rec) setCoContact(rec);
      else authFetch(`/.netlify/functions/get-single-contact?contactId=${contactId}`).then(x=>x.json()).then(setCoContact).catch(()=>{});
      setCoOpen(false);
    }catch(e){ setCoErr('Could not link co-borrower to the deal: '+e.message+'. The contact was still created in Contacts. Check that the Co-borrower linked field exists on the Deals table in Airtable.'); setCoOpen(true); console.error('Co-borrower link failed',e); }
  }

  async function unlinkCoBorrower(){
    setCoErr('');
    // The Deal Borrowers row is found BY CONTACT ID, and the unlink clears that
    // link. Looking for the row afterwards found nothing, so the delete quietly
    // did nothing and reported success. Delete the row FIRST, then unlink.
    const gone=coContacts[coIdx]&&coContacts[coIdx].id;
    const dbRow=gone?dbRowFor(gone):null;
    if(dbRow&&dbRow.id){
      try{
        const dr=await authFetch('/.netlify/functions/delete-deal-borrower',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({recordId:dbRow.id})});
        const dd=await dr.json();
        if(dd&&dd.error) throw new Error(dd.error?.message||JSON.stringify(dd.error));
        setDbRows(p=>(p||[]).filter(r=>r.id!==dbRow.id));
      }catch(de){
        // Stop here. Unlinking now would orphan the row and make it
        // unreachable, which is exactly the mess this is meant to prevent.
        setCoErr('Could not delete their answers for this deal, so nothing was removed: '+de.message);
        console.error('Deal Borrowers row delete failed',de);
        return;
      }
    }
    try{
      const r=await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:liveDealId,fields:{'Co-borrower':coContacts.filter((_,x)=>x!==coIdx).map(c=>c.id)}})});
      const d=await r.json();
      if(d.error) throw new Error(d.error?.message||'unlink failed');
      setCoContacts(p=>p.filter((_,x)=>x!==coIdx)); setCoForms(p=>p.filter((_,x)=>x!==coIdx)); setCoSreoRows([]); setCoAssetRows([]);
    }catch(e){ setCoErr('Could not remove them from the deal: '+e.message); console.error('Co-borrower unlink failed',e); }
  }

  const loadDisc = useCallback(async()=>{
    setDiscErr('');
    try{
      const res=await authFetch(`/.netlify/functions/list-discrepancies?dealId=${liveDealId}`);
      const data=await res.json();
      if(data.error) throw new Error(data.error?.message||JSON.stringify(data.error));
      setDiscItems(data.discrepancies||[]);
    }catch(e){ setDiscErr('Could not load checks: '+e.message); }
    finally{ setDiscLoading(false); }
  },[liveDealId]);
  useEffect(()=>{ loadDisc(); },[loadDisc]);

  // Overlay the saved Deal Borrowers answers on top of what was seeded from the
  // contact. A field the deal row does not hold keeps the contact value, so an
  // empty or missing row changes nothing on screen.
  const dbRowFor = React.useCallback((contactId)=>{
    if(!contactId) return null;
    return (dbRows||[]).find(r=>{
      const c=r.fields&&r.fields['Contact'];
      return Array.isArray(c)&&c.includes(contactId);
    })||null;
  },[dbRows]);

  useEffect(()=>{
    if(!dbSeeded||!dbRows.length) return;
    const overlay=(formObj,contactId)=>{
      const row=dbRowFor(contactId);
      if(!row||!formObj) return formObj;
      let out=formObj, touched=false;
      DEAL_BORROWER_FIELDS.forEach(k=>{
        const v=row.fields?.[k];
        if(v===undefined||v===null||v==='') return;
        if(out[k]===v) return;
        if(!touched){ out={...formObj}; touched=true; }
        out[k]=v;
      });
      return touched?out:formObj;
    };
    setBForm(p=>p?overlay(p,borrowerContact&&borrowerContact.id):p);
    setCoForms(p=>{
      if(!p||!p.length) return p;
      let changed=false;
      const next=p.map((cf,ci)=>{
        const t=overlay(cf,coContacts[ci]&&coContacts[ci].id);
        if(t!==cf) changed=true;
        return t;
      });
      return changed?next:p;
    });
  },[dbSeeded,dbRows,dbRowFor,borrowerContact,coContacts]);

  async function runDiscCheck(){
    setDiscRunning(true); setDiscMsg(''); setDiscErr('');
    try{
      const res=await authFetch('/.netlify/functions/flag-discrepancies',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:liveDealId})});
      const data=await res.json();
      if(data.error) throw new Error(data.error?.message||JSON.stringify(data.error));
      const parts=[];
      if(data.created) parts.push(`${data.created} new`);
      if(data.updated) parts.push(`${data.updated} refreshed`);
      if(data.removed) parts.push(`${data.removed} cleared`);
      setDiscMsg(data.detected===0?`No mismatches found across ${data.documentTypesSeen?.length||0} reviewed document type(s).`:`Checked. ${parts.join(', ')||'No changes'}.`);
      await loadDisc();
    }catch(e){ setDiscErr('Check failed: '+e.message); }
    finally{ setDiscRunning(false); }
  }

  async function resolveDisc(item,action,note){
    setDiscBusyId(item.id); setDiscErr('');
    try{
      const res=await authFetch('/.netlify/functions/update-discrepancy',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({discrepancyId:item.id,action,note:note||''})});
      const data=await res.json();
      if(data.error) throw new Error(data.error?.message||JSON.stringify(data.error));
      if(action==='use_verified'){
        const ak=CHECKKEY_TO_AIRTABLE[item.checkKey];
        const raw=item.verifiedValue;
        if(ak&&raw!=null&&raw!==''){
          if(ak==='FICO score'){ const n=parseInt(pN(String(raw))); if(!isNaN(n)) setBForm(p=>p?{...p,'FICO score':String(n)}:p); }
          else { setForm(p=>({...p,[ak]:pN(String(raw))})); }
        }
      }
      await loadDisc();
    }catch(e){ setDiscErr('Could not update: '+e.message); }
    finally{ setDiscBusyId(null); }
  }

  const [form,setFormRaw] = useState(withStoredZeros(f,{
    'Deal name':f['Deal name']||'','Deal summary':f['Deal summary']||'',
    'Property address':f['Property address']||'','Property city':f['Property city']||'',
    'Property state':f['Property state']||'','Property ZIP':f['Property ZIP']||'',
    'Property type':f['Property type']||'','Year built':f['Year built']||'',
    'Amortization type':f['Amortization type']||'','Rate type':f['Rate type']||'','Index and margin':f['Index and margin']||'',
    'Subordinate financing':f['Subordinate financing']||'','Subordinate amount':f['Subordinate amount']||'',
    'Original purchase date':f['Original purchase date']||'',
    'APN':f['APN']||'','Flood zone':f['Flood zone']||'','Property condition':f['Property condition']||'','Currently listed for sale':f['Currently listed for sale']||'',
    'Occupancy status':f['Occupancy status']||'',
    'Target close date':f['Target close date']||'',
    'Sqft':f['Sqft']||'','Bedrooms':f['Bedrooms']||'','Bathrooms':f['Bathrooms']||'',
    'Stories':f['Stories']||'','Lot size (sqft)':f['Lot size (sqft)']||'',
    'Floor level':f['Floor level']||'','HOA':f['HOA']||false,
    'HOA name':f['HOA name']||'','HOA dues':f['HOA dues']||'','HOA dues frequency':f['HOA dues frequency']||'',
    'Property taxes':f['Property taxes']||'','Property taxes frequency':f['Property taxes frequency']||'',
    'Paid outside closing':f['Paid outside closing']||'',
    'Insurance':f['Insurance']||'','Insurance frequency':f['Insurance frequency']||'',
    'Garage':f['Garage']||false,'Garage count':f['Garage count']||'','Garage type':f['Garage type']||'',
    'ADU':f['ADU']||false,'ADU type':f['ADU type']||'','ADU sqft':f['ADU sqft']||'','ADU monthly rent':f['ADU monthly rent']||'',
    'Basement':f['Basement']||false,'Basement type':f['Basement type']||'','Basement sqft':f['Basement sqft']||'','Basement rent':f['Basement rent']||'',
    'MH sections':f['MH sections']||'','MH width':f['MH width']||'','MH length':f['MH length']||'',
    'MH decal number':f['MH decal number']||'','MH HUD label':f['MH HUD label']||'',
    '433A':f['433A']||false,'MH park name':f['MH park name']||'','MH park type':f['MH park type']||'',
    'MH fee type':f['MH fee type']||'','MH monthly fee':f['MH monthly fee']||'',
    'MH purchase date':f['MH purchase date']||'','MH purchase price':f['MH purchase price']||'','Appraised value':f['Appraised value']||'','Refi type':f['Refi type']||'','Term months':f['Term months']||'',
    'Refinance type':f['Refinance type']||'','Existing lien amount':f['Existing lien amount']||'',
    'Mortgage type':f['Mortgage type']||'','CDC portion':f['CDC portion']||'','CDC rate':f['CDC rate']||'','CDC term':f['CDC term']||'',
    'Title held by':f['Title held by']||'','Current vesting':f['Current vesting']||'','Vesting to read as':f['Vesting to read as']||'',
    'Entity name':f['Entity name']||'','Entity state':f['Entity state']||'','Entity EIN':f['Entity EIN']||'','Entity formation date':f['Entity formation date']||'','Entity members':f['Entity members']||'',
    'Trust name':f['Trust name']||'','Trust type':f['Trust type']||'','Trust date':f['Trust date']||'','Trustees':f['Trustees']||'',
    'On title':f['On title']||'','Guarantors':f['Guarantors']||'',
    'Lender':f['Lender']||'','Submitted to lender':f['Submitted to lender']||'','Originally purchased date':f['Originally purchased date']||'','Original purchase price':f['Original purchase price']||'',
    'Property space / unit':f['Property space / unit']||'','Address TBD':f['Address TBD']||false,'Property county':f['Property county']||'',
    'MH make':f['MH make']||'','MH model':f['MH model']||'','MH serial number':f['MH serial number']||'',
    'MH new or used':f['MH new or used']||'','MH residency age':f['MH residency age']||'','MH site placement':f['MH site placement']||'',
    'MH park manager first':f['MH park manager first']||'','MH park manager last':f['MH park manager last']||'','MH park manager phone':f['MH park manager phone']||'','MH park manager email':f['MH park manager email']||'',
    'MH park owner first':f['MH park owner first']||'','MH park owner last':f['MH park owner last']||'','MH park owner phone':f['MH park owner phone']||'','MH park owner email':f['MH park owner email']||'',
    'Mortgage insurance':f['Mortgage insurance']||'','Other financing':f['Other financing']||'',
    'Number of units':f['Number of units']||f['# of Units']||'',
    'Total beds':f['Total beds']||'','Total baths':f['Total baths']||'','Occupied units':f['Occupied units']||'',
    'Annual NOI':f['Annual NOI']||'','Annual rent':f['Annual rent']||'',
    'Current monthly rent':f['Current monthly rent']||'',
    'Projected monthly rent':f['Projected monthly rent']||'',
    'Commercial sqft':f['Commercial sqft']||'','Residential sqft':f['Residential sqft']||'',
    'Commercial %':f['Commercial %']||'',
    'Deal situations':Array.isArray(f['Deal situations'])?f['Deal situations']:[],
    'Asset class':Array.isArray(f['Asset class'])?f['Asset class']:[],
    'Assignment fee amount':f['Assignment fee amount']||'',
    'Assignment fee basis':f['Assignment fee basis']||'',
    'Assignment fee paid by':f['Assignment fee paid by']||'',
    'Tenant count':f['Tenant count']||'',
    'Residential tenants':f['Residential tenants']||'','Commercial tenants':f['Commercial tenants']||'',
    'Lease terms':f['Lease terms']||'',
    'Land acreage':f['Land acreage']||'','Land zoning':f['Land zoning']||'',
    'Land entitled':f['Land entitled']||'','Utilities':f['Utilities']||'','Access / ingress':f['Access / ingress']||'',
    'Loan type':f['Loan type']||'','Loan purpose':f['Loan purpose']||'',
    'Loan amount':f['Loan amount']||'','Requested rate':f['Requested rate']||'',
    'Requested loan term':f['Requested loan term']||'','Requested points':f['Requested points']||'',
    'Requested prepayment penalty':f['Requested prepayment penalty']||'',
    'Exit strategy':f['Exit strategy']||'',
    'Post-rehab notes':f['Post-rehab notes']||'',
    'Purchase price':f['Purchase price']||'','Down payment':f['Down payment']||'',
    'As-is value':f['As-is value']||'','Existing loan balance':f['Existing loan balance']||'',
    'Cash-out amount':f['Cash-out amount']||'','ARV':f['ARV']||'',
    'Rehab budget':f['Rehab budget']||'','Construction budget':f['Construction budget']||'',
    'Includes rehab/improvements':f['Includes rehab/improvements']||false,
    'Rehab financing':f['Rehab financing']||'',
    'Vacancy rate':f['Vacancy rate']!=null?(f['Vacancy rate']*100).toFixed(1):'',
    'Operating expense ratio':f['Operating expense ratio']!=null?(f['Operating expense ratio']*100).toFixed(1):'',
    'LTV':'',
    'LTARV':'',
    'LTC':'',
    'CLTV':'',
    'Lien position':f['Lien position']||'',
    'Recourse preference':f['Recourse preference']||'',
    'Appraisal preference':f['Appraisal preference']||'',
    'Cap rate':f['Cap rate']!=null?(f['Cap rate']*100).toFixed(2):'',
    'Occupancy %':f['Occupancy %']!=null?(f['Occupancy %']*100).toFixed(1):'',
    'Est. DSCR':f['Est. DSCR']||'','Total project cost':f['Total project cost']||'',
    'Est. profit at ARV':f['Est. profit at ARV']||'','Entity type':f['Entity type']||'',
    'Co-borrower name':f['Co-borrower name']||'','Co-borrower email':f['Co-borrower email']||'',
    'Co-borrower phone':f['Co-borrower phone']||'','AI review status':f['AI review status']||'',
    'Submitted by':f['Submitted by']||'',
    'Broker':Array.isArray(f['Broker'])&&f['Broker'].length?f['Broker'][0]:'',
    'Title officer':Array.isArray(f['Title officer'])&&f['Title officer'].length?f['Title officer'][0]:'',
    'Escrow officer':Array.isArray(f['Escrow officer'])&&f['Escrow officer'].length?f['Escrow officer'][0]:'',
    'Attorney':Array.isArray(f['Attorney'])&&f['Attorney'].length?f['Attorney'][0]:'',
    'Listing agent':Array.isArray(f['Listing agent'])&&f['Listing agent'].length?f['Listing agent'][0]:'',
    "Buyer's agent":Array.isArray(f["Buyer's agent"])&&f["Buyer's agent"].length?f["Buyer's agent"][0]:'',
    'Seller':Array.isArray(f['Seller'])&&f['Seller'].length?f['Seller'][0]:'',
    'Wholesaler':Array.isArray(f['Wholesaler'])&&f['Wholesaler'].length?f['Wholesaler'][0]:'',
    'Insurance agent':Array.isArray(f['Insurance agent'])&&f['Insurance agent'].length?f['Insurance agent'][0]:'',
    'Appraiser':Array.isArray(f['Appraiser'])&&f['Appraiser'].length?f['Appraiser'][0]:'',
    'Capital partner':Array.isArray(f['Capital partner'])&&f['Capital partner'].length?f['Capital partner'][0]:'',
    'JV partner':Array.isArray(f['JV partner'])&&f['JV partner'].length?f['JV partner'][0]:'',
    'GAP funder':Array.isArray(f['GAP funder'])&&f['GAP funder'].length?f['GAP funder'][0]:'',
    'Contractor':Array.isArray(f['Contractor'])&&f['Contractor'].length?f['Contractor'][0]:'',
    'Property manager':Array.isArray(f['Property manager'])&&f['Property manager'].length?f['Property manager'][0]:'',
    'Notary':Array.isArray(f['Notary'])&&f['Notary'].length?f['Notary'][0]:'',
    'CPA':Array.isArray(f['CPA'])&&f['CPA'].length?f['CPA'][0]:'',
  }));

  const [bForm,setBFormRaw] = useState(null);
  const markDirty = () => setDirty(true);
  const setForm = (u) => { markDirty(); setFormRaw(u); };
  const setBForm = (u) => { markDirty(); setBFormRaw(u); };
  const setNotes = (u) => { markDirty(); setNotesRaw(u); };
  const setCorr = (u) => { markDirty(); setCorrRaw(u); };
  const [dealContactNames,setDealContactNames] = useState([]);
  const corrWithOptions = React.useMemo(()=>{
    const out=[];
    const bn=[clean(bForm&&bForm['First name']),clean(bForm&&bForm['Last name'])].filter(Boolean).join(' ');
    if(bn) out.push(bn);
    (coForms||[]).forEach(c=>{const n=[clean(c['First name']),clean(c['Last name'])].filter(Boolean).join(' ');if(n)out.push(n);});
    dealContactNames.forEach(n=>{ if(n) out.push(n); });
    return [...new Set(out)];
  },[bForm,coForms,dealContactNames]);
  useEffect(()=>{
    if(!liveDealId){ setDealContactNames([]); return; }
    authFetch(`/.netlify/functions/list-deal-contacts?dealId=${liveDealId}`)
      .then(r=>r.json())
      .then(d=>setDealContactNames((d.records||[]).map(r=>{
        const fn=(r.fields&&r.fields['Contact first name']||[])[0]||'';
        const ln=(r.fields&&r.fields['Contact last name']||[])[0]||'';
        const nm=[fn,ln].filter(Boolean).join(' ');
        const role=(r.fields&&r.fields.Role)||'';
        return nm?(role?nm+' ('+role+')':nm):'';
      }).filter(Boolean)))
      .catch(()=>{});
  },[liveDealId]);
  const setSreoRows = (u) => { markDirty(); setSreoRowsRaw(u); };
  const setAssetRows = (u) => { markDirty(); setAssetRowsRaw(u); };
  const setLiabRows = (u) => { markDirty(); setLiabRowsRaw(u); };
  function deleteLiabRow(i){
    setLiabOpen(p=>{const n={};Object.keys(p).forEach(k=>{const x=Number(k);if(x<i)n[x]=p[k];else if(x>i)n[x-1]=p[k];});return n;});
    const row=liabRows[i];
    if(row&&row._id) setLiabDeleted(d=>[...d,row._id]);
    setLiabRows(prev=>prev.filter((_,x)=>x!==i));
  }
  const setIncRows = (u) => { markDirty(); setIncRowsRaw(u); };
  function deleteIncRow(i){
    setIncOpen(p=>{const n={};Object.keys(p).forEach(k=>{const x=Number(k);if(x<i)n[x]=p[k];else if(x>i)n[x-1]=p[k];});return n;});
    const row=incRows[i];
    if(row&&row._id) setIncDeleted(d=>[...d,row._id]);
    setIncRows(prev=>prev.filter((_,x)=>x!==i));
  }
  useEffect(()=>{
    if(!borrowerContact){ setBFormRaw(p=>p||{}); return; }
    const bf=borrowerContact.fields||{};
    if(!clean(bf['First name'])&&!clean(bf['Last name'])) return;
    const bo={};
    BORROWER_FIELDS.forEach(k=>{ bo[k]= bf[k]!==undefined ? bf[k] : ''; });
    bo['Home state']=Array.isArray(bf['Home state'])?bf['Home state']:[bf['Home state']].filter(Boolean);
    CONTACT_BOOLEANS.forEach(k=>{ bo[k]=!!bf[k]; });
    // The application answers live on the Deal Borrowers row, not the contact.
    // Lay them on here, in the same pass that seeds from the contact, so this
    // effect can never land after the overlay effect and blank them out.
    const dbRow=dbRowFor(borrowerContact.id);
    if(dbRow&&dbRow.fields){
      DEAL_BORROWER_FIELDS.forEach(k=>{
        const v=dbRow.fields[k];
        if(v===undefined||v===null||v==='') return;
        bo[k]=v;
      });
    }
    setBFormRaw(bo);
  },[borrowerContact,dbRows,dbRowFor]);

  useEffect(()=>{
    if(!coContact||coForms[coIdx]) return;
    const cf=coContact.fields||{};
    setCoForm({
      'First name':cf['First name']||'','Last name':cf['Last name']||'',
      'Email':cf['Email']||'','Phone':cf['Phone']||'',
      'Street address':cf['Street address']||'','City':cf['City']||'',
      'Home state':Array.isArray(cf['Home state'])?cf['Home state']:[cf['Home state']].filter(Boolean),
      'ZIP':cf['ZIP']||'',
      'Company / entity':cf['Company / entity']||'','Entity type':cf['Entity type']||'',
    });
  },[coContact]);

  useEffect(()=>{
    if(!coContact) return;
    authFetch(`/.netlify/functions/get-sreo?contactId=${coContact.id}`).then(r=>r.json()).then(d=>{setCoSreoRows((d.records||[]).map(r=>r.fields));appendFinRows(setSreoRowsRaw,liveDealId)(d);}).catch(()=>{});
    authFetch(`/.netlify/functions/get-assets?contactId=${coContact.id}`).then(r=>r.json()).then(d=>{setCoAssetRows((d.records||[]).map(r=>r.fields));appendFinRows(setAssetRowsRaw,liveDealId)(d);}).catch(()=>{});
  },[coContact]);


  const propType = form['Property type']||'';
  const loanType = form['Loan type']||'';
  const isMH = loanType==='MH Chattel' || loanType==='MH Land/Home';
  const hasCollateral = loanType !== 'Working capital / LOC';
  const loanPurpose = form['Loan purpose']||'';
  const ovBorrowerName = [bForm?.['First name'],bForm?.['Last name']].filter(Boolean).join(' ').trim();
  const lienPos = form['Lien position'] || defaultLienPosition(loanType);
  const isSecond = lienPos === '2nd lien';
  const isCCCoBrokered = (()=>{ const b=clean(form&&form['Brokerage']); return !b || /crafted/i.test(b); })();
  const ptFields = PT_FIELDS[propType]||[];
  const showMH = isMH || ptFields.includes('_mh');
  const wholesaleOn = (form['Deal situations']||[]).includes('Wholesale');

  const spec = getLoanFieldSpec(loanType,loanPurpose,!!form['Includes rehab/improvements']);
  if(isSecond){ spec.show.existingLoanBalance = true; spec.required.existingLoanBalance = true; spec.labels.existingLoanBalance = 'Existing senior lien balance'; }
  const availablePurposes = getAvailablePurposes(loanType);

  const hoaChecked = !!form['HOA'];
  const garageChecked = !!form['Garage'];
  const aduChecked = !!form['ADU'];
  const basementChecked = !!form['Basement'];

  const showRehabToggle = REHAB_TOGGLE_LOAN_TYPES.includes(loanType);
  const rehabInScope = !!spec.show.rehabBudget;

  const showPostRehab =
    ['Fix & Flip','Ground-Up Construction'].includes(loanType)
    || (REHAB_TOGGLE_LOAN_TYPES.includes(loanType) && !!form['Includes rehab/improvements'])
    || loanPurpose === 'Construction';

  const hasIncomeFields = INCOME_FIELD_ORDER.some(k=>spec.show[k]);
  const hasSecondaryIncome = SECONDARY_INCOME_ORDER.some(k=>spec.show[k]);

  const mFv = form;
  // Money in the form can carry a $ and thousands separators. The bare
  // `num` helper is Number(x)||0, which returns 0 on "$190,000", so every
  // metric below silently read zero while the Loan card, which uses its own
  // tolerant parser, showed the right figure. Same value, two parsers, two
  // answers. `mn` is the tolerant one and every metric helper uses it.
  const mn = v => Number(String(v==null?'':v).replace(/[^0-9.-]/g,'')) || 0;
  const calcAnnualInc = () => (incRows||[]).reduce((a,r)=>a+(incIsPrev(r)?0:num(r['Annual amount'])*((r['Frequency']||'Annually')==='Monthly'?12:1)),0)+((sreoRows||[]).filter(x=>x['Status']!=='Sold'&&num(x['Rental income'])>0).reduce((a,x)=>a+(num(x['Rental income'])-pmtShareFor(x._id)),0))*12;
  const calcPITI = () => {
    const nnp=v=>Number(String(v||'').replace(/[^0-9.]/g,''))||0;
    const r=nnp(form['Requested rate']),t=nnp(form['Term months']),l=nnp(form['Loan amount']),mr=r/100/12;
    const pi=(l&&r&&t)?(l*mr/(1-Math.pow(1+mr,-t))):0;
    if(!pi) return 0;
    const tax=nnp(form['Property taxes'])*((form['Property taxes frequency']||'Monthly')==='Annually'?1/12:1);
    const ins=nnp(form['Insurance'])*((form['Insurance frequency']||'Monthly')==='Annually'?1/12:1);
    const hoa=nnp(form['HOA dues'])*(form['HOA dues frequency']==='Annually'?1/12:form['HOA dues frequency']==='Quarterly'?1/3:1);
    return pi+tax+ins+nnp(form['MH monthly fee'])+hoa+nnp(form['Mortgage insurance'])+nnp(form['Other financing']);
  };
  const calcMoDebt = () => reoHousingDti() + liabDtiTotal();
  // ONE value base for LTV and CLTV, mirroring exactly what the Loan card puts
  // on screen. It used to read Purchase price or As-is value only, so a
  // refinance where the value lives in Appraised value computed nothing: the
  // card showed 55% and Airtable saved blank. Never let the screen and the
  // save use different formulas for the same number.
  const mIsRef = () => String(mFv['Loan purpose']||'').toLowerCase().indexOf('refi')>=0;
  const mValueBase = () => {
    const appr=mn(mFv['Appraised value']), asis=mn(mFv['As-is value']), prc=mn(mFv['Purchase price']);
    if(mIsRef()) return appr||asis||prc||0;
    if(prc&&appr) return Math.min(prc,appr);
    return prc||appr||asis||0;
  };
  const mLTV = () => { const l=mn(mFv['Loan amount']),b=mValueBase(); return l&&b?((l/b)*100).toFixed(2):''; };
  const mCLTV = () => { const l=mn(mFv['Loan amount']),e=mn(mFv['Existing loan balance']),v=mValueBase()||mn(mFv['ARV']); return (l&&v)?(((l+e)/v)*100).toFixed(2):''; };
  const mLTARV = () => { const l=mn(mFv['Loan amount']),a=mn(mFv['ARV']); return l&&a?((l/a)*100).toFixed(2):''; };
  const mLTC = () => { const l=mn(mFv['Loan amount']),c=mn(mFv['Purchase price'])+(mn(mFv['Rehab budget'])||mn(mFv['Construction budget'])); return l&&c?((l/c)*100).toFixed(2):''; };
  const mTotalCost = () => { const a=mn(mFv['Purchase price']),b=mn(mFv['Rehab budget'])||mn(mFv['Construction budget']); return (a||b)?(a+b):''; };
  const mProfit = () => { const a=mn(mFv['ARV']),p=mn(mFv['Purchase price']),r=mn(mFv['Rehab budget']); return a?(a-p-r):''; };
  const mRent = () => { const m=mn(mFv['Current monthly rent']); return m?m*12:''; };
  const mNOI = () => { const r=mRent(); if(!r) return ''; const vr=mn(mFv['Vacancy rate'])/100,e=mn(mFv['Operating expense ratio'])/100; return r*(1-vr)*(1-e); };
  const mDSCR = () => { const n=mNOI(); if(!n) return ''; const l=mn(mFv['Loan amount']),r=(mn(mFv['Requested rate'])||8)/100; const d=l*r; if(!d) return ''; return (n/d).toFixed(2); };
  const mCapRate = () => { const n=mNOI(); if(!n) return ''; const b=mn(mFv['As-is value']); if(!b) return ''; return ((n/b)*100).toFixed(2); };
  const mOccupancy = () => { const u=mn(mFv['Number of units']),o=mn(mFv['Occupied units']); return u>0?((o/u)*100).toFixed(2):''; };

  function toggleCard(id){setCollapsed(p=>({...p,[id]:!p[id]}));}

  function toggleDealSituation(sit){ setForm(p=>{const a=Array.isArray(p['Deal situations'])?p['Deal situations']:[];const set=new Set(a);set.has(sit)?set.delete(sit):set.add(sit);return{...p,'Deal situations':[...set]};}); }
  function toggleAssetClass(sp){ setForm(p=>{const a=Array.isArray(p['Asset class'])?p['Asset class']:[];const set=new Set(a);set.has(sp)?set.delete(sp):set.add(sp);return{...p,'Asset class':[...set]};}); }

  function handleLoanTypeChange(newLoanType){
    setForm(p => {
      const validPurposes = getAvailablePurposes(newLoanType);
      const keepPurpose = validPurposes.includes(p['Loan purpose']);
      return { ...p, 'Loan type': newLoanType, 'Loan purpose': keepPurpose ? p['Loan purpose'] : '' };
    });
  }

  // The folder also syncs when a deal is OPENED, not only when it is saved.
  // Save is disabled unless the page is dirty, so a deal whose folder is out
  // of date could never catch up without an unrelated edit. The function is a
  // no-op when the names already match, so this is cheap and self-healing.
  useEffect(()=>{
    if(!liveDealId) return;
    let gone=false;
    authFetch('/.netlify/functions/sync-deal-folder',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:liveDealId})})
      .then(r=>r.json())
      .then(d=>{ if(!gone&&d&&d.success===false) console.warn('Drive folder sync:',d.error); })
      .catch(e=>{ if(!gone) console.warn('Drive folder sync failed:',e); });
    return ()=>{ gone=true; };
  },[liveDealId]);

  async function saveAll() {
    setSaving(true); setSaveError('');
    try {
      let did = liveDealId;
      if(!did){
        const seed={
          loanType: form['Loan type']||'',
          loanPurpose: form['Loan purpose']||'',
          propertyType: form['Property type']||'',
          propertyAddress: form['Property address']||'',
          propertyCity: form['Property city']||'',
          propertyState: form['Property state']||'',
          propertyZip: form['Property ZIP']||'',
          borrowerFirstName: clean(bForm?.['First name'])||'',
          borrowerLastName: clean(bForm?.['Last name'])||'',
          borrowerEmail: clean(bForm?.['Email'])||'',
        };
        if(!seed.propertyAddress && !seed.borrowerLastName) throw new Error('Add a property address or a borrower last name before saving.');
        const cr=await authFetch('/.netlify/functions/submit-deal-internal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(seed)});
        const cd=await cr.json();
        if(!cr.ok||!cd.success) throw new Error('Could not create the deal: '+(cd.error||cr.status));
        const newId=cd.dealId||cd.id||(cd.record&&cd.record.id);
        if(!newId) throw new Error('The deal was created but no id came back. Reload the deals list and open it.');
        did = newId; setLiveDealId(newId);
        if(cd.contactId && !borrowerContact) setBorrowerContact({id:cd.contactId,fields:{'First name':clean(bForm?.['First name'])||'','Last name':clean(bForm?.['Last name'])||''}});
        if(typeof onCreated==='function') onCreated(newId);
      }
      const payload={};
      Object.entries(form).forEach(([k,val])=>{
        if(typeof val==='boolean'){ payload[k]=val; return; }
        if(val===''||val===null||val===undefined) return;
        payload[k]=val;
      });
      payload['Stage']=stage; payload['Additional notes']=notes;
      payload['Correspondence log']=JSON.stringify(corr);
      if(!showCo){ delete payload['Co-borrower name']; delete payload['Co-borrower email']; delete payload['Co-borrower phone']; }
      CURRENCY_FIELDS.forEach(k=>{ if(payload[k]!==undefined&&typeof payload[k]!=='boolean'){ const p=parseFloat(String(payload[k]).replace(/[^0-9.]/g,'')); if(!isNaN(p)) payload[k]=p; else delete payload[k]; } });
      NUMBER_FIELDS.forEach(k=>{ if(payload[k]!==undefined&&typeof payload[k]!=='boolean'){ const p=parseFloat(payload[k]); if(!isNaN(p)) payload[k]=p; else delete payload[k]; } });
      PCT_FIELDS.forEach(k=>{ if(payload[k]!==undefined&&payload[k]!==''&&typeof payload[k]!=='boolean'){ const p=parseFloat(payload[k]); if(!isNaN(p)) payload[k]=p/100; else delete payload[k]; } });
      {const a=mLTV(); if(a) payload['LTV']=parseFloat(a)/100;}
      {const a=mLTARV(); if(a) payload['LTARV']=parseFloat(a)/100;}
      {const a=mLTC(); if(a) payload['LTC']=parseFloat(a)/100;}
      if(loanType) payload['Lien position'] = form['Lien position'] || defaultLienPosition(loanType);
      if(isSecond){ {const a=mCLTV(); if(a) payload['CLTV']=parseFloat(a)/100;} } else { delete payload['CLTV']; }
      {const a=mTotalCost(); if(a) payload['Total project cost']=a;}
      {const a=mProfit(); if(a) payload['Est. profit at ARV']=a;}
      {const a=mRent(); if(a) payload['Annual rent']=a;}
      {const a=mNOI(); if(a) payload['Annual NOI']=a;}
      {const a=mDSCR(); if(a) payload['Est. DSCR']=parseFloat(a);}
      {const a=mCapRate(); if(a) payload['Cap rate']=parseFloat(a)/100;}
      {const a=mOccupancy(); if(a) payload['Occupancy %']=parseFloat(a)/100;}
      {
        const nnp=v=>Number(String(v||'').replace(/[^0-9.]/g,''))||0;
        const prc=nnp(payload['Purchase price'])||nnp(form['Purchase price']);
        const dp=nnp(payload['Down payment'])||nnp(form['Down payment']);
        const amtL=nnp(payload['Loan amount'])||nnp(form['Loan amount']);
        const sub=nnp(form['Subordinate amount']);
        payload['Down payment percent'] = (prc&&dp) ? Math.round((dp/prc)*100000)/1000/100 : null;
        payload['Total loan amount']    = amtL ? amtL+sub : null;
        const isRefP = String(form['Loan purpose']||'').toLowerCase().indexOf('refi')>=0;
        payload['Cash to borrower']     = isRefP ? Math.max(amtL-nnp(form['Existing lien amount']),0) : null;
        const piti = calcPITI();
        const moInc = calcAnnualInc()/12;
        const moDebt = calcMoDebt();
        payload['Total PITI']    = piti||null;
        payload['Monthly income']= moInc||null;
        payload['Monthly debt']  = moDebt||null;
        payload['Front DTI'] = (moInc&&piti) ? Math.round(((piti+reoHousingDti())/moInc)*10000)/100/100 : null;
        payload['Back DTI']  = (moInc&&piti) ? Math.round(((piti+moDebt)/moInc)*10000)/100/100 : null;
        Object.keys(payload).forEach(k=>{ if(payload[k]===null) delete payload[k]; });
      }
      ALL_TX_ROLES.forEach(k=>{
        if(payload[k]&&typeof payload[k]==='string') payload[k]=[payload[k]]; else delete payload[k];
      });
      {
        // Rebuild the deal name from whatever we now know. It fills itself in
        // as the borrower and the address arrive, and it is never written blank
        // over a name that already exists.
        const computedName = buildDealName(
          [clean(bForm&&bForm['First name']),clean(bForm&&bForm['Last name'])].filter(Boolean).join(' '),
          payload['Property address']!==undefined ? payload['Property address'] : form['Property address']
        );
        if(computedName) payload['Deal name']=computedName; else delete payload['Deal name'];
      }
      const skipped=[];
      let dealData=null;
      for(let attempt=0;attempt<12;attempt++){
        const dealRes=await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:did,fields:payload})});
        dealData=await dealRes.json();
        if(!dealData.error) break;
        const msg=dealData.error?.message||JSON.stringify(dealData.error)||'';
        const bad=(msg.match(/Unknown field name:\s*"([^"]+)"/)||msg.match(/Field "([^"]+)" cannot accept/)||[])[1];
        if(!bad||payload[bad]===undefined) throw new Error(msg);
        delete payload[bad];
        skipped.push(bad);
      }
      if(dealData&&dealData.error) throw new Error(dealData.error?.message||JSON.stringify(dealData.error));
      if(skipped.length) setSaveError('Saved, but these fields have no matching Airtable column and were skipped: '+skipped.join(', '));

      let bContact = borrowerContact;
      if(bForm && !bContact && (clean(bForm['First name'])||clean(bForm['Last name']))){
        const bnm=[clean(bForm['First name']),clean(bForm['Last name'])].filter(Boolean).join(' ');
        const bcr=await authFetch('/.netlify/functions/create-contact',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fields:{'First name':clean(bForm['First name']),'Last name':clean(bForm['Last name']),'Role(s)':['Borrower']},typecast:true})});
        const bcd=await bcr.json();
        if(bcd.error) throw new Error('Borrower create failed: '+(bcd.error?.message||JSON.stringify(bcd.error)));
        if(!bcd.id) throw new Error('Borrower create failed: no id returned');
        bContact={id:bcd.id,fields:{Name:bnm}};
        setBorrowerContact(bContact);
        const blr=await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:did,fields:{'Borrower':[bcd.id]}})});
        const bld=await blr.json();
        if(bld.error) throw new Error('Could not link the borrower to this deal: '+(bld.error?.message||JSON.stringify(bld.error)));
      }
      if(bForm && !bContact && Object.keys(bForm).some(k=>bForm[k]!==''&&bForm[k]!=null&&bForm[k]!==false&&!Array.isArray(bForm[k]))){
        throw new Error('Borrower details were not saved. Add a first and last name so the borrower record can be created.');
      }
      if(bForm&&bContact){
        const bFields={...bForm};
        ['Same address as borrower 1','_snap','_uid','_isNew','_id'].forEach(k=>delete bFields[k]);
        Object.keys(bFields).forEach(k=>{ if(!BORROWER_FIELDS.includes(k)) delete bFields[k]; });
        // These 29 belong to the deal now. Send them to the Deal Borrowers row and
        // stop writing them onto the contact.
        await saveDealBorrower(did, bContact.id, bForm, 1);
        DEAL_BORROWER_FIELDS.forEach(k=>delete bFields[k]);
        if(bFields['FICO score']) bFields['FICO score']=parseInt(bFields['FICO score'])||0;
        if(bFields['Mortgage lates 12mo']) bFields['Mortgage lates 12mo']=parseInt(bFields['Mortgage lates 12mo'])||0;
        if(bFields['Other lates 12mo']) bFields['Other lates 12mo']=parseInt(bFields['Other lates 12mo'])||0;
        ['Dependents count','Former years','Former months'].forEach(k=>{ if(bFields[k]!==undefined&&bFields[k]!=='') bFields[k]=parseInt(bFields[k])||0; });
        if(bFields['Monthly rent']!==undefined&&bFields['Monthly rent']!=='') bFields['Monthly rent']=parseFloat(pN(String(bFields['Monthly rent'])))||0;
        if(!Array.isArray(bFields['Home state'])) bFields['Home state']=[bFields['Home state']].filter(Boolean);
        bFields['Home state']=(bFields['Home state']||[]).filter(v=>v&&v.trim()!=='');
        if (!bFields['Bankruptcy history']) {
          delete bFields['Bankruptcy type'];
          delete bFields['Bankruptcy date'];
          delete bFields['Bankruptcy status'];
        }
        if (!bFields['Foreclosure history']) {
          delete bFields['Foreclosure date'];
        }
        Object.keys(bFields).forEach(k=>{ if(!Array.isArray(bFields[k])&&typeof bFields[k]!=='boolean'&&(bFields[k]===''||bFields[k]==null)) delete bFields[k]; });
        const bSkipped=[];
        let cData=null;
        for(let attempt=0;attempt<12;attempt++){
          const cRes=await authFetch('/.netlify/functions/update-contact',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({contactId:bContact.id,fields:bFields})});
          cData=await cRes.json();
          if(!cData.error) break;
          const bmsg=cData.error?.message||JSON.stringify(cData.error)||'';
          const bbad=(bmsg.match(/Unknown field name:\s*"([^"]+)"/)||bmsg.match(/Field "([^"]+)" cannot accept/)||[])[1];
          if(!bbad||bFields[bbad]===undefined) throw new Error('Borrower save failed: '+bmsg);
          delete bFields[bbad];
          bSkipped.push(bbad);
        }
        if(cData&&cData.error) throw new Error('Borrower save failed: '+(cData.error?.message||JSON.stringify(cData.error)));
        if(bSkipped.length) setSaveError('NOT SAVED, Airtable refused these borrower fields: '+bSkipped.join(', '));
      }

      {
        const nextCo=[...coContacts];
        let idsChanged=false;
        for(let ci=0; ci<coForms.length; ci++){
          const cform=coForms[ci];
          if(!cform) continue;
          let target=nextCo[ci]||null;
          const hasName=clean(cform['First name'])||clean(cform['Last name']);
          if(!target){
            if(!hasName) continue;
            const nm=[clean(cform['First name']),clean(cform['Last name'])].filter(Boolean).join(' ');
            const cr=await authFetch('/.netlify/functions/create-contact',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fields:{'First name':clean(cform['First name']),'Last name':clean(cform['Last name']),'Name':nm}})});
            const cd=await cr.json();
            if(cd.error) throw new Error('Borrower '+(ci+2)+' create failed: '+(cd.error?.message||JSON.stringify(cd.error)));
            const newId=cd.id||cd.record?.id;
            if(!newId) throw new Error('Borrower '+(ci+2)+' create failed: no id returned');
            target={id:newId,fields:{Name:nm}};
            nextCo[ci]=target; idsChanged=true;
          }
          const coFields={...cform};
          ['Same address as borrower 1','_snap','_uid','_isNew','_id'].forEach(k=>delete coFields[k]);
          await saveDealBorrower(did, target.id, cform, ci+2);
          DEAL_BORROWER_FIELDS.forEach(k=>delete coFields[k]);
          if(coFields['FICO score']) coFields['FICO score']=parseInt(coFields['FICO score'])||0;
          if(!Array.isArray(coFields['Home state'])) coFields['Home state']=[coFields['Home state']].filter(Boolean);
          Object.keys(coFields).forEach(k=>{ if(!Array.isArray(coFields[k])&&typeof coFields[k]!=='boolean'&&(coFields[k]===''||coFields[k]==null)) delete coFields[k]; });
          const coRes=await authFetch('/.netlify/functions/update-contact',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({contactId:target.id,fields:coFields})});
          const coData=await coRes.json();
          if(coData.error) throw new Error('Borrower '+(ci+2)+' save failed: '+(coData.error?.message||JSON.stringify(coData.error)));
        }
        if(idsChanged){
          await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:did,fields:{'Co-borrower':nextCo.filter(Boolean).map(c=>c.id)}})});
          setCoContacts(nextCo.filter(Boolean));
        }
      }

      for(const row of sreoRows){
        const sf={
          'Property address':row['Property address']||'',
          'Current value':parseFloat(pN(String(row['Current value']||'')))||0,
          'Purchase price':parseFloat(pN(String(row['Purchase price']||'')))||0,
          'Rehab budget':parseFloat(pN(String(row['Rehab budget']||'')))||0,
          'Sale price':parseFloat(pN(String(row['Sale price']||'')))||0,
          'Rental income':parseFloat(pN(String(row['Rental income']||'')))||0,
          'Lender name':row['Lender name']||'',
        };
        ['Insurance','Property taxes','HOA dues','Other monthly expenses'].forEach(k=>{ if(row[k]!==undefined&&row[k]!==null&&String(row[k])!=='') sf[k]=parseFloat(pN(String(row[k])))||0; });
        if (typeof row['Apply rental income to DTI']==='boolean') sf['Apply rental income to DTI']=row['Apply rental income to DTI'];
        if (row['Property type']) sf['Property type']=row['Property type'];
        if (row['Use']) sf['Use']=row['Use'];
        if (row['Status']) sf['Status']=row['Status'];
        if (isoDate(row['Purchase date'])) sf['Purchase date']=isoDate(row['Purchase date']);
        if (isoDate(row['Date rented'])) sf['Date rented']=isoDate(row['Date rented']);
        if (isoDate(row['Date sold'])) sf['Date sold']=isoDate(row['Date sold']);
        if (Array.isArray(row['Contact'])&&row['Contact'].length) sf['Contact']=row['Contact'];
        sf['Property']=rowLabel(clean(form['Deal name']),sf['Property address']||row['Property address']);
        if(row._id){ const r=await authFetch('/.netlify/functions/update-sreo',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sreoId:row._id,fields:sf})}); const d=await r.json(); if(d.error) throw new Error('SREO save failed: '+d.error?.message); }
        else if(borrowerContact){ const r=await authFetch('/.netlify/functions/create-sreo',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fields:{...sf,Deal:did?[did]:undefined,Contact:(Array.isArray(row['Contact'])&&row['Contact'].length)?row['Contact']:[borrowerContact.id]}})}); const d=await r.json(); if(d.error) throw new Error('SREO create failed: '+d.error?.message); row._id=d.id; row._isNew=false; }
      }

      for(const row of assetRows){
        const af={
          'Institution name':row['Institution name']||'',
          'Balance':parseFloat(pN(String(row['Balance']||'')))||0,
        };
        if (row['Asset type']) af['Asset type']=row['Asset type'];
        if (row['Account last 4']) af['Account last 4']=row['Account last 4'];
        if (row['Description']!==undefined) af['Description']=row['Description']||'';
        if (isoDate(row['Verification date'])) af['Verification date']=isoDate(row['Verification date']);
        if (typeof row['Verified'] === 'boolean') af['Verified']=row['Verified'];
        if (Array.isArray(row['Contact'])&&row['Contact'].length) af['Contact']=row['Contact'];
        af['Asset']=rowLabel(clean(form['Deal name']),af['Institution name']||row['Institution name']);
        if(row._id){ const r=await authFetch('/.netlify/functions/update-asset',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({assetId:row._id,fields:af})}); const d=await r.json(); if(d.error) throw new Error('Asset save failed: '+d.error?.message); }
        else if(borrowerContact){ const r=await authFetch('/.netlify/functions/create-asset',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fields:{...af,Deal:did?[did]:undefined,Contact:(Array.isArray(row['Contact'])&&row['Contact'].length)?row['Contact']:[borrowerContact.id]}})}); const d=await r.json(); if(d.error) throw new Error('Asset create failed: '+d.error?.message); row._id=d.id; row._isNew=false; }
      }

      for(const row of liabRows){
        const lf={
          'Creditor':row['Creditor']||'',
          'Balance':parseFloat(pN(String(row['Balance']||'')))||0,
          'Monthly payment':parseFloat(pN(String(row['Monthly payment']||'')))||0,
          'Secured by':row['Secured by']||'',
        };
        if (row['Type']) lf['Type']=row['Type'];
        ['Mortgage type','Lien position','Account number','Account status','Current rating','Liability description','Creditor street','Creditor unit','Creditor city','Creditor state','Creditor zip','Creditor county','Creditor email','Creditor phone','Creditor ext'].forEach(k=>{ if(clean(row[k])) lf[k]=row[k]; });
        if (row['Line amount']!==undefined&&row['Line amount']!==null&&String(row['Line amount'])!=='') lf['Line amount']=parseFloat(pN(String(row['Line amount'])))||0;
        ['Derogatory','Will be paid off','Omitted','Exclude from DTI','HOI escrowed','Re-subordinate','Property taxes escrowed'].forEach(k=>{ if (typeof row[k]==='boolean') lf[k]=row[k]; });
        if (Array.isArray(row['Linked property'])) lf['Linked property']=row['Linked property'];
        if (Array.isArray(row['Linked asset'])) lf['Linked asset']=row['Linked asset'];
        if (String(row['Months left']||'').trim()!=='') lf['Months left']=parseInt(pN(String(row['Months left'])),10)||0;
        if (Array.isArray(row['Contact'])&&row['Contact'].length) lf['Contact']=row['Contact'];
        lf['Liabilities']=rowLabel(clean(form['Deal name']),lf['Creditor']||row['Creditor']);
        if(row._id){ const r=await authFetch('/.netlify/functions/update-liability',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({liabilityId:row._id,fields:lf})}); const d=await r.json(); if(d.error) throw new Error('Liability save failed: '+d.error?.message); }
        else { const r=await authFetch('/.netlify/functions/create-liability',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fields:{...lf,Deal:[did],...((Array.isArray(row['Contact'])&&row['Contact'].length)?{Contact:row['Contact']}:(borrowerContact?{Contact:[borrowerContact.id]}:{}))}})}); const d=await r.json(); if(d.error) throw new Error('Liability create failed: '+d.error?.message); row._id=d.id; row._isNew=false; }
      }
      for(const id of liabDeleted){ const r=await authFetch('/.netlify/functions/delete-liability',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({liabilityId:id})}); const d=await r.json(); if(d.error) throw new Error('Liability delete failed: '+d.error?.message); }
      setLiabDeleted([]);

      for(const row of incRows){
        const inf={
          'Source':row['Source']||'',
          'Annual amount':parseFloat(pN(String(row['Annual amount']||'')))||0,
        };
        if (row['Type']) inf['Type']=row['Type'];
        if (row['Frequency']) inf['Frequency']=row['Frequency'];
        ['Position or title','Employer street','Employer phone','Employer ext','Start date','End date','Other description','Pay type'].forEach(k=>{ const val=String(row[k]==null?'':row[k]).trim(); if(val!=='') inf[k]=val; });
        ['Current','Primary','Self employed','Seasonal','Foreign income','Employed by family or party to transaction'].forEach(k=>{ if(typeof row[k]==='boolean') inf[k]=row[k]; });
        ['Annual base','Monthly base','Hourly rate','Hours per week','Bonus monthly','Commission monthly','Overtime monthly','Other monthly','Monthly total','Years in line of work','Months in line of work','Military base income','Military clothes allowance','Military combat pay','Military flight pay','Military hazard pay','Military overseas pay','Military prop pay','Military quarters allowance','Military rations allowance','Military variable housing allowance'].forEach(k=>{ if(row[k]!==undefined&&row[k]!==null&&String(row[k])!=='') inf[k]=parseFloat(pN(String(row[k])))||0; });
        if (typeof row['Verified'] === 'boolean') inf['Verified']=row['Verified'];
        if (Array.isArray(row['Contact'])&&row['Contact'].length) inf['Contact']=row['Contact'];
        inf['Income']=rowLabel(clean(form['Deal name']),inf['Source']||row['Source']);
        if(row._id){ const r=await authFetch('/.netlify/functions/update-income',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({incomeId:row._id,fields:inf})}); const d=await r.json(); if(d.error) throw new Error('Income save failed: '+d.error?.message); }
        else { const r=await authFetch('/.netlify/functions/create-income',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fields:{...inf,Deal:[did],...((Array.isArray(row['Contact'])&&row['Contact'].length)?{Contact:row['Contact']}:(borrowerContact?{Contact:[borrowerContact.id]}:{}))}})}); const d=await r.json(); if(d.error) throw new Error('Income create failed: '+d.error?.message); row._id=d.id; row._isNew=false; }
      }
      for(const id of assetDeleted){ const r=await authFetch('/.netlify/functions/delete-asset',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({assetId:id})}); const d=await r.json(); if(d.error) throw new Error('Asset delete failed: '+d.error?.message); }
      setAssetDeleted([]);
      for(const id of sreoDeleted){ const r=await authFetch('/.netlify/functions/delete-sreo',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sreoId:id})}); const d=await r.json(); if(d.error) throw new Error('Property delete failed: '+d.error?.message); }
      setSreoDeleted([]);
      for(const id of incDeleted){ const r=await authFetch('/.netlify/functions/delete-income',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({incomeId:id})}); const d=await r.json(); if(d.error) throw new Error('Income delete failed: '+d.error?.message); }
      setIncDeleted([]);

      setSavedFields(prev=>({...prev,...payload}));
      setDirty(false);
      setSaved(true);
      setTimeout(()=>setSaved(false),3000);

      // Keep the Drive folder named like the deal. Deliberately fire-and-forget
      // and never awaited into the try above: a Drive hiccup must not make a
      // save that already succeeded report as failed.
      try{
        authFetch('/.netlify/functions/sync-deal-folder',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:did})})
          .then(r=>r.json())
          .then(d=>{ if(d&&d.success===false) console.warn('Drive folder sync:',d.error); })
          .catch(e=>console.warn('Drive folder sync failed:',e));
      }catch(e){ console.warn('Drive folder sync not started:',e); }
    } catch(e){ setSaveError('Save failed: '+e.message); console.error(e); }
    finally{ setSaving(false); }
  }

  async function saveStage(newStage) {
    setStage(newStage);
    setSaving(true); setSaveError('');
    try {
      const res=await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:liveDealId,fields:{Stage:newStage}})});
      const data=await res.json();
      if(data.error) throw new Error(data.error?.message||JSON.stringify(data.error));
      setSavedFields(prev=>({...prev,Stage:newStage}));
      setSaved(true); setTimeout(()=>setSaved(false),3000);
    } catch(e){ setSaveError('Save failed: '+e.message); }
    finally{ setSaving(false); }
  }

  async function saveStageNotes() {
    setSaving(true); setSaveError('');
    try {
      const res=await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:liveDealId,fields:{Stage:stage,'Additional notes':notes}})});
      const data=await res.json();
      if(data.error) throw new Error(data.error?.message||JSON.stringify(data.error));
      setSavedFields(prev=>({...prev,Stage:stage,'Additional notes':notes}));
      setSaved(true); setTimeout(()=>setSaved(false),3000);
    } catch(e){ setSaveError('Save failed: '+e.message); }
    finally{ setSaving(false); }
  }

  function addCorrEntry(){
    if(!corrDraft.note.trim()&&!corrDraft.with.trim()) return;
    const entry={id:Date.now(),type:corrDraft.type,date:corrDraft.date||new Date().toISOString().slice(0,10),with:corrDraft.with.trim(),note:corrDraft.note.trim()};
    setCorr(prev=>[entry,...prev]);
    setCorrDraft({type:'Call',date:'',with:'',note:''});
    setShowCorrAdd(false);
  }
  function removeCorrEntry(id){ setCorr(prev=>prev.filter(e=>e.id!==id)); }
  async function saveCorrespondence(){
    setSaving(true); setSaveError('');
    try{
      const res=await authFetch('/.netlify/functions/update-deal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({dealId:liveDealId,fields:{'Correspondence log':JSON.stringify(corr)}})});
      const data=await res.json();
      if(data.error) throw new Error(data.error?.message||JSON.stringify(data.error));
      setSavedFields(prev=>({...prev,'Correspondence log':JSON.stringify(corr)}));
      setCorrSaved(true); setTimeout(()=>setCorrSaved(false),3000);
    }catch(e){ setSaveError('Save failed: '+e.message); }
    finally{ setSaving(false); }
  }

  const setBFormField = (k,val) => setBForm(p=>({...p,[k]:val}));
  const setF = (k) => (v) => setForm(p=>({...p,[k]:v}));
  const setB = (k) => (v) => setBFormField(k,v);
  const setCoFormField = (k,v) => { setCoForm(p=>({...p,[k]:v})); setDirty(true); };
  const bActive = activeB>0;
  const xForm = bActive ? coForm : bForm;
  const setX = (k) => (v) => { if(bActive) setCoFormField(k,v); else setBFormField(k,v); };
  const setXField = (k,v) => { if(bActive) setCoFormField(k,v); else setBFormField(k,v); };
  const setCo = (k) => (v) => { setCoForm(p=>({...p,[k]:v})); setDirty(true); };

  const [bankPct,setBankPct] = useState(null);
  const [cdcPct,setCdcPct] = useState(null);
  const sbaNN = v=>Number(String(v||'').replace(/[^0-9.]/g,''))||0;
  const sbaProj = () => sbaNN(form['Purchase price'])+sbaNN(form['Rehab budget'])+sbaNN(form['Other project costs']);
  useEffect(()=>{
    if(loanType!=='SBA 504'||bankPct!==null) return;
    const proj=sbaProj(); const bank=sbaNN(form['Loan amount']); const gross=sbaNN(form['CDC portion']);
    setBankPct(proj>0&&bank>0?Math.round(bank/proj*1000)/10:50);
    setCdcPct(proj>0&&gross>0?Math.round(((gross-3500)/1.0265)/proj*1000)/10:40);
  },[loanType,form,bankPct]);
  function sbaGrossFromNet(net){ return Math.ceil((net*1.0265+3500)/1000)*1000; }
  function sbaApplyPcts(bp,cp){
    const proj=sbaProj(); if(!proj) return;
    const b=Number(bp)||0, c=Number(cp)||0;
    if(b>0) setF('Loan amount')(String(Math.round(proj*b/100)));
    if(c>0) setF('CDC portion')(String(sbaGrossFromNet(proj*c/100)));
    const ip=100-b-c;
    if(ip>0) setF('Down payment')(String(Math.round(proj*ip/100)));
  }
  function sbaOnProject(fieldKey){ return v=>{
    setF(fieldKey)(v);
    const vals={'Purchase price':form['Purchase price'],'Rehab budget':form['Rehab budget'],'Other project costs':form['Other project costs']};
    vals[fieldKey]=v;
    const proj=sbaNN(vals['Purchase price'])+sbaNN(vals['Rehab budget'])+sbaNN(vals['Other project costs']);
    const b=Number(bankPct)||0,c=Number(cdcPct)||0;
    if(proj>0&&b>0&&c>0){
      setF('Loan amount')(String(Math.round(proj*b/100)));
      setF('CDC portion')(String(sbaGrossFromNet(proj*c/100)));
      const ip=100-b-c; if(ip>0) setF('Down payment')(String(Math.round(proj*ip/100)));
    }
  };}
  function sbaBalanceInjection(bankAmt,net){
    const proj=sbaProj(); if(!proj) return;
    const inj=Math.round(proj-(bankAmt||0)-(net||0));
    if(inj>0) setF('Down payment')(String(inj));
  }
  const togF = (k) => setForm(p=>({...p,[k]:!p[k]}));
  const appDone = {
    borrower: !!(bForm && bForm['First name'] && bForm['Last name'] && bForm['FICO score']),
    entity: !!(bForm && bForm['Company / entity'] && bForm['Entity type']),
    property: !!(form['Property address'] && form['Property type'] && form['Occupancy status'] && form['Purchase price']),
    loan: !!(form['Loan type'] && form['Loan purpose'] && form['Loan amount']),
    sreo: (sreoRows && sreoRows.length) > 0,
    assets: (assetRows && assetRows.length) > 0,
    liabilities: (liabRows && liabRows.length) > 0,
    income: (incRows && incRows.length) > 0,
    declarations: true,
    documents: Array.isArray(f['Documents']) && f['Documents'].length > 0,
  };
  const appDoneCount = Object.values(appDone).filter(Boolean).length;

  useEffect(() => {
    registerSelect((key) => {
      if (key === 'overview') { setTab('overview'); return; }
      if (key === 'lenders') { setTab('match'); return; }
      if (key === 'termsheets') { setTab('submissions'); return; }
      if (key === 'closing') { setTab('closing'); return; }
      if (key === 'notes') { setTab('notes'); return; }
      setTab('application'); setAppSection(key);
      setTimeout(()=>{ try{ window.scrollTo({top:0,behavior:'smooth'}); }catch(e){ window.scrollTo(0,0); } },0);
    });
    return () => { registerSelect(null); publishNav({ open: false, sections: [], active: null }); };
  }, []);
  useEffect(() => {
    const navActive = tab === 'overview' ? 'overview' : tab === 'match' ? 'lenders' : tab === 'submissions' ? 'termsheets' : tab === 'closing' ? 'closing' : tab === 'notes' ? 'notes' : appSection;
    const rbAnnualInc = (incRows||[]).reduce((a,r)=>a+(incIsPrev(r)?0:num(r['Annual amount'])*((r['Frequency']||'Annually')==='Monthly'?12:1)),0)+((sreoRows||[]).filter(x=>x['Status']!=='Sold'&&num(x['Rental income'])>0).reduce((a,x)=>a+(num(x['Rental income'])-pmtShareFor(x._id)),0))*12;
    const rbAssets = (assetRows||[]).reduce((a,r)=>a+num(r['Balance']),0);
    const rbEquity = (sreoRows||[]).filter(r=>r['Status']!=='Sold').reduce((a,r)=>a+num(r['Current value'])-(r._id?debtShareFor(r._id):0),0);
    const rbNetWorth = rbAssets + (sreoRows||[]).filter(r=>r['Status']!=='Sold').reduce((a,r)=>a+num(r['Current value']),0) - (liabRows||[]).reduce((a,r)=>a+num(r['Balance']),0);
    const rbLoan = num(form['Loan amount']);
    const rbPct = (raw,calc) => { const v = clean(raw)||calc(); return v?((v+'').replace(/%$/,'')+'%'):'-'; };
    const rbShowLTARV = !!spec.show.arv;
    const rbShowLTC = !!spec.show.rehabBudget || !!spec.show.constructionBudget;
    const rbShowFF = loanType==='Fix & Flip' || loanType==='Ground-Up Construction';
    const rbMetrics = [
      { k: 'Loan', v: rbLoan?fmtK(rbLoan):'-' },
      { k: 'LTV', v: rbPct(form['LTV'],mLTV) },
    ];
    if (isSecond) rbMetrics.push({ k: 'CLTV', v: rbPct(form['CLTV'],mCLTV) });
    if (rbShowLTARV) rbMetrics.push({ k: 'LTARV', v: rbPct(form['LTARV'],mLTARV) });
    if (rbShowLTC) rbMetrics.push({ k: 'LTC', v: rbPct(form['LTC'],mLTC) });
    if (rbShowFF) rbMetrics.push({ k: 'Project cost', v: mTotalCost()?fmtK(mTotalCost()):'-' });
    if (loanType==='Fix & Flip') rbMetrics.push({ k: 'Est. profit', v: mProfit()!==''?fmtK(mProfit()):'-' });
    rbMetrics.push({ k: 'Income (yr)', v: rbAnnualInc?fmtK(rbAnnualInc):'-' });
    rbMetrics.push({ k: 'Equity', v: rbEquity?fmtK(rbEquity):'-' });
    rbMetrics.push({ k: 'Total assets', v: rbAssets?fmtK(rbAssets):'-' });
    rbMetrics.push({ k: 'Net worth', v: rbNetWorth?fmtK(rbNetWorth):'-' });
    if(isMH){
      const nnp=v=>Number(String(v||'').replace(/[^0-9.]/g,''))||0;
      const mhRate=nnp(form['Requested rate']),mhTerm=nnp(form['Term months']),mhLoan=nnp(form['Loan amount']),mhR=mhRate/100/12;
      const mhPI=(mhLoan&&mhRate&&mhTerm)?(mhLoan*mhR/(1-Math.pow(1+mhR,-mhTerm))):0;
      const mhTax=nnp(form['Property taxes'])*((form['Property taxes frequency']||'Monthly')==='Annually'?1/12:1);
      const mhIns=nnp(form['Insurance'])*((form['Insurance frequency']||'Monthly')==='Annually'?1/12:1);
      const mhRent=nnp(form['MH monthly fee']);
      const mhHoa=nnp(form['HOA dues'])*(form['HOA dues frequency']==='Annually'?1/12:form['HOA dues frequency']==='Quarterly'?1/3:1);
      const mhPITI=mhPI?(mhPI+mhTax+mhIns+mhRent+mhHoa+nnp(form['Mortgage insurance'])+nnp(form['Other financing'])):0;
      const mhMoInc=rbAnnualInc/12;
      const mhReoHousing=reoHousingDti();
      const mhOtherDebt=liabDtiTotal();
      const mhMoDebt=mhReoHousing+mhOtherDebt;
      const mhFront=(mhMoInc&&mhPITI)?Math.round((mhPITI+mhReoHousing)/mhMoInc*10000)/100:null;
      const mhBack=(mhMoInc&&mhPITI)?Math.round((mhPITI+mhMoDebt)/mhMoInc*10000)/100:null;
      rbMetrics.length=0;
      rbMetrics.push({k:'Front DTI',v:mhFront!=null?mhFront+'%':'-'});
      rbMetrics.push({k:'Back DTI',v:mhBack!=null?mhBack+'%':'-'});
      rbMetrics.push({k:'LTV',v:rbPct(form['LTV'],mLTV)});
      rbMetrics.push({k:'CLTV',v:rbPct(form['CLTV'],mCLTV)});
      rbMetrics.push({k:'Loan',v:rbLoan?fmtK(rbLoan):'-'});
      rbMetrics.push({k:'PITI',v:mhPITI?fmtX(mhPITI):'-'});
      rbMetrics.push({k:'Monthly income',v:mhMoInc?fmtX(mhMoInc):'-'});
      rbMetrics.push({k:'Monthly debt',v:mhMoDebt?fmtX(mhMoDebt):'-'});
    }
    publishNav({ open: true, active: navActive, metrics: rbMetrics, qualifies: loanType||'', context: { label: 'Viewing deal', name: clean(form['Property address']) || deal.name || 'New deal', sub: [clean(form['Loan type']), [clean(bForm?.['First name']), clean(bForm?.['Last name'])].filter(Boolean).join(' ')].filter(Boolean).join(' \u00B7 ') }, sections: [
      { type: 'group', label: 'Application' },
      { key: 'deal', label: 'Loan / Property', done: appDone.loan && appDone.property },
      { key: 'borrower', label: 'Borrower / sponsor', done: appDone.borrower && appDone.entity },
      { key: 'income', label: 'Income & Employment', done: appDone.income },
      { key: 'assets', label: 'Assets', done: appDone.assets },
      { key: 'liabilities', label: 'Liabilities', done: appDone.liabilities },
      { key: 'reo', label: 'Real estate owned', done: appDone.sreo },
      { key: 'documents', label: 'Documents needed', done: appDone.documents },
      { type: 'group', label: 'Placement', divider: true },
      { key: 'lenders', label: 'Lender match' },
      { key: 'termsheets', label: 'Term sheets' },
      { key: 'closing', label: 'Closing' },
      { type: 'group', label: 'Communications', divider: true },
      { key: 'contacts', label: 'Parties' },
      { key: 'notes', label: 'Notes and correspondence' },
    ] });
  }, [appSection, appDoneCount, liveDealId, tab, form, bForm, incRows, assetRows, sreoRows, liabRows]); // eslint-disable-line
  const totalAllAssets  = assetRows.reduce((s,r)=>s+num(r['Balance']),0);
  const liabLinksFor = (pid) => pid ? (liabRows||[]).filter(l=>Array.isArray(l['Linked property'])&&l['Linked property'].includes(pid)) : [];
  const debtShareFor = (pid) => liabLinksFor(pid).reduce((a,l)=>a+num(l['Balance'])/Math.max((l['Linked property']||[]).length,1),0);
  const pmtShareFor = (pid) => liabLinksFor(pid).reduce((a,l)=>a+num(l['Monthly payment'])/Math.max((l['Linked property']||[]).length,1),0);
  // ONE definition of what counts in DTI, shared by the ribbon, the save and
  // the two section footers. The sections were already right; the ribbon was
  // re-summing raw payments and counting a property-linked mortgage twice
  // while leaving it out of the housing ratio entirely.
  const reoDtiFor = (r) => { const mtg=r._id?pmtShareFor(r._id):0; const exp=num(r['Insurance'])+num(r['Property taxes'])+num(r['HOA dues'])+num(r['Other monthly expenses']); const rent=r['Apply rental income to DTI']?num(r['Rental income'])*0.75:0; return mtg+exp-rent; };
  const reoHousingDti = () => (sreoRows||[]).filter(r=>r['Status']!=='Sold').reduce((a,r)=>a+reoDtiFor(r),0);
  const liabCountsInDti = (r) => !r['Will be paid off']&&!r['Omitted']&&!r['Exclude from DTI']&&!(Array.isArray(r['Linked property'])&&r['Linked property'].length>0);
  const liabDtiTotal = () => (liabRows||[]).filter(liabCountsInDti).reduce((a,r)=>a+num(r['Monthly payment']),0);
  const finBId = borrowerContact?.id || null;
  const finCId = (coContacts[0] && coContacts[0].id) || null;
  const nameOfRec = (rec,fallback) => clean(rec?.fields?.Name) || [clean(rec?.fields?.['First name']),clean(rec?.fields?.['Last name'])].filter(Boolean).join(' ') || fallback;
  const finBFull = nameOfRec(borrowerContact,'Borrower 1');
  const finBFirst = (finBFull.split(' ')[0])||'Borrower';
  const finCFull = coContacts[0] ? nameOfRec(coContacts[0],'Borrower 2') : 'Borrower 2';
  const finCFirst = (finCFull.split(' ')[0])||'Co-borrower';
  const borrowerList = [{id:finBId,name:finBFull}].concat(coContacts.map((c,ci)=>({id:c&&c.id,name:nameOfRec(c,'Borrower '+(ci+2))}))).filter(x=>x.id);
  const rowOwners = (row) => { const c = Array.isArray(row['Contact'])?row['Contact']:[]; return borrowerList.filter(b=>c.includes(b.id)); };
  const ownerKeyOf = (row) => { const o=rowOwners(row); if(o.length>1) return 'both'; if(o.length===1&&o[0].id!==finBId) return 'co'; return 'b'; };
  const ownerNameOf = (row) => { const o=rowOwners(row); if(!o.length) return 'Not assigned'; if(o.length===borrowerList.length&&o.length>1) return 'All'; return o.map(x=>x.name).join(' + '); };
  const ownerDotEl = (row) => { if(borrowerList.length<2) return null; const o=rowOwners(row); const base={width:'20px',height:'20px',borderRadius:'50%',fontSize:'10px',fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,margin:'0 2px 0 14px'}; if(o.length>1) return <span style={{...base,border:'1.5px solid #154D97',color:'#154D97',background:'#fff'}} title={o.map(x=>x.name).join(', ')}>+</span>; const one=o[0]||borrowerList[0]; return <span style={{...base,background:one.id===finBId?'#154D97':'#4A7FBF',color:'#fff'}} title={one.name}>{(one.name[0]||'B').toUpperCase()}</span>; };
  const ownerSelEl = (row,upd) => {
    if(!borrowerList.length) return null;
    const c = Array.isArray(row['Contact'])?row['Contact']:[];
    const toggle=(id)=>{ const has=c.includes(id); const next=has?c.filter(x=>x!==id):[...c,id]; upd('Contact', next); };
    return (<div>
      <div style={s.appLbl}>Whose</div>
      <div style={{display:'flex',gap:'16px',flexWrap:'wrap',paddingTop:'12px'}}>
        {borrowerList.map(b=>(<label key={b.id} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'7px',fontSize:'12.5px',color:'#33415A',cursor:'pointer'}}><input type="checkbox" checked={c.includes(b.id)} onChange={()=>toggle(b.id)} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/>{b.name}</label>))}
      </div>
    </div>);
  };
  const sreoOwnedValue = sreoRows.filter(r=>r['Status']!=='Sold').reduce((s,r)=>s+num(r['Current value']),0);
  const totalLiabilities = liabRows.reduce((s,r)=>s+num(r['Balance']),0);
  const totalSREOEquity = sreoRows.filter(r=>r['Status']!=='Sold').reduce((s,r)=>s+num(r['Current value'])-debtShareFor(r._id),0);
  const netWorth = totalAllAssets + sreoOwnedValue - totalLiabilities;

  async function generateSummary() {
    setGenSummary(true); setGenSummaryErr('');
    try {
      const res = await authFetch('/.netlify/functions/generate-deal-summary',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          dealId:liveDealId,
          context:{ assetsTotal:totalAllAssets, sreoEquity:totalSREOEquity, propertyCount:sreoRows.length },
        }),
      });
      const data=await res.json();
      if(!res.ok||data.error) throw new Error(data.error||'Failed to generate summary');
      if(data.summary) setForm(p=>({...p,'Deal summary':data.summary}));
    } catch(e){
      setGenSummaryErr('Generate failed: '+e.message);
    } finally {
      setGenSummary(false);
    }
  }

  function renderSpecField(key) {
    if(!spec.show[key]) return null;
    const airtableField = FIELD_TO_AIRTABLE[key];
    const label = spec.labels[key] || DEFAULT_LABELS[key];
    const isReq = !!spec.required[key];
    const fieldType = FIELD_TYPES[key];
    const isCurrency = fieldType === 'currency';
    const isNumber = fieldType === 'percent';
    const finding = dealFindings.find(d=>CHECKKEY_TO_AIRTABLE[d.checkKey]===airtableField);
    return (
      <EF key={key} label={label} fieldKey={airtableField} form={form} setForm={setForm}
        isCurrency={isCurrency} isNumber={isNumber} required={isReq} options={SPEC_FIELD_OPTIONS[key]}
        below={finding && <InlineFinding item={finding} busy={discBusyId===finding.id}
          onUse={()=>resolveDisc(finding,'use_verified')}
          onKeep={()=>resolveDisc(finding,'keep_stated','Reviewed.')}/>}/>
    );
  }

  function DealMetricsBar(){
    const showLTARV = !!spec.show.arv;
    const showLTC = !!spec.show.rehabBudget || !!spec.show.constructionBudget;
    const pct = x => (x===''||x==null||x==='-')?'-':x+'%';
    if(loanType==='SBA 504'){
      const n=v=>Number(String(v||'').replace(/[^0-9.]/g,''))||0;
      const proj=n(form['Purchase price'])+n(form['Rehab budget'])+n(form['Construction budget'])+n(form['Other project costs']);
      const bank=n(form['Loan amount']), cdc=n(form['CDC portion']), inj=n(form['Down payment']);
      const injPct=proj?Math.round(inj/proj*1000)/10:0;
      const rB=n(form['Rate (%)'])||n(form['Requested rate']), rC=n(form['CDC rate']);
      const blended=(bank&&cdc&&rB&&rC)?Math.round((bank*rB+cdc*rC)/(bank+cdc)*100)/100:null;
      const sbaTiles=[
        ['Project cost', proj?fmtK(proj):'-'],
        ['Bank 1st', bank?fmtK(bank):'-'],
        ['CDC / SBA 2nd', cdc?fmtK(cdc):'-'],
        ['Injection', inj?fmtK(inj)+(injPct?` (${injPct}%)`:''):'-'],
        ['Blended rate', blended!=null?blended+'%':'-'],
      ];
      return (
        <div style={s.mbBar}>
          {sbaTiles.map(([k,val],i)=>(
            <React.Fragment key={k}>
              {i>0&&<div style={s.mbDiv}/>}
              <div style={s.mbTile}><div style={s.mbK}>{k}</div><div style={s.mbV}>{val}</div></div>
            </React.Fragment>
          ))}
          {(()=>{const occ=form['Occupancy status'];const op=Number(String(form['Occupancy %']||'').replace(/[^0-9.]/g,''))||0;const bad=occ==='Tenant-occupied'||(occ==='Owner + tenant'&&op>0&&op<51);return bad?<div style={{...s.mbElig,color:'#8a5a12'}}><i className="ti ti-alert-triangle" style={{fontSize:'12px'}}/> Occupancy below SBA minimum</div>:<div style={s.mbElig}><i className="ti ti-check" style={{fontSize:'12px'}}/> Qualifies: SBA 504</div>;})()}
        </div>
      );
    }
    const tiles = [['Loan', form['Loan amount']?fmtK(form['Loan amount']):'-'],['LTV', pct(form['LTV']||mLTV())]];
    if(showLTARV) tiles.push(['LTARV', pct(form['LTARV']||mLTARV())]);
    if(showLTC) tiles.push(['LTC', pct(form['LTC']||mLTC())]);
    return (
      <div style={s.mbBar}>
        {tiles.map(([k,val],i)=>(
          <React.Fragment key={k}>
            {i===1&&<div style={s.mbDiv}/>}
            <div style={s.mbTile}><div style={s.mbK}>{k}</div><div style={s.mbV}>{val}</div></div>
          </React.Fragment>
        ))}
        {loanType&&<div style={s.mbElig}><i className="ti ti-check" style={{fontSize:'12px'}}/> Qualifies: {loanType}</div>}
      </div>
    );
  }


  function BorrowerMetricStrip(){
    const fico = (bForm&&bForm['FICO score'])?bForm['FICO score']:'-';
    const proj = (()=>{const n=(sreoRows||[]).filter(x=>x['Status']==='Sold').length;return n?n:'-';})();
    const items = [
      ['Total assets', totalAllAssets?fmtK(totalAllAssets):'-', false],
      ['SREO equity', totalSREOEquity?fmtK(totalSREOEquity):'-', true],
      ['FICO', fico, false],
      ['Completed', proj, false],
    ];
    return (
      <div style={s.stripWrap}>
        {items.map(([k,val,auto])=>(
          <div key={k} style={s.stripTile}>
            <div style={s.stripK}>{k} {auto&&<span style={s.stripAuto}>AUTO</span>}</div>
            <div style={s.stripV}>{val}</div>
          </div>
        ))}
      </div>
    );
  }

  function MetricsGrid() {
    const showLTARV = !!spec.show.arv;
    const showLTC = !!spec.show.rehabBudget || !!spec.show.constructionBudget;
    const showFF = loanType==='Fix & Flip' || loanType==='Ground-Up Construction';
    const showProfit = loanType==='Fix & Flip';
    const showIncome = INCOME_LOAN_TYPES.includes(loanType);
    const showCap = NOI_LOAN_TYPES.includes(loanType);

    return(
      <div style={s.metricTileRow}>
        <div style={s.metricTile}>
          <div style={s.metricTileLabel}>LTV <span style={s.metricTileAuto}>AUTO</span></div>
          <input style={s.metricInput} value={form['LTV']||''} onChange={e=>setForm(p=>({...p,'LTV':e.target.value}))} placeholder={mLTV()||'-'}/>
        </div>
        {isSecond&&<div style={s.metricTile}>
          <div style={s.metricTileLabel}>CLTV <span style={s.metricTileAuto}>AUTO</span></div>
          <input style={s.metricInput} value={form['CLTV']||''} onChange={e=>setForm(p=>({...p,'CLTV':e.target.value}))} placeholder={mCLTV()||'-'}/>
        </div>}
        {showLTARV&&<div style={s.metricTile}>
          <div style={s.metricTileLabel}>LTARV <span style={s.metricTileAuto}>AUTO</span></div>
          <input style={s.metricInput} value={form['LTARV']||''} onChange={e=>setForm(p=>({...p,'LTARV':e.target.value}))} placeholder={mLTARV()||'-'}/>
        </div>}
        {showLTC&&<div style={s.metricTile}>
          <div style={s.metricTileLabel}>LTC <span style={s.metricTileAuto}>AUTO</span></div>
          <input style={s.metricInput} value={form['LTC']||''} onChange={e=>setForm(p=>({...p,'LTC':e.target.value}))} placeholder={mLTC()||'-'}/>
        </div>}
        {showFF&&<div style={s.metricTile}>
          <div style={s.metricTileLabel}>Total project cost <span style={s.metricTileAuto}>AUTO</span></div>
          <input style={s.metricInput} value={form['Total project cost']||''} onChange={e=>setForm(p=>({...p,'Total project cost':e.target.value}))} placeholder={mTotalCost()?fmt(mTotalCost()):'-'}/>
        </div>}
        {showProfit&&<div style={s.metricTile}>
          <div style={s.metricTileLabel}>Est. profit at ARV <span style={s.metricTileAuto}>AUTO</span></div>
          <input style={s.metricInput} value={form['Est. profit at ARV']||''} onChange={e=>setForm(p=>({...p,'Est. profit at ARV':e.target.value}))} placeholder={mProfit()?fmt(mProfit()):'-'}/>
        </div>}
        {showIncome&&<>
          <div style={s.metricTile}>
            <div style={s.metricTileLabel}>Annual rent <span style={s.metricTileAuto}>AUTO</span></div>
            <input style={s.metricInput} value={form['Annual rent']||''} onChange={e=>setForm(p=>({...p,'Annual rent':e.target.value}))} placeholder={mRent()?fmt(mRent()):'-'}/>
          </div>
          {NOI_LOAN_TYPES.includes(loanType)&&<div style={s.metricTile}>
            <div style={s.metricTileLabel}>Annual NOI <span style={s.metricTileAuto}>AUTO</span></div>
            <input style={s.metricInput} value={form['Annual NOI']||''} onChange={e=>setForm(p=>({...p,'Annual NOI':e.target.value}))} placeholder={mNOI()?fmt(mNOI()):'-'}/>
          </div>}
          <div style={s.metricTile}>
            <div style={s.metricTileLabel}>Est. DSCR <span style={s.metricTileAuto}>AUTO</span></div>
            <input style={s.metricInput} value={form['Est. DSCR']||''} onChange={e=>setForm(p=>({...p,'Est. DSCR':e.target.value}))} placeholder={mDSCR()||'-'}/>
          </div>
        </>}
        {showCap&&<div style={s.metricTile}>
          <div style={s.metricTileLabel}>Cap rate <span style={s.metricTileAuto}>AUTO</span></div>
          <input style={s.metricInput} value={form['Cap rate']||''} onChange={e=>setForm(p=>({...p,'Cap rate':e.target.value}))} placeholder={mCapRate()||'-'}/>
        </div>}
      </div>
    );
  }

  const getSectionLabel = () => {
    if(propType==='2-4 Units') return '2-4 UNITS: PROPERTY DETAILS';
    if(propType==='Multifamily 5+') return '5+ UNITS: PROPERTY DETAILS';
    if(propType==='Mixed-Use') return 'MIXED-USE: PROPERTY DETAILS';
    if(propType==='Manufactured / Mobile') return 'MANUFACTURED / MOBILE: PROPERTY DETAILS';
    return `${propType.toUpperCase()}: PROPERTY DETAILS`;
  };

  const docCount = Array.isArray(f['Documents']) ? f['Documents'].length : null;

  const openDisc = discItems.filter(d=>d.status==='Open');
  const dealFindings = openDisc.filter(d=>['purchase','asis','arv'].includes(d.checkKey));
  const borrowerFindings = openDisc.filter(d=>['fico','liquid_stated'].includes(d.checkKey));
  const ficoFinding = borrowerFindings.find(d=>d.checkKey==='fico');

  const dealCriteria = {
    state: (form['Property state']||'').toUpperCase(),
    loanType, loanPurpose, lienPos, isSecond,
    loanAmount: num(form['Loan amount']),
    propertyType: form['Property type']||'',
    assetClasses: Array.isArray(form['Asset class'])?form['Asset class']:[],
    situations: (Array.isArray(form['Deal situations'])?form['Deal situations']:[]).filter(x=>x!=='Wholesale'),
    wholesale: (Array.isArray(form['Deal situations'])?form['Deal situations']:[]).includes('Wholesale'),
    ownerOcc: form['Occupancy status']==='Owner-Occupied',
    borrowerIsIndividual: !!(bForm && bForm['Entity type']==='Individual'),
    recoursePref: form['Recourse preference']||'',
    appraisalPref: form['Appraisal preference']||'',
    fico: num(bForm&&bForm['FICO score']),
    ltv: parseFloat(form['LTV']||mLTV())||0,
    cltv: parseFloat(form['CLTV']||mCLTV())||0,
    ltarv: parseFloat(form['LTARV']||mLTARV())||0,
    ltc: parseFloat(form['LTC']||mLTC())||0,
    dscr: parseFloat(form['Est. DSCR']||mDSCR())||0,
  };
  const matchResult = (tab==='match' && matchList) ? matchLenders(dealCriteria, matchList) : null;
  function toggleMatchSel(id){ setMatchMsg(''); setMatchSel(p=>({...p,[id]:!p[id]})); }
  function isSel(id,def){ return (id in matchSel) ? matchSel[id] : def; }
  let selectedCount = 0;
  const selectedLenders = [];
  if(matchResult){
    const collectLender = (o)=>{ const L=o.lender; const sub=L.submissionEmail||''; const ae=L.aeEmail||''; const lend=L.lenderEmail||''; selectedLenders.push({ id:L.id, name:L.name, aeName:L.aeName||'', email:sub||ae||lend||'', submissionEmail:sub, aeEmail:ae, lenderEmail:lend }); };
    matchResult.potential.forEach(o=>{ if(isSel(o.lender.id,true)){ selectedCount++; collectLender(o); } });
    matchResult.maybe.forEach(o=>{ if(isSel(o.lender.id,true)){ selectedCount++; collectLender(o); } });
    matchResult.ruledout.forEach(o=>{ if(isSel(o.lender.id,false)){ selectedCount++; collectLender(o); } });
  }

  return (
    <div style={{minWidth:0,maxWidth:'100%',overflowX:'hidden'}}>
      <div style={s.chrome}>
      <button style={s.backBtn} onClick={onBack}><i className="ti ti-arrow-left"/> Back to deals</button>

      <div style={s.dealHeadCard}>
        <div style={{padding:'11px 16px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
            <div style={{minWidth:0}}>
              <div style={s.dealTitle}>{buildDealName(ovBorrowerName,form['Property address'])||clean(form['Deal name'])||'Unnamed deal'}</div>
              <div style={s.dealSub}>{clean([loanType,loanPurpose].filter(Boolean).join(' · '))||'Deal'}</div>
            </div>
            <div style={{flex:1}}/>
            <select style={s.stageSelect} value={stage} onChange={e=>saveStage(e.target.value)}>{STAGES.map(st=><option key={st}>{st}</option>)}</select>
            <span style={{fontSize:'11px',fontWeight:600,color:'#3A6BA8',background:'#EAF1FA',padding:'4px 10px',borderRadius:'13px',whiteSpace:'nowrap'}}>{appDoneCount} of 10 done</span>
            {appDoneCount<10&&<span style={{fontSize:'11px',fontWeight:600,color:'#8a5a12',background:'#FBF0DA',padding:'4px 10px',borderRadius:'13px',whiteSpace:'nowrap'}}>{10-appDoneCount} gaps</span>}
            <button style={{border:'1px solid #C9D9EE',color:'#154D97',background:clean(form['Deal summary'])?'#EAF1FA':'#fff',borderRadius:'8px',padding:'6px 12px',fontSize:'12px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}} onClick={()=>setNarOpen(o=>!o)}>Narrative</button>
            <button style={s.saveBtnD(dirty)} onClick={saveAll} disabled={!dirty||saving}>{saving?'Saving':saved&&!dirty?'Saved':'Save'}</button>
            {saveError&&<span style={s.errorMsg}>{saveError}</span>}
          </div>
          
          {narOpen&&<div style={{marginTop:'10px',border:'1px solid #C9D9EE',borderRadius:'11px',padding:'14px 16px'}}>
            <textarea style={{width:'100%',boxSizing:'border-box',border:'1px solid #C9D9EE',borderRadius:'8px',padding:'9px 11px',fontSize:'12.5px',lineHeight:1.65,color:'#33415A',fontFamily:'inherit',minHeight:'110px',resize:'vertical',outline:'none'}} value={form['Deal summary']||''} onChange={e=>setForm(p=>({...p,'Deal summary':e.target.value}))} placeholder="The written summary that leads your lender submissions. Generate it or write your own."/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
              <button style={{border:'1px solid #C9D7EA',background:'#fff',borderRadius:'8px',padding:'6px 13px',fontSize:'12px',fontWeight:600,color:'#154D97',cursor:genSummary?'wait':'pointer',fontFamily:'inherit',opacity:genSummary?0.6:1}} onClick={generateSummary} disabled={genSummary}>{genSummary?'Generating...':clean(form['Deal summary'])?'Regenerate':'Generate'}</button>
            </div>
            {genSummaryErr&&<div style={{fontSize:'12px',color:'#a02020',marginTop:'8px'}}>{genSummaryErr}</div>}
          </div>}
          <div style={{height:'3px',background:'#eef2f8',borderRadius:'2px',overflow:'hidden',marginTop:'10px'}}><div style={{height:'100%',width:(appDoneCount/10*100)+'%',background:'#4A7FBF'}}/></div>
          {/* LEGACY TAB BAR REMOVED 8/14. The Deal and Borrower buttons opened the
              pre-rebuild page, whose co-borrower block writes 'Co-borrower name',
              a column that does not exist on Deals, which failed the whole save.
              The two blocks below are gated off, not deleted. */}
        </div>
      </div>

      </div>

      {tab==='overview'&&<div style={{marginTop:'14px'}}>
        {(()=>{ const ovLoan=num(form['Loan amount']); const ovArv=num(form['ARV']); const ovLtc=form['LTC']||mLTC(); return (
        <div style={s.ovTiles}>
          <div style={s.ovTile}><div style={s.ovTileK}>Loan</div>{ovLoan?<div style={s.ovTileV(false)}>{fmtK(ovLoan)}</div>:<div style={s.ovMiss}>Needs you</div>}</div>
          <div style={s.ovTile}><div style={s.ovTileK}>ARV</div>{ovArv?<div style={s.ovTileV(false)}>{fmtK(ovArv)}</div>:<div style={s.ovMiss}>Needs you</div>}</div>
          <div style={s.ovTile}><div style={s.ovTileK}>LTC</div>{ovLtc?<div style={s.ovTileV(false)}>{ovLtc}%</div>:<div style={s.ovMiss}>Needs you</div>}</div>
        </div>
        ); })()}
        <div style={s.ovCard}>
          <div style={s.ovLbl}>The deal</div>
          <div style={s.ovGrid}>
            {[
              ['Purchase price', form['Purchase price']?fmtK(num(form['Purchase price'])):null],
              ['Rehab budget', form['Rehab budget']?fmtK(num(form['Rehab budget'])):null],
              ['Exit', clean(form['Exit strategy'])||null],
              ['Target close', form['Target close date']||null],
            ].map(([k,v])=>(
              <div key={k}><div style={s.ovK}>{k}</div>{v?<div style={s.ovV(false)}>{v}</div>:<div style={s.ovMiss}>Needs you</div>}</div>
            ))}
          </div>
        </div>
        <div style={s.ovCard}>
          <div style={s.ovLbl}>Borrower</div>
          <div style={s.ovName}>{clean(ovBorrowerName)||'Borrower not linked'}</div>
          <div style={s.ovLine}>
            {[bForm?.['Company / entity']||bForm?.['Entity type'], (()=>{const n=(sreoRows||[]).filter(x=>x['Status']==='Sold').length;return n?n+' completed projects':'';})()].filter(Boolean).join(' · ')}
            {(bForm?.['Email']||bForm?.['Phone'])&&<br/>}
            {[bForm?.['Email'], bForm?.['Phone']?formatPhone(bForm['Phone']):''].filter(Boolean).join(' · ')}
          </div>
          {!bForm?.['FICO score']&&<div style={{marginTop:'11px'}}><span style={s.ovFlag}><span style={s.ovDot}/>FICO not found</span></div>}
        </div>
      </div>}

      {tab==='application'&&<div style={{marginTop:'14px',minWidth:0,maxWidth:'100%',overflowX:'hidden'}}>

        <div style={{...s.ovCard,display:appSection==='borrower'?undefined:'none'}}>
          <div style={s.ovLbl}>Borrower / sponsor</div>
          {(()=>{const bn=[clean(bForm?.['First name']),clean(bForm?.['Last name'])].filter(Boolean).join(' ')||'Borrower 1';const cn=[clean(coForm?.['First name']),clean(coForm?.['Last name'])].filter(Boolean).join(' ')||('Borrower '+(activeB+1));const total=1+coForms.length;const pill=(on)=>({background:on?'#154D97':'#fff',color:on?'#fff':'#3A6BA8',border:on?'1px solid #154D97':'1px solid #C5D5EA',borderRadius:'20px',padding:'8px 20px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'});return(
          <div style={{display:'flex',alignItems:'center',gap:'9px',flexWrap:'wrap',marginBottom:'14px'}}>
            <button style={pill(activeB===0)} onClick={()=>setActiveB(0)}>{bn}</button>
            {coForms.map((cfm,ci)=>(<button key={ci} style={pill(activeB===ci+1)} onClick={()=>setActiveB(ci+1)}>{[clean(cfm?.['First name']),clean(cfm?.['Last name'])].filter(Boolean).join(' ')||('Borrower '+(ci+2))}</button>))}
            {total<MAX_BORROWERS&&<button style={{background:'#fff',border:'1px dashed #7EA3D7',color:'#154D97',borderRadius:'20px',padding:'8px 18px',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'}} onClick={()=>{setCoForms(p=>[...p,{}]);setCoOpen(false);setActiveB(coForms.length+1);setDirty(true);}}>+ Add borrower</button>}
            {activeB>0&&<button style={{...s.finDel,marginLeft:'6px'}} onClick={()=>{if(coContact){if(!window.confirm('Remove '+cn+' from this deal?\n\nThis also deletes their answers for this deal: declarations, demographics, address history, citizenship, marital status and FICO. It cannot be undone.\n\nTheir contact record is not affected.'))return;unlinkCoBorrower&&unlinkCoBorrower();}else{setCoForms(p=>p.filter((_,x)=>x!==coIdx));}setActiveB(0);setDirty(true);}}>Delete borrower</button>}
          </div>);})()}

          <div style={{border:'1px solid #DCE7F5',borderRadius:'10px',overflow:'hidden',marginBottom:'14px',background:'#fff'}}>
            <div style={{background:'#EAF1FA',padding:'10px 15px',fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#154D97'}}>Personal</div>
            <div style={{padding:'15px 16px'}}>
              <div style={{display:'grid',gridTemplateColumns:'1.4fr 1fr 1.4fr 0.7fr',gap:'14px 18px'}}>
                <AF label="First name" val={xForm?.['First name']} onChange={setX('First name')}/>
                <AF label="Middle" val={xForm?.['Middle name']} onChange={setX('Middle name')}/>
                <AF label="Last name" val={xForm?.['Last name']} onChange={setX('Last name')}/>
                <AF label="Suffix" val={xForm?.['Suffix']} onChange={setX('Suffix')}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1.2fr 1.35fr 1.1fr 1fr',gap:'14px 18px',marginTop:'14px'}}>
                <div>
                  <div style={{...s.appLbl,display:'flex',alignItems:'center',justifyContent:'space-between',gap:'6px'}}><span>SSN / ITIN</span>
                    <MiniChip label={ssnShown[activeB]?'Hide':'Show'} onClick={()=>setSsnShown(p=>({...p,[activeB]:!p[activeB]}))}/>
                  </div>
                  {ssnShown[activeB]
                    ? <AF ssn val={xForm?.['SSN']} onChange={setX('SSN')}/>
                    : <input readOnly value={maskedSsn(xForm?.['SSN'])} onFocus={()=>setSsnShown(p=>({...p,[activeB]:true}))} style={s.appInput}/>}
                </div>
                <div>
                  <div style={{...s.appLbl,display:'flex',alignItems:'center'}}>Date of birth<span style={{marginLeft:'auto',color:'#8A94A6'}}>Age</span></div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 62px',gap:'8px'}}>
                    <AF date val={xForm?.['Date of birth']} onChange={setX('Date of birth')} placeholder="mm/dd/yyyy"/>
                    <input readOnly value={(()=>{const d=xForm?.['Date of birth'];if(!d)return '';const t=Date.parse(d);if(isNaN(t))return '';const b=new Date(t);const n=new Date();let a=n.getFullYear()-b.getFullYear();const m=n.getMonth()-b.getMonth();if(m<0||(m===0&&n.getDate()<b.getDate()))a--;return (a>0&&a<120)?String(a):'';})()} style={{...s.appInput,background:'#F0F5FC',color:'#154D97',fontWeight:700,textAlign:'center'}}/>
                  </div>
                </div>
                <ASel label="Residency type" val={xForm?.['Citizenship']} onChange={setX('Citizenship')} options={['US Citizen','Permanent Resident Alien','Non-Permanent Resident Alien']}/>
                <ASel label="Marital status" val={xForm?.['Marital status']} onChange={setX('Marital status')} options={['Married','Separated','Unmarried']}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1.5fr 1.1fr 1.3fr 1.1fr',gap:'14px 18px',marginTop:'14px'}}>
                <div>
                  <div style={{...s.appLbl,display:'flex',alignItems:'center',justifyContent:'space-between',gap:'6px'}}><span>Email</span>
                    <MiniChip label="Copy" doneLabel="Copied" onClick={()=>copyText(clean(xForm?.['Email']))}/>
                  </div>
                  <AF val={xForm?.['Email']} onChange={setX('Email')}/>
                </div>
                <div>
                  <div style={{...s.appLbl,display:'flex',alignItems:'center',justifyContent:'space-between',gap:'6px'}}><span>Cell phone</span>
                    <MiniChip label="Copy" doneLabel="Copied" onClick={()=>copyText(clean(xForm?.['Phone']))}/>
                  </div>
                  <AF phone val={xForm?.['Phone']} onChange={setX('Phone')}/>
                </div>
                <div>
                  <div style={{...s.appLbl,display:'flex',alignItems:'center'}}>Work phone<span style={{marginLeft:'auto',color:'#8A94A6'}}>Ext</span></div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 62px',gap:'8px'}}>
                    <AF phone val={xForm?.['Work phone']} onChange={setX('Work phone')}/>
                    <AF val={xForm?.['Work phone ext']} onChange={setX('Work phone ext')}/>
                  </div>
                </div>
                <AF phone label="Home phone" val={xForm?.['Home phone']} onChange={setX('Home phone')}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'0.9fr 1.3fr 1.1fr 1fr',gap:'14px 18px',marginTop:'14px'}}>
                <AF label="No. of dependents" val={xForm?.['Dependents count']} onChange={setX('Dependents count')}/>
                <AF label="Dependents ages" placeholder="Separate by commas" val={xForm?.['Dependent ages']} onChange={setX('Dependent ages')}/>
                <ASel label="Estimated credit score" val={xForm?.['Est credit score']} onChange={setX('Est credit score')} options={['760+','740-759','720-739','700-719','680-699','660-679','640-659','620-639','600-619','Below 600']}/>
                <AF label="Actual FICO (from pull)" val={xForm?.['FICO score']} onChange={setX('FICO score')}/>
              </div>
              <div style={{display:'flex',gap:'30px',marginTop:'16px',flexWrap:'wrap'}}>
                <label onClick={()=>setXField('eConsent authorized',!xForm?.['eConsent authorized'])} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer'}}><input type="checkbox" readOnly checked={!!xForm?.['eConsent authorized']} style={{width:'14px',height:'14px',accentColor:'#154D97',cursor:'pointer'}}/>eConsent authorized</label>
                <label onClick={()=>setXField('Credit pull authorized',!xForm?.['Credit pull authorized'])} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer'}}><input type="checkbox" readOnly checked={!!xForm?.['Credit pull authorized']} style={{width:'14px',height:'14px',accentColor:'#154D97',cursor:'pointer'}}/>Credit pull authorized</label>
              </div>
            </div>
          </div>

          <div style={{border:'1px solid #DCE7F5',borderRadius:'10px',overflow:'hidden',marginBottom:'14px',background:'#fff'}}>
            <div style={{background:'#EAF1FA',padding:'10px 15px',fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#154D97'}}>Address history</div>
            <div style={{padding:'13px 16px 0'}}>
              <label onClick={()=>{const on=!xForm?.['Same as subject property'];setXField('Same as subject property',on);if(on){setXField('Street address',form['Property address']);setXField('Current unit',form['Property space / unit']);setXField('City',form['Property city']);setXField('Home state',[form['Property state']].filter(Boolean));setXField('ZIP',form['Property ZIP']);setXField('County',form['Property county']);}}} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'9px',fontSize:'13px',color:'#33415A',cursor:'pointer'}}>
                <span style={{width:'34px',height:'19px',borderRadius:'11px',background:xForm?.['Same as subject property']?'#154D97':'#C9D9EE',position:'relative',flexShrink:0,transition:'background .15s'}}>
                  <span style={{position:'absolute',top:'2px',left:xForm?.['Same as subject property']?'17px':'2px',width:'15px',height:'15px',borderRadius:'50%',background:'#fff',transition:'left .15s'}}></span>
                </span>
                Same as the subject property
              </label>
            </div>
            {bActive&&<div style={{padding:'11px 16px 0'}}><label style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer'}}><input type="checkbox" checked={!!xForm?.['Same address as borrower 1']} onChange={e=>{const on=e.target.checked;setXField('Same address as borrower 1',on);if(on){['Street address','Current unit','City','Home state','ZIP','Own or rent','Monthly rent','Years at current address','Months at current address','Mailing address different','Mailing street','Mailing city','Mailing state','Mailing zip','Previous addresses'].forEach(k=>setXField(k,bForm?.[k]));}}} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/>Same address history as Borrower 1</label></div>}
            <div style={{padding:'15px 16px'}}>
              <div style={{display:'grid',gridTemplateColumns:'3fr 0.7fr',gap:'14px 18px'}}>
                <AF label="Current street" readOnly={!!xForm?.['Same as subject property']} val={xForm?.['Street address']} onChange={setX('Street address')}/>
                <AF label="Unit" readOnly={!!xForm?.['Same as subject property']} val={xForm?.['Current unit']} onChange={setX('Current unit')}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1.3fr 0.85fr 0.85fr 1fr',gap:'14px 18px',marginTop:'14px'}}>
                <AF label="City" readOnly={!!xForm?.['Same as subject property']} val={xForm?.['City']} onChange={setX('City')}/>
                <ASel label="State" val={Array.isArray(xForm?.['Home state'])?xForm['Home state'][0]:(xForm?.['Home state']||'')} onChange={v=>setXField('Home state',[v].filter(Boolean))} options={STATES}/>
                <AF label="ZIP" val={xForm?.['ZIP']} onChange={v=>{setXField('ZIP',v);if(String(v||'').replace(/\D/g,'').length===5){lookupZip(v).then(r=>{if(!r)return;setXField('City',r.city);setXField('Home state',[r.state]);if(r.county)setXField('County',r.county);});}}}/>
                <AF label="County" readOnly={!!xForm?.['Same as subject property']} val={xForm?.['County']} onChange={setX('County')}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:clean(xForm?.['Own or rent'])==='Rent'?'1.15fr 0.95fr 0.6fr 0.6fr':'1.15fr 0.6fr 0.6fr',gap:'14px 18px',marginTop:'14px'}}>
                <ASel label="Own or rent" val={xForm?.['Own or rent']} onChange={setX('Own or rent')} options={['Own','Rent','No primary housing expense']}/>
                {clean(xForm?.['Own or rent'])==='Rent'&&<AF label="Monthly rent" money val={xForm?.['Monthly rent']} onChange={setX('Monthly rent')}/>}
                <ASel label="Years" val={xForm?.['Years at current address']} onChange={setX('Years at current address')} options={YEARS_OPTS}/>
                <ASel label="Months" val={xForm?.['Months at current address']} onChange={setX('Months at current address')} options={MONTHS_OPTS}/>
              </div>
              <div style={{display:'flex',gap:'26px',marginTop:'14px',flexWrap:'wrap',alignItems:'center'}}>
                {(()=>{const y=parseInt(xForm?.['Years at current address'])||0;const mo=parseInt(xForm?.['Months at current address'])||0;let pv=[];try{pv=JSON.parse(xForm?.['Previous addresses']||'[]');if(!Array.isArray(pv))pv=[];}catch(e){pv=[];}const pmo=pv.reduce((sm,a)=>sm+(parseInt(a.Years)||0)*12+(parseInt(a.Months)||0),0);const tot=y*12+mo+pmo;if(!(y||mo))return null;const met=tot>=24;return <span style={{fontSize:'12px',fontWeight:600,color:met?'#2E7D5B':'#8a5a12'}}>{met?'\u2713 ':''}History total {Math.floor(tot/12)} yr {tot%12} mo{met?': 2-year requirement met':': under 2 years, add a previous address below'}</span>;})()}
              </div>
              {(()=>{
            let prevs=[];try{const raw=xForm?.['Previous addresses'];prevs=raw?JSON.parse(raw):[];if(!Array.isArray(prevs))prevs=[];}catch(e){prevs=[];}
            const savePrevs=a=>setXField('Previous addresses',JSON.stringify(a));
            const setPrev=(i,k)=>v=>{const a=prevs.map((x,j)=>j===i?{...x,[k]:v}:x);savePrevs(a);};
            const addPrev=()=>savePrevs([...prevs,{}]);
            const removePrev=i=>savePrevs(prevs.filter((_,j)=>j!==i));
            const prevKey=(pv,i)=>(pv&&(pv._k||pv.Street))?String(pv._k||pv.Street)+'_'+i:'prev'+i;
            return (<React.Fragment>
              {prevs.map((pv,i)=>(
              <div key={prevKey(pv,i)} style={{border:'1px solid #DCE7F5',borderRadius:'10px',overflow:'hidden',marginBottom:'14px',background:'#fff'}}>
                <div style={{background:'#EAF1FA',padding:'10px 15px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span style={{fontSize:'11px',fontWeight:700,letterSpacing:'.05em',textTransform:'uppercase',color:'#154D97'}}>Previous address {i+1}</span>
                  <span style={{fontSize:'11px',color:'#B23A48',cursor:'pointer'}} onClick={()=>removePrev(i)}>&times; Remove</span>
                </div>
                <div style={{padding:'15px 16px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'3fr 0.7fr',gap:'14px 18px'}}>
                    <AF label="Street" val={pv['Street']} onChange={setPrev(i,'Street')}/>
                    <AF label="Unit" val={pv['Unit']} onChange={setPrev(i,'Unit')}/>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1.3fr 0.85fr 0.85fr 1fr',gap:'14px 18px',marginTop:'14px'}}>
                    <AF label="City" val={pv['City']} onChange={setPrev(i,'City')}/>
                    <AF label="State" val={pv['State']} onChange={setPrev(i,'State')}/>
                    <AF label="ZIP" val={pv['ZIP']} onChange={v=>{setPrev(i,'ZIP')(v);if(String(v||'').replace(/\D/g,'').length===5){lookupZip(v).then(r=>{if(!r)return;savePrevs(prevs.map((x,j)=>j===i?{...x,'ZIP':v,'City':r.city,'State':r.state,'County':r.county||''}:x));});}}}/>
                    <AF label="County" val={pv['County']} onChange={setPrev(i,'County')}/>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1.15fr 0.95fr 0.6fr 0.6fr',gap:'14px 18px',marginTop:'14px'}}>
                    <ASel label="Own or rent" val={pv['Own or rent']} onChange={setPrev(i,'Own or rent')} options={['Own','Rent','No primary housing expense']}/>
                    <AF label="Monthly rent" money val={pv['Monthly rent']} onChange={setPrev(i,'Monthly rent')}/>
                    <ASel label="Years" val={pv['Years']} onChange={setPrev(i,'Years')} options={YEARS_OPTS}/>
                    <ASel label="Months" val={pv['Months']} onChange={setPrev(i,'Months')} options={MONTHS_OPTS}/>
                  </div>
                </div>
              </div>
              ))}
              <div onClick={addPrev} style={{display:'inline-flex',alignItems:'center',gap:'6px',border:'1px dashed #7EA3D7',color:'#154D97',borderRadius:'20px',padding:'8px 16px',fontSize:'12.5px',fontWeight:600,cursor:'pointer',marginBottom:'14px'}}>+ Previous address</div>
            </React.Fragment>);
              })()}
              <label onClick={()=>setXField('Mailing address different',!xForm?.['Mailing address different'])} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer'}}><input type="checkbox" readOnly checked={!!xForm?.['Mailing address different']} style={{width:'14px',height:'14px',accentColor:'#154D97',cursor:'pointer'}}/>Mailing address is different</label>
              {xForm?.['Mailing address different']&&<div style={{marginTop:'14px'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr',gap:'14px 18px'}}>
                <AF label="Mailing street" val={xForm?.['Mailing street']} onChange={setX('Mailing street')}/>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1.3fr 0.85fr 0.85fr 1fr',gap:'14px 18px',marginTop:'14px'}}>
                <AF label="Mailing city" val={xForm?.['Mailing city']} onChange={setX('Mailing city')}/>
                <AF label="Mailing state" val={xForm?.['Mailing state']} onChange={setX('Mailing state')}/>
                <AF label="Mailing zip" val={xForm?.['Mailing zip']} onChange={v=>{setXField('Mailing zip',v);if(String(v||'').replace(/\D/g,'').length===5){lookupZip(v).then(r=>{if(!r)return;setXField('Mailing city',r.city);setXField('Mailing state',r.state);if(r.county)setXField('Mailing county',r.county);});}}}/>
                <AF label="Mailing county" val={xForm?.['Mailing county']} onChange={setX('Mailing county')}/>
                </div>
              </div>}
            </div>
          </div>

          {(()=>{
            const bArr=k=>{const v=xForm?.[k];return Array.isArray(v)?v:(v?[v]:[]);};
            const togArr=(k,opt)=>{const a=bArr(k);setXField(k,a.includes(opt)?a.filter(x=>x!==opt):[...a,opt]);};
            const CB=({on,label,onClick,pad,key})=>(<label key={key} onClick={onClick} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer',padding:pad||'3px 0'}}><input type="checkbox" readOnly checked={!!on} style={{width:'14px',height:'14px',accentColor:'#154D97',cursor:'pointer'}}/>{label}</label>);
            const YN=({k})=>(<select style={{width:'104px',flexShrink:0,border:'1px solid #C9D9EE',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:'#1a2b40',fontFamily:'inherit',outline:'none',background:'#fff',cursor:'pointer'}} value={clean(xForm?.[k])||''} onChange={e=>setXField(k,e.target.value)}><option value=""></option><option>Yes</option><option>No</option></select>);
            const RAD=({k,label})=>(<div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'14px',padding:'7px 0'}}><div style={{fontSize:'13px',color:'#33415A'}}>{label}</div><div style={{display:'flex',gap:'16px',flexShrink:0,fontSize:'13px'}}>{['Yes','No','Leave unmarked'].map(o=>(<label key={o} onClick={()=>setXField(k,clean(xForm?.[k])===o?'':o)} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'6px',cursor:'pointer',color:o==='Leave unmarked'?'#3A6BA8':'#33415A'}}><input type="radio" readOnly checked={clean(xForm?.[k])===o} style={{width:'13px',height:'13px',accentColor:'#154D97',cursor:'pointer'}}/>{o}</label>))}</div></div>);
            return (<React.Fragment>

            <div style={BAND}>
              <div style={{...BANDH,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span>Declarations {'\u00B7'} this borrower</span>
                <span onClick={()=>{const patch={};DECL_ROWS.forEach(([k])=>{if(!clean(xForm?.[k]))patch[k]='No';});Object.keys(patch).forEach(k=>setXField(k,patch[k]));}} style={{fontSize:'10.5px',fontWeight:600,letterSpacing:0,textTransform:'none',color:'#154D97',cursor:'pointer',background:'#fff',border:'1px solid #C9D9EE',borderRadius:'14px',padding:'3px 11px'}}>Set blanks to No</span>
              </div>
              <div style={{padding:'6px 16px 15px'}}>
                {DECL_ROWS.map(([k,label,ind])=>(
                  (k==='Decl A ownership interest 3yr' && clean(xForm?.['Decl A occupy primary'])!=='Yes') ? null : (
                  <React.Fragment key={k}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'14px',padding:'10px 0',borderBottom:'1px solid #EEF3F9'}}>
                      <div style={{fontSize:'13px',color:'#33415A',paddingLeft:ind?'24px':0}}>{label}</div>
                      {YN({k:k})}
                    </div>
                    {k==='Decl A ownership interest 3yr'&&clean(xForm?.[k])==='Yes'&&<div style={{...SUBBOX,marginLeft:'24px'}}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 18px'}}>
                        <ASel label="Property type owned" val={xForm?.['Decl A property type owned']} onChange={setX('Decl A property type owned')} options={DECL_PROP_TYPES}/>
                        <ASel label="How title was held" val={xForm?.['Decl A title held']} onChange={setX('Decl A title held')} options={DECL_TITLE_HELD}/>
                      </div>
                    </div>}
                    {k==='Decl C borrowing undisclosed money'&&clean(xForm?.[k])==='Yes'&&<div style={SUBBOX}>
                      <div style={{maxWidth:'240px'}}><AF label="Amount" money val={xForm?.['Decl C amount']} onChange={setX('Decl C amount')}/></div>
                    </div>}
                    {k==='Decl M bankruptcy 7yr'&&clean(xForm?.[k])==='Yes'&&<div style={SUBBOX}>
                      <div style={{fontSize:'11.5px',color:'#3A6BA8',marginBottom:'7px'}}>Bankruptcy type</div>
                      <div style={{display:'flex',gap:'18px',flexWrap:'wrap'}}>
                        {['Chapter 7','Chapter 11','Chapter 12','Chapter 13'].map(o=>CB({key:o,on:bArr('Decl M bankruptcy type').includes(o),label:o,onClick:()=>togArr('Decl M bankruptcy type',o)}))}
                      </div>
                    </div>}
                  </React.Fragment>)
                ))}
                <div style={{marginTop:'14px'}}>
                  <AF label="Notes on any Yes answers" val={xForm?.['Declarations notes']} onChange={setX('Declarations notes')}/>
                </div>
              </div>
            </div>

            <div style={BAND}>
              <div style={BANDH}>Military service {'\u00B7'} this borrower</div>
              <div style={{padding:'15px 16px',display:'flex',alignItems:'flex-end',gap:'26px',flexWrap:'wrap'}}>
                <div style={{width:'320px'}}><ASel label="Military service" val={xForm?.['Military service']} onChange={setX('Military service')} options={MILITARY_OPTS}/></div>
                <div style={{paddingBottom:'8px'}}>{CB({on:xForm?.['Surviving spouse'],label:"Surviving spouse",onClick:()=>setXField('Surviving spouse',!xForm?.['Surviving spouse'])})}</div>
                <div style={{width:'220px'}}><AF date label="Projected expiration of service" val={xForm?.['Projected expiration of service']} onChange={setX('Projected expiration of service')} placeholder="mm/dd/yyyy"/></div>
              </div>
            </div>

            <div style={BAND}>
              <div style={BANDH}>HMDA demographics {'\u00B7'} this borrower</div>
              <div style={{padding:'15px 16px'}}>
                <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'.05em',color:'#154D97',marginBottom:'8px'}}>ETHNICITY {'\u00B7'} CHECK ALL THAT APPLY</div>
                {CB({on:bArr('Ethnicity').includes('Hispanic or Latino'),label:"Hispanic or Latino",onClick:()=>togArr('Ethnicity','Hispanic or Latino')})}
                {bArr('Ethnicity').includes('Hispanic or Latino')&&<div style={SUBBOX}>
                  <div style={{display:'flex',gap:'18px',flexWrap:'wrap'}}>{ETH_SUB.map(o=>CB({key:o,on:bArr('Hispanic origin').includes(o),label:o,onClick:()=>togArr('Hispanic origin',o)}))}</div>
                  <div style={{maxWidth:'260px',marginTop:'9px'}}><AF label="Origin" val={xForm?.['Hispanic origin other']} onChange={setX('Hispanic origin other')} placeholder="Enter origin"/></div>
                </div>}
                {CB({on:bArr('Ethnicity').includes('Not Hispanic or Latino'),label:"Not Hispanic or Latino",onClick:()=>togArr('Ethnicity','Not Hispanic or Latino')})}
                {CB({on:bArr('Ethnicity').includes('I do not wish to provide'),label:"I do not wish to provide",onClick:()=>togArr('Ethnicity','I do not wish to provide')})}

                <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'.05em',color:'#154D97',margin:'18px 0 8px'}}>RACE {'\u00B7'} CHECK ALL THAT APPLY</div>
                {CB({on:bArr('Race').includes('American Indian or Alaska Native'),label:"American Indian or Alaska Native",onClick:()=>togArr('Race','American Indian or Alaska Native')})}
                {bArr('Race').includes('American Indian or Alaska Native')&&<div style={SUBBOX}>
                  <div style={{maxWidth:'320px'}}><AF label="Name of enrolled or principal tribe" val={xForm?.['American Indian tribe']} onChange={setX('American Indian tribe')}/></div>
                </div>}
                {CB({on:bArr('Race').includes('Asian'),label:"Asian",onClick:()=>togArr('Race','Asian')})}
                {bArr('Race').includes('Asian')&&<div style={SUBBOX}>
                  <div style={{display:'flex',gap:'18px',flexWrap:'wrap'}}>{RACE_ASIAN.map(o=>CB({key:o,on:bArr('Asian subtype').includes(o),label:o,onClick:()=>togArr('Asian subtype',o)}))}</div>
                  <div style={{maxWidth:'260px',marginTop:'9px'}}><AF label="Description" val={xForm?.['Asian other description']} onChange={setX('Asian other description')} placeholder="Description"/></div>
                </div>}
                {CB({on:bArr('Race').includes('Black or African American'),label:"Black or African American",onClick:()=>togArr('Race','Black or African American')})}
                {CB({on:bArr('Race').includes('Native Hawaiian or Other Pacific Islander'),label:"Native Hawaiian or Other Pacific Islander",onClick:()=>togArr('Race','Native Hawaiian or Other Pacific Islander')})}
                {bArr('Race').includes('Native Hawaiian or Other Pacific Islander')&&<div style={SUBBOX}>
                  <div style={{display:'flex',gap:'18px',flexWrap:'wrap'}}>{RACE_PI.map(o=>CB({key:o,on:bArr('Pacific Islander subtype').includes(o),label:o,onClick:()=>togArr('Pacific Islander subtype',o)}))}</div>
                  <div style={{maxWidth:'260px',marginTop:'9px'}}><AF label="Description" val={xForm?.['Pacific Islander other description']} onChange={setX('Pacific Islander other description')} placeholder="Description"/></div>
                </div>}
                {CB({on:bArr('Race').includes('White'),label:"White",onClick:()=>togArr('Race','White')})}
                {CB({on:bArr('Race').includes('I do not wish to provide'),label:"I do not wish to provide",onClick:()=>togArr('Race','I do not wish to provide')})}

                <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'.05em',color:'#154D97',margin:'18px 0 8px'}}>SEX</div>
                <div style={{display:'flex',gap:'24px',flexWrap:'wrap'}}>{SEX_OPTS.map(o=>CB({key:o,on:bArr('Sex').includes(o),label:o,onClick:()=>togArr('Sex',o)}))}</div>

                <div style={{marginTop:'16px',borderTop:'1px solid #EEF3F9',paddingTop:'8px'}}>
                  {RAD({k:"Ethnicity by visual observation",label:"Was the ethnicity collected by visual observation or surname?"})}
                  {RAD({k:"Sex by visual observation",label:"Was the sex collected by visual observation or surname?"})}
                  {RAD({k:"Race by visual observation",label:"Was the race collected by visual observation or surname?"})}
                </div>

                <div style={{width:'320px',marginTop:'14px'}}>
                  <ASel label="Demographic info provided through" val={xForm?.['Demographic info provided through']} onChange={setX('Demographic info provided through')} options={DEMO_THROUGH}/>
                </div>
              </div>
            </div>

            <div style={BAND}>
              <div style={BANDH}>Language preference {'\u00B7'} this borrower</div>
              <div style={{padding:'15px 16px',display:'flex',gap:'18px',alignItems:'flex-end',flexWrap:'wrap'}}>
                <div style={{width:'320px'}}><ASel label="Preferred language" val={xForm?.['Language preference']} onChange={setX('Language preference')} options={LANGUAGE_OPTS}/></div>
                {clean(xForm?.['Language preference'])==='Other'&&<div style={{width:'240px'}}><AF label="Other language" val={xForm?.['Language other']} onChange={setX('Language other')}/></div>}
              </div>
            </div>

            </React.Fragment>);
          })()}

        </div>


        {false && coContact && <div style={{...s.ovCard,display:appSection==='borrower'?undefined:'none'}}>
          <div style={{...s.ovLbl,justifyContent:'space-between'}}>
            <span>Co-borrower {'\u00B7'} {coContact.fields?.Name || `${coForm?.['First name']||''} ${coForm?.['Last name']||''}`.trim()}</span>
            <span style={{display:'inline-flex',gap:'8px',alignItems:'center'}}>
              <span style={{fontSize:'10px',fontWeight:600,letterSpacing:0,textTransform:'none',padding:'2px 8px',borderRadius:'10px',background:'#F0F5FB',color:'#1A5FA8'}}>Saves to their contact record</span>
              <span style={{fontSize:'11.5px',fontWeight:600,letterSpacing:0,textTransform:'none',color:'#154D97',cursor:'pointer',background:'#fff',border:'1px solid #C9D9EE',borderRadius:'16px',padding:'3px 11px'}} onClick={unlinkCoBorrower}>Unlink</span>
            </span>
          </div>
          <div style={{...s.mbBar,marginTop:'12px',marginBottom:'16px'}}>
            <div style={s.mbTile}><div style={s.mbK}>FICO</div><div style={s.mbV}>{clean(coForm?.['FICO score'])||'-'}</div></div>
            <div style={s.mbDiv}/>
            <div style={s.mbTile}><div style={s.mbK}>Completed projects</div><div style={s.mbV}>{(()=>{const n=(sreoRows||[]).filter(x=>x['Status']==='Sold').length;return n?n+' sold':'-';})()}</div></div>
            <div style={s.mbDiv}/>
            <div style={s.mbTile}><div style={s.mbK}>Assets + equity</div><div style={s.mbV}>{(()=>{const n=coAssetRows.reduce((a,r)=>a+(Number(r['Balance'])||0),0)+coSreoRows.reduce((a,r)=>a+((Number(r['Current value'])||0)-(Number(r['Loan balance'])||0)),0);return n?fmtK(n):'-';})()}</div></div>
            <div style={s.mbDiv}/>
            <div style={s.mbTile}><div style={s.mbK}>Deals with you</div><div style={s.mbV}>{(coContact.fields?.['Deals']||[]).length||1}</div></div>
          </div>
          <div style={s.appGrid}>
            <AF label="First name" val={coForm?.['First name']} onChange={setCo('First name')}/>
            <AF label="Last name" val={coForm?.['Last name']} onChange={setCo('Last name')}/>
            <AF label="Email" val={coForm?.['Email']} onChange={setCo('Email')}/>
            <AF label="Phone" val={coForm?.['Phone']} onChange={setCo('Phone')}/>
            <AF label="FICO score" val={coForm?.['FICO score']} onChange={setCo('FICO score')}/>
          </div>
          <div style={{...s.subLabel,marginTop:'18px'}}>Home address</div>
          <div style={s.appGrid}>
            <AF label="Street address" val={coForm?.['Street address']} onChange={setCo('Street address')}/>
            <AF label="City" val={coForm?.['City']} onChange={setCo('City')}/>
            <ASel label="State" val={Array.isArray(coForm?.['Home state'])?coForm['Home state'][0]:(coForm?.['Home state']||'')} onChange={v=>setCo('Home state')([v].filter(Boolean))} options={STATES}/>
            <AF label="ZIP" val={coForm?.['ZIP']} onChange={setCo('ZIP')}/>
          </div>
          <div style={{...s.subLabel,marginTop:'18px'}}>Entity / vesting</div>
          <div style={s.appGrid}>
            <AF label="Entity name" val={coForm?.['Company / entity']} onChange={setCo('Company / entity')}/>
            <ASel label="Entity type" val={coForm?.['Entity type']} onChange={setCo('Entity type')} options={ENTITY_TYPES}/>
          </div>
        </div>}

        <div style={{...s.secWrap,display:appSection==='deal'?undefined:'none'}}>

          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="setup" title="Setup" done={!!form['Loan type']} summary={[clean(form['Loan type']),clean(form['Loan purpose']),hasCollateral?clean(form['Property type']):null].filter(Boolean).join(' \u00B7 ')||'Pick a loan type'}>
            <div style={{...s.dealGrid,gridTemplateColumns:'repeat(5,minmax(0,1fr))'}}>
              <ASel label="Loan type" val={form['Loan type']} onChange={handleLoanTypeChange} options={LOAN_TYPES_ALPHA}/>
              <ASel label="Loan purpose" val={form['Loan purpose']} onChange={setF('Loan purpose')} options={availablePurposes} disabled={!loanType}/>
              {hasCollateral
                ? <ASel label="Property type" val={form['Property type']} onChange={setF('Property type')} options={PROP_TYPES}/>
                : <div/>}
              <div>
                <div style={s.appLbl}>Asset class</div>
                <div style={s.pickField((form['Asset class']||[]).length>0)} onClick={()=>setTagOpen(pp=>({ac:!pp.ac}))}>
                  <span style={s.pickVal}>{(form['Asset class']||[]).length?(form['Asset class']||[]).join(', '):'Select asset class'}</span>
                  <span style={s.pickCaret}>{'\u25BE'}</span>
                </div>
              </div>
              <div>
                <div style={s.appLbl}>Special circumstances</div>
                <div style={s.pickField((form['Deal situations']||[]).length>0)} onClick={()=>setTagOpen(pp=>({sc:!pp.sc}))}>
                  <span style={s.pickVal}>{(form['Deal situations']||[]).length?(form['Deal situations']||[]).join(', '):'Add circumstances'}</span>
                  <span style={s.pickCaret}>{'\u25BE'}</span>
                </div>
              </div>
            </div>
            {tagOpen.ac&&<div style={s.tagCloud}>
              <div style={s.pickTop}>
                <input style={s.pickSearch} placeholder="Search asset class" value={acSearch} onChange={e=>setAcSearch(e.target.value)}/>
                <span style={s.pickCount}>{(form['Asset class']||[]).length} selected</span>
                <span style={s.pickClear} onClick={()=>{setForm(p=>({...p,'Asset class':[]}));markDirty();}}>Clear all</span>
              </div>
              <div style={s.pickCols}>
                {ASSET_CLASS_GROUPS.map(g=>{
                  const items=g[1].filter(x=>!acSearch||x.toLowerCase().indexOf(acSearch.toLowerCase())>-1);
                  if(!items.length) return null;
                  return (<div key={g[0]} style={s.pickGroup}>
                    <div style={s.pickGroupHead}>{g[0]}</div>
                    {items.map(sp=>{const on=(form['Asset class']||[]).includes(sp);return (
                      <label key={sp} style={s.pickChk}>
                        <input type="checkbox" checked={on} onChange={()=>toggleAssetClass(sp)} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer',flexShrink:0}}/>{sp}
                      </label>);})}
                  </div>);
                })}
              </div>
            </div>}
            {tagOpen.sc&&<div style={s.tagCloud}>
              <div style={s.pickTop}>
                <span style={s.pickCount}>{(form['Deal situations']||[]).length} selected</span>
                <span style={s.pickClear} onClick={()=>{setForm(p=>({...p,'Deal situations':[]}));markDirty();}}>Clear all</span>
              </div>
              <div style={s.pickCols}>
                {SITUATION_GROUPS.map(g=>(<div key={g[0]} style={s.pickGroup}>
                  <div style={s.pickGroupHead}>{g[0]}</div>
                  {g[1].map(sit=>{const on=(form['Deal situations']||[]).includes(sit);return (
                    <label key={sit} style={s.pickChk}>
                      <input type="checkbox" checked={on} onChange={()=>toggleDealSituation(sit)} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer',flexShrink:0}}/>{sit}
                    </label>);})}
                </div>))}
              </div>
            </div>}
          </DealSec>

          {(()=>{
            const nn=v=>Number(String(v||'').replace(/[^0-9.]/g,''))||0;
            const L=nn(form['Loan amount']);const rate=nn(form['Requested rate']);const nMo=nn(form['Term months'])||360;const r=rate/100/12;
            const hasTerm=nn(form['Term months'])>0;const showPay=!!(L&&rate&&hasTerm);const pi=showPay?(L*r/(1-Math.pow(1+r,-nMo))):0;
            const taxM=nn(form['Property taxes'])*((form['Property taxes frequency']||'Monthly')==='Annually'?1/12:1);
            const insM=nn(form['Insurance'])*((form['Insurance frequency']||'Monthly')==='Annually'?1/12:1);
            const rent=nn(form['MH monthly fee']);
            const hoaM=nn(form['HOA dues'])*(form['HOA dues frequency']==='Annually'?1/12:form['HOA dues frequency']==='Quarterly'?1/3:1);
            const mi=nn(form['Mortgage insurance']);const oth=nn(form['Other financing']);
            const piti=showPay?(pi+taxM+insM+rent+hoaM+mi+oth):0;
            const m=v=>v?'$'+usd(v):'$0.00';
            const price=nn(form['Purchase price']),dp=nn(form['Down payment']);
            const dpPct=price&&dp?((dp/price)*100).toFixed(2)+'%':'-';
            const isRefi=(form['Loan purpose']||'').toLowerCase().indexOf('refi')>-1;
            const feeLabel=form['MH fee type']==='HOA dues'?'HOA (site)':'Space rent';
            const rows=[['Principal & interest',pi,true],['Property taxes',taxM,false],['Insurance',insM,false],[feeLabel,rent,false],['HOA dues',hoaM,false],['Mortgage insurance',mi,false],['Other financing',oth,false]];
            return (
          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="pricing" title="Loan and monthly payment" done={!!(form['Loan amount']&&form['Requested rate'])} summary={[form['Loan amount']?fmtK(form['Loan amount']):null,(showPay&&piti)?m(piti)+'/mo PITI':null].filter(Boolean).join(' \u00B7 ')||'Add loan terms'}>
            <div style={{display:'flex',gap:'18px',flexWrap:'wrap',alignItems:'flex-start'}}>
              <div style={{flex:'1 1 380px',minWidth:'340px'}}>
                {(()=>{
                  const isRef = String(form['Loan purpose']||'').toLowerCase().indexOf('refi')>=0;
                  const setPurpose = (p) => setForm(prev=>({...prev,'Loan purpose':p}));
                  const segBase = {padding:'12px',textAlign:'center',fontSize:'14px',fontWeight:600,cursor:'pointer',fontFamily:'inherit',border:'none',background:'#fff',color:'#3A6BA8'};
                  const segOn = {...segBase,background:'#EAF1FA',color:'#154D97',fontWeight:700};
                  const appr = nn(form['Appraised value']);
                  const prc  = nn(form['Purchase price']);
                  const amt  = nn(form['Loan amount']);
                  const ltvBase = isRef ? appr : (prc && appr ? Math.min(prc,appr) : (prc||appr));
                  const ltv = (ltvBase && amt) ? (Math.round((amt/ltvBase)*10000)/100)+'%' : '';
                  const isCashOut = String(form['Refinance type']||'')==='Cash out';
                  const cashOut = Math.max(amt - nn(form['Existing lien amount']),0);
                  const ro = {...s.appInput,background:'#F0F5FC',color:'#154D97',fontWeight:700};
                  return (<React.Fragment>

                  {showMH&&<React.Fragment>
                    {!isRef&&<React.Fragment>
                      <div style={s.dealGrid}>
                        <AF money label="Purchase price" val={form['Purchase price']} onChange={v=>{const pr=nn(v);setForm(p=>{const d=nn(p['Down payment']);return {...p,'Purchase price':v,'Loan amount':pr?String(Math.max(pr-d,0)):(p['Loan amount']||''),'Down payment percent':pr&&d?String(Math.round((d/pr)*100000)/1000):(p['Down payment percent']||'')};});markDirty();}}/>
                        <AF money label="Appraised value" val={form['Appraised value']} onChange={setF('Appraised value')}/>
                      </div>
                      <div style={s.dealGrid}>
                        <AF money label="Down payment" val={form['Down payment']} onChange={v=>{const d=nn(v);setForm(p=>{const base=nn(p['Purchase price']);return {...p,'Down payment':v,'Down payment percent':base?String(Math.round((d/base)*100000)/1000):(p['Down payment percent']||''),'Loan amount':base?String(Math.max(base-d,0)):(p['Loan amount']||'')};});markDirty();}}/>
                        <AF pct label="Down payment %" val={form['Down payment percent']||((prc&&nn(form['Down payment']))?String(Math.round((nn(form['Down payment'])/prc)*100000)/1000):'')} onChange={v=>{const pc=nn(v);setForm(p=>{const base=nn(p['Purchase price']);const d=base?Math.round(base*pc)/100:0;return {...p,'Down payment percent':v,'Down payment':base?String(d):(p['Down payment']||''),'Loan amount':base?String(Math.max(base-d,0)):(p['Loan amount']||'')};});markDirty();}}/>
                        <AF money label="Base loan amount" val={form['Loan amount']} onChange={v=>{const l=nn(v);setForm(p=>{const base=nn(p['Purchase price']);return {...p,'Loan amount':v,'Down payment':base?String(Math.max(base-l,0)):(p['Down payment']||''),'Down payment percent':base?String(Math.round(((base-l)/base)*100000)/1000):(p['Down payment percent']||'')};});markDirty();}}/>
                        <AF readOnly label="LTV" val={ltv} onChange={()=>{}}/>
                      </div>
                    </React.Fragment>}
                    {isRef&&<React.Fragment>
                      <div style={s.dealGrid}>
                        <AF money label="Requested loan amount" val={form['Loan amount']} onChange={setF('Loan amount')}/>
                        <AF money label="Appraised value" val={form['Appraised value']} onChange={setF('Appraised value')}/>
                        <AF money label="Existing lien amount" val={form['Existing lien amount']} onChange={setF('Existing lien amount')}/>
                      </div>
                      <div style={s.dealGrid}>
                        <AF money label="Original purchase price" val={form['Original purchase price']} onChange={setF('Original purchase price')}/>
                        <AF date label="Original purchase date" val={form['Original purchase date']} onChange={setF('Original purchase date')}/>
                        <AF readOnly label="LTV" val={ltv} onChange={()=>{}}/>
                        {isCashOut&&<AF readOnly label="Cash to borrower" val={cashOut?fmtK(cashOut):''} onChange={()=>{}}/>}
                      </div>
                    </React.Fragment>}
                    <div style={s.dealGrid}>
                      <ASel label="Mortgage type" val={form['Mortgage type']} onChange={setF('Mortgage type')} options={MORTGAGE_TYPES}/>
                      <ASel label="Lien position" val={form['Lien position']} onChange={setF('Lien position')} options={LIEN_POSITIONS}/>
                      <AF pct label="Note rate" val={form['Requested rate']} onChange={setF('Requested rate')}/>
                      <AF label="Term (months)" val={form['Term months']} onChange={setF('Term months')}/>
                    </div>
                    <label style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer',marginTop:'15px'}}>
                      <input type="checkbox" checked={subOpen===null?hasSubAmount(form):subOpen} onChange={e=>{const on=e.target.checked;setSubOpen(on);if(!on)setForm(p=>({...p,'Subordinate financing':'','Subordinate amount':''}));}} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/>
                      Subordinate liens
                    </label>
                    {(subOpen===null?hasSubAmount(form):subOpen)&&<div style={s.dealGrid}>
                      <ASel label="Subordinate financing" val={form['Subordinate financing']} onChange={setF('Subordinate financing')} options={SUBORDINATE_TYPES}/>
                      <AF money label="Subordinate amount" val={form['Subordinate amount']} onChange={setF('Subordinate amount')}/>
                    </div>}
                  </React.Fragment>}

                  {!showMH&&!isRef&&<React.Fragment>
                    <div style={s.dealGrid}>
                      <AF money label="Purchase price" val={form['Purchase price']} onChange={v=>{const pr=nn(v);setForm(p=>{const d=nn(p['Down payment']);return {...p,'Purchase price':v,'Loan amount':pr?String(Math.max(pr-d,0)):(p['Loan amount']||''),'Down payment percent':pr&&d?String(Math.round((d/pr)*100000)/1000):(p['Down payment percent']||'')};});markDirty();}}/>
                      <AF money label="Appraised value" val={form['Appraised value']} onChange={setF('Appraised value')}/>
                      <div style={{width:'110px',flexShrink:0}}><div style={s.appLbl}>LTV</div><input readOnly value={ltv} style={ro}/></div>
                    </div>
                    <div style={s.dealGrid}>
                      <AF money label="Down payment" val={form['Down payment']} onChange={v=>{const d=nn(v);setForm(p=>{const base=nn(p['Purchase price']);return {...p,'Down payment':v,'Down payment percent':base?String(Math.round((d/base)*100000)/1000):(p['Down payment percent']||''),'Loan amount':base?String(Math.max(base-d,0)):(p['Loan amount']||'')};});markDirty();}}/>
                      <AF pct label="Down payment %" val={form['Down payment percent']||((prc&&nn(form['Down payment']))?String(Math.round((nn(form['Down payment'])/prc)*100000)/1000):'')} onChange={v=>{const pc=nn(v);setForm(p=>{const base=nn(p['Purchase price']);const d=base?Math.round(base*pc)/100:0;return {...p,'Down payment percent':v,'Down payment':base?String(d):(p['Down payment']||''),'Loan amount':base?String(Math.max(base-d,0)):(p['Loan amount']||'')};});markDirty();}}/>
                      <AF money label="Loan amount" val={form['Loan amount']} onChange={v=>{const l=nn(v);setForm(p=>{const base=nn(p['Purchase price']);return {...p,'Loan amount':v,'Down payment':base?String(Math.max(base-l,0)):(p['Down payment']||''),'Down payment percent':base?String(Math.round(((base-l)/base)*100000)/1000):(p['Down payment percent']||'')};});markDirty();}}/>
                    </div>
                  </React.Fragment>}

                  {!showMH&&isRef&&<React.Fragment>
                    <div style={s.dealGrid}>
                      <AF money label="Appraised value" val={form['Appraised value']} onChange={setF('Appraised value')}/>
                      <AF money label="Loan amount" val={form['Loan amount']} onChange={setF('Loan amount')}/>
                      <div style={{width:'110px',flexShrink:0}}><div style={s.appLbl}>LTV</div><input readOnly value={ltv} style={ro}/></div>
                    </div>
                    <div style={s.dealGrid}>
                      <ASel label="Refinance type" val={form['Refinance type']} onChange={setF('Refinance type')} options={REFI_TYPES}/>
                      <AF money label="Existing lien amount" val={form['Existing lien amount']} onChange={setF('Existing lien amount')}/>
                      {isCashOut&&<div style={{width:'150px',flexShrink:0}}><div style={s.appLbl}>Cash to borrower</div><input readOnly value={cashOut?fmtK(cashOut):''} style={ro}/></div>}
                    </div>
                  </React.Fragment>}

                  {!showMH&&<React.Fragment>
                  <div style={s.dealGrid}>
                    <ASel label="Mortgage type" val={form['Mortgage type']} onChange={setF('Mortgage type')} options={MORTGAGE_TYPES}/>
                    <ASel label="Lien position" val={form['Lien position']} onChange={setF('Lien position')} options={LIEN_POSITIONS}/>
                    <AF pct label="Note rate" val={form['Requested rate']} onChange={setF('Requested rate')}/>
                  </div>
                  <div style={s.dealGrid}>
                    <AF label="Term (months)" val={form['Term months']} onChange={setF('Term months')}/>
                  </div>
                  </React.Fragment>}

                  {!isMH&&(<React.Fragment>
                    <div style={{...s.dealGrid,marginTop:'15px'}}>
                      {['asIsValue','existingLoanBalance','rehabBudget','constructionBudget','arv','cashOutAmount'].filter(k=>spec.show[k]).map(k=>{const af=FIELD_TO_AIRTABLE[k];const lbl=spec.labels[k]||DEFAULT_LABELS[k];const amber=!!spec.required[k]&&!form[af];return <AF key={k} money label={lbl} val={form[af]} onChange={setF(af)} amber={amber}/>;})}
                    </div>
                    <div style={{...s.dealGrid,marginTop:'15px'}}>
                      <AF label="Points" val={form['Requested points']} onChange={setF('Requested points')}/>
                      <ASel label="Amortization type" val={form['Amortization type']} onChange={setF('Amortization type')} options={AMORT_TYPES}/>
                      <ASel label="Rate type" val={form['Rate type']} onChange={setF('Rate type')} options={RATE_TYPES}/>
                      <AF label="Index and margin" val={form['Index and margin']} onChange={setF('Index and margin')}/>
                    </div>
                    <div style={{...s.dealGrid,marginTop:'15px'}}>
                      <ASel label="Prepay penalty" val={form['Requested prepayment penalty']} onChange={setF('Requested prepayment penalty')} options={PPP_OPTS}/>
                      <ASel label="Subordinate financing" val={form['Subordinate financing']} onChange={setF('Subordinate financing')} options={SUBORDINATE_TYPES}/>
                      <AF money label="Subordinate amount" val={form['Subordinate amount']} onChange={setF('Subordinate amount')}/>
                      <ASel label="Recourse" val={form['Recourse preference']} onChange={setF('Recourse preference')} options={RECOURSE_PREF}/>
                    </div>
                    <div style={{...s.dealGrid,marginTop:'15px'}}>
                      <ASel label="Appraisal" val={form['Appraisal preference']} onChange={setF('Appraisal preference')} options={APPRAISAL_PREF}/>
                      <ASel label="Exit strategy" val={form['Exit strategy']} onChange={setF('Exit strategy')} options={EXIT_STRATEGIES}/>
                      <ASel label="Rehab financing" val={form['Rehab financing']} onChange={setF('Rehab financing')} options={REHAB_FINANCING}/>
                      <AF date label="Target close date" val={form['Target close date']} onChange={setF('Target close date')}/>
                    </div>
                  </React.Fragment>)}

                  <div style={{display:'flex',alignItems:'baseline',gap:'10px',marginTop:'18px',paddingTop:'14px',borderTop:'1px solid #EEF3F9',fontSize:'14px',color:'#33415A'}}>
                    <span>Total loan amount</span><span style={{fontSize:'16px',fontWeight:700,color:'#154D97'}}>{amt?fmtK(amt):'-'}</span>
                  </div>

                  </React.Fragment>);
                })()}
              </div>
              <div style={{flex:'1 1 260px',minWidth:'250px',background:'#F7FAFE',border:'1px solid #DCE7F5',borderRadius:'10px',padding:'12px 14px'}}>
                <div style={{fontSize:'11px',fontWeight:600,letterSpacing:'.04em',textTransform:'uppercase',color:'#3A6BA8',marginBottom:'8px'}}>Proposed monthly payment</div>
                {(()=>{
                  const inpS={width:'92px',textAlign:'right',border:'1px solid #C9D9EE',borderRadius:'6px',padding:'4px 7px',fontSize:'12px',color:'#0D2545',fontFamily:'inherit'};
                  const roRow=(lab,val,strong)=>(<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid #EAF1FA'}}><span style={{fontSize:'12.5px',color:strong?'#0D3565':'#33415A',fontWeight:strong?600:400}}>{lab}</span><span style={{fontSize:'12.5px',color:strong?'#154D97':'#0D2545',fontWeight:strong?600:400}}>{val}</span></div>);
                  const edRow=(lab,key)=>(<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'5px 0',borderBottom:'1px solid #EAF1FA'}}><span style={{fontSize:'12.5px',color:'#33415A'}}>{lab}</span><MoneyCell value={form[key]} onChange={setF(key)} onCommit={v=>{const p=money2dp(v);if(p!==clean(form[key]))setF(key)(p);}} placeholder="$0.00" style={inpS}/></div>);
                  return (<React.Fragment>{roRow('Principal & interest',showPay?m(pi):'-',true)}{edRow('Property taxes','Property taxes')}{edRow('Insurance','Insurance')}{edRow(feeLabel,'MH monthly fee')}{edRow('HOA dues','HOA dues')}{edRow('Mortgage insurance','Mortgage insurance')}{edRow('Other financing','Other financing')}</React.Fragment>);
                })()}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#154D97',borderRadius:'8px',padding:'9px 11px',marginTop:'8px'}}>
                  <span style={{fontSize:'12.5px',color:'#fff',fontWeight:600}}>Total PITI</span>
                  <span style={{fontSize:'14px',color:'#fff',fontWeight:700}}>{showPay?m(piti):'-'}</span>
                </div>
                <div style={{fontSize:'10px',color:'#8A94A6',marginTop:'6px'}}>P&I is from your rate and term. Type taxes, insurance and the rest right here.</div>
              </div>
            </div>
          </DealSec>);})()}
            {(form['Deal situations']||[]).includes('Wholesale')&&<div style={s.appSub}>
              <div style={s.appSubTitle}>Assignment fee <span style={{fontWeight:400,color:'#8a9ab5',textTransform:'none',letterSpacing:0}}>shown because Wholesale is tagged</span></div>
              <div style={s.dealFlow}>
                <AF w="160px" money label="Fee amount ($ or %)" val={form['Assignment fee amount']} onChange={setF('Assignment fee amount')}/>
                <ASel w="150px" label="Fee basis" val={form['Assignment fee basis']} onChange={setF('Assignment fee basis')} options={ASSIGNMENT_FEE_BASIS}/>
                <ASel w="170px" label="Paid by" val={form['Assignment fee paid by']} onChange={setF('Assignment fee paid by')} options={ASSIGNMENT_FEE_PAID_BY}/>
              </div>
            </div>}

          {loanType==='SBA 504'&&(()=>{const nn=sbaNN;const proj=sbaProj();const bank=nn(form['Loan amount']);const gross=nn(form['CDC portion']);
            const net=proj&&cdcPct?proj*(Number(cdcPct)||0)/100:(gross?(gross-3500)/1.0265:0);
            const inj=nn(form['Down payment']);
            const injPct=proj&&inj?Math.round(inj/proj*1000)/10:0;
            const fees=gross&&net?gross-net:0;
            const yrs=v=>nn(String(v||''));
            const pmt=(P,rate,years)=>{if(!P||!rate||!years)return 0;const r=rate/1200,n=years*12;const f=Math.pow(1+r,n);return P*r*f/(f-1);};
            const bankPmt=pmt(bank,nn(form['Requested rate']),yrs(form['Requested loan term']));
            const cdcPmt=pmt(gross,nn(form['CDC rate']),yrs(form['CDC term']));
            const ds=bankPmt+cdcPmt;
            const totalSources=bank+gross+inj;
            const recon=proj?Math.round(bank+net+inj-proj):0;
            const balanced=proj>0&&bank>0&&inj>0&&Math.abs(recon)<=100;
            const money=v=>v?'$'+usd(v):'-';
            const blended=(bank&&gross&&nn(form['Requested rate'])&&nn(form['CDC rate']))?Math.round((bank*nn(form['Requested rate'])+gross*nn(form['CDC rate']))/(bank+gross)*100)/100:null;
            return(
          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="sbaStructure" title="SBA structure, additional" done={!!(form['Loan amount']&&form['Purchase price'])} summary={[form['Loan amount']?fmtK(form['Loan amount'])+' bank':null,form['CDC portion']?fmtK(form['CDC portion'])+' CDC':null,proj?'on $'+proj.toLocaleString():null,injPct?injPct+'% in':null].filter(Boolean).join(' \u00B7 ')||'Add structure'}>
            <div style={s.noteRow}>
              <span style={s.noteTag}>Project (uses)</span>
              <AF w="150px" money label="Purchase price" val={form['Purchase price']} onChange={sbaOnProject('Purchase price')} amber={!form['Purchase price']}/>
              <AF w="140px" money label="Improvements" val={form['Rehab budget']} onChange={sbaOnProject('Rehab budget')}/>
              <AF w="185px" money label="Other costs (closing, fees)" val={form['Other project costs']} onChange={sbaOnProject('Other project costs')}/>
              <div style={{width:'160px',flexShrink:0}}><div style={s.appLbl}>Total project cost</div><div style={s.noteAuto}>{proj?'$'+usd(proj):'-'}</div></div>
            </div>
            <div style={{...s.noteRow,background:'#F0F5FB',border:'1px solid #D9E5F4'}}>
              <span style={s.noteTag}>Structure</span>
              <AF w="90px" pct label="Bank %" val={bankPct==null?'':String(bankPct)} onChange={v=>{setBankPct(v); sbaApplyPcts(v,cdcPct);}}/>
              <AF w="90px" pct label="CDC %" val={cdcPct==null?'':String(cdcPct)} onChange={v=>{setCdcPct(v); sbaApplyPcts(bankPct,v);}}/>
              <AF w="100px" pct label="Injection %" val={(bankPct!=null&&cdcPct!=null)?String(Math.round((100-(Number(bankPct)||0)-(Number(cdcPct)||0))*10)/10):''} onChange={v=>{const ip=Number(v)||0; const c=Math.round((100-(Number(bankPct)||0)-ip)*10)/10; setCdcPct(c); sbaApplyPcts(bankPct,c);}}/>
              <div style={{alignSelf:'center',minWidth:'230px'}}>
                {proj>0&&(balanced
                  ?<span style={{fontSize:'11px',fontWeight:600,color:'#3B6D11'}}>{'\u2713'} Sources = uses (${proj.toLocaleString()})</span>
                  :<span style={{fontSize:'11px',fontWeight:600,color:'#8a5a12'}}>{(bank&&inj&&Math.abs(recon)>100)?'Off by '+money(Math.abs(recon)):'Enter project cost and structure'}</span>)}
                <div style={{fontSize:'10px',color:'#8A9AB5'}}>percentages drive the amounts below</div>
              </div>
            </div>
            <div style={s.noteRow}>
              <span style={s.noteTag}>Bank 1st</span>
              <AF w="150px" money label="Amount" val={form['Loan amount']} onChange={v=>{setF('Loan amount')(v); const p=sbaProj(); if(p&&nn(v)){setBankPct(Math.round(nn(v)/p*1000)/10); sbaBalanceInjection(nn(v),net);}}} amber={!form['Loan amount']}/>
              <AF w="90px" pct label="Rate (%)" val={form['Requested rate']} onChange={setF('Requested rate')}/>
              <AF w="80px" label="Points" val={form['Requested points']} onChange={setF('Requested points')}/>
              <ASel w="130px" label="Term" val={form['Requested loan term']} onChange={setF('Requested loan term')} options={SBA_BANK_TERMS}/>
              <div style={{width:'130px',flexShrink:0}}><div style={s.appLbl}>Payment /mo</div><div style={s.noteAuto}>{money(bankPmt)}</div></div>
            </div>
            <div style={s.noteRow}>
              <span style={s.noteTag}>CDC / SBA 2nd</span>
              <AF w="150px" money label="Gross debenture" val={form['CDC portion']} onChange={v=>{setF('CDC portion')(v); const p=sbaProj(); if(p&&nn(v)){const nv=(nn(v)-3500)/1.0265; setCdcPct(Math.round(nv/p*1000)/10); sbaBalanceInjection(bank,nv);}}}/>
              <AF w="90px" pct label="Rate (%)" val={form['CDC rate']} onChange={setF('CDC rate')}/>
              <ASel w="130px" label="Debenture term" val={form['CDC term']} onChange={setF('CDC term')} options={SPEC_FIELD_OPTIONS.cdcTerm}/>
              <div style={{width:'130px',flexShrink:0}}><div style={s.appLbl}>Payment /mo</div><div style={s.noteAuto}>{money(cdcPmt)}</div></div>
              <div style={{width:'130px',flexShrink:0}}><div style={s.appLbl}>Net proceeds</div><div style={s.noteAuto}>{net?money(net):'-'}</div></div>
              <div style={{width:'140px',flexShrink:0}}><div style={s.appLbl}>Fees financed (est.)</div><div style={s.noteAuto}>{fees>0?money(fees):'-'}</div></div>
            </div>
            <div style={s.noteRow}>
              <span style={s.noteTag}>Borrower</span>
              <AF w="150px" money label="Injection" val={form['Down payment']} onChange={setF('Down payment')}/>
              <div style={{width:'110px',flexShrink:0}}><div style={s.appLbl}>Injection %</div><div style={s.noteAuto}>{injPct?injPct+'%':'-'}</div></div>
              <AF w="170px" money label="Paid outside closing (POC)" val={form['Paid outside closing']} onChange={setF('Paid outside closing')}/>
              <div style={{width:'150px',flexShrink:0}}><div style={s.appLbl}>Total out of pocket</div><div style={s.noteAuto}>{(inj||nn(form['Paid outside closing']))?money(inj+nn(form['Paid outside closing'])):'-'}</div></div>
            </div>
            <div style={{...s.noteRow,background:'#F0F5FB',border:'1px solid #D9E5F4'}}>
              <span style={s.noteTag}>Totals</span>
              <div style={{width:'130px',flexShrink:0}}><div style={s.appLbl}>Total loans</div><div style={s.noteAuto}>{(bank||gross)?money(bank+gross):'-'}</div></div>
              <div style={{width:'150px',flexShrink:0}}><div style={s.appLbl}>Total financed + equity</div><div style={s.noteAuto}>{money(totalSources)}</div></div>
              <div style={{width:'160px',flexShrink:0}}><div style={s.appLbl}>Total debt service /mo</div><div style={s.noteAuto}>{money(ds)}</div></div>
              <div style={{width:'140px',flexShrink:0}}><div style={s.appLbl}>Debt service /yr</div><div style={s.noteAuto}>{money(ds*12)}</div></div>
              <div style={{width:'120px',flexShrink:0}}><div style={s.appLbl}>Blended rate</div><div style={s.noteAuto}>{blended!=null?blended+'%':'-'}</div></div>
            </div>
            <div style={{...s.dealFlow,marginTop:'12px'}}>
              <ASel w="160px" label="Recourse" val={form['Recourse preference']} onChange={setF('Recourse preference')} options={RECOURSE_PREF.filter(Boolean)}/>
              <ASel w="160px" label="Appraisal" val={form['Appraisal preference']} onChange={setF('Appraisal preference')} options={APPRAISAL_PREF.filter(Boolean)}/>
              <ASel w="160px" label="Exit strategy" val={form['Exit strategy']} onChange={setF('Exit strategy')} options={EXIT_STRATEGIES}/>
              <AF w="150px" date label="Target close date" val={form['Target close date']} onChange={setF('Target close date')}/>
              <ASel w="140px" label="Prepay penalty" val={form['Requested prepayment penalty']} onChange={setF('Requested prepayment penalty')} options={PPP_OPTS}/>
            </div>
          </DealSec>);})()}

        </div>

        <div style={{...s.secWrap,display:(appSection==='deal'&&hasCollateral)?undefined:'none'}}>
          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="location" title="Property" done={!!(form['Property address']&&form['Occupancy status'])} summary={[[clean(form['Property address']),clean(form['Property city']),clean(form['Property state'])].filter(Boolean).join(', ')||'Add address',clean(form['Occupancy status'])].filter(Boolean).join(' \u00B7 ')}>

            <div style={s.subBand}>
              <div style={s.subBandHead}>Address</div>
              <div style={s.subBandBody}>
                <label style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'13px',color:'#33415A',cursor:'pointer',margin:'0 0 10px'}}>
                  <input type="checkbox" checked={!!form['Address TBD']} onChange={e=>{const on=e.target.checked;setForm(p=>{const cur=clean(p['Property address']);if(on){if(cur&&cur!=='TBD') prevAddrRef.current=cur;return {...p,'Address TBD':true,'Property address':'TBD'};}return {...p,'Address TBD':false,'Property address':cur==='TBD'?(prevAddrRef.current||''):p['Property address']};});markDirty();}} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/>
                  Street address is TBD
                </label>
                <div style={s.dealGrid}>
                  <div style={{gridColumn:'span 2'}}><AF label="Address" val={form['Property address']} onChange={setF('Property address')}/></div>
                  {showMH&&<AF label="Space / Unit" val={form['Property space / unit']} onChange={setF('Property space / unit')}/>}
                </div>
                <div style={{...s.dealGrid,marginTop:'14px'}}>
                  <AF label="City" val={form['Property city']} onChange={setF('Property city')}/>
                  <AF label="State" val={form['Property state']} onChange={setF('Property state')}/>
                  <AF label="ZIP" val={form['Property ZIP']} onChange={v=>{setF('Property ZIP')(v);if(String(v||'').replace(/\D/g,'').length===5){lookupZip(v).then(r=>{if(!r)return;setF('Property city')(r.city);setF('Property state')(r.state);if(r.county)setF('Property county')(r.county);});}}}/>
                  <AF label="County" val={form['Property county']} onChange={setF('Property county')}/>
                </div>
              </div>
            </div>

            <div style={s.subBand}>
              <div style={s.subBandHead}>Property details</div>
              <div style={s.subBandBody}>
                <div style={s.dealFlow}>
                  <ASel w="165px" label="Occupancy" val={form['Occupancy status']} onChange={setF('Occupancy status')} options={isMH?MH_OCCUPANCY:OCC_SETUP}/>
                  {!showMH&&<AF w="110px" label="Year built" val={form['Year built']} onChange={setF('Year built')}/>}
                  {!showMH&&<AF w="150px" label="APN" val={form['APN']} onChange={setF('APN')}/>}
                  {!showMH&&<ASel w="190px" label="Flood zone" val={form['Flood zone']} onChange={setF('Flood zone')} options={FLOOD_ZONES}/>}
                  {!showMH&&<ASel w="185px" label="Property condition" val={form['Property condition']} onChange={setF('Property condition')} options={PROPERTY_CONDITIONS}/>}
                  {!showMH&&<ASel w="200px" label="Currently listed for sale" val={form['Currently listed for sale']} onChange={setF('Currently listed for sale')} options={LISTED_STATUSES}/>}

                  {showMH&&<React.Fragment>
                    <AF w="90px" label="Year" val={form['Year built']} onChange={setF('Year built')}/>
                    <AF w="130px" label="Make" val={form['MH make']} onChange={setF('MH make')}/>
                    <AF w="130px" label="Model" val={form['MH model']} onChange={setF('MH model')}/>
                    <AF w="90px" label="Width (ft)" val={form['MH width']} onChange={setF('MH width')}/>
                    <AF w="90px" label="Length (ft)" val={form['MH length']} onChange={setF('MH length')}/>
                    <ASel w="140px" label="Sections" val={form['MH sections']} onChange={setF('MH sections')} options={MH_SECTIONS}/>
                    <ASel w="120px" label="New or used" val={form['MH new or used']} onChange={setF('MH new or used')} options={MH_NEW_USED}/>
                    <AF w="180px" label="Park name" val={form['MH park name']} onChange={setF('MH park name')}/>
                    <ASel w="140px" label="Site placement" val={form['MH site placement']} onChange={setF('MH site placement')} options={MH_SITE_PLACEMENTS}/>
                    <ASel w="130px" label="Residency age" val={form['MH residency age']} onChange={setF('MH residency age')} options={MH_RESIDENCY_AGES}/>
                    <AF w="150px" label="Decal number" val={form['MH decal number']} onChange={setF('MH decal number')}/>
                    <AF w="160px" label="Serial number" val={form['MH serial number']} onChange={setF('MH serial number')}/>
                    <AF w="170px" label="HUD label / insignia" val={form['MH HUD label']} onChange={setF('MH HUD label')}/>
                    <AF w="170px" label="HOA name" val={form['HOA name']} onChange={setF('HOA name')}/>
                  </React.Fragment>}

                  {!showMH&&(()=>{const occF=form['Occupancy status'];const tenantKeys=['Tenant count','Current monthly rent','Annual NOI','Lease terms','Vacancy rate','Occupied units','Residential tenants','Commercial tenants'];return ptFields.filter(k=>!k.startsWith('_')&&k!=='HOA').filter(k=>!((occF==='Owner-occupied'||occF==='Vacant')&&tenantKeys.includes(k)));})().map(k=>(
                    <AF key={k} label={k} val={form[k]} onChange={setF(k)}/>
                  ))}
                  {!showMH&&form['Occupancy status']==='Owner + tenant'&&<AF pct label="Occupancy % (owner)" val={form['Occupancy %']} onChange={setF('Occupancy %')}/>}
                </div>

                <div style={s.appToggleRow}>
                  {ptFields.includes('HOA')&&<button style={s.appToggle(hoaChecked)} onClick={()=>togF('HOA')}>{hoaChecked&&<i className="ti ti-check" style={{fontSize:'14px'}}/>}HOA</button>}
                  {ptFields.includes('_garage')&&<button style={s.appToggle(garageChecked)} onClick={()=>togF('Garage')}>{garageChecked&&<i className="ti ti-check" style={{fontSize:'14px'}}/>}Garage</button>}
                  {ptFields.includes('_adu')&&<button style={s.appToggle(aduChecked)} onClick={()=>togF('ADU')}>{aduChecked&&<i className="ti ti-check" style={{fontSize:'14px'}}/>}ADU</button>}
                  {ptFields.includes('_basement')&&<button style={s.appToggle(basementChecked)} onClick={()=>togF('Basement')}>{basementChecked&&<i className="ti ti-check" style={{fontSize:'14px'}}/>}Basement</button>}
                </div>

                {ptFields.includes('_garage')&&garageChecked&&<div style={{...s.dealGrid,marginTop:'14px'}}>
                  <AF label="Garage count" val={form['Garage count']} onChange={setF('Garage count')}/>
                  <ASel label="Garage type" val={form['Garage type']} onChange={setF('Garage type')} options={GARAGE_TYPES}/>
                </div>}
                {ptFields.includes('_adu')&&aduChecked&&<div style={{...s.dealGrid,marginTop:'14px'}}>
                  <ASel label="ADU type" val={form['ADU type']} onChange={setF('ADU type')} options={ADU_TYPES}/>
                  <AF label="ADU sqft" val={form['ADU sqft']} onChange={setF('ADU sqft')}/>
                  <AF money label="ADU monthly rent" val={form['ADU monthly rent']} onChange={setF('ADU monthly rent')}/>
                </div>}
                {ptFields.includes('_basement')&&basementChecked&&<div style={{...s.dealGrid,marginTop:'14px'}}>
                  <ASel label="Basement type" val={form['Basement type']} onChange={setF('Basement type')} options={BASEMENT_TYPES}/>
                  <AF label="Basement sqft" val={form['Basement sqft']} onChange={setF('Basement sqft')}/>
                  <AF money label="Basement rent" val={form['Basement rent']} onChange={setF('Basement rent')}/>
                </div>}
              </div>
            </div>

          </DealSec>
        </div>

        <div style={{...s.secWrap,display:(appSection==='deal'&&hasCollateral)?undefined:'none'}}>
          {(()=>{
            const held = clean(form['Title held by']);
            const showEnt = held==='An entity' || held==='Entity and individuals';
            const showTru = held==='A trust';
            const roleOpts = showTru ? TITLE_ROLES_TRUST : showEnt ? TITLE_ROLES_ENTITY : TITLE_ROLES_INDIV;

            // One list of people for every held-by option. Stored in the existing
            // 'Entity members' JSON column. 'On title' and 'Guarantors' are still
            // written as plain name arrays so anything downstream keeps working.
            const rawPpl = (()=>{ try{ const a=JSON.parse(form['Entity members']||'[]'); return Array.isArray(a)?a:[]; }catch(e){ return []; } })();
            const ppl = rawPpl;
            const savePpl = a => {
              setForm(p=>({
                ...p,
                'Entity members':JSON.stringify(a),
                'On title':JSON.stringify(a.filter(x=>x.onTitle).map(x=>clean(x.name)).filter(Boolean)),
                'Guarantors':JSON.stringify(a.filter(x=>x.guarantor).map(x=>clean(x.name)).filter(Boolean)),
              }));
              markDirty();
            };
            const setPpl = (i,k)=>v=>savePpl(ppl.map((x,j)=>j===i?{...x,[k]:v}:x));
            const pctTotal = ppl.reduce((a,r)=>a+num(r.pct),0);
            const listed = ppl.map(r=>clean(r.name).toLowerCase());
            const missingBorrowers = borrowerList
              .map(b=>({name:clean(b.name),contactId:b.id||''}))
              .filter(b=>b.name&&!listed.includes(b.name.toLowerCase()));
            const cols = showEnt ? '2.2fr 1.5fr 90px 90px 90px 100px' : '2.2fr 1.5fr 90px 90px 100px';
            const slbl = {fontSize:'10.5px',letterSpacing:'.07em',fontWeight:700,color:'#154D97',textTransform:'uppercase',margin:'22px 0 12px',paddingBottom:'6px',borderBottom:'1px solid #EEF3F9'};
            const cellIn = {...s.appInput,height:'32px',fontSize:'12.5px'};
            const cellSel = {...s.appSelect,height:'32px',fontSize:'12.5px'};
            const closeTitleAdd = ()=>{ setTitleAdd(false); setTitleQ(''); setTitleCreating(false); };
            const addTitleRow = (r)=>{ savePpl([...ppl,{name:r.name,contactId:r.contactId||'',role:'',pct:'',onTitle:false,guarantor:false}]); closeTitleAdd(); };
            const ck = (i,k)=>(<label style={{display:'flex',width:'fit-content',alignItems:'center',cursor:'pointer'}}><input type="checkbox" checked={!!ppl[i][k]} onChange={e=>setPpl(i,k)(e.target.checked)} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/></label>);
              return (<DealSec openSec={openSec} setOpenSec={setOpenSec} k="titleinfo" title="Title info" done={!!clean(form['Title held by'])} summary={[clean(form['Title held by'])]}>

            <div style={{display:'flex',flexWrap:'wrap',gap:'15px 18px',alignItems:'flex-end'}}>
              <div style={{width:'230px'}}><ASel label="Title is held by" val={form['Title held by']} onChange={setF('Title held by')} options={TITLE_HELD_BY}/></div>
              <div style={{width:'420px'}}><AF label="Vesting to read as" placeholder="Optional. How title should read at closing." val={form['Vesting to read as']} onChange={setF('Vesting to read as')}/></div>
            </div>

            {showEnt&&<div style={{display:'flex',flexWrap:'wrap',gap:'15px 18px',alignItems:'flex-end',marginTop:'14px'}}>
              <div style={{width:'245px'}}><AF label="Entity name" val={form['Entity name']} onChange={setF('Entity name')}/></div>
              <div style={{width:'150px'}}><ASel label="Entity type" val={form['Entity type']} onChange={setF('Entity type')} options={ENTITY_TYPES_FULL}/></div>
              <div style={{width:'110px'}}><AF label="State" val={form['Entity state']} onChange={setF('Entity state')}/></div>
              <div style={{width:'130px'}}><AF label="EIN" val={form['Entity EIN']} onChange={setF('Entity EIN')}/></div>
              <div style={{width:'150px'}}><AF date label="Formation date" val={form['Entity formation date']} onChange={setF('Entity formation date')}/></div>
            </div>}

            {showTru&&<div style={{display:'flex',flexWrap:'wrap',gap:'15px 18px',alignItems:'flex-end',marginTop:'14px'}}>
              <div style={{width:'245px'}}><AF label="Trust name" val={form['Trust name']} onChange={setF('Trust name')}/></div>
              <div style={{width:'150px'}}><ASel label="Trust type" val={form['Trust type']} onChange={setF('Trust type')} options={TRUST_TYPES}/></div>
              <div style={{width:'150px'}}><AF date label="Date of trust" val={form['Trust date']} onChange={setF('Trust date')}/></div>
            </div>}

            <div style={slbl}>People</div>
            <div style={{border:'1px solid #E9EFF6',borderRadius:'10px'}}>
              <div style={{display:'grid',gridTemplateColumns:cols,gap:'14px',padding:'9px 13px',background:'#F0F5FB',fontSize:'10px',letterSpacing:'.06em',textTransform:'uppercase',color:'#7189B0',fontWeight:700}}>
                <span>Name</span><span>Role</span>{showEnt&&<span>Owns %</span>}<span>On title</span><span>Guarantor</span><span></span>
              </div>
              {ppl.length?ppl.map((pr,i)=>(
                <div key={i} style={{display:'grid',gridTemplateColumns:cols,gap:'14px',padding:'8px 13px',borderTop:'1px solid #EEF3F9',alignItems:'center'}}>
                  {pr.contactId
                    ? <div style={{height:'32px',border:'1px solid #C9D9EE',borderRadius:'8px',background:'#fff',fontSize:'12.5px',color:'#1a2b40',padding:'0 9px',display:'flex',alignItems:'center',gap:'7px',overflow:'hidden'}}>
                        <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{clean(pr.name)}</span>
                        <span style={{fontSize:'9.5px',color:'#154D97',background:'#E4EDF8',borderRadius:'9px',padding:'1px 6px',fontWeight:700,flexShrink:0}}>contact</span>
                      </div>
                    : <input value={clean(pr.name)} onChange={e=>setPpl(i,'name')(e.target.value)} style={cellIn}/>}
                  <select value={clean(pr.role)} onChange={e=>setPpl(i,'role')(e.target.value)} style={cellSel}><option value=""></option>{roleOpts.map(o=><option key={o}>{o}</option>)}</select>
                  {showEnt&&<input value={clean(pr.pct)} onChange={e=>setPpl(i,'pct')(e.target.value)} style={cellIn}/>}
                  {ck(i,'onTitle')}
                  {ck(i,'guarantor')}
                  <button style={{...s.finDel,padding:'6px 0',fontSize:'12px',width:'100%'}} onClick={()=>{if(!window.confirm('Remove '+(clean(pr.name)||'this person')+' from title? Their contact record is not deleted.'))return;savePpl(ppl.filter((_,j)=>j!==i));}}>Remove</button>
                </div>
              )):<div style={{padding:'12px 13px',fontSize:'12.5px',color:'#8A94A6'}}>Nobody added yet.</div>}
              {titleAdd&&<div style={{display:'flex',alignItems:'center',gap:'14px',padding:'8px 13px',borderTop:'1px solid #EEF3F9',position:'relative'}}>
                <div style={{position:'relative',width:'430px',flexShrink:0}}>
                  <input autoFocus value={titleQ} onChange={e=>setTitleQ(e.target.value)} placeholder="Search contacts or type a name" style={{...cellIn,width:'100%',borderColor:'#4A7FBF'}}/>
                  {clean(titleQ)&&<div style={{...s.dpDrop,top:'38px',left:0,width:'430px'}}>
                    {(allContacts||[]).filter(c=>(c.fields?.Name||'').toLowerCase().includes(titleQ.trim().toLowerCase())).slice(0,6).map(c=>(
                      <div key={c.id} style={s.dpOpt} onClick={()=>addTitleRow({name:clean(c.fields?.Name),contactId:c.id})}>
                        {c.fields?.Name}<span style={s.dpOptMeta}>{[(c.fields?.['Role(s)']||[]).join(', '),c.fields?.['Company / entity']].filter(Boolean).join(' \u00B7 ')}</span>
                      </div>
                    ))}
                    <div style={s.dpOptNew} onClick={()=>setTitleCreating(true)}>{'Create new contact: "'+titleQ.trim()+'"'}</div>
                    <div style={{...s.dpOptNew,borderTop:'none',marginTop:0,paddingTop:'7px'}} onClick={()=>addTitleRow({name:titleQ.trim(),contactId:''})}>{'Use "'+titleQ.trim()+'" as a name only'}</div>
                  </div>}
                </div>
                <span style={{marginLeft:'auto',fontSize:'11px',color:'#8A9AB5',cursor:'pointer'}} onClick={closeTitleAdd}>Cancel</span>
              </div>}
              {showEnt&&ppl.length>0&&<div style={{display:'flex',padding:'8px 13px',borderTop:'1px solid #EEF3F9',fontSize:'11.5px',color:'#8A9AB5'}}>
                <span style={{marginLeft:'auto'}}>Ownership totals {Math.round(pctTotal*100)/100}%</span>
              </div>}
            </div>
            <div style={{display:'flex',gap:'9px',alignItems:'center',marginTop:'13px'}}>
              <button style={s.finAdd} onClick={()=>{ if(titleAdd){closeTitleAdd();} else {setTitleAdd(true);} }}>+ Add person</button>
              {missingBorrowers.length>0&&<button style={s.finAdd} onClick={()=>savePpl([...ppl,...missingBorrowers.map(b=>({name:b.name,contactId:b.contactId||'',role:'',pct:'',onTitle:false,guarantor:false}))])}>+ Add borrowers</button>}
            </div>
            {titleAdd&&titleCreating&&<div style={{...s.dpNewBox,marginTop:'12px'}}>
              <InlineCreateContact
                initialName={titleQ}
                onCreate={(rec)=>{ handleContactCreated(rec); addTitleRow({name:clean(rec.fields?.Name)||titleQ.trim(),contactId:rec.id}); }}
                onCancel={()=>setTitleCreating(false)}
              />
            </div>}

            <div id="titleinfo-end"></div>

              </DealSec>);
          })()}
        </div>


        <div style={{...s.ovCard,display:appSection==='reo'?undefined:'none'}}>
          <div style={s.appTitleRow}><div style={s.barLbl}>Real estate owned</div><span style={s.appTag}>feeds SREO</span></div>
          {(()=>{const R=sreoRows||[];const owned=R.filter(r=>r['Status']!=='Sold');const sold=R.filter(r=>r['Status']==='Sold');const sum=(rows,k)=>rows.reduce((a,r)=>a+num(r[k]),0);const linkedDebt=owned.reduce((a,r)=>a+(r._id?debtShareFor(r._id):0),0);
          const reoDti=(r)=>{const mtg=r._id?pmtShareFor(r._id):0;const exp=num(r['Insurance'])+num(r['Property taxes'])+num(r['HOA dues'])+num(r['Other monthly expenses']);const rent=r['Apply rental income to DTI']?num(r['Rental income'])*0.75:0;return mtg+exp-rent;};
          return(<>
          <div style={s.mbBar}>
            <div style={s.mbTile}><div style={s.mbK}>Portfolio value</div><div style={s.mbV}>{fmtK(sum(owned,'Current value'))}</div></div>
            <div style={s.mbDiv}></div>
            <div style={s.mbTile}><div style={s.mbK}>Linked debt</div><div style={s.mbV}>{fmtK(linkedDebt)}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Equity</div><div style={s.mbV}>{fmtK(sum(owned,'Current value')-linkedDebt)}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Monthly rent</div><div style={s.mbV}>{fmtK(sum(owned,'Rental income'))}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Properties</div><div style={s.mbV}>{owned.length}</div></div>
          </div>
          {(()=>{const ownedIdx=[];const soldIdx=[];const newIdx=[];R.forEach((r,i)=>{if(!r._id)newIdx.push(i);else if(r['Status']==='Sold')soldIdx.push(i);else ownedIdx.push(i);});
          const renderReoRow=(r,i)=>{
            const open=!!reoOpen[i];
            const upd=(k,val)=>setSreoRows(prev=>prev.map((x,y)=>y===i?{...x,[k]:val}:x));
            const openRow=()=>{setSreoRows(prev=>prev.map((x,y)=>y===i?{...x,_snap:JSON.stringify(x)}:x));setReoOpen(p=>({...p,[i]:true}));};
            const closeRow=()=>setReoOpen(p=>({...p,[i]:false}));
            const cancelRow=()=>{setSreoRows(prev=>prev.map((x,y)=>{if(y!==i)return x;try{const b=JSON.parse(x._snap||'null');if(b){delete b._snap;return b;}}catch(e){}return x;}));closeRow();};
            const isSold=r['Status']==='Sold';
            const mtg=r._id?pmtShareFor(r._id):0;
            const links=r._id?liabLinksFor(r._id):[];
            const dti=reoDti(r);
            const netProfit=num(r['Sale price'])-num(r['Purchase price'])-num(r['Rehab budget']);
            return(
            <div key={r._uid||i} style={s.reoRow}>
              <div style={{...s.finHead,cursor:'pointer'}} onClick={()=>open?closeRow():openRow()}>
                <span style={s.reoCaret}>{open?'\u25BE':'\u25B8'}</span>
                <span style={s.finC1}>{ownerNameOf(r)||'Not assigned'}</span>
                <span style={{...s.finC2,color:clean(r['Status'])?'#3A6BA8':'#8A5A12'}}>{clean(r['Status'])||'Status needed'}</span>
                <span style={s.finC3}>{clean(r['Property address'])||'New property'}</span>
                <span style={s.finAmt}>{fmtK(isSold?num(r['Sale price']):num(r['Current value']))}{isSold?<span style={s.finSub}>{'Net '+fmtK(netProfit)}</span>:(dti?<span style={s.finSub}>{fmtK(dti)+'/mo in DTI'}</span>:null)}</span>
              </div>
              {open&&<div style={s.reoBody}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'24px'}}>
                  <div style={{borderRight:'1px solid #EEF3F9',paddingRight:'22px'}}>
                    <div style={s.finColH}>PROPERTY</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px'}}>
                      <ASel label="Status" val={r['Status']} onChange={v=>upd('Status',v)} options={SREO_STATUSES}/>
                      {!isSold&&<ASel label="Use" val={r['Use']} onChange={v=>upd('Use',v)} options={SREO_USES}/>}
                    </div>
                    <div style={{marginTop:'13px'}}><AF label="Property address" val={r['Property address']} onChange={v=>upd('Property address',v)}/></div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px',marginTop:'13px'}}>
                      <ASel label="Property type" val={r['Property type']} onChange={v=>upd('Property type',v)} options={SREO_PROP_TYPES}/>
                      {!isSold&&<AF money label="Current value" val={r['Current value']} onChange={v=>upd('Current value',v)}/>}
                      {isSold&&<AF date label="Date sold" val={r['Date sold']} onChange={v=>upd('Date sold',v)}/>}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px',marginTop:'13px'}}>
                      <AF money label="Purchase price" val={r['Purchase price']} onChange={v=>upd('Purchase price',v)}/>
                      <AF date label="Purchase date" val={r['Purchase date']} onChange={v=>upd('Purchase date',v)}/>
                    </div>
                    {isSold&&<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px',marginTop:'13px'}}>
                      <AF money label="Sale price" val={r['Sale price']} onChange={v=>upd('Sale price',v)}/>
                    </div>}
                    {!isSold&&<div style={{marginTop:'13px'}}><AF money label="Rental income" val={r['Rental income']} onChange={v=>upd('Rental income',v)}/></div>}
                    {!isSold&&<label style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'12.5px',color:'#33415A',cursor:'pointer',marginTop:'11px'}}><input type="checkbox" checked={!!r['Apply rental income to DTI']} onChange={e=>upd('Apply rental income to DTI',e.target.checked)} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/>Apply rental income to DTI (credits 75%)</label>}
                    {ownerSelEl(r,upd)?<div style={{marginTop:'13px'}}>{ownerSelEl(r,upd)}</div>:null}
                  </div>
                  <div>
                    <div style={s.finColH}>LINKED LIABILITIES</div>
                    <div style={{border:'1px solid #C5D5EA',borderRadius:'8px',background:'#fff',fontSize:'12px',overflow:'hidden'}}>
                      <div style={{display:'grid',gridTemplateColumns:'1.1fr .9fr .6fr .9fr',gap:'9px',padding:'8px 11px',background:'#EAF1FA',color:'#3A6BA8',fontWeight:600}}><span>Borrower</span><span>Payment</span><span>Lien</span><span>Creditor</span></div>
                      {links.length?links.map((l,li)=>(<div key={li} style={{display:'grid',gridTemplateColumns:'1.1fr .9fr .6fr .9fr',gap:'9px',padding:'8px 11px',borderTop:'1px solid #EEF3F9'}}><span>{ownerNameOf(l)}</span><span>{fmtK(num(l['Monthly payment'])/Math.max((l['Linked property']||[]).length,1))}</span><span>{clean(l['Lien position'])||'-'}</span><span>{clean(l['Creditor'])||'Debt'}</span></div>)):<div style={{padding:'9px 11px',color:'#8A94A6'}}>No liability linked to this property yet.</div>}
                    </div>
                    <div style={{fontSize:'11.5px',color:'#8A94A6',margin:'7px 0 17px'}}>Read only. The liability owns the link.</div>
                    <div style={s.finColH}>MONTHLY EXPENSES</div>
                    <div style={s.finMRow}><span style={{flex:1,color:'#8A94A6'}}>First mortgage (linked)</span><span style={{fontWeight:600}}>{fmtK(mtg)}</span></div>
                    <div style={s.finMRow}><span style={{flex:1}}>Insurance</span><div style={{width:'128px'}}><AF money val={r['Insurance']} onChange={v=>upd('Insurance',v)}/></div></div>
                    <div style={s.finMRow}><span style={{flex:1}}>Property taxes</span><div style={{width:'128px'}}><AF money val={r['Property taxes']} onChange={v=>upd('Property taxes',v)}/></div></div>
                    <div style={s.finMRow}><span style={{flex:1}}>HOA dues</span><div style={{width:'128px'}}><AF money val={r['HOA dues']} onChange={v=>upd('HOA dues',v)}/></div></div>
                    <div style={s.finMRow}><span style={{flex:1}}>Other</span><div style={{width:'128px'}}><AF money val={r['Other monthly expenses']} onChange={v=>upd('Other monthly expenses',v)}/></div></div>
                    <div style={s.finBand}><span>Total in DTI</span><span>{fmtK(dti)}</span></div>
                  </div>
                </div>
                <div style={{display:'flex',gap:'9px',alignItems:'center',marginTop:'15px'}}>
                  <button style={s.finSave} onClick={closeRow}>Save</button>
                  <button style={s.finCancel} onClick={cancelRow}>Cancel</button>
                  <button style={s.finDel} onClick={()=>{if(r._id)setSreoDeleted(d=>[...d,r._id]);setReoOpen(p=>{const n={};Object.keys(p).forEach(k=>{const x=Number(k);if(x<i)n[x]=p[k];else if(x>i)n[x-1]=p[k];});return n;});setSreoRows(prev=>prev.filter((_,x)=>x!==i));}}>Delete</button>
                </div>
              </div>}
            </div>);
          };return(<>
          {ownedIdx.map(i=>renderReoRow(R[i],i))}
          {newIdx.map(i=>renderReoRow(R[i],i))}
          <div style={{display:'flex',alignItems:'center',marginTop:'12px'}}>
            <button style={s.finAdd} onClick={()=>{const idx=(sreoRows||[]).length;setSreoRows(prev=>[...(prev||[]),{'Property address':'','Property type':'','Use':'','Purchase date':'','Purchase price':'','Rehab budget':'','Current value':'','Rental income':'','Lender name':'','Status':'','Date rented':'','Sale price':'','Date sold':'','Insurance':'','Property taxes':'','HOA dues':'','Other monthly expenses':'','Apply rental income to DTI':false,'Contact':[],_id:null,_isNew:true,_uid:newUid()}]);setReoOpen(p=>({...p,[idx]:true}));}}>+ Real estate</button>
            <span style={s.finTot}>Total equity {fmtK(sum(owned,'Current value')-linkedDebt)}</span>
          </div>
          <div style={{background:'#E4EDF8',borderRadius:'8px',padding:'6px 12px',fontSize:'10.5px',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'#154D97',margin:'18px 0 8px',display:'flex',alignItems:'center'}}><span>Sold / past transactions{soldIdx.length?'':' \u00B7 none on file'}</span><span style={{marginLeft:'auto',cursor:'pointer',fontWeight:600,letterSpacing:0,textTransform:'none',fontSize:'12px'}} onClick={()=>{const idx=(sreoRows||[]).length;setSreoRows(prev=>[...(prev||[]),{'Property address':'','Property type':'','Use':'','Purchase date':'','Purchase price':'','Rehab budget':'','Current value':'','Rental income':'','Lender name':'','Status':'Sold','Date rented':'','Sale price':'','Date sold':'','Insurance':'','Property taxes':'','HOA dues':'','Other monthly expenses':'','Apply rental income to DTI':false,'Contact':[],_id:null,_isNew:true,_uid:newUid()}]);setReoOpen(p=>({...p,[idx]:true}));}}>+ Add sold</span></div>
          {soldIdx.map(i=>renderReoRow(R[i],i))}
          </>);})()}
          </>);})()}
          <button style={s.appAddBtn} onClick={()=>{const idx=(sreoRows||[]).length;setSreoRows(prev=>[...(prev||[]),{'Property address':'','City':'','State':'','Zip':'','Property type':'','Use':'','Purchase date':'','Purchase price':'','Rehab budget':'','Current value':'','Loan balance':'','Monthly payment':'','Rental income':'','Lender name':'','Status':'','Date rented':'','Sale price':'','Date sold':'',_id:null,_isNew:true,_uid:newUid()}]);setReoOpen(p=>({...p,[idx]:true}));}}><i className="ti ti-plus" style={{fontSize:'14px',verticalAlign:'-2px',marginRight:'4px'}}/>Add property</button>
        </div>

        <div style={{...s.ovCard,display:appSection==='assets'?undefined:'none'}}>
          <div style={s.appTitleRow}><div style={s.barLbl}>Assets</div><span style={s.appTag}>feeds PFS</span></div>
          {(()=>{const R=assetRows||[];const total=R.reduce((a,r)=>a+num(r['Balance']),0);const liquid=R.filter(r=>ASSET_LIQUID.includes(r['Asset type'])).reduce((a,r)=>a+num(r['Balance']),0);const verTot=R.filter(r=>!!r['Verified']).reduce((a,r)=>a+num(r['Balance']),0);return(<>
          <div style={s.mbBar}>
            <div style={s.mbTile}><div style={s.mbK}>Total assets</div><div style={s.mbV}>{fmtK(total)}</div></div>
            <div style={s.mbDiv}></div>
            <div style={s.mbTile}><div style={s.mbK}>Liquid</div><div style={s.mbV}>{fmtK(liquid)}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Verified</div><div style={s.mbV}>{total?fmtK(verTot)+' of '+fmtK(total):'-'}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Accounts</div><div style={s.mbV}>{R.length}</div></div>
          </div>
          {R.map((r,i)=>{
            const open=!!assetOpen[i];
            const upd=(k,val)=>setAssetRows(prev=>prev.map((x,y)=>y===i?{...x,[k]:val}:x));
            const openRow=()=>{setAssetRows(prev=>prev.map((x,y)=>y===i?{...x,_snap:JSON.stringify(x)}:x));setAssetOpen(p=>({...p,[i]:true}));};
            const closeRow=()=>setAssetOpen(p=>({...p,[i]:false}));
            const cancelRow=()=>{setAssetRows(prev=>prev.map((x,y)=>{if(y!==i)return x;try{const b=JSON.parse(x._snap||'null');if(b){delete b._snap;return b;}}catch(e){}return x;}));closeRow();};
            const typeOpts=(!clean(r['Asset type'])||ASSET_TYPES.includes(clean(r['Asset type'])))?ASSET_TYPES:[...ASSET_TYPES,clean(r['Asset type'])];
            return (
            <div key={r._uid||i} style={s.reoRow}>
              <div style={{...s.finHead6,cursor:'pointer'}} onClick={()=>open?closeRow():openRow()}>
                <span style={s.reoCaret}>{open?'\u25BE':'\u25B8'}</span>
                <span style={s.finC1}>{ownerNameOf(r)||'Not assigned'}</span>
                <span style={s.finC2}>{clean(r['Asset type'])||'No type'}</span>
                <span style={s.finC3}>{clean(r['Institution name'])||'New account'}</span>
                <span style={s.finC3}>{clean(r['Account last 4'])}</span>
                <span style={s.finAmt}>{fmtK(num(r['Balance']))}{r['Verified']?<span style={s.finSub}>Verified</span>:null}</span>
              </div>
              {open&&<div style={s.reoBody}>
                <div style={{display:'flex',gap:'14px',flexWrap:'wrap'}}>
                  <div style={{width:'200px'}}><ASel label="Asset type" val={r['Asset type']} onChange={v=>upd('Asset type',v)} options={typeOpts}/></div>
                  <div style={{width:'190px'}}><AF label="Institution" val={r['Institution name']} onChange={v=>upd('Institution name',v)}/></div>
                  <div style={{width:'150px'}}><AF money label="Value" val={r['Balance']} onChange={v=>upd('Balance',v)}/></div>
                  <div style={{width:'130px'}}><AF label="Account last 4" val={r['Account last 4']} onChange={v=>upd('Account last 4',v)}/></div>
                  <label style={s.rowChk}><input type="checkbox" checked={!!r['Verified']} onChange={e=>upd('Verified',e.target.checked)} style={{width:'16px',height:'16px',accentColor:'#1C4E8A',cursor:'pointer'}}/>Verified</label>
                </div>
                {ownerSelEl(r,upd)?<div style={{marginTop:'14px'}}>{ownerSelEl(r,upd)}</div>:null}
                <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'13px 16px',marginTop:'13px'}}>
                  <AF label="Description" placeholder="e.g. 60% member interest, 2023 F-150" val={r['Description']} onChange={v=>upd('Description',v)}/>
                  {!!r['Verified']&&<AF date label="Verification date" val={r['Verification date']} onChange={v=>upd('Verification date',v)}/>}
                </div>
                <div style={{display:'flex',gap:'9px',alignItems:'center',marginTop:'15px'}}>
                  <button style={s.finSave} onClick={closeRow}>Save</button>
                  <button style={s.finCancel} onClick={cancelRow}>Cancel</button>
                  <button style={s.finDel} onClick={()=>{if(r._id)setAssetDeleted(d=>[...d,r._id]);setAssetOpen(p=>{const n={};Object.keys(p).forEach(k=>{const x=Number(k);if(x<i)n[x]=p[k];else if(x>i)n[x-1]=p[k];});return n;});setAssetRows(prev=>prev.filter((_,x)=>x!==i));}}>Delete</button>
                </div>
              </div>}
            </div>);
          })}
          <div style={{display:'flex',alignItems:'center',marginTop:'12px'}}>
            <button style={s.finAdd} onClick={()=>{const idx=(assetRows||[]).length;setAssetRows(prev=>[...(prev||[]),{'Institution name':'','Asset type':'','Account last 4':'','Balance':'','Description':'','Verified':false,'Verification date':'','Contact':[],_id:null,_isNew:true,_uid:newUid()}]);setAssetOpen(p=>({...p,[idx]:true}));}}>+ Asset</button>
            <span style={s.finTot}>Total assets {fmtK(total)}</span>
          </div>
          </>);})()}
        </div>

        <div style={{...s.ovCard,display:appSection==='liabilities'?undefined:'none'}}>
          <div style={s.appTitleRow}><div style={s.barLbl}>Liabilities</div><span style={s.appTag}>feeds PFS</span></div>
          {(()=>{const R=liabRows||[];const ownedProps=(sreoRows||[]).filter(x=>x['Status']!=='Sold'&&x._id);const propName=pid=>{const p=(sreoRows||[]).find(x=>x._id===pid);return p?(clean(p['Property address'])||'Property'):'Property';};const isLinked=r=>Array.isArray(r['Linked property'])&&r['Linked property'].length>0;const tb=R.reduce((a,r)=>a+num(r['Balance']),0);const tm=R.reduce((a,r)=>a+num(r['Monthly payment']),0);const net=totalAllAssets+sreoOwnedValue-tb;
          const countsInDti=r=>!r['Will be paid off']&&!r['Omitted']&&!r['Exclude from DTI']&&!isLinked(r);
          const dtiTot=R.filter(countsInDti).reduce((a,r)=>a+num(r['Monthly payment']),0);
          return(<>
          <div style={s.mbBar}>
            <div style={s.mbTile}><div style={s.mbK}>Total liabilities</div><div style={s.mbV}>{fmtK(tb)}</div></div>
            <div style={s.mbDiv}></div>
            <div style={s.mbTile}><div style={s.mbK}>Monthly obligations</div><div style={s.mbV}>{fmtK(tm)}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Counted in DTI</div><div style={s.mbV}>{fmtK(dtiTot)+'/mo'}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Accounts</div><div style={s.mbV}>{R.length}</div></div>
          </div>
          {R.map((r,i)=>{
            const open=!!liabOpen[i];
            const upd=(k,val)=>setLiabRows(prev=>prev.map((x,y)=>y===i?{...x,[k]:val}:x));
            const updM=(obj)=>setLiabRows(prev=>prev.map((x,y)=>y===i?{...x,...obj}:x));
            const openRow=()=>{setLiabRows(prev=>prev.map((x,y)=>y===i?{...x,_snap:JSON.stringify(x)}:x));setLiabOpen(p=>({...p,[i]:true}));};
            const closeRow=()=>setLiabOpen(p=>({...p,[i]:false}));
            const cancelRow=()=>{setLiabRows(prev=>prev.map((x,y)=>{if(y!==i)return x;try{const b=JSON.parse(x._snap||'null');if(b){delete b._snap;return b;}}catch(e){}return x;}));closeRow();};
            const links=Array.isArray(r['Linked property'])?r['Linked property']:[];
            const aLinks=Array.isArray(r['Linked asset'])?r['Linked asset']:[];
            const assetName=aid=>{const a=(assetRows||[]).find(x=>x._id===aid);return a?(clean(a['Description'])||clean(a['Institution name'])||clean(a['Asset type'])||'Asset'):'Asset';};
            const secAssets=(assetRows||[]).filter(a=>a._id&&(!ASSET_LIQUID.includes(a['Asset type'])||aLinks.includes(a._id)));
            const isMtg=/mortgage|heloc|blanket/i.test(clean(r['Type'])||'');
            const disp=r['Will be paid off']?'Paid at close':(r['Omitted']?'Omitted':(r['Exclude from DTI']?'Excluded':''));
            const sublineTxt=links.length?('counts via '+propName(links[0])):(disp?disp:(num(r['Balance'])>0?fmtK(num(r['Balance']))+' balance':''));
            const ownerEl=ownerSelEl(r,upd);
            return(
            <div key={r._uid||i} style={s.reoRow}>
              <div style={{...s.finHead,cursor:'pointer',opacity:disp?0.6:1}} onClick={()=>open?closeRow():openRow()}>
                <span style={s.reoCaret}>{open?'\u25BE':'\u25B8'}</span>
                <span style={s.finC1}>{ownerNameOf(r)||'Not assigned'}</span>
                <span style={s.finC2}>{clean(r['Type'])||'No type'}</span>
                <span style={s.finC3}>{clean(r['Creditor'])||'New liability'}</span>
                <span style={s.finAmt}>{fmtK(num(r['Monthly payment']))+'/mo'}{sublineTxt?<span style={s.finSub}>{sublineTxt}</span>:null}</span>
              </div>
              {open&&<div style={s.reoBody}>
                {(()=>{
                  const shape = LIAB_SIMPLE.includes(clean(r['Type'])) ? 'C' : (LIAB_SECURED.includes(clean(r['Type'])) ? 'A' : 'B');
                  const CBX=({k,label})=>(<label style={{display:'flex',width:'fit-content',alignItems:'center',gap:'8px',fontSize:'12.5px',color:'#33415A',cursor:'pointer'}}><input type="checkbox" checked={!!r[k]} onChange={e=>upd(k,e.target.checked)} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/>{label}</label>);
                  const typeSel = <ASel label="Liability type" val={r['Type']} onChange={v=>upd('Type',v)} options={(!clean(r['Type'])||LIABILITY_TYPES.includes(clean(r['Type'])))?LIABILITY_TYPES:[...LIABILITY_TYPES,clean(r['Type'])]}/>;
                  const owners = ownerEl ? (<div style={{marginTop:'14px'}}>{ownerEl}</div>) : null;

                  if(shape==='C') return (<React.Fragment>
                    <div style={{display:'flex',gap:'15px',flexWrap:'wrap'}}>
                      <div style={{width:'220px'}}>{typeSel}</div>
                      <div style={{width:'150px'}}><AF money label="Monthly payment" val={r['Monthly payment']} onChange={v=>upd('Monthly payment',v)}/></div>
                      <div style={{width:'150px'}}><AF label="Remaining months" placeholder="Optional" val={r['Months left']} onChange={v=>upd('Months left',v)}/></div>
                      {LIAB_DESC.includes(clean(r['Type']))&&<div style={{width:'240px'}}><AF label="Description" placeholder="Optional" val={r['Liability description']} onChange={v=>upd('Liability description',v)}/></div>}
                      <div style={s.rowChk}>{CBX({k:'Exclude from DTI',label:'Exclude from DTI'})}</div>
                    </div>
                    {owners}
                  </React.Fragment>);

                  return (<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'24px'}}>
                    <div style={{borderRight:'1px solid #EEF3F9',paddingRight:'22px'}}>
                      <div style={s.finColH}>LIABILITY DETAILS</div>
                      {typeSel}
                      {owners}
                      {shape==='A'&&<React.Fragment>
                        <div style={{marginTop:'14px'}}>
                          <div style={s.appLbl}>Select REO address</div>
                          <select style={s.appSelect} value={links.length?links[0]:''} onChange={e=>{const v=e.target.value;updM(v?{'Linked property':[v],'Linked asset':[]}:{'Linked property':[]});}}>
                            <option value="">Not linked to a property</option>
                            {ownedProps.map(p=><option key={p._id} value={p._id}>{clean(p['Property address'])||'Property'}</option>)}
                          </select>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px',marginTop:'14px'}}>
                          <ASel label="Mortgage type" val={r['Mortgage type']} onChange={v=>upd('Mortgage type',v)} options={MORTGAGE_TYPES}/>
                          <ASel label="Lien position" val={r['Lien position']} onChange={v=>upd('Lien position',v)} options={LIEN_POSITIONS}/>
                        </div>
                      </React.Fragment>}
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px',marginTop:'14px'}}>
                        <AF money label="Unpaid balance" val={r['Balance']} onChange={v=>upd('Balance',v)}/>
                        <AF money label="Monthly payment" val={r['Monthly payment']} onChange={v=>upd('Monthly payment',v)}/>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px',marginTop:'14px'}}>
                        <AF label="Remaining months" placeholder="Optional" val={r['Months left']} onChange={v=>upd('Months left',v)}/>
                        <AF label="Account number" placeholder="Optional" val={r['Account number']} onChange={v=>upd('Account number',v)}/>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'13px 15px',marginTop:'14px'}}>
                        <ASel label="Account status" val={r['Account status']} onChange={v=>upd('Account status',v)} options={ACCOUNT_STATUSES}/>
                        <ASel label="Current rating" val={r['Current rating']} onChange={v=>upd('Current rating',v)} options={CURRENT_RATINGS}/>
                      </div>
                      {LIAB_LINE.includes(clean(r['Type']))&&<div style={{marginTop:'14px',maxWidth:'50%'}}><AF money label="Line amount" val={r['Line amount']} onChange={v=>upd('Line amount',v)}/></div>}
                      {LIAB_DESC.includes(clean(r['Type']))&&<div style={{marginTop:'14px'}}><AF label="Liability description" placeholder="Optional" val={r['Liability description']} onChange={v=>upd('Liability description',v)}/></div>}
                      {shape==='B'&&<div style={{marginTop:'14px',maxWidth:'340px'}}>
                        <div style={s.appLbl}>Secured by</div>
                        <select style={s.appSelect} value={aLinks.length?('a:'+aLinks[0]):(links.length?('p:'+links[0]):'')} onChange={e=>{const v=e.target.value;if(!v)updM({'Linked property':[],'Linked asset':[]});else if(v.indexOf('p:')===0)updM({'Linked property':[v.slice(2)],'Linked asset':[]});else updM({'Linked property':[],'Linked asset':[v.slice(2)]});}}>
                          <option value="">Nothing / unsecured</option>
                          {ownedProps.map(p=><option key={p._id} value={'p:'+p._id}>{clean(p['Property address'])||'Property'}</option>)}
                          {secAssets.map(a=><option key={a._id} value={'a:'+a._id}>{assetName(a._id)}</option>)}
                        </select>
                      </div>}
                    </div>

                    <div>
                      <div style={s.finColH}>CREDITOR</div>
                      <AF label="Creditor name" val={r['Creditor']} onChange={v=>upd('Creditor',v)}/>
                      <div style={{display:'grid',gridTemplateColumns:'2.6fr 0.8fr',gap:'13px 15px',marginTop:'14px'}}>
                        <AF label="Street address" placeholder="Optional" val={r['Creditor street']} onChange={v=>upd('Creditor street',v)}/>
                        <AF label="Unit / Apt" val={r['Creditor unit']} onChange={v=>upd('Creditor unit',v)}/>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1.4fr 0.8fr 0.9fr 1fr',gap:'13px 15px',marginTop:'13px'}}>
                        <AF label="City" val={r['Creditor city']} onChange={v=>upd('Creditor city',v)}/>
                        <AF label="State" val={r['Creditor state']} onChange={v=>upd('Creditor state',v)}/>
                        <AF label="Zip" val={r['Creditor zip']} onChange={v=>{upd('Creditor zip',v);if(String(v||'').replace(/\D/g,'').length===5){lookupZip(v).then(x=>{if(!x)return;updM({'Creditor zip':v,'Creditor city':x.city,'Creditor state':x.state,'Creditor county':x.county||''});});}}}/>
                        <AF label="County" val={r['Creditor county']} onChange={v=>upd('Creditor county',v)}/>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1.6fr 1.2fr 0.6fr',gap:'13px 15px',marginTop:'13px'}}>
                        <AF label="Email" val={r['Creditor email']} onChange={v=>upd('Creditor email',v)}/>
                        <AF phone label="Work phone" val={r['Creditor phone']} onChange={v=>upd('Creditor phone',v)}/>
                        <AF label="Ext" val={r['Creditor ext']} onChange={v=>upd('Creditor ext',v)}/>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'11px 18px',marginTop:'16px'}}>
                        {CBX({k:'Will be paid off',label:'Will be paid off'})}
                        {CBX({k:'Derogatory',label:'Derogatory'})}
                        {CBX({k:'Omitted',label:'Omitted'})}
                        {CBX({k:'Exclude from DTI',label:'Exclude from DTI'})}
                        {shape==='A'&&CBX({k:'HOI escrowed',label:'HOI escrowed'})}
                        {shape==='A'&&CBX({k:'Re-subordinate',label:'Re-subordinate'})}
                        {shape==='A'&&CBX({k:'Property taxes escrowed',label:'Property taxes escrowed'})}
                      </div>
                    </div>
                  </div>);
                })()}
                <div style={{display:'flex',gap:'9px',alignItems:'center',marginTop:'16px'}}>
                  <button style={s.finSave} onClick={closeRow}>Save</button>
                  <button style={s.finCancel} onClick={cancelRow}>Cancel</button>
                  <button style={s.finDel} onClick={()=>deleteLiabRow(i)}>Delete</button>
                </div>
              </div>}
            </div>);
          })}
          <div style={{display:'flex',alignItems:'center',marginTop:'12px'}}>
            <button style={s.finAdd} onClick={()=>{const idx=(liabRows||[]).length;setLiabRows(prev=>[...(prev||[]),{'Creditor':'','Type':'','Balance':'','Monthly payment':'','Months left':'','Secured by':'','Disposition':'','Mortgage type':'','Lien position':'','Derogatory':false,'Linked property':[],'Linked asset':[],'Contact':[],_id:null,_isNew:true,_uid:newUid()}]);setLiabOpen(p=>({...p,[idx]:true}));}}>+ Liability</button>
            <span style={s.finTot}>Counted in DTI {fmtK(dtiTot)}/mo</span>
          </div>
          <div style={{...s.subHint,marginTop:'10px'}}>Every debt lives here. A mortgage linked to a property counts inside that property, not twice. Disposition left blank means it counts.</div>
          </>);})()}
        </div>

        <div style={{...s.ovCard,display:appSection==='income'?undefined:'none'}}>
          <div style={s.appTitleRow}><div style={s.barLbl}>Income & Employment</div><span style={s.appTag}>feeds PFS</span></div>
          {(()=>{const R=incRows||[];const annOf=r=>incIsPrev(r)?0:num(r['Annual amount'])*((r['Frequency']||'Annually')==='Monthly'?12:1);const typedAnn=R.reduce((a,r)=>a+annOf(r),0);const rentals=(sreoRows||[]).filter(x=>x['Status']!=='Sold'&&num(x['Rental income'])>0);const rentNetM=rentals.reduce((a,x)=>a+(num(x['Rental income'])-(x._id?pmtShareFor(x._id):0)),0);const totAnn=typedAnn+rentNetM*12;const proj=(()=>{const n=(sreoRows||[]).filter(x=>x['Status']==='Sold').length;return n?n+' sold':'-';})();return(<>
          <div style={s.mbBar}>
            <div style={s.mbTile}><div style={s.mbK}>Total annual</div><div style={s.mbV}>{fmtX(totAnn)}</div></div>
            <div style={s.mbDiv}></div>
            <div style={s.mbTile}><div style={s.mbK}>Total monthly</div><div style={s.mbV}>{fmtX(totAnn/12)}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Verified</div><div style={s.mbV}>{totAnn?fmtX(R.filter(x=>!!x['Verified']).reduce((a,x)=>a+annOf(x),0))+' of '+fmtX(totAnn):'-'}</div></div>
            <div style={s.mbTile}><div style={s.mbK}>Sources</div><div style={s.mbV}>{R.length+(rentals.length?1:0)}</div></div>
            {finCId&&<div style={s.mbTile}><div style={s.mbK}>{finBFirst}</div><div style={s.mbV}>{fmtX(R.filter(x=>ownerKeyOf(x)==='b').reduce((a,x)=>a+annOf(x),0))}</div></div>}
            {finCId&&<div style={s.mbTile}><div style={s.mbK}>{finCFirst}</div><div style={s.mbV}>{fmtX(R.filter(x=>ownerKeyOf(x)==='co').reduce((a,x)=>a+annOf(x),0))}</div></div>}
          </div>
          {(()=>{const renderIncRow=(r,i)=>{const open=!!incOpen[i];const upd=(k,val)=>setIncRows(prev=>prev.map((x,y)=>y===i?{...x,[k]:val}:x));const updMoney=(k,val)=>setIncRows(prev=>prev.map((x,y)=>{if(y!==i)return x;const n2={...x,[k]:val};if(k==='Annual base')n2['Monthly base']=String(Math.round((num(val)/12)*100)/100);if(k==='Monthly base')n2['Annual base']=String(Math.round(num(val)*12*100)/100);const mil=String(n2['Type']||'')==='Military employment income';const hr=String(n2['Pay type']||'')==='Hourly';const base=hr?num(n2['Hourly rate'])*num(n2['Hours per week'])*52/12:num(n2['Monthly base']);const tot=mil?['Military base income','Military clothes allowance','Military combat pay','Military flight pay','Military hazard pay','Military overseas pay','Military prop pay','Military quarters allowance','Military rations allowance','Military variable housing allowance'].reduce((a,k)=>a+num(n2[k]),0):(base+num(n2['Bonus monthly'])+num(n2['Commission monthly'])+num(n2['Overtime monthly'])+num(n2['Other monthly']));n2['Annual amount']=String(Math.round(tot*100)/100);n2['Frequency']='Monthly';n2['Monthly total']=n2['Annual amount'];return n2;}));const mo=(r['Frequency']||'Annually')==='Monthly';const sub=[clean(r['Type']),num(r['Annual amount'])>0?fmtK(num(r['Annual amount']))+(mo?' /mo':' /yr'):'',r['Verified']?'Verified':''].filter(Boolean).join(' \u00B7 ');return(
            <div key={r._uid||i} style={s.reoRow}>
              <div style={{...s.finHeadI,cursor:'pointer',opacity:incIsPrev(r)?0.6:1}} onClick={()=>setIncOpen(p=>({...p,[i]:!p[i]}))}>
                <span style={s.reoCaret}>{open?'\u25BE':'\u25B8'}</span>
                <span style={s.finC1}>{ownerNameOf(r)||'Not assigned'}</span>
                <span style={s.finC2}>{clean(r['Type'])||'No type'}</span>
                <span style={s.finC3}>{clean(r['Source'])||clean(r['Other description'])||'New source'}</span>
                <span style={s.finC3}>{(()=>{const st=clean(r['Start date']),en=clean(r['End date']);const mmYY=d=>{const m2=String(d||'').match(/^(\d{1,2})\/\d{1,2}\/(\d{2,4})$/);return m2?(String(m2[1]).padStart(2,'0')+'/'+(m2[2].length===2?'20'+m2[2]:m2[2])):String(d||'');};if(!st&&!en)return '';if(st&&!en)return mmYY(st)+' \u2013 present';if(!st&&en)return 'to '+mmYY(en);return mmYY(st)+' \u2013 '+mmYY(en);})()}</span>
                <span style={s.finAmt}>{fmtX(num(r['Annual amount']))+'/mo'}{incIsPrev(r)?<span style={s.finSub}>history only</span>:(r['Verified']?<span style={s.finSub}>Verified</span>:null)}</span>
              </div>
              {open&&<div style={s.reoBody}>
                {(()=>{
                  const isEmp=INC_EMP.includes(clean(r['Type']));
                  const typeOpts=(!clean(r['Type'])||INCOME_TYPES.includes(clean(r['Type'])))?INCOME_TYPES:[...INCOME_TYPES,clean(r['Type'])];
                  const CB2=({on,label,onChange})=>(<label style={{display:'flex',width:'fit-content',alignItems:'center',gap:'7px',fontSize:'12.5px',color:'#33415A',cursor:'pointer'}}><input type="checkbox" checked={!!on} onChange={e=>onChange(e.target.checked)} style={{width:'15px',height:'15px',accentColor:'#154D97',cursor:'pointer'}}/>{label}</label>);
                  const MROW=({label,fk,ph})=>(<div style={{display:'flex',alignItems:'center',gap:'10px',padding:'7px 0',borderBottom:'1px solid #EEF3F9'}}><div style={{flex:1,fontSize:'12.5px',color:'#33415A'}}>{label}</div>{ph?<div style={{width:'110px'}}><AF val={r['Other description']} onChange={v=>upd('Other description',v)} placeholder="Describe"/></div>:null}<div style={{width:'120px'}}><AF money val={r[fk]} onChange={v=>updMoney(fk,v)}/></div></div>);
                  if(!isEmp) return (<div>
                  <div style={s.dealFlow}>
                    <ASel w="220px" label="Income type" val={r['Type']} onChange={v=>upd('Type',v)} options={typeOpts}/>
                    <AF w="260px" label="Description" val={r['Other description']} onChange={v=>upd('Other description',v)}/>
                    <AF w="140px" money label="Monthly amount" val={r['Annual amount']} onChange={v=>{upd('Annual amount',v);upd('Frequency','Monthly');}}/>
                    <label style={s.rowChk}><input type="checkbox" checked={!!r['Verified']} onChange={e=>upd('Verified',e.target.checked)} style={{width:'16px',height:'16px',accentColor:'#1C4E8A',cursor:'pointer'}}/> Verified</label>
                  </div>
                  {ownerSelEl(r,upd)?<div style={{marginTop:'14px'}}>{ownerSelEl(r,upd)}</div>:null}
                  </div>);
                  const hourly=clean(r['Pay type'])==='Hourly';
                  const isMil=clean(r['Type'])==='Military employment income';
                  return (<div>
                    <div style={{maxWidth:'320px'}}>
                      <ASel label="Income type" val={r['Type']} onChange={v=>upd('Type',v)} options={typeOpts}/>
                    </div>
                    <div style={{display:'flex',gap:'26px',margin:'12px 0 4px'}}>
                      {CB2({on:r['Current'],label:"Current",onChange:v=>upd('Current',v)})}
                      {CB2({on:r['Primary'],label:"Primary",onChange:v=>upd('Primary',v)})}
                    </div>
                    <div style={{marginTop:'12px'}}>{ownerSelEl(r,upd)}</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'22px',marginTop:'12px'}}>
                      <div style={{borderRight:'1px solid #EEF3F9',paddingRight:'20px'}}>
                        <div style={{fontSize:'10.5px',fontWeight:700,letterSpacing:'.06em',color:'#154D97',marginBottom:'10px'}}>EMPLOYMENT DETAILS</div>
                        <div style={{marginTop:'0'}}><AF label="Employer or business name" val={r['Source']} onChange={v=>upd('Source',v)}/></div>
                        <div style={{marginTop:'12px'}}><AF label="Employer address (one line)" val={r['Employer street']} onChange={v=>upd('Employer street',v)}/></div>
                        <div style={{display:'grid',gridTemplateColumns:'1.4fr 1.2fr 0.6fr',gap:'12px 14px',marginTop:'12px'}}>
                          <AF label="Position or title" val={r['Position or title']} onChange={v=>upd('Position or title',v)}/>
                          <AF phone label="Employer contact no." val={r['Employer phone']} onChange={v=>upd('Employer phone',v)}/>
                          <AF label="Ext" val={r['Employer ext']} onChange={v=>upd('Employer ext',v)}/>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 14px',marginTop:'12px'}}>
                          <AF date label="Start date" val={r['Start date']} onChange={v=>upd('Start date',v)} placeholder="mm/dd/yyyy"/>
                          <AF date label="End date (previous only)" val={r['End date']} onChange={v=>upd('End date',v)} placeholder="mm/dd/yyyy"/>
                        </div>
                        <div style={{marginTop:'12px'}}>
                          <div style={s.appLbl}>Employment duration in this line of work</div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 14px',maxWidth:'260px'}}>
                            <AF placeholder="years" val={r['Years in line of work']} onChange={v=>upd('Years in line of work',v)}/>
                            <AF placeholder="months" val={r['Months in line of work']} onChange={v=>upd('Months in line of work',v)}/>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div style={{fontSize:'10.5px',fontWeight:700,letterSpacing:'.06em',color:'#154D97',marginBottom:'10px'}}>GROSS MONTHLY INCOME</div>
                        {isMil&&<React.Fragment>
                          {MROW({label:"Military base income", fk:"Military base income"})}
                          {MROW({label:"Clothes allowance", fk:"Military clothes allowance"})}
                          {MROW({label:"Combat pay", fk:"Military combat pay"})}
                          {MROW({label:"Flight pay", fk:"Military flight pay"})}
                          {MROW({label:"Hazard pay", fk:"Military hazard pay"})}
                          {MROW({label:"Overseas pay", fk:"Military overseas pay"})}
                          {MROW({label:"Prop pay", fk:"Military prop pay"})}
                          {MROW({label:"Quarters allowance", fk:"Military quarters allowance"})}
                          {MROW({label:"Rations allowance", fk:"Military rations allowance"})}
                          {MROW({label:"Variable housing allowance", fk:"Military variable housing allowance"})}
                        </React.Fragment>}
                        {!isMil&&<React.Fragment>
                        {CB2({on:r['Self employed'],label:"Business owner or self-employed",onChange:v=>upd('Self employed',v)})}
                        <div style={{display:'flex',gap:'22px',margin:'9px 0 11px'}}>
                          {['Salary','Hourly'].map(o=>(<label key={o} style={{display:'flex',width:'fit-content',alignItems:'center',gap:'7px',fontSize:'12.5px',color:'#33415A',cursor:'pointer'}}><input type="radio" checked={hourly?o==='Hourly':o==='Salary'} onChange={()=>upd('Pay type',o)} style={{width:'14px',height:'14px',accentColor:'#154D97',cursor:'pointer'}}/>{o}</label>))}
                        </div>
                        {!hourly&&<React.Fragment>
                          {MROW({label:"Annual salary", fk:"Annual base"})}
                          {MROW({label:"Monthly (type either)", fk:"Monthly base"})}
                        </React.Fragment>}
                        {hourly&&<React.Fragment>
                          <div style={{display:'flex',alignItems:'center',gap:'10px',padding:'7px 0',borderBottom:'1px solid #EEF3F9'}}><div style={{flex:1,fontSize:'12.5px',color:'#33415A'}}>Hourly rate</div><div style={{width:'120px'}}><AF money val={r['Hourly rate']} onChange={v=>updMoney('Hourly rate',v)}/></div></div>
                          <div style={{display:'flex',alignItems:'center',gap:'10px',padding:'7px 0',borderBottom:'1px solid #EEF3F9'}}><div style={{flex:1,fontSize:'12.5px',color:'#33415A'}}>Hours per week</div><div style={{width:'120px'}}><AF val={r['Hours per week']} onChange={v=>updMoney('Hours per week',v)}/></div></div>
                          <div style={{display:'flex',alignItems:'center',gap:'10px',padding:'7px 0',borderBottom:'1px solid #EEF3F9'}}><div style={{flex:1,fontSize:'12.5px',color:'#8A94A6'}}>Base monthly (computed)</div><div style={{width:'120px',fontSize:'12.5px',fontWeight:600,textAlign:'right',color:'#154D97'}}>{fmtX(num(r['Hourly rate'])*num(r['Hours per week'])*52/12)}</div></div>
                        </React.Fragment>}
                        {MROW({label:"Bonus", fk:"Bonus monthly"})}
                        {MROW({label:"Commissions", fk:"Commission monthly"})}
                        {MROW({label:"Overtime", fk:"Overtime monthly"})}
                        {MROW({label:"Other", fk:"Other monthly", ph:true})}
                        </React.Fragment>}
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 0 4px',marginTop:'6px',borderTop:'1px solid #EEF3F9',fontSize:'13.5px',color:'#33415A'}}><span style={{fontWeight:600}}>Monthly total</span><span style={{fontWeight:700,color:'#154D97'}}>{fmtX(num(r['Annual amount']))}<span style={{color:'#8A94A6',fontWeight:400,fontSize:'11.5px'}}> /mo</span></span></div>
                        <div style={{display:'flex',gap:'18px',flexWrap:'wrap',marginTop:'11px'}}>
                          {!isMil&&CB2({on:r['Seasonal'],label:"Seasonal income",onChange:v=>upd('Seasonal',v)})}
                          {!isMil&&CB2({on:r['Foreign income'],label:"Foreign income",onChange:v=>upd('Foreign income',v)})}
                        </div>
                        <div style={{marginTop:'7px'}}>{CB2({on:r['Employed by family or party to transaction'],label:"Employed by a family member, property seller, real estate agent, etc.",onChange:v=>upd('Employed by family or party to transaction',v)})}</div>
                        {incIsPrev(r)&&<div style={{marginTop:'9px',fontSize:'11.5px',color:'#8A5A12'}}>Previous job. Counts toward the two year history, not toward DTI.</div>}
                      </div>
                    </div>
                  </div>);
                })()}
                <div style={{display:'flex',marginTop:'10px'}}><button style={{marginLeft:'auto',background:'#fff',border:'1px solid #E0B4B4',color:'#A33333',borderRadius:'8px',padding:'6px 16px',fontSize:'12.5px',fontWeight:600,cursor:'pointer',fontFamily:'inherit'}} onClick={()=>deleteIncRow(i)}>Delete</button></div>
              </div>}
            </div>
          );};if(!finCId)return(<>{R.map((r,i)=>renderIncRow(r,i))}</>);const IG=[[finBFirst,'b'],[finCFirst,'co'],['Both','both']];const newIdx=[];R.forEach((x,i)=>{if(!x._id)newIdx.push(i);});return(<>
          {IG.map(([g,k])=>{const idx=[];R.forEach((x,i)=>{if(!x._id)return;if(ownerKeyOf(x)===k)idx.push(i);});if(!idx.length)return null;const gTot=idx.reduce((a,i2)=>a+annOf(R[i2]),0);return(<div key={g}>
            <div style={{background:'#E4EDF8',borderRadius:'8px',padding:'6px 12px',fontSize:'10.5px',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'#154D97',margin:'12px 0 8px',display:'flex',alignItems:'center'}}><span>{g}</span><span style={{marginLeft:'auto',letterSpacing:0,textTransform:'none',fontSize:'11px',fontWeight:600}}>{fmtX(gTot)+' /yr'}</span></div>
            {idx.map(i2=>renderIncRow(R[i2],i2))}
          </div>);})}
          {newIdx.map(i2=>renderIncRow(R[i2],i2))}
          </>);})()}
          {rentals.length>0&&<div style={{background:'#E4EDF8',borderRadius:'8px',padding:'6px 12px',fontSize:'10.5px',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'#154D97',margin:'12px 0 8px'}}>Net rental income \u00B7 carried from Real estate owned</div>}
          {rentals.length>0&&(
            <div style={{...s.reoRow,background:'#F6F9FC',border:'1px solid #D9E5F4'}}>
              <div style={{...s.reoHead,cursor:'default'}}>
                <span style={{...s.reoCaret,opacity:0}}>{'\u25B8'}</span>
                <div style={s.reoMain}><div style={s.reoAddr}>{rentals.length+(rentals.length===1?' rental property':' rental properties')}</div><div style={s.reoMeta}>gross rent minus linked debt payments \u00B7 edit on the properties and in Liabilities</div></div>
                <span style={{...s.reoType,background:'#F0F5FB',border:'1px solid #D9E5F4',color:'#7189B0',marginRight:'16px'}}>Net rental</span>
                <div style={s.reoRight}>
                  <div style={s.reoCol}><div style={s.reoColK}>Monthly</div><div style={s.reoColV}>{fmtX(rentNetM)}</div></div>
                  <div style={{...s.reoCol,minWidth:'80px'}}><div style={s.reoColK}>Annual</div><div style={s.reoColV}>{fmtX(rentNetM*12)}</div></div>
                </div>
              </div>
            </div>
          )}
          {rentals.length>0&&<div style={{...s.subHint,marginTop:'2px'}}>Rent is never typed here. It carries from each property so income counts once.</div>}
          </>);})()}
          {(()=>{const RR=incRows||[];const moTot=RR.reduce((a,r)=>a+(incIsPrev(r)?0:num(r['Annual amount'])*((r['Frequency']||'Annually')==='Monthly'?1:1/12)),0);return(
          <div style={{display:'flex',alignItems:'center',marginTop:'12px'}}>
            <button style={s.finAdd} onClick={()=>{const idx=(incRows||[]).length;setIncRows(prev=>[...(prev||[]),{'Source':'','Type':'','Annual amount':'','Frequency':'Monthly','Verified':false,'Primary':false,'Pay type':'','Contact':[],_id:null,_isNew:true,_uid:newUid()}]);setIncOpen(p=>({...p,[idx]:true}));}}>+ Income</button>
            <span style={s.finTot}>Total monthly income for DTI {fmtX(moTot)}</span>
          </div>);})()}
        </div>

        <div style={{display:appSection==='documents'?undefined:'none'}}>
          <DocumentsCard
            deal={deal}
            borrowerContactId={borrowerContact?.id || null}
            collapsed={!!collapsed['docs']}
            onToggle={toggleCard}
            title="Documents needed"
          />
        </div>

        <div style={{...s.secWrap,display:appSection==='contacts'?undefined:'none'}}>
          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="contacts" title="Parties" done={false} summary="">
            <DealParties dealId={liveDealId} dealLabel={clean(form['Deal name'])} allContacts={allContacts} onCreateNew={handleContactCreated} borrowerContactId={borrowerContact&&borrowerContact.id} borrowerName={[clean(bForm&&bForm['First name']),clean(bForm&&bForm['Last name'])].filter(Boolean).join(' ')}/>
          </DealSec>
        </div>
      </div>}

      {false&&tab==='deal'&&<div style={{marginTop:'14px'}}>
      <ReconcileBar label="the Deal tab" findings={dealFindings} onResolve={resolveDisc} busyId={discBusyId}/>
      <Card id="c0" title="DEAL SUMMARY" icon="ti-file-description" collapsed={!!collapsed['c0']} onToggle={toggleCard}>
        <div style={{fontSize:'12px',color:'#8a9ab5',marginBottom:'12px'}}>Write a short summary in your voice: a story with the numbers woven in. It scales with the deal and goes straight into the lender email.</div>
        <textarea style={s.summaryArea} value={form['Deal summary']||''} onChange={e=>setForm(p=>({...p,'Deal summary':e.target.value}))}
          placeholder="e.g. Jane Smith is requesting a $780K fix and flip loan on a 2BD/1.5BA SFR in Beverly Hills. Purchase price $800K, ARV $1.2M, $80K rehab budget. Borrower has 8 completed projects, 720 FICO, and strong liquidity."/>
        <div style={{display:'flex',alignItems:'center',gap:'12px',marginTop:'10px',flexWrap:'wrap'}}>
          <button style={genSummary?{...s.saveBtn,opacity:0.6,cursor:'wait'}:s.saveBtn} onClick={generateSummary} disabled={genSummary}>
            <i className={`ti ${genSummary?'ti-loader-2':'ti-robot'}`} style={{fontSize:'13px',marginRight:'6px'}}/>
            {genSummary?'Generating…':'Generate with AI'}
          </button>
          <span style={{fontSize:'11px',color:'#8a9ab5'}}>Drafts from this deal's confirmed numbers. Review, edit, then Save.</span>
        </div>
        {genSummaryErr&&<div style={{...s.errorMsg,marginTop:'8px'}}>{genSummaryErr}</div>}
      </Card>

      <Card id="c1" title="SUBJECT PROPERTY" icon="ti-home" collapsed={!!collapsed['c1']} onToggle={toggleCard}>
        <div>
          <div style={s.fieldGrid}>
            <EF label="Deal name" fieldKey="Deal name" form={form} setForm={setForm}/>
            <EF label="Street address" fieldKey="Property address" form={form} setForm={setForm}/>
            <EF label="City" fieldKey="Property city" form={form} setForm={setForm}/>
            <EF label="State" fieldKey="Property state" form={form} setForm={setForm} options={STATES}/>
            <EF label="ZIP" fieldKey="Property ZIP" form={form} setForm={setForm}/>
            <EF label="Property type" fieldKey="Property type" form={form} setForm={setForm} options={PROP_TYPES}/>
            <EF label="Year built" fieldKey="Year built" form={form} setForm={setForm} isNumber/>
            <EF label="Occupancy status" fieldKey="Occupancy status" form={form} setForm={setForm} options={OCC_SETUP}/>
          </div>
          <div style={s.subHint}>Property type is the asset being financed. For ground-up, tag the completed building, not the raw land.</div>
          {propType&&(
            <>
              <div style={s.divider}/>
              <div style={s.subLabel}>{getSectionLabel()}</div>
              <div style={s.subHint}>All property details capture CURRENT state. Future / post-rehab plans go at the bottom of the Loan Structure card.</div>
              {ptFields.includes('_mh')?(
                <>
                  <div style={s.fieldGrid3}>
                    <EF label="Sections" fieldKey="MH sections" form={form} setForm={setForm} options={MH_SECTIONS}/>
                    <EF label="Width (ft)" fieldKey="MH width" form={form} setForm={setForm} isNumber/>
                    <EF label="Length (ft)" fieldKey="MH length" form={form} setForm={setForm} isNumber/>
                    <EF label="MH park name" fieldKey="MH park name" form={form} setForm={setForm}/>
                    <EF label="Park type" fieldKey="MH park type" form={form} setForm={setForm} options={MH_PARK_TYPES}/>
                    <EF label="Fee type" fieldKey="MH fee type" form={form} setForm={setForm} options={MH_FEE_TYPES}/>
                    <EF label="Monthly fee" fieldKey="MH monthly fee" form={form} setForm={setForm} isCurrency/>
                  </div>
                  <div style={{marginTop:'12px'}}>
                    {CB({label:"433A (permanent foundation, titled as real property)",checked:form['433A'],onChange:val=>setForm(p=>({...p,'433A':val}))})}
                  </div>
                </>
              ):(
              <div style={s.fieldGrid3}>
                {ptFields.includes('Number of units')&&<EF label="# of Units" fieldKey="Number of units" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Sqft')&&<EF label="Sqft" fieldKey="Sqft" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Stories')&&<EF label="Stories" fieldKey="Stories" form={form} setForm={setForm} options={STORIES}/>}
                {ptFields.includes('Bedrooms')&&<EF label="Bedrooms" fieldKey="Bedrooms" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Bathrooms')&&<EF label="Bathrooms" fieldKey="Bathrooms" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Lot size (sqft)')&&<EF label="Lot size (sqft)" fieldKey="Lot size (sqft)" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Floor level')&&<EF label="Floor level" fieldKey="Floor level" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('HOA')&&(
                  <div style={{...s.field,gridColumn:'1/-1'}}>
                    {CB({label:"Property has HOA",checked:form['HOA'],onChange:val=>setForm(p=>({...p,HOA:val}))})}
                    {hoaChecked&&<div style={s.indented}>
                      <EF label="HOA name" fieldKey="HOA name" form={form} setForm={setForm}/>
                      <EF label="HOA dues" fieldKey="HOA dues" form={form} setForm={setForm} isCurrency/>
                    </div>}
                  </div>
                )}
                {ptFields.includes('_garage')&&(
                  <div style={{...s.field,gridColumn:'1/-1'}}>
                    {CB({label:"Garage",checked:form['Garage'],onChange:val=>setForm(p=>({...p,Garage:val}))})}
                    {garageChecked&&<div style={s.indented}>
                      <EF label="Garage type" fieldKey="Garage type" form={form} setForm={setForm} options={GARAGE_TYPES}/>
                      <EF label="# of Cars" fieldKey="Garage count" form={form} setForm={setForm} isNumber/>
                    </div>}
                  </div>
                )}
                {ptFields.includes('_adu')&&(
                  <div style={{...s.field,gridColumn:'1/-1'}}>
                    {CB({label:"ADU / additional dwelling unit",checked:form['ADU'],onChange:val=>setForm(p=>({...p,ADU:val}))})}
                    {aduChecked&&<div style={s.indented}>
                      <EF label="ADU type" fieldKey="ADU type" form={form} setForm={setForm} options={ADU_TYPES}/>
                      <EF label="ADU sqft" fieldKey="ADU sqft" form={form} setForm={setForm} isNumber/>
                      <EF label="ADU monthly rent" fieldKey="ADU monthly rent" form={form} setForm={setForm} isCurrency/>
                    </div>}
                  </div>
                )}
                {ptFields.includes('_basement')&&(
                  <div style={{...s.field,gridColumn:'1/-1'}}>
                    {CB({label:"Basement",checked:form['Basement'],onChange:val=>setForm(p=>({...p,Basement:val}))})}
                    {basementChecked&&<div style={s.indented}>
                      <EF label="Basement type" fieldKey="Basement type" form={form} setForm={setForm} options={BASEMENT_TYPES}/>
                      <EF label="Basement sqft" fieldKey="Basement sqft" form={form} setForm={setForm} isNumber/>
                      <EF label="Basement rent" fieldKey="Basement rent" form={form} setForm={setForm} isCurrency/>
                    </div>}
                  </div>
                )}
                {ptFields.includes('Total beds')&&<EF label="Total beds" fieldKey="Total beds" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Total baths')&&<EF label="Total baths" fieldKey="Total baths" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Occupied units')&&<EF label="Occupied units" fieldKey="Occupied units" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Current monthly rent')&&<EF label="Current monthly rent" fieldKey="Current monthly rent" form={form} setForm={setForm} isCurrency/>}
                {ptFields.includes('Tenant count')&&<EF label="Tenant count" fieldKey="Tenant count" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Residential tenants')&&<EF label="Residential tenants" fieldKey="Residential tenants" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Commercial tenants')&&<EF label="Commercial tenants" fieldKey="Commercial tenants" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Lease terms')&&<EF label="Lease terms" fieldKey="Lease terms" form={form} setForm={setForm}/>}
                {ptFields.includes('Commercial sqft')&&<EF label="Commercial sqft" fieldKey="Commercial sqft" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Residential sqft')&&<EF label="Residential sqft" fieldKey="Residential sqft" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Commercial %')&&<EF label="Commercial %" fieldKey="Commercial %" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Land acreage')&&<EF label="Acreage" fieldKey="Land acreage" form={form} setForm={setForm} isNumber/>}
                {ptFields.includes('Land zoning')&&<EF label="Zoning" fieldKey="Land zoning" form={form} setForm={setForm}/>}
                {ptFields.includes('Land entitled')&&<EF label="Entitled" fieldKey="Land entitled" form={form} setForm={setForm}/>}
                {ptFields.includes('Utilities')&&<EF label="Utilities" fieldKey="Utilities" form={form} setForm={setForm} options={UTILITIES}/>}
                {ptFields.includes('Access / ingress')&&<EF label="Access / ingress" fieldKey="Access / ingress" form={form} setForm={setForm} options={ACCESS}/>}
              </div>
              )}
              {(ptFields.includes('Total beds')||ptFields.includes('Occupied units'))&&(
                <div style={{marginTop:'14px'}}>
                  <div style={s.fieldLabelCalc}>OCCUPANCY % <span style={{fontWeight:400,opacity:0.6,fontSize:'9px'}}>AUTO</span></div>
                  <div style={s.autoCalc}>{mOccupancy()?mOccupancy()+'%':'-'}{mOccupancy()&&` (${form['Occupied units']||0} of ${form['Number of units']||0} units occupied)`}</div>
                </div>
              )}
              {ptFields.includes('Annual NOI')&&(
                <>
                  <div style={s.divider}/>
                  <EF label="Annual NOI" fieldKey="Annual NOI" form={form} setForm={setForm} isCurrency/>
                </>
              )}
            </>
          )}
          <div style={s.divider}/>
          <div style={s.subLabel}>Asset class</div>
          <div style={s.subHint}>Commercial subset or risk flag. Optional, applies to any property type.</div>
          <div style={s.pillbox}>
            {ASSET_CLASSES.map(sp=>{
              const on=(form['Asset class']||[]).includes(sp);
              return <span key={sp} style={s.pill(on)} onClick={()=>toggleAssetClass(sp)}>{on&&<i className="ti ti-check" style={{fontSize:'11px'}}/>}{sp}</span>;
            })}
          </div>
        </div>
      </Card>

      <Card id="cSC" title="SPECIAL CIRCUMSTANCES" icon="ti-clipboard-list" collapsed={!!collapsed['cSC']} onToggle={toggleCard}>
        <div style={s.subHint}>Special conditions on the transaction. Usually empty. Applies to any property type.</div>
        <div style={s.pillbox}>
          {SITUATIONS.map(sit=>{
            const on=(form['Deal situations']||[]).includes(sit);
            return <span key={sit} style={s.pill(on)} onClick={()=>toggleDealSituation(sit)}>{on&&<i className="ti ti-check" style={{fontSize:'11px'}}/>}{sit}</span>;
          })}
        </div>
        {wholesaleOn&&(
          <div style={s.reveal}>
            <div style={s.subLabel}>Assignment fee <span style={{fontWeight:400,color:'#8a9ab5',textTransform:'none',letterSpacing:0}}>shown because Wholesale is tagged</span></div>
            <div style={s.fieldGrid3}>
              <EF label="Fee amount ($ or %)" fieldKey="Assignment fee amount" form={form} setForm={setForm} isNumber/>
              <EF label="Fee basis" fieldKey="Assignment fee basis" form={form} setForm={setForm} options={ASSIGNMENT_FEE_BASIS}/>
              <EF label="Paid by" fieldKey="Assignment fee paid by" form={form} setForm={setForm} options={ASSIGNMENT_FEE_PAID_BY}/>
            </div>
          </div>
        )}
      </Card>

      <Card id="c2" title={`LOAN STRUCTURE${loanType?': '+loanType.toUpperCase():''}`} icon="ti-cash" collapsed={!!collapsed['c2']} onToggle={toggleCard}>
        <div>
          <div style={s.subLabel}>Deal metrics (auto-calculated)</div>
          <div style={s.subHint}>Updates live as you fill in figures below.</div>
          <MetricsGrid/>

          <div style={s.divider}/>
          <div style={s.subLabel}>Loan type & purpose</div>
          <div style={s.fieldGrid3}>
            <div style={s.field}>
              <div style={s.fieldLabel}>Loan type</div>
              <select style={s.select} value={loanType} onChange={e=>handleLoanTypeChange(e.target.value)}>
                <option value="">Select…</option>
                {LOAN_TYPE_GROUPS.map(([g,opts])=>(
                  <optgroup key={g} label={g}>
                    {opts.map(o=><option key={o}>{o}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <div style={s.fieldLabel}>Loan purpose</div>
              <select style={s.select} value={loanPurpose} onChange={e=>setForm(p=>({...p,'Loan purpose':e.target.value}))} disabled={!loanType}>
                <option value="">Select…</option>
                {availablePurposes.map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <div style={s.fieldLabel}>Lien position</div>
              <select style={s.select} value={lienPos} onChange={e=>setForm(p=>({...p,'Lien position':e.target.value}))} disabled={!loanType}>
                {LIEN_POSITIONS.map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          {loanType&&(isSecond
            ? <div style={s.subHint}>2nd lien: the existing senior lien stays in place. Leverage is sized on CLTV (existing + new) / value, shown in the metrics above.</div>
            : ((loanPurpose==='Refinance'||loanPurpose==='Cash-out refinance')
                ? <div style={s.subHint}>1st lien refinance: the existing balance is the payoff at closing, not added on. Leverage is plain LTV.</div>
                : null))}

          <div style={{...s.subLabel,marginTop:'16px'}}>Borrower preferences</div>
          <div style={s.fieldGrid}>
            <div style={s.field}>
              <div style={s.fieldLabel}>Recourse preference</div>
              <select style={s.select} value={form['Recourse preference']||''} onChange={e=>setForm(p=>({...p,'Recourse preference':e.target.value}))}>
                {RECOURSE_PREF.map(o=><option key={o} value={o}>{o||'No preference'}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <div style={s.fieldLabel}>Appraisal preference</div>
              <select style={s.select} value={form['Appraisal preference']||''} onChange={e=>setForm(p=>({...p,'Appraisal preference':e.target.value}))}>
                {APPRAISAL_PREF.map(o=><option key={o} value={o}>{o||'No preference'}</option>)}
              </select>
            </div>
          </div>
          <div style={s.subHint}>These filter the match. Non-recourse required hides full-recourse-only lenders; no appraisal needed hides lenders that require one.</div>

          {showRehabToggle&&(
            <div style={s.rehabToggleBox}>
              <input type="checkbox" id="includesRehab" checked={!!form['Includes rehab/improvements']}
                onChange={e=>setForm(p=>({...p,'Includes rehab/improvements':e.target.checked}))}
                style={{accentColor:'#2B72C2',width:'15px',height:'15px'}}/>
              <label htmlFor="includesRehab" style={{fontSize:'13px',color:'#0D2545',fontWeight:600,cursor:'pointer'}}>
                This deal includes rehab or improvements
              </label>
            </div>
          )}

          {loanType&&loanPurpose&&(
            <>
              <div style={s.divider}/>
              <div style={s.subLabel}>Deal financials</div>
              <div style={s.subHint}>Enter all known figures. Metrics above update as you type.</div>
              <div style={s.fieldGrid3}>
                {(loanType==='SBA 504'?SBA_FIELD_ORDER:LOAN_FIELD_ORDER).map(k=>renderSpecField(k))}
              </div>

              {rehabInScope&&(
                <div style={{marginTop:'14px',maxWidth:'320px'}}>
                  <EF label="Rehab financing" fieldKey="Rehab financing" form={form} setForm={setForm} options={REHAB_FINANCING}/>
                  <div style={{fontSize:'11px',color:'#8a9ab5',marginTop:'4px',fontStyle:'italic'}}>
                    Default: Lender funds rehab. Choose "Borrower self-funds" if rehab is paid out of pocket.
                  </div>
                </div>
              )}
            </>
          )}

          {hasIncomeFields&&(
            <>
              <div style={s.divider}/>
              <div style={s.subLabel}>Income & rents</div>
              <div style={s.subHint}>Current rent if leased, projected/market rent if vacant. Either or both can be entered.</div>
              <div style={s.fieldGrid3}>
                {INCOME_FIELD_ORDER.map(k=>renderSpecField(k))}
              </div>
              {hasSecondaryIncome&&(
                <div style={{...s.fieldGrid3,marginTop:'14px'}}>
                  {SECONDARY_INCOME_ORDER.map(k=>renderSpecField(k))}
                </div>
              )}
            </>
          )}

          <div style={s.divider}/>
          <div style={s.subLabel}>Loan request</div>
          <div style={s.subHint}>Target terms, lenders may counter.</div>
          <div style={s.fieldGrid3}>
            <EF label="Rate (%)" fieldKey="Requested rate" form={form} setForm={setForm} isNumber/>
            <EF label="Loan term" fieldKey="Requested loan term" form={form} setForm={setForm} options={LOAN_TERMS}/>
            <EF label="Points" fieldKey="Requested points" form={form} setForm={setForm} isNumber/>
            <EF label="Prepay penalty" fieldKey="Requested prepayment penalty" form={form} setForm={setForm} options={PPP_OPTS}/>
          </div>

          <div style={s.divider}/>
          <div style={s.subLabel}>Timeline & exit</div>
          <div style={s.fieldGrid3}>
            <EF label="Target close date" fieldKey="Target close date" form={form} setForm={setForm} type="date"/>
            <EF label="Exit strategy" fieldKey="Exit strategy" form={form} setForm={setForm} options={EXIT_STRATEGIES}/>
          </div>

          {showPostRehab&&(
            <>
              <div style={s.divider}/>
              <div style={s.subLabel}>Post-rehab / stabilization plan</div>
              <div style={s.subHint}>What will the property look like after rehab / construction / stabilization? Include new unit count, projected rents, conversion plans, ADU additions, etc.</div>
              <textarea style={s.postRehabArea} value={form['Post-rehab notes']||''} onChange={e=>setForm(p=>({...p,'Post-rehab notes':e.target.value}))}
                placeholder="e.g. Converting 4-unit to 6-unit by adding 2 ADUs. Projected rent $2,800/unit post-construction. Estimated stabilized NOI $168K annually."/>
            </>
          )}
        </div>
      </Card>
      </div>}

      {false&&tab==='borrower'&&<div style={{marginTop:'14px'}}>
      <ReconcileBar label="the Borrower tab" findings={borrowerFindings} onResolve={resolveDisc} busyId={discBusyId}/>
      <Card id="c3" title="BORROWER / SPONSOR" icon="ti-user" collapsed={!!collapsed['c3']} onToggle={toggleCard}>
        {!borrowerContact?(
          <div style={s.emptyText}>No borrower linked to this deal.</div>
        ):(
          <div>
            <div style={s.syncNote}><i className="ti ti-refresh" style={{fontSize:'12px'}}/> Borrower info syncs with the contact record. Changes here update the contact.</div>
            {bForm?(
              <>
                <div style={s.subLabel}>Identity & profile</div>
                <div style={s.fieldGrid}>
                  <EF label="First name" fieldKey="First name" form={bForm} setForm={setBForm}/>
                  <EF label="Last name" fieldKey="Last name" form={bForm} setForm={setBForm}/>
                  <EF label="Email" fieldKey="Email" form={bForm} setForm={setBForm} type="email"/>
                  <EF label="Phone" fieldKey="Phone" form={bForm} setForm={setBForm}/>
                  <EF label="Company / entity" fieldKey="Company / entity" form={bForm} setForm={setBForm}/>
                  <EF label="Entity type" fieldKey="Entity type" form={bForm} setForm={setBForm} options={ENTITY_TYPES}/>
                  <EF label="FICO score" fieldKey="FICO score" form={bForm} setForm={setBForm} isNumber
                    below={ficoFinding && <InlineFinding item={ficoFinding} busy={discBusyId===ficoFinding.id}
                      onUse={()=>resolveDisc(ficoFinding,'use_verified')}
                      onKeep={()=>resolveDisc(ficoFinding,'keep_stated','Reviewed.')}/>}/>
                </div>
                <div style={s.divider}/>
                <div style={s.subLabel}>Home address</div>
                <div style={s.fieldGrid}>
                  <EF label="Street address" fieldKey="Street address" form={bForm} setForm={setBForm}/>
                  <EF label="City" fieldKey="City" form={bForm} setForm={setBForm}/>
                  <div style={s.field}>
                    <div style={s.fieldLabel}>State</div>
                    <select style={s.select} value={Array.isArray(bForm['Home state'])?bForm['Home state'][0]:(bForm['Home state']||'')} onChange={e=>setBFormField('Home state',[e.target.value].filter(Boolean))}>
                      <option value="">Select…</option>
                      {STATES.map(st=><option key={st}>{st}</option>)}
                    </select>
                  </div>
                  <EF label="ZIP" fieldKey="ZIP" form={bForm} setForm={setBForm}/>
                </div>
              </>
            ):(
              <div style={s.emptyText}>Loading borrower profile…</div>
            )}
            <div style={s.divider}/>
            <div style={s.subLabel}>Assets & accounts</div>
            <AssetsTable assetRows={assetRows} setAssetRows={setAssetRows} editing={true}/>
            <div style={s.divider}/>
            <div style={s.subLabel}>Real estate owned (SREO)</div>
            <SreoTable sreoRows={sreoRows} setSreoRows={setSreoRows} editing={true}/>
            <div style={s.divider}/>
            <div style={s.subLabel}>Liquidity summary</div>
            <div style={s.metricTileRow}>
              <div style={s.metricTile}><div style={s.metricTileLabel}>Total assets on file</div><div style={s.metricTileVal}>{fmt(totalAllAssets)}</div></div>
              <div style={s.metricTile}><div style={s.metricTileLabel}>Total SREO equity <span style={s.metricTileAuto}>AUTO</span></div><div style={s.metricTileVal}>{fmt(totalSREOEquity)}</div></div>
            </div>

            {bForm&&(
              <>
                <div style={s.divider}/>
                <div style={s.subLabel}>Credit events (last 7 years)</div>
                <div style={s.subHint}>Lenders ask about these on every deal. Updates here save to the linked contact.</div>
                <div style={s.chipGrid}>
                  <div style={s.chip(!!bForm['Bankruptcy history'])} onClick={()=>setBFormField('Bankruptcy history',!bForm['Bankruptcy history'])}>
                    <span>{bForm['Bankruptcy history']?'☑':'☐'}</span>Bankruptcy
                  </div>
                  <div style={s.chip(!!bForm['Foreclosure history'])} onClick={()=>setBFormField('Foreclosure history',!bForm['Foreclosure history'])}>
                    <span>{bForm['Foreclosure history']?'☑':'☐'}</span>Foreclosure
                  </div>
                  <div style={s.chip(!!bForm['Short sale / deed in lieu'])} onClick={()=>setBFormField('Short sale / deed in lieu',!bForm['Short sale / deed in lieu'])}>
                    <span>{bForm['Short sale / deed in lieu']?'☑':'☐'}</span>Short sale / deed in lieu
                  </div>
                  <div style={s.chip(!!bForm['Pending litigation'])} onClick={()=>setBFormField('Pending litigation',!bForm['Pending litigation'])}>
                    <span>{bForm['Pending litigation']?'☑':'☐'}</span>Pending litigation
                  </div>
                  <div style={s.chip(!!bForm['Tax liens or judgments'])} onClick={()=>setBFormField('Tax liens or judgments',!bForm['Tax liens or judgments'])}>
                    <span>{bForm['Tax liens or judgments']?'☑':'☐'}</span>Tax liens / judgments
                  </div>
                </div>

                {bForm['Bankruptcy history']&&(
                  <div style={{marginTop:'14px'}}>
                    <div style={s.subLabel}>Bankruptcy details</div>
                    <div style={s.fieldGrid3}>
                      <EF label="Type" fieldKey="Bankruptcy type" form={bForm} setForm={setBForm} options={BANKRUPTCY_TYPES}/>
                      <EF label="Date" fieldKey="Bankruptcy date" form={bForm} setForm={setBForm} type="date"/>
                      <EF label="Status" fieldKey="Bankruptcy status" form={bForm} setForm={setBForm} options={BANKRUPTCY_STATUS}/>
                    </div>
                  </div>
                )}

                {bForm['Foreclosure history']&&(
                  <div style={{marginTop:'14px'}}>
                    <div style={s.subLabel}>Foreclosure details</div>
                    <div style={s.fieldGrid3}>
                      <EF label="Date completed" fieldKey="Foreclosure date" form={bForm} setForm={setBForm} type="date"/>
                    </div>
                  </div>
                )}

                <div style={{marginTop:'14px'}}>
                  <div style={s.subLabel}>Lates &amp; collections</div>
                  <div style={s.fieldGrid3}>
                    <EF label="Mortgage lates (12mo)" fieldKey="Mortgage lates 12mo" form={bForm} setForm={setBForm} isNumber/>
                    <EF label="Other lates (12mo)" fieldKey="Other lates 12mo" form={bForm} setForm={setBForm} isNumber/>
                    <EF label="Collections / charge-offs" fieldKey="Collections / charge-offs" form={bForm} setForm={setBForm} options={COLLECTIONS_OPTS}/>
                  </div>
                </div>

                <div style={{marginTop:'14px'}}>
                  <div style={s.field}>
                    <div style={s.fieldLabel}>Credit events notes (optional)</div>
                    <textarea style={s.notesArea} value={bForm['Credit events notes']||''}
                      onChange={e=>setBFormField('Credit events notes',e.target.value)}
                      placeholder="Context borrower wants to provide, e.g. bankruptcy was 6 years ago due to medical, discharged clean, no debt since."/>
                  </div>
                </div>
              </>
            )}

            <div style={s.divider}/>
            <div style={s.subLabel}>Co-borrower</div>
            {!showCo?(
              <button style={s.addCoBtn} onClick={()=>setShowCo(true)}><i className="ti ti-plus" style={{fontSize:'13px'}}/> Add co-borrower</button>
            ):(
              <div>
                <div style={s.fieldGrid}>
                  <EF label="Name" fieldKey="Co-borrower name" form={form} setForm={setForm}/>
                  <EF label="Email" fieldKey="Co-borrower email" form={form} setForm={setForm} type="email"/>
                  <EF label="Phone" fieldKey="Co-borrower phone" form={form} setForm={setForm}/>
                </div>
                <button style={{...s.addCoBtn,borderColor:'#B08968',color:'#B08968',marginTop:'10px'}}
                  onClick={()=>{setShowCo(false);setForm(p=>({...p,'Co-borrower name':'','Co-borrower email':'','Co-borrower phone':''}));}}>
                  <i className="ti ti-trash" style={{fontSize:'13px'}}/> Remove co-borrower
                </button>
              </div>
            )}
          </div>
        )}
      </Card>
      </div>}

      {tab==='docs'&&<div style={{marginTop:'14px'}}>
      <DocumentsCard
        deal={deal}
        borrowerContactId={borrowerContact?.id || null}
        collapsed={!!collapsed['docs']}
        onToggle={toggleCard}
      />
      <DiscrepanciesCard
        items={discItems}
        loading={discLoading}
        running={discRunning}
        busyId={discBusyId}
        msg={discMsg}
        err={discErr}
        onRun={runDiscCheck}
        onResolve={resolveDisc}
        collapsed={!!collapsed['disc']}
        onToggle={toggleCard}
      />
      </div>}

      {tab==='closing'&&<div style={{marginTop:'14px'}}>
        <div style={s.secWrap}>
          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="closing" title="Closing" done={!!clean(form['Funded date'])} summary={[clean(form['Lender loan number']),clean(form['Funded date'])].filter(Boolean).join(' \u00B7 ')}>

            <div style={{padding:'14px 16px 4px'}}>
              <div style={{...s.cSubLbl,marginTop:0}}>Final terms</div>
                <div style={s.dealFlow}>
                  <div style={{width:'220px',flexShrink:0}}>
                    <div style={s.appLbl}>Lender</div>
                    {Array.isArray(form['Lender'])&&form['Lender'].length
                      ? <div style={{display:'flex',alignItems:'center',gap:'8px',marginTop:'4px'}}>
                          <span style={{fontSize:'13px',color:'#0D2545',fontWeight:600}}>{lenderNameOf(form['Lender'][0])}</span>
                          <span style={s.dcAct} onClick={()=>{setF('Lender')([]);}}>Change</span>
                        </div>
                      : <select style={{...s.appSelect,marginTop:'4px'}} value="" onChange={e=>{ if(e.target.value) setF('Lender')([e.target.value]); }}>
                          <option value="">Select lender</option>
                          {(allLenders||[]).map(l=><option key={l.id} value={l.id}>{clean(l.fields&&l.fields['Lender name'])||'Unnamed'}</option>)}
                        </select>}
                  </div>
                  <div style={{width:'180px',flexShrink:0}}><AF label="Lender loan number" val={form['Lender loan number']} onChange={setF('Lender loan number')}/></div>
                  <div style={{width:'150px',flexShrink:0}}><AF date label="Funded date" val={form['Funded date']} onChange={setF('Funded date')}/></div>
                </div>
                <div style={{...s.dealFlow,marginTop:'14px'}}>
                  <div style={{width:'175px',flexShrink:0}}><AF money placeholder="$" label="Base loan amount" val={form['Final base loan amount']} onChange={setF('Final base loan amount')}/></div>
                  <div style={{width:'175px',flexShrink:0}}><AF money placeholder="$" label="Total loan amount" val={form['Final total loan amount']} onChange={setF('Final total loan amount')}/></div>
                  <div style={{width:'120px',flexShrink:0}}><AF pct placeholder="%" label="Final rate" val={form['Final rate']} onChange={setF('Final rate')}/></div>
                  <div style={{width:'150px',flexShrink:0}}><AF label="Final term (months)" val={form['Final term months']} onChange={setF('Final term months')}/></div>
                </div>
                <div style={{...s.subHint,marginTop:'10px'}}>Base is what compensation is figured on. Total is base plus anything the lender financed in.</div>
            </div>

            <div style={{padding:'0 16px 4px'}}>
              <div style={s.cSubLbl}>Broker fee</div>
                <div style={{display:'flex',gap:'14px',alignItems:'flex-end',flexWrap:'wrap'}}>
                  <div style={{width:'215px'}}>
                    <ASel label="Brokerage" val={form['Brokerage']} onChange={setF('Brokerage')} options={BROKERAGES}/>
                  </div>
                  <div style={{width:'105px'}}>
                    <AF pct placeholder="%" label="Rate" val={form['Comp rate']} onChange={v=>{
                      const nnc=x=>Number(String(x||'').replace(/[^0-9.]/g,''))||0;
                      const base=nnc(form['Final base loan amount'])||nnc(form['Loan amount']);
                      const feeV=base?Math.round(base*nnc(v))/100:0;
                      setForm(pp=>({...pp,'Comp rate':v,'Broker fee':feeV?String(feeV):(pp['Broker fee']||'')}));
                      markDirty();
                    }}/>
                  </div>
                  <div style={{width:'155px'}}><AF money placeholder="$" label="Broker fee" val={form['Broker fee']} onChange={setF('Broker fee')}/></div>
                  <div style={{width:'160px'}}><ASel label="Paid by" val={form['Broker fee paid by']} onChange={setF('Broker fee paid by')} options={FEE_PAID_BY}/></div>
                  <div style={{width:'185px'}}><ASel label="Lead source" val={form['Lead source']} onChange={setF('Lead source')} options={LEAD_SOURCES}/></div>
                </div>
                <div style={{...s.subHint,marginTop:'9px'}}>Brokerage is whose shop this deal was written under. Rate times the base loan amount fills the fee, and you can type over it.</div>
            </div>

            <div style={{padding:'0 16px 4px'}}>
              <div style={s.cSubLbl}>Payouts</div>
                <DealPayouts dealId={liveDealId} dealLabel={clean(form['Deal name'])} brokerFee={form['Broker fee']} allContacts={allContacts} onContactSaved={handleContactCreated} onContactPatched={patchContactFields}/>
            </div>

            {isCCCoBrokered&&<div style={{padding:'0 16px 16px'}}>
              <div style={s.cSubLbl}>Invoice</div>
                <div style={s.dealFlow}>
                  <div style={{width:'200px',flexShrink:0}}><AF label="Invoice number" val={form['Invoice number']} onChange={setF('Invoice number')}/></div>
                  <div style={{width:'150px',flexShrink:0}}><AF date label="Sent" val={form['Invoice sent']} onChange={setF('Invoice sent')}/></div>
                  <div style={{width:'150px',flexShrink:0}}><AF date label="Paid" val={form['Invoice paid']} onChange={setF('Invoice paid')}/></div>
                </div>
                <div style={{...s.subHint,marginTop:'10px'}}>Your invoice to the settlement agent. Anything you pay out has its own paid-on date on its row above.</div>
            </div>}

          </DealSec>
        </div>
      </div>}

      {tab==='notes'&&<div style={{marginTop:'14px'}}>
        <div style={s.secWrap}>
          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="dealnotes" title="Deal notes" done={!!clean(notes)} summary={clean(form['Submitted by'])?('Submitted by '+clean(form['Submitted by'])):''}>
            <div style={{width:'280px',marginBottom:'12px'}}>
              <AF label="Submitted by" val={form['Submitted by']} onChange={setF('Submitted by')}/>
            </div>
            <textarea style={s.notesArea} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Anything about this deal you want on the file."/>
            <div style={{...s.subHint,marginTop:'8px'}}>Saves with "Save" at the top.</div>
          </DealSec>

          <DealSec openSec={openSec} setOpenSec={setOpenSec} k="activity" title="Activity" done={corr.length>0} summary={corr.length?(corr.length+' '+(corr.length===1?'entry':'entries')):'Nothing logged yet'}>
            {corr.length===0&&<div style={{fontSize:'12.5px',color:'#8a9ab5',marginBottom:'8px'}}>Nothing logged yet.</div>}
            {corr.map(e=>(
              <div key={e.id} style={s.actRow}>
                <span style={s.actChip}>{e.type||'Note'}</span>
                <span style={s.actDate}>{e.date||''}</span>
                <span style={s.actWho}>{clean(e.with)||<span style={{color:'#8a9ab5',fontWeight:400}}>Internal</span>}</span>
                <span style={s.actTxt}>{e.note||''}</span>
                <span style={{textAlign:'right'}}><span style={s.dcActRm} onClick={()=>removeCorrEntry(e.id)}>Remove</span></span>
              </div>
            ))}

            <div style={s.actAdd}>
              <div>
                <div style={s.appLbl}>Type</div>
                <select style={{...s.appSelect,marginTop:'4px'}} value={corrDraft.type} onChange={ev=>setCorrDraft(d=>({...d,type:ev.target.value}))}>
                  {CORR_TYPES.map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <div style={s.appLbl}>Date</div>
                <input style={{...s.appInput,marginTop:'4px'}} type="date" value={corrDraft.date} onChange={ev=>setCorrDraft(d=>({...d,date:ev.target.value}))}/>
              </div>
              <div>
                <div style={s.appLbl}>With</div>
                <select style={{...s.appSelect,marginTop:'4px'}} value={corrDraft.with} onChange={ev=>setCorrDraft(d=>({...d,with:ev.target.value}))}>
                  <option value="">Internal</option>
                  {corrWithOptions.map(o=><option key={o} value={o}>{o}</option>)}
                  {clean(corrDraft.with)&&!corrWithOptions.includes(corrDraft.with)&&<option value={corrDraft.with}>{corrDraft.with}</option>}
                </select>
              </div>
              <div>
                <div style={s.appLbl}>What happened</div>
                <input style={{...s.appInput,marginTop:'4px'}} value={corrDraft.note} onChange={ev=>setCorrDraft(d=>({...d,note:ev.target.value}))} placeholder="One line is fine"/>
              </div>
              <button style={s.actLog} onClick={addCorrEntry}>Log it</button>
            </div>
            <div style={{...s.subHint,marginTop:'10px'}}>With lists the people on this deal. Leave it on Internal for a note to yourself. Logged entries save with "Save" at the top.</div>
          </DealSec>
        </div>
      </div>}

      {tab==='match'&&<div style={{marginTop:'14px'}}>
      <Card id="cMatch" title="LENDER MATCH" icon="ti-target-arrow" collapsed={!!collapsed['cMatch']} onToggle={toggleCard}>
        {!loanType ? (
          <div style={ms.empty}>Set a loan type on the Deal tab first, then come back here to match.</div>
        ) : matchLoading ? (
          <div style={ms.empty}>Loading lenders…</div>
        ) : matchErr ? (
          <div style={{...ms.empty,color:'#c0392b'}}>{matchErr}</div>
        ) : !matchResult ? (
          <div style={ms.empty}>No lenders loaded yet.</div>
        ) : (
        <>
          <div style={ms.bar}>
            <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',minWidth:0}}>
              <span style={ms.count}>
                {matchResult.potential.length} potential · {matchResult.maybe.length} maybe
              </span>
              <span style={{fontSize:'11px',color:'#8a9ab5'}}>{loanType}{loanPurpose?' · '+loanPurpose:''} · {lienPos} · {(form['Property state']||'?').toUpperCase()}</span>
            </div>
            <button style={ms.draftBtn(selectedCount>0)} disabled={selectedCount===0}
              onClick={()=>setShowDrafts(true)}>
              <i className="ti ti-mail" style={{fontSize:'13px'}}/> Generate emails ({selectedCount})
            </button>
          </div>

          {showDrafts&&(
            <LenderEmailDrafts
              dealId={liveDealId}
              driveLink={f['Drive folder URL']}
              lenders={selectedLenders}
              onClose={()=>setShowDrafts(false)}
            />
          )}

          {matchResult.potential.length>0&&<div style={ms.tierPot}><span style={ms.pip('#4A6885')}/> Potential · data matches the deal</div>}
          {matchResult.potential.map(o=>{
            const checked=isSel(o.lender.id,true);
            const ae=o.lender.aeName||'';
            const email=o.lender.submissionEmail||o.lender.aeEmail||o.lender.lenderEmail||'';
            const pass=o.pass||[];
            const reason='All criteria match'+(pass.length?': '+pass.join(', '):'');
            const open=expMatch.has(o.lender.id);
            return (
              <div key={o.lender.id} style={ms.aRow}>
                <input type="checkbox" style={ms.chk} checked={checked} onChange={()=>toggleMatchSel(o.lender.id)}/>
                <div style={ms.aMain}>
                  <div style={ms.top2}>
                    <span style={ms.name}>{o.lender.name}</span>
                    <span style={{...ms.tag,...(o.vstatus==='verified'?ms.badgeV:ms.badgeU)}}>{o.vstatus==='verified'?'Verified':'Unconfirmed'}</span>
                  </div>
                  {(ae||email)&&<div style={ms.contact}><i className="ti ti-user" style={{fontSize:'11px'}}/> {ae}{ae&&email?' · ':''}{email}</div>}
                  <div style={ms.aReason('match')}>{reason}{pass.length>0&&<span style={ms.detailsLink} onClick={()=>toggleMatchDetails(o.lender.id)}>{open?'hide':'details'}</span>}</div>
                  {open&&<div style={ms.passrow}>{pass.map((t,i)=><span key={i} style={ms.pchip}><i className="ti ti-check" style={{fontSize:'9px'}}/> {t}</span>)}</div>}
                </div>
                <div style={ms.aStatusCol}><span style={ms.statusMini('match')}>Match</span></div>
              </div>
            );
          })}

          {matchResult.maybe.length>0&&<div style={ms.tierMay}><span style={ms.pip('#B08968')}/> Maybe · unknown or unconfirmed, not ruled out</div>}
          {matchResult.maybe.map(o=>{
            const checked=isSel(o.lender.id,true);
            const ae=o.lender.aeName||'';
            const email=o.lender.submissionEmail||o.lender.aeEmail||o.lender.lenderEmail||'';
            const contradict=o.contradict||[];
            const unknown=o.unknown||[];
            const issues=[...contradict,...unknown];
            const reason='Matches on file'+(issues.length?'; '+issues.join(', '):'');
            const open=expMatch.has(o.lender.id);
            return (
              <div key={o.lender.id} style={ms.aRow}>
                <input type="checkbox" style={ms.chk} checked={checked} onChange={()=>toggleMatchSel(o.lender.id)}/>
                <div style={ms.aMain}>
                  <div style={ms.top2}>
                    <span style={ms.name}>{o.lender.name}</span>
                    <span style={{...ms.tag,...(o.vstatus==='verified'?ms.badgeV:ms.badgeU)}}>{o.vstatus==='verified'?'Verified':'Unconfirmed'}</span>
                  </div>
                  {(ae||email)&&<div style={ms.contact}><i className="ti ti-user" style={{fontSize:'11px'}}/> {ae}{ae&&email?' · ':''}{email}</div>}
                  <div style={ms.aReason('check')}>{reason}{issues.length>0&&<span style={ms.detailsLink} onClick={()=>toggleMatchDetails(o.lender.id)}>{open?'hide':'details'}</span>}</div>
                  {open&&<div style={ms.passrow}>{contradict.map((t,i)=><span key={'c'+i} style={ms.mchipNo}>{t}</span>)}{unknown.map((t,i)=><span key={'u'+i} style={ms.mchip}>{t}</span>)}</div>}
                </div>
                <div style={ms.aStatusCol}><span style={ms.statusMini('check')}>Check</span></div>
              </div>
            );
          })}

          {matchResult.ruledout.length>0&&<div style={ms.tierRem} onClick={()=>setShowRuled(s=>!s)}><span style={ms.pip('#C0392B')}/> Ruled out · verified no · {matchResult.ruledout.length} <span style={{textDecoration:'underline',marginLeft:'4px',fontWeight:600}}>{showRuled?'hide':'show'}</span></div>}
          {showRuled&&matchResult.ruledout.map(o=>{
            const checked=isSel(o.lender.id,false);
            const reason=(o.contradict||[]).join(', ');
            return (
              <div key={o.lender.id} style={ms.aRow}>
                <input type="checkbox" style={ms.chk} checked={checked} onChange={()=>toggleMatchSel(o.lender.id)}/>
                <div style={ms.aMain}>
                  <div style={ms.top2}>
                    <span style={ms.nameX}>{o.lender.name}</span>
                    <span style={{...ms.tag,...ms.badgeV}}>Verified</span>
                  </div>
                  {reason&&<div style={ms.aReason('out')}>{reason}</div>}
                </div>
                <div style={ms.aStatusCol}><span style={ms.statusMini('out')}>Ruled out</span></div>
              </div>
            );
          })}

          <div style={ms.selBar}>
            <span style={{fontSize:'12.5px',fontWeight:600,color:'#154D97'}}>Drafting to {selectedCount} lender{selectedCount===1?'':'s'}</span>
            <span style={{fontSize:'11.5px',color:'#8a9ab5'}}>Use "Generate emails" at the top to review and send.</span>
          </div>
          {matchMsg&&<div style={{fontSize:'11.5px',color:'#185FA5',background:'#E6F1FB',borderRadius:'6px',padding:'8px 10px',marginTop:'8px'}}>{matchMsg}</div>}
          <div style={{fontSize:'11px',color:'#8a9ab5',marginTop:'8px',fontStyle:'italic'}}>Potentials and maybes are sent by default. Uncheck any to skip, or tick a ruled-out lender to send anyway.</div>
        </>
        )}
      </Card>
      </div>}

      {tab==='submissions'&&<div style={{marginTop:'14px'}}>
        <SubmissionsCard
          deal={deal}
          lenders={matchList}
          collapsed={!!collapsed['submissions']}
          onToggle={toggleCard}
        />
      </div>}
    </div>
  );
}
